// ✅ Trekkin.app — MapView.jsx v8.5.4 (COMPASS STATES + AUTO-FOLLOW 30s FINAL)
// -----------------------------------------------------------------------------
// 🔹 Base = v8.5.3 (mantém TUDO: GPS, viewer, grupos, LIVE multi, rotas, etc.)
// 🔹 FIXES (os 4 patches certos):
//    1) Auto-follow após 30s sem interação (SEM mudar o botão/estado)
//    2) Volta a seguir posição (re-centra) mantendo o zoom atual
//    3) Rotação automática EXCLUSIVA do estado "follow" (estado 2)
//    4) applyMapRotation único (sem versões múltiplas)
// -----------------------------------------------------------------------------
// 🔘 Estado 0 (off): segue posição + drag/zoom/rotação manual, sem rotação auto; 30s → volta a follow (re-centra)
// 🟡 Estado 1 (needle): segue posição + drag/zoom/rotação manual + agulha; sem rotação auto; 30s → volta a follow (re-centra)
// 🟢 Estado 2 (follow): segue posição + zoom; sem drag/rotação manual; rotação auto ON; 30s → volta a follow (re-centra)
// -----------------------------------------------------------------------------
// ✅ Regra de Ouro: nada removido, só acrescentado/estabilizado

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// 🔥 ANDROID WEBVIEW FIX — Leaflet polylines invisíveis no Android
if (typeof L !== "undefined") {
  L.VectorCanvas = null;
  L.Canvas = null;
}

import { usePanelsStore } from "../stores/usePanelsStore.jsx";
import { useGroupStore } from "../stores/useGroupStore.jsx";
import { useAuthStore } from "../stores/useAuthStore.jsx";
import { useLanguageStore } from "../stores/useLanguageStore";
import useGPSHybrid from "../hooks/useGPSHybrid.js";
import { useShareStore } from "../stores/useShareStore.jsx";
import { useActivityModeStore } from "../stores/useActivityModeStore.jsx";
import { useEventsStore, uploadGPX } from "../stores/useEventsStore.jsx";
import { useEventRouteRenderer } from "../hooks/useEventRouteRenderer.js";
import { useCan } from "../hooks/useCan.js";
import { useBackgroundIdle } from "../hooks/useBackgroundIdle.js";
import useNavGridSync from "../hooks/useNavGridSync.jsx";
import { logEvent } from "../stores/useLogsStore.jsx";
import { showToast } from "./NotifyToast.jsx";
import TrekkinActionModal from "./TrekkinActionModal.jsx";
import { showPlanUpgradeToast } from "../utils/planUpgradeToast.js";
import { playPing } from "../utils/sounds.js";
import { triggerAlertVibration } from "../utils/alertFeedback.js";
import { exportGPX } from "../functions/exportGPX.js";
import { parseGPX } from "../functions/parseGPX.js";
import {
  normalizeTrekkinUrl,
  buildGpxFetchUrl,
} from "../utils/normalizeTrekkinUrl.js";
import {
  toLeafletOptimizedPolyline,
  flattenLatLngPairs,
} from "../utils/polylineOptimization.js";
import { snapCoordinateToRoad } from "../utils/roadSnap.js";
import { logger } from "../utils/logger.js";
import { extractYouTubeVideoId } from "../utils/videoUrls.js";
import {
  finishPendingOp,
  startPendingOp,
  updatePendingOp,
} from "../utils/crashBreadcrumb.js";
import { queueSaveNavGridPersistent } from "../utils/navGridPersistent.js";
import {
  saveNavGridChangesToCloud,
  saveNavGridDiffToCloud,
  saveNavGridSegmentToCloud,
  saveNavGridToCloud,
} from "../utils/navGridCloudSync.js";

import "../styles/map-user-marker.css";
import {
  addSegmentToGrid,
  buildNavGridGraph,
  computeNavGridRoute,
  createGridSegment,
  ensureGraphElevationForLowAscent,
  filterNavGridByAccess,
  filterPointsByNodes,
  findNearestNodeInGraph,
  fuseSegmentEndpoints,
  haversineMeters,
  insertPointInSegmentAtIndex,
  loadNavGrid,
  movePointsNear,
  optimizeStraightSegmentPoints,
  pickGridEntryNodeForWaypoint,
  removePointsNear,
  removeSegmentFromGrid,
  saveNavGrid,
  withUpdatedAt,
} from "../utils/navGrid.js";
import {
  ACTIVITY_MODE_BIKE,
  ACTIVITY_MODE_BOTH,
  ACTIVITY_MODE_WALK,
  getActivityModeColor,
  getActivityModeLabel,
  normalizeActivityMode,
} from "../utils/activityMode.js";
import {
  detectSegmentTransition,
  normalizeSegmentDefinitions,
  persistSegmentAttempt,
} from "../utils/segments.js";
import {
  loadCachedWaypoints,
  fetchAndCacheWaypoints,
} from "../utils/waypointsCache.js";
import {
  WAYPOINTS_OPS_EVENT,
  applyWaypointOpsToList,
  enqueueRemoveWaypoint,
  loadWaypointOpsQueue,
  shouldQueueWaypointWrite,
} from "../utils/waypointOpsQueue.js";
import { db } from "../firebaseConfig.js";
import {
  get,
  limitToLast,
  onValue,
  orderByChild,
  query,
  ref,
  remove,
  runTransaction,
  set,
  update,
} from "firebase/database";
import {
  buildLiteStyle,
  buildProStyle,
  ensurePmtilesProtocol,
  FORCE_REMOTE_LITE_KEY,
  FORCE_REMOTE_PRO_KEY,
  MAPS_CLEARED_LOCAL_KEY,
  MAP_STYLE_KEY,
  PMTILES_SIZE_HINTS_DEFAULT,
  RUNTIME_MAP_STYLE_OVERRIDE_KEY,
  RUNTIME_DISABLE_CONTOURS_LITE_KEY,
  RUNTIME_DISABLE_CONTOURS_PRO_KEY,
  LOCAL_PM_CONTOURS_KEY,
  LOCAL_PM_LITE_KEY,
  LOCAL_PM_PRO_KEY,
  PORTUGAL_PMTILES_BOUNDS,
  readMapSourceModePref,
  readMapStylePref,
  resolveMapStyle,
} from "../utils/mapStyle.js";
import { createMapLibreLayer } from "../utils/leafletMaplibreLayer.js";
import { touchMapCache } from "../utils/mapCache.js";
import { statNativePmtiles } from "../utils/nativePmtilesSource.js";
import {
  localizedWaypointCategory,
  localizedWaypointDescription,
  localizedWaypointName,
} from "../utils/waypointLocalization.js";

const PUBLIC_TRAILS_MAP_CACHE_KEY = "trekkin_public_trails_map_cache_v1";
const PUBLIC_TRAILS_LIST_URL =
  "https://www.trekkin.pt/app/app-upload/list-public-gpx.php";

const waypointStartLabel = (activityMode, language) =>
  activityMode === ACTIVITY_MODE_BIKE
    ? (language === "en" ? "Start ride" : "Iniciar pedalada")
    : (language === "en" ? "Start hike" : "Iniciar caminhada");

const waypointStartIcon = (activityMode) =>
  activityMode === ACTIVITY_MODE_BIKE ? "bicycle.svg" : "boot.svg";

const NATIVE_LITE_READY_KEY = "trekkin_lite_native_ready_v1";
const normalizePartnerRecommendationCategory = (value) => {
  const name = String(value || "").trim();
  return name.toLocaleLowerCase("pt-PT") === "abrigo" ? "Abrigos" : name;
};
const NATIVE_PRO_READY_KEY = "trekkin_pro_native_ready_v1";
const NATIVE_LITE_FILE = "portugal-lite.pmtiles";
const NATIVE_PRO_FILE = "portugal-pro.pmtiles";
const MOBILE_MAP_MAX_ZOOM = 18;
const NATIVE_CONTOURS_FILE = "portugal-contours.pmtiles";
const NATIVE_LITE_BYTES_KEY = "trekkin_lite_native_bytes_v1";
const NATIVE_PRO_BYTES_KEY = "trekkin_pro_native_bytes_v1";
const NATIVE_CONTOURS_BYTES_KEY = "trekkin_contours_native_bytes_v1";
const MAP_MIN_REQUIRED_FILE_BYTES_HARD = 5 * 1024 * 1024;
const MAP_MIN_COMPLETE_RATIO = 0.98;
const RASTER_TILE_CACHE_NAME = "trekkin-remote-raster-tiles-v1";
const TRANSPARENT_TILE_URL =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
let androidNavEntryIndexPromise = null;

async function findNearestAndroidNavEntry(position, options = {}) {
  const userLat = Number(position?.lat);
  const userLon = Number(position?.lon ?? position?.lng);
  if (!Number.isFinite(userLat) || !Number.isFinite(userLon)) return null;
  if (!androidNavEntryIndexPromise) {
    const baseUrl = import.meta.env.BASE_URL || "/";
    androidNavEntryIndexPromise = fetch(`${baseUrl}nav-grid-entry-index.json`, {
      cache: "force-cache",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`nav_entry_index_${response.status}`);
        return response.json();
      })
      .then((payload) => ({
        points: Array.isArray(payload?.points) ? payload.points : [],
        segments: Array.isArray(payload?.segments) ? payload.segments : [],
      }))
      .catch((error) => {
        androidNavEntryIndexPromise = null;
        throw error;
      });
  }
  const index = await androidNavEntryIndexPromise;
  const points = index.points;
  const lonScale = Math.cos((userLat * Math.PI) / 180);
  let best = null;
  let bestScore = Infinity;
  for (const point of points) {
    const segmentIndex = Number(point?.[2]);
    const segmentMeta = index.segments?.[segmentIndex] || [];
    const premium = Number(segmentMeta?.[1]) === 1;
    const pointMode = Number(segmentMeta?.[2]) || 0;
    const requestedMode = String(options?.activityMode || "walk").toLowerCase();
    if (premium && options?.allowPremium !== true) continue;
    if (requestedMode === "bike" && pointMode !== 1 && pointMode !== 2) continue;
    if (requestedMode !== "bike" && pointMode === 1) continue;
    const lat = Number(point?.[0]);
    const lon = Number(point?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const dLat = lat - userLat;
    const dLon = (lon - userLon) * lonScale;
    const score = dLat * dLat + dLon * dLon;
    if (score < bestScore) {
      bestScore = score;
      best = { lat, lon, segmentIndex };
    }
  }
  if (!best) return null;
  return {
    node: best,
    dist: haversineMeters({ lat: userLat, lon: userLon }, best),
  };
}

async function prefetchAndroidNavRouteGrid(entry, target, options = {}) {
  const index = await androidNavEntryIndexPromise;
  if (!index?.segments?.length) return null;
  const entryLat = Number(entry?.lat);
  const entryLon = Number(entry?.lon);
  const targetLat = Number(target?.lat);
  const targetLon = Number(target?.lon ?? target?.lng);
  if (![entryLat, entryLon, targetLat, targetLon].every(Number.isFinite)) return null;
  const requestedMode = String(options?.activityMode || "walk").toLowerCase();
  const latPadding = 0.12;
  const lonPadding = 0.16;
  const minLat = Math.min(entryLat, targetLat) - latPadding;
  const maxLat = Math.max(entryLat, targetLat) + latPadding;
  const minLon = Math.min(entryLon, targetLon) - lonPadding;
  const maxLon = Math.max(entryLon, targetLon) + lonPadding;
  const ids = index.segments
    .filter((meta) => {
      const premium = Number(meta?.[1]) === 1;
      const pointMode = Number(meta?.[2]) || 0;
      if (premium && options?.allowPremium !== true) return false;
      if (requestedMode === "bike" && pointMode !== 1 && pointMode !== 2) return false;
      if (requestedMode !== "bike" && pointMode === 1) return false;
      const segMinLat = Number(meta?.[3]);
      const segMinLon = Number(meta?.[4]);
      const segMaxLat = Number(meta?.[5]);
      const segMaxLon = Number(meta?.[6]);
      return (
        segMaxLat >= minLat &&
        segMinLat <= maxLat &&
        segMaxLon >= minLon &&
        segMinLon <= maxLon
      );
    })
    .map((meta) => String(meta?.[0] || ""))
    .filter(Boolean);
  const segments = [];
  for (let offset = 0; offset < ids.length; offset += 16) {
    const chunk = ids.slice(offset, offset + 16);
    const rows = await Promise.all(
      chunk.map(async (id) => {
        try {
          const snap = await get(ref(db, `navGridSegments/${id}`));
          return snap.exists() ? snap.val() : null;
        } catch {
          return null;
        }
      })
    );
    segments.push(...rows.filter((segment) => segment?.points?.length));
  }
  if (!segments.length) return null;
  const baseGrid = options?.baseGrid || {};
  const grid = {
    ...baseGrid,
    segments,
    updatedAt: Date.now(),
  };
  saveNavGrid(grid, { skipPersistentQueue: true });
  window.dispatchEvent(
    new CustomEvent("trekkin:navgrid-updated", {
      detail: { context: "android-route-prefetch" },
    })
  );
  return grid;
}

const nativePmtilesUrl = (fileName) =>
  `native-map://${encodeURIComponent(String(fileName || "").trim())}`;

function isInsidePortugalPmtilesBounds(latLng) {
  const lat = Number(latLng?.lat);
  const lng = Number(latLng?.lng ?? latLng?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return true;
  const [minLon, minLat, maxLon, maxLat] = PORTUGAL_PMTILES_BOUNDS;
  return lng >= minLon && lng <= maxLon && lat >= minLat && lat <= maxLat;
}

function isNativeMapRuntimeNow() {
  if (typeof window === "undefined") return false;
  try {
    return (
      window?.Capacitor?.isNativePlatform?.() === true ||
      typeof window?.webkit?.messageHandlers?.nativeBridge?.postMessage === "function"
    );
  } catch {
    return false;
  }
}

function readStoredMapBytes(key) {
  try {
    const n = Number(localStorage.getItem(key) || 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeStoredMapBytes(key, value) {
  try {
    const n = Number(value || 0);
    if (Number.isFinite(n) && n > 0) {
      localStorage.setItem(key, String(Math.floor(n)));
    }
  } catch {}
}

function minLocalMapBytes(styleOrContours) {
  const style = String(styleOrContours || "").toLowerCase();
  const url =
    style === "pro"
      ? "pro"
      : style === "contours"
        ? "contours"
        : "lite";
  const expected =
    url === "pro"
      ? PMTILES_SIZE_HINTS_DEFAULT[Object.keys(PMTILES_SIZE_HINTS_DEFAULT).find((key) => key.includes("portugal-pro.pmtiles"))]
      : url === "contours"
        ? PMTILES_SIZE_HINTS_DEFAULT[Object.keys(PMTILES_SIZE_HINTS_DEFAULT).find((key) => key.includes("portugal-contours.pmtiles"))]
        : PMTILES_SIZE_HINTS_DEFAULT[Object.keys(PMTILES_SIZE_HINTS_DEFAULT).find((key) => key.includes("portugal-lite.pmtiles"))];
  const n = Number(expected || 0);
  if (!Number.isFinite(n) || n <= 0) return MAP_MIN_REQUIRED_FILE_BYTES_HARD;
  return Math.max(MAP_MIN_REQUIRED_FILE_BYTES_HARD, Math.floor(n * MAP_MIN_COMPLETE_RATIO));
}

function ensureNativeLocalKey(storageKey, fileName) {
  try {
    if (!String(localStorage.getItem(storageKey) || "").trim()) {
      localStorage.setItem(storageKey, nativePmtilesUrl(fileName));
      return true;
    }
  } catch {}
  return false;
}

function primeNativeMapLocalKeysFromStorage() {
  if (!isNativeMapRuntimeNow()) return 0;
  let changed = 0;
  try {
    const contoursBytes = readStoredMapBytes(NATIVE_CONTOURS_BYTES_KEY);
    const hasContours = contoursBytes >= minLocalMapBytes("contours");
    if (hasContours) {
      if (ensureNativeLocalKey(LOCAL_PM_CONTOURS_KEY, NATIVE_CONTOURS_FILE)) changed += 1;
    }

    const proBytes = readStoredMapBytes(NATIVE_PRO_BYTES_KEY);
    const proReady =
      (localStorage.getItem(NATIVE_PRO_READY_KEY) === "1" ||
        proBytes >= MAP_MIN_REQUIRED_FILE_BYTES_HARD) &&
      proBytes >= minLocalMapBytes("pro");
    if (proReady && hasContours) {
      if (ensureNativeLocalKey(LOCAL_PM_PRO_KEY, NATIVE_PRO_FILE)) changed += 1;
      localStorage.removeItem(FORCE_REMOTE_PRO_KEY);
    }

    const liteBytes = readStoredMapBytes(NATIVE_LITE_BYTES_KEY);
    const liteReady =
      (localStorage.getItem(NATIVE_LITE_READY_KEY) === "1" ||
        liteBytes >= MAP_MIN_REQUIRED_FILE_BYTES_HARD) &&
      liteBytes >= minLocalMapBytes("lite");
    if (liteReady && hasContours) {
      if (ensureNativeLocalKey(LOCAL_PM_LITE_KEY, NATIVE_LITE_FILE)) changed += 1;
      localStorage.removeItem(FORCE_REMOTE_LITE_KEY);
    }
  } catch {}
  return changed;
}

function hasUsableNativeLocalMapHint(style) {
  if (typeof window === "undefined") return false;
  if (style !== "pro" && style !== "lite") return false;
  try {
    if (localStorage.getItem(MAPS_CLEARED_LOCAL_KEY)) return false;
    const nativeRuntime = isNativeMapRuntimeNow();
    if (nativeRuntime) primeNativeMapLocalKeysFromStorage();

    const primaryKey = style === "pro" ? LOCAL_PM_PRO_KEY : LOCAL_PM_LITE_KEY;
    const primaryFile = style === "pro" ? NATIVE_PRO_FILE : NATIVE_LITE_FILE;
    const primaryReadyKey =
      style === "pro" ? NATIVE_PRO_READY_KEY : NATIVE_LITE_READY_KEY;
    const primaryBytesKey =
      style === "pro" ? NATIVE_PRO_BYTES_KEY : NATIVE_LITE_BYTES_KEY;
    const forceRemoteKey =
      style === "pro" ? FORCE_REMOTE_PRO_KEY : FORCE_REMOTE_LITE_KEY;

    const primaryUrl = String(localStorage.getItem(primaryKey) || "").trim();
    const contoursUrl = String(localStorage.getItem(LOCAL_PM_CONTOURS_KEY) || "").trim();
    const primaryReady =
      (!!primaryUrl ||
        localStorage.getItem(primaryReadyKey) === "1" ||
        readStoredMapBytes(primaryBytesKey) >= MAP_MIN_REQUIRED_FILE_BYTES_HARD) &&
      readStoredMapBytes(primaryBytesKey) >= minLocalMapBytes(style);
    const contoursReady =
      (!!contoursUrl ||
        readStoredMapBytes(NATIVE_CONTOURS_BYTES_KEY) >= MAP_MIN_REQUIRED_FILE_BYTES_HARD) &&
      readStoredMapBytes(NATIVE_CONTOURS_BYTES_KEY) >= minLocalMapBytes("contours");

    if (!primaryReady || !contoursReady) return false;

    if (nativeRuntime) {
      ensureNativeLocalKey(primaryKey, primaryFile);
      ensureNativeLocalKey(LOCAL_PM_CONTOURS_KEY, NATIVE_CONTOURS_FILE);
      try {
        localStorage.removeItem(forceRemoteKey);
      } catch {}
    }

    if (localStorage.getItem(forceRemoteKey) === "1") return false;

    return true;
  } catch {
    return false;
  }
}

function appendTileCacheBust(tileUrl, value, paramName = "tkretry") {
  if (!value) return tileUrl;
  const param = String(paramName || "tkretry");
  const token = String(value);
  try {
    const baseUrl =
      typeof window !== "undefined" && window.location?.href
        ? window.location.href
        : "https://trekkin.local/";
    const parsed = new URL(tileUrl, baseUrl);
    parsed.searchParams.set(param, token);
    return parsed.toString();
  } catch {
    const separator = tileUrl.includes("?") ? "&" : "?";
    return `${tileUrl}${separator}${encodeURIComponent(param)}=${encodeURIComponent(token)}`;
  }
}

function createRetryTileLayer(url, options = {}) {
  const configuredSubdomains = Array.isArray(options.subdomains)
    ? options.subdomains
    : String(options.subdomains || "abc")
        .split("")
        .filter(Boolean);
  const retrySubdomains = configuredSubdomains.length
    ? configuredSubdomains
    : ["a", "b", "c"];
  const tileSize = Number(options.tileSize || 256) || 256;
  const parentFallback = options.parentFallback === true;
  const parentFallbackLevels = Math.max(
    0,
    Math.min(3, Number(options.parentFallbackLevels || 1) || 0)
  );
  const tileTimeoutMs = Math.max(
    0,
    Number(options.tileTimeoutMs || 0) || 0
  );

  const RetriedTileLayer = L.TileLayer.extend({
    createTile(coords, done) {
      const emitTileFallback = (payload) => {
        try {
          this.options.onTileFallback?.(payload);
        } catch {}
      };
      const withCacheBust = (tileUrl, tileCoords, subdomain, attemptKey = 0) => {
        const shouldBust =
          this.options.cacheBustFirstLoad === true ||
          (this.options.cacheBustRetries === true && attemptKey > 0);
        if (!shouldBust) return tileUrl;
        return appendTileCacheBust(
          tileUrl,
          `${tileCoords.z}-${tileCoords.x}-${tileCoords.y}-${subdomain}-${attemptKey}-${Date.now()}`,
          this.options.cacheBustParam || options.cacheBustParam || "tkretry"
        );
      };
      const cleanupObjectUrl = (img) => {
        try {
          if (img.__trekkinObjectUrl) {
            URL.revokeObjectURL(img.__trekkinObjectUrl);
            img.__trekkinObjectUrl = "";
          }
        } catch {}
      };
      const assignTileImageSource = (img, tileUrl) => {
        cleanupObjectUrl(img);
        const cacheName = String(this.options.tileCacheName || "");
        const canUseCache =
          !!cacheName &&
          typeof caches !== "undefined" &&
          typeof fetch === "function" &&
          !this.options.cacheBustFirstLoad;
        if (!canUseCache) {
          img.src = tileUrl;
          return;
        }
        const seq = Number(img.__trekkinTileSeq || 0) + 1;
        img.__trekkinTileSeq = seq;
        (async () => {
          const cache = await caches.open(cacheName);
          let response = await cache.match(tileUrl, { ignoreVary: true });
          if (response) touchMapCache();
          if (!response) {
            response = await fetch(tileUrl, {
              mode: "cors",
              credentials: "omit",
              cache: "default",
            });
            if (response && response.ok) {
              cache.put(tileUrl, response.clone()).catch(() => {});
              touchMapCache();
            }
          }
          if (!response || (!response.ok && response.type !== "opaque")) {
            throw new Error("tile_cache_response_invalid");
          }
          const blob = await response.blob();
          if (!blob || !blob.size) throw new Error("tile_cache_blob_empty");
          return URL.createObjectURL(blob);
        })()
          .then((objectUrl) => {
            if (img.__trekkinTileSeq !== seq) {
              try {
                URL.revokeObjectURL(objectUrl);
              } catch {}
              return;
            }
            img.__trekkinObjectUrl = objectUrl;
            img.src = objectUrl;
          })
          .catch(() => {
            if (img.__trekkinTileSeq === seq) img.src = tileUrl;
          });
      };

      if (parentFallback) {
        const canvas = document.createElement("canvas");
        canvas.width = tileSize;
        canvas.height = tileSize;
        canvas.setAttribute("role", "presentation");
        const ctx = canvas.getContext("2d");
        let settled = false;

        let subdomains = retrySubdomains;
        try {
          const preferred = this._getSubdomain?.(coords);
          if (preferred) {
            subdomains = [
              preferred,
              ...retrySubdomains.filter((subdomain) => subdomain !== preferred),
            ];
          }
        } catch {}

        const buildUrlFor = (subdomain, tileCoords, attemptKey = 0) => {
          const data = {
            r:
              L.Browser.retina && this.options.detectRetina
                ? "@2x"
                : "",
            s: subdomain,
            x: tileCoords.x,
            y:
              this.options.tms && this._globalTileRange
                ? this._globalTileRange.max.y - tileCoords.y
                : tileCoords.y,
            z: tileCoords.z,
          };
          return withCacheBust(
            L.Util.template(this._url, { ...this.options, ...data }),
            tileCoords,
            subdomain,
            attemptKey
          );
        };

        const finish = (error = null) => {
          if (settled) return;
          settled = true;
          done(error, canvas);
        };

        const drawTileImage = (img) => {
          try {
            ctx?.clearRect(0, 0, tileSize, tileSize);
            ctx?.drawImage(img, 0, 0, tileSize, tileSize);
          } catch {}
          cleanupObjectUrl(img);
          finish(null);
        };

        const armImageTimeout = (img, onTimeout) => {
          if (!tileTimeoutMs) return null;
          const timer = window.setTimeout(() => {
            if (settled) return;
            try {
              img.onload = null;
              img.onerror = null;
              img.src = TRANSPARENT_TILE_URL;
            } catch {}
            try {
              onTimeout?.();
            } catch {}
          }, tileTimeoutMs);
          return timer;
        };

        const clearImageTimeout = (timer) => {
          if (!timer) return;
          try {
            window.clearTimeout(timer);
          } catch {}
        };

        const tryParentFallback = (level = 1) => {
          if (!ctx || level > parentFallbackLevels || coords.z - level < 0) {
            try {
              ctx?.clearRect(0, 0, tileSize, tileSize);
            } catch {}
            emitTileFallback({
              type: "failed",
              z: coords.z,
              x: coords.x,
              y: coords.y,
              level,
            });
            finish(null);
            return;
          }

          const scale = 2 ** level;
          const parentCoords = {
            x: Math.floor(coords.x / scale),
            y: Math.floor(coords.y / scale),
            z: coords.z - level,
          };
          const quadrantX = ((coords.x % scale) + scale) % scale;
          const quadrantY = ((coords.y % scale) + scale) % scale;
          const sourceSize = tileSize / scale;
          const sx = quadrantX * sourceSize;
          const sy = quadrantY * sourceSize;

          let parentAttempt = 0;
          const parentImg = new Image();
          let parentTimer = null;
          parentImg.alt = "";
          if (this.options.crossOrigin) {
            parentImg.crossOrigin =
              this.options.crossOrigin === true ? "" : this.options.crossOrigin;
          }
          parentImg.onload = () => {
            clearImageTimeout(parentTimer);
            if (settled) return;
            try {
              ctx.clearRect(0, 0, tileSize, tileSize);
              ctx.drawImage(
                parentImg,
                sx,
                sy,
                sourceSize,
                sourceSize,
                0,
                0,
                tileSize,
                tileSize
              );
              emitTileFallback({
                type: "parent-success",
                z: coords.z,
                x: coords.x,
                y: coords.y,
                parentZ: parentCoords.z,
                parentX: parentCoords.x,
                parentY: parentCoords.y,
                level,
              });
              finish(null);
              cleanupObjectUrl(parentImg);
            } catch {
              tryParentFallback(level + 1);
            }
          };
          parentImg.onerror = () => {
            clearImageTimeout(parentTimer);
            parentAttempt += 1;
            if (parentAttempt < subdomains.length) {
              parentTimer = armImageTimeout(parentImg, parentImg.onerror);
              assignTileImageSource(
                parentImg,
                buildUrlFor(
                  subdomains[parentAttempt],
                  parentCoords,
                  retrySubdomains.length + level * 10 + parentAttempt
                )
              );
              return;
            }
            tryParentFallback(level + 1);
          };
          parentTimer = armImageTimeout(parentImg, parentImg.onerror);
          assignTileImageSource(
            parentImg,
            buildUrlFor(
              subdomains[parentAttempt],
              parentCoords,
              retrySubdomains.length + level * 10 + parentAttempt
            )
          );
        };

        let attempt = 0;
        const img = new Image();
        let tileTimer = null;
        img.alt = "";
        if (this.options.crossOrigin) {
          img.crossOrigin =
            this.options.crossOrigin === true ? "" : this.options.crossOrigin;
        }
        img.onload = () => {
          clearImageTimeout(tileTimer);
          drawTileImage(img);
        };
        img.onerror = () => {
          clearImageTimeout(tileTimer);
          attempt += 1;
          if (attempt < subdomains.length) {
            tileTimer = armImageTimeout(img, img.onerror);
            assignTileImageSource(img, buildUrlFor(subdomains[attempt], coords, attempt));
            return;
          }
          emitTileFallback({
            type: "parent-start",
            z: coords.z,
            x: coords.x,
            y: coords.y,
            attempts: attempt,
          });
          tryParentFallback(1);
        };
        tileTimer = armImageTimeout(img, img.onerror);
        assignTileImageSource(img, buildUrlFor(subdomains[attempt], coords, attempt));
        return canvas;
      }

      const img = document.createElement("img");
      img.alt = "";
      img.setAttribute("role", "presentation");
      if (this.options.crossOrigin) {
        img.crossOrigin =
          this.options.crossOrigin === true ? "" : this.options.crossOrigin;
      }

      let subdomains = retrySubdomains;
      try {
        const preferred = this._getSubdomain?.(coords);
        if (preferred) {
          subdomains = [
            preferred,
            ...retrySubdomains.filter((subdomain) => subdomain !== preferred),
          ];
        }
      } catch {}

      let attempt = 0;
      let retryRound = 0;
      let tileTimer = null;
      let settled = false;
      const maxRetryRounds = Math.max(
        0,
        Number(this.options.maxRetryRounds || 0) || 0
      );
      const retryDelayMs = Math.max(
        0,
        Number(this.options.retryDelayMs || 0) || 0
      );
      const buildUrl = (subdomain, attemptKey = 0) => {
        const data = {
          r:
            L.Browser.retina && this.options.detectRetina
              ? "@2x"
              : "",
          s: subdomain,
          x: coords.x,
          y:
            this.options.tms && this._globalTileRange
              ? this._globalTileRange.max.y - coords.y
              : coords.y,
          z: this._getZoomForUrl(),
        };
        const tileCoords = {
          x: coords.x,
          y: data.y,
          z: data.z,
        };
        return withCacheBust(
          L.Util.template(this._url, { ...this.options, ...data }),
          tileCoords,
          subdomain,
          attemptKey
        );
      };
      const clearTileTimer = () => {
        if (!tileTimer) return;
        try {
          window.clearTimeout(tileTimer);
        } catch {}
        tileTimer = null;
      };
      const armTileTimer = () => {
        clearTileTimer();
        if (!tileTimeoutMs) return;
        tileTimer = window.setTimeout(() => {
          if (settled) return;
          try {
            img.onload = null;
            img.onerror = null;
            img.src = TRANSPARENT_TILE_URL;
          } catch {}
          handleTileError();
        }, tileTimeoutMs);
      };
      const isRejectedBlankTile = () => {
        if (this.options.rejectBlankTiles !== true) return false;
        try {
          const sampleSize = 12;
          const canvas = document.createElement("canvas");
          canvas.width = sampleSize;
          canvas.height = sampleSize;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (!ctx) return false;
          ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
          const pixels = ctx.getImageData(0, 0, sampleSize, sampleSize).data;
          let count = 0;
          let sumR = 0;
          let sumG = 0;
          let sumB = 0;
          let sumDelta = 0;
          for (let i = 0; i < pixels.length; i += 4) {
            const alpha = pixels[i + 3];
            if (alpha < 220) continue;
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            count += 1;
            sumR += r;
            sumG += g;
            sumB += b;
            sumDelta += Math.abs(r - g) + Math.abs(g - b) + Math.abs(r - b);
          }
          if (count < sampleSize * sampleSize * 0.75) return false;
          const avgR = sumR / count;
          const avgG = sumG / count;
          const avgB = sumB / count;
          const avgDelta = sumDelta / count;
          let variance = 0;
          for (let i = 0; i < pixels.length; i += 4) {
            if (pixels[i + 3] < 220) continue;
            variance +=
              Math.abs(pixels[i] - avgR) +
              Math.abs(pixels[i + 1] - avgG) +
              Math.abs(pixels[i + 2] - avgB);
          }
          variance /= count;
          const greyish = avgDelta < 14 && Math.abs(avgR - avgG) < 6 && Math.abs(avgG - avgB) < 6;
          const blankGrey =
            greyish &&
            variance < 16 &&
            avgR >= 170 &&
            avgR <= 235 &&
            avgG >= 170 &&
            avgG <= 235 &&
            avgB >= 170 &&
            avgB <= 235;
          if (blankGrey) {
            emitTileFallback({
              type: "blank-grey",
              z: this._getZoomForUrl(),
              x: coords.x,
              y:
                this.options.tms && this._globalTileRange
                  ? this._globalTileRange.max.y - coords.y
                  : coords.y,
              avgR: Math.round(avgR),
              avgG: Math.round(avgG),
              avgB: Math.round(avgB),
              variance: Math.round(variance),
            });
          }
          return blankGrey;
        } catch {
          return false;
        }
      };

      const handleTileError = () => {
        clearTileTimer();
        if (settled) return;
        attempt += 1;
        if (attempt < subdomains.length) {
          img.onload = handleTileLoad;
          img.onerror = handleTileError;
          armTileTimer();
          assignTileImageSource(img, buildUrl(subdomains[attempt], attempt));
          return;
        }
        if (retryRound < maxRetryRounds) {
          retryRound += 1;
          attempt = 0;
          emitTileFallback({
            type: "retry-round",
            z: this._getZoomForUrl(),
            x: coords.x,
            y:
              this.options.tms && this._globalTileRange
                ? this._globalTileRange.max.y - coords.y
                : coords.y,
            round: retryRound,
          });
          window.setTimeout(() => {
            if (settled) return;
            img.onload = handleTileLoad;
            img.onerror = handleTileError;
            armTileTimer();
            assignTileImageSource(
              img,
              buildUrl(subdomains[attempt], retryRound * 10 + attempt)
            );
          }, retryDelayMs);
          return;
        }
        emitTileFallback({
          type: "failed",
          z: this._getZoomForUrl(),
          x: coords.x,
          y:
            this.options.tms && this._globalTileRange
              ? this._globalTileRange.max.y - coords.y
              : coords.y,
          attempts: attempt,
        });
        img.onerror = null;
        img.src = TRANSPARENT_TILE_URL;
        settled = true;
        done(null, img);
      };
      const handleTileLoad = () => {
        clearTileTimer();
        if (settled) return;
        if (isRejectedBlankTile()) {
          handleTileError();
          return;
        }
        settled = true;
        done(null, img);
        window.setTimeout(() => cleanupObjectUrl(img), 1000);
      };
      img.onload = handleTileLoad;
      img.onerror = handleTileError;
      armTileTimer();
      assignTileImageSource(img, buildUrl(subdomains[attempt], attempt));
      return img;
    },
  });

  return new RetriedTileLayer(url, {
    ...options,
    subdomains: retrySubdomains,
  });
}

function readLocalFlag(key) {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function resolveBootIconUrl() {
  try {
    if (typeof window !== "undefined" && window?.location?.href) {
      return new URL("icons/boot.svg", window.location.href).toString();
    }
  } catch {}
  return "icons/boot.svg";
}

function isIosSafeModeActive() {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.__TREKKIN_IOS_SAFE_MODE__ === true ||
      window.localStorage.getItem("trekkin.ios.safeMode") === "1"
    );
  } catch {
    return false;
  }
}

function simplifyRouteByTolerance(points, toleranceMeters) {
  const source = Array.isArray(points) ? points : [];
  if (source.length <= 2) return source;
  const averageLat = source.reduce((sum, point) => sum + Number(point?.lat || 0), 0) / source.length;
  const xScale = 111320 * Math.max(0.1, Math.cos((averageLat * Math.PI) / 180));
  const yScale = 110540;
  const projected = source.map((point) => ({
    x: Number(point?.lon ?? point?.lng) * xScale,
    y: Number(point?.lat) * yScale,
  }));
  const toleranceSq = Math.max(0, Number(toleranceMeters) || 0) ** 2;
  const keep = new Uint8Array(source.length);
  keep[0] = 1;
  keep[source.length - 1] = 1;
  const stack = [[0, source.length - 1]];

  while (stack.length) {
    const [startIndex, endIndex] = stack.pop();
    const start = projected[startIndex];
    const end = projected[endIndex];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const segmentLengthSq = dx * dx + dy * dy;
    let furthestIndex = -1;
    let furthestDistanceSq = -1;

    for (let index = startIndex + 1; index < endIndex; index += 1) {
      const point = projected[index];
      let ratio = segmentLengthSq > 0
        ? ((point.x - start.x) * dx + (point.y - start.y) * dy) / segmentLengthSq
        : 0;
      ratio = Math.max(0, Math.min(1, ratio));
      const offsetX = point.x - (start.x + ratio * dx);
      const offsetY = point.y - (start.y + ratio * dy);
      const distanceSq = offsetX * offsetX + offsetY * offsetY;
      if (distanceSq > furthestDistanceSq) {
        furthestDistanceSq = distanceSq;
        furthestIndex = index;
      }
    }

    if (furthestIndex > startIndex && furthestDistanceSq > toleranceSq) {
      keep[furthestIndex] = 1;
      stack.push([startIndex, furthestIndex], [furthestIndex, endIndex]);
    }
  }

  return source.filter((_, index) => keep[index] === 1);
}

function limitRoutePointsForRender(points, maxPoints) {
  const source = Array.isArray(points) ? points : [];
  const limit = Math.max(2, Number(maxPoints) || 2);
  if (source.length <= limit) return source;

  let lowerTolerance = 0;
  let upperTolerance = 2;
  let simplified = simplifyRouteByTolerance(source, upperTolerance);
  while (simplified.length > limit && upperTolerance < 5000) {
    lowerTolerance = upperTolerance;
    upperTolerance *= 2;
    simplified = simplifyRouteByTolerance(source, upperTolerance);
  }

  for (let attempt = 0; attempt < 12; attempt += 1) {
    const tolerance = (lowerTolerance + upperTolerance) / 2;
    const candidate = simplifyRouteByTolerance(source, tolerance);
    if (candidate.length > limit) {
      lowerTolerance = tolerance;
    } else {
      upperTolerance = tolerance;
      simplified = candidate;
    }
  }
  return simplified;
}

function readBoolPref(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    return raw === "true" || raw === "1";
  } catch {
    return fallback;
  }
}

function toFirebaseSafeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.#$/\[\]]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function isMapDebugEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.__TREKKIN_DEBUG_MAP__ === true ||
      window.localStorage.getItem("trekkin.debug.map") === "1"
    );
  } catch {
    return false;
  }
}

function mapDebug(...args) {
  if (isMapDebugEnabled()) {
    console.log(...args);
  }
}

const OFF_TRAIL_THRESHOLD_VALUES = [20, 40, 60, 80, 100];
const OFF_TRAIL_ALERT_INTERVAL_MS = 60000;
const SEGMENT_PROX_ALERT_INTERVAL_MS = 60000;
const SEGMENTS_RUNTIME_DEFAULT_ON = true;
function readOffTrailThresholdPref(fallback = 40) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = Number(window.localStorage.getItem("offTrailThresholdM"));
    if (!Number.isFinite(raw)) return fallback;
    if (OFF_TRAIL_THRESHOLD_VALUES.includes(raw)) return raw;
  } catch {}
  return fallback;
}

function getArrowSpacingByZoom(zoom) {
  const z = Number.isFinite(Number(zoom)) ? Number(zoom) : 13;
  if (z >= 16) return 100;
  if (z >= 13) return 500;
  if (z >= 11) return 1000;
  return 5000;
}

function formatSecondsLabel(totalSec) {
  const sec = Math.max(0, Number(totalSec) || 0);
  const mins = Math.floor(sec / 60);
  const rem = sec - mins * 60;
  if (mins <= 0) return `${rem.toFixed(1)}s`;
  return `${mins}m ${rem.toFixed(1)}s`;
}

function readWaypointContactValue(source, keys = []) {
  if (!source || typeof source !== "object" || !Array.isArray(keys)) return "";
  for (const key of keys) {
    const raw = source?.[key];
    if (raw === undefined || raw === null) continue;
    const text = String(raw).trim();
    if (text) return text;
  }
  return "";
}

function normalizeWebsiteHref(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^[a-z]+:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function openWaypointPhone(phoneRaw) {
  const phone = String(phoneRaw || "").trim();
  if (!phone) return false;
  const normalized = phone.replace(/[^\d+]/g, "");
  const url = `tel:${normalized || phone}`;
  const launcher = window?.Capacitor?.Plugins?.AppLauncher;
  if (launcher?.openUrl) {
    launcher.openUrl({ url }).catch(() => {});
    return true;
  }
  try {
    window.location.href = url;
    return true;
  } catch {
    return false;
  }
}

function openWaypointEmail(emailRaw) {
  const email = String(emailRaw || "").trim();
  if (!email) return false;
  const url = `mailto:${email}`;
  const launcher = window?.Capacitor?.Plugins?.AppLauncher;
  if (launcher?.openUrl) {
    launcher.openUrl({ url }).catch(() => {});
    return true;
  }
  try {
    window.location.href = url;
    return true;
  } catch {
    return false;
  }
}

function openWaypointWebsite(siteRaw) {
  const site = String(siteRaw || "").trim();
  if (!site) return false;
  const url = encodeURI(normalizeWebsiteHref(site));
  const capacitor = window?.Capacitor;
  const isNativeApp = Boolean(
    capacitor?.isNativePlatform?.() ||
      (typeof capacitor?.getPlatform === "function" && capacitor.getPlatform() !== "web")
  );
  if (!isNativeApp) {
    try {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch {
      try {
        window.location.assign(url);
        return true;
      } catch {
        return false;
      }
    }
  }
  try {
    const isIOS =
      typeof navigator !== "undefined" &&
      (/iPad|iPhone|iPod/i.test(navigator.userAgent || "") ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
    const bridge = window?.webkit?.messageHandlers?.nativeBridge;
    if (isIOS && bridge?.postMessage) {
      bridge.postMessage({ action: "openExternalUrl", url });
      return true;
    }
  } catch {}
  const launcher = capacitor?.Plugins?.AppLauncher;
  if (launcher?.openUrl) {
    launcher
      .openUrl({ url })
      .then((res) => {
        if (res?.completed) return;
        try {
          window.open(url, "_system");
        } catch {}
      })
      .catch(() => {
        try {
          window.open(url, "_system");
        } catch {}
      });
    return true;
  }
  if (typeof window !== "undefined" && window?.cordova?.InAppBrowser?.open) {
    try {
      window.cordova.InAppBrowser.open(url, "_system");
      return true;
    } catch {}
  }
  try {
    window.open(url, "_system");
    return true;
  } catch {
    try {
      window.location.href = url;
      return true;
    } catch {
      return false;
    }
  }
}

function styleWaypointContactButton(btn) {
  btn.type = "button";
  btn.style.border = "1px solid #d7d7d7";
  btn.style.background = "#fff";
  btn.style.color = "#276b3e";
  btn.style.borderRadius = "10px";
  btn.style.padding = "7px 9px";
  btn.style.fontSize = "13px";
  btn.style.fontWeight = "800";
  btn.style.lineHeight = "1.2";
  btn.style.cursor = "pointer";
  btn.style.whiteSpace = "nowrap";
  btn.style.flex = "1 1 0";
  btn.style.minWidth = "0";
  btn.style.display = "flex";
  btn.style.alignItems = "center";
  btn.style.justifyContent = "center";
  btn.style.gap = "6px";
}

function createWaypointContactButtons(parent, contacts = {}, language = "pt") {
  const isEnglish = language === "en";
  const tel = String(contacts.tel || "").trim();
  const email = String(contacts.email || "").trim();
  const web = String(contacts.web || "").trim();
  if (!tel && !email && !web) return null;

  const row = L.DomUtil.create("div", "", parent);
  row.style.display = "flex";
  row.style.gap = "6px";
  row.style.flexWrap = "nowrap";
  row.style.marginTop = "2px";

  const addButton = (icon, label, onClick) => {
    const btn = L.DomUtil.create("button", "", row);
    styleWaypointContactButton(btn);
    const iconEl = icon.endsWith(".svg")
      ? L.DomUtil.create("img", "", btn)
      : L.DomUtil.create("span", "", btn);
    if (icon.endsWith(".svg")) {
      iconEl.src = `${import.meta.env.BASE_URL || "/"}icons/${icon}`;
      iconEl.alt = "";
      iconEl.style.width = "18px";
      iconEl.style.height = "18px";
    } else {
      iconEl.textContent = icon;
      iconEl.setAttribute("aria-hidden", "true");
      iconEl.style.fontSize = "17px";
      iconEl.style.lineHeight = "1";
    }
    const labelEl = L.DomUtil.create("span", "", btn);
    labelEl.textContent = label;
    L.DomEvent.on(btn, "click", (event) => {
      L.DomEvent.stop(event);
      onClick();
    });
  };

  if (tel) addButton("phone.svg", isEnglish ? "Call" : "Ligar", () => openWaypointPhone(tel));
  if (email) addButton("✉", "E-mail", () => openWaypointEmail(email));
  if (web) {
    addButton("www.svg", "WWW", () => {
      if (typeof window !== "undefined") {
        const okConfirm = window.confirm(isEnglish ? "Open the website in your browser?" : "Queres abrir o website no navegador?");
        if (!okConfirm) return;
      }
      const ok = openWaypointWebsite(web);
      if (!ok) showToast(isEnglish ? "The website could not be opened." : "Não foi possível abrir o website.", "warning");
    });
  }
  return row;
}

function buildWaypointImageCandidates(rawValue, preferStatic = false) {
  const raw = String(rawValue || "").trim();
  if (!raw) return [];
  const out = [];
  const add = (v) => {
    const s = String(v || "").trim();
    if (!s || out.includes(s)) return;
    out.push(s);
  };
  const replaceExt = (url, nextExt) =>
    url.replace(/(\.webp)(\?.*)?$/i, `${nextExt}$2`);
  const isSafeRaster = (url) => /\.(png|jpe?g)(\?.*)?$/i.test(String(url || "").trim());
  const looksWebp =
    /\.webp(\?.*)?$/i.test(raw) ||
    /[?&](format|fm)=webp\b/i.test(raw) ||
    /image\/webp/i.test(raw);

  // WKWebView/mobile-safe mode: evitar assets WEBP em popup de waypoint.
  if (preferStatic) {
    if (looksWebp) {
      mapDebug("[MAP] WEBP bloqueado em popup, a tentar fallback png/jpg:", raw);
      const png = replaceExt(raw, ".png");
      const jpg = replaceExt(raw, ".jpg");
      const jpeg = replaceExt(raw, ".jpeg");
      if (isSafeRaster(png)) add(png);
      if (isSafeRaster(jpg)) add(jpg);
      if (isSafeRaster(jpeg)) add(jpeg);
      return out;
    }
    if (isSafeRaster(raw)) add(raw);
    return out;
  }

  add(raw);
  if (/\.webp(\?.*)?$/i.test(raw)) {
    add(replaceExt(raw, ".png"));
    add(replaceExt(raw, ".jpg"));
    add(replaceExt(raw, ".jpeg"));
  }
  return out;
}

function pointToSegmentDistanceMeters(point, a, b) {
  const pLat = Number(point?.lat);
  const pLon = Number(point?.lon ?? point?.lng);
  const aLat = Number(a?.lat);
  const aLon = Number(a?.lon ?? a?.lng);
  const bLat = Number(b?.lat);
  const bLon = Number(b?.lon ?? b?.lng);
  if (
    !Number.isFinite(pLat) ||
    !Number.isFinite(pLon) ||
    !Number.isFinite(aLat) ||
    !Number.isFinite(aLon) ||
    !Number.isFinite(bLat) ||
    !Number.isFinite(bLon)
  ) {
    return Number.POSITIVE_INFINITY;
  }

  const meanLatRad = ((pLat + aLat + bLat) / 3) * (Math.PI / 180);
  const metersPerDegLat = 111132.92;
  const metersPerDegLon = Math.max(1e-6, 111320 * Math.cos(meanLatRad));

  const ax = (aLon - pLon) * metersPerDegLon;
  const ay = (aLat - pLat) * metersPerDegLat;
  const bx = (bLon - pLon) * metersPerDegLon;
  const by = (bLat - pLat) * metersPerDegLat;
  const abx = bx - ax;
  const aby = by - ay;
  const denom = abx * abx + aby * aby;
  if (denom <= 1e-6) {
    return Math.hypot(ax, ay);
  }

  const t = Math.max(0, Math.min(1, -((ax * abx + ay * aby) / denom)));
  const cx = ax + t * abx;
  const cy = ay + t * aby;
  return Math.hypot(cx, cy);
}

function minDistanceToRouteMeters(point, routePoints) {
  if (!Array.isArray(routePoints) || routePoints.length < 2) {
    return Number.POSITIVE_INFINITY;
  }

  const n = routePoints.length;
  const coarseStride = n > 9000 ? 4 : n > 4500 ? 3 : n > 2200 ? 2 : 1;

  let bestDist = Number.POSITIVE_INFINITY;
  let bestIdx = 0;

  for (let i = 0; i < n - 1; i += coarseStride) {
    const d = pointToSegmentDistanceMeters(
      point,
      routePoints[i],
      routePoints[Math.min(n - 1, i + 1)]
    );
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }

  if (coarseStride > 1) {
    const windowSize = coarseStride * 10;
    const from = Math.max(0, bestIdx - windowSize);
    const to = Math.min(n - 2, bestIdx + windowSize);
    for (let i = from; i <= to; i++) {
      const d = pointToSegmentDistanceMeters(point, routePoints[i], routePoints[i + 1]);
      if (d < bestDist) bestDist = d;
    }
  }

  if (!Number.isFinite(bestDist)) {
    return Number.POSITIVE_INFINITY;
  }
  return bestDist;
}

function readNavGridNodesFilter() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem("navGridNodesFilter");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const DEFAULT_MAP_CENTER = [41.55, -8.42];
const LAST_KNOWN_POS_KEY = "trekkin_last_known_pos_v1";
const PORTUGAL_BOUNDS = L.latLngBounds([36.8, -9.6], [42.3, -6.1]);

function readInitialMapCenter() {
  try {
    const pos = usePanelsStore.getState?.().userPosition;
    if (
      pos &&
      Number.isFinite(Number(pos.lat)) &&
      Number.isFinite(Number(pos.lon ?? pos.lng))
    ) {
      return [Number(pos.lat), Number(pos.lon ?? pos.lng)];
    }
  } catch {}
  if (typeof window === "undefined") return DEFAULT_MAP_CENTER;
  try {
    const raw = window.localStorage.getItem(LAST_KNOWN_POS_KEY);
    if (!raw) return DEFAULT_MAP_CENTER;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lon = Number(parsed?.lon ?? parsed?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return [lat, lon];
    }
  } catch {}
  return DEFAULT_MAP_CENTER;
}

function openExternalUrl(url, opts = {}) {
  if (!url) return;
  const isHttp = /^https?:/i.test(url);
  const forceBrowser = !!opts.forceBrowser;
  const browser = window?.Capacitor?.Plugins?.Browser;
  if (!isHttp) {
    try {
      window.location.href = url;
      return;
    } catch {}
  }
  if (forceBrowser && browser?.open) {
    browser.open({ url }).catch(() => {});
    return;
  }
  try {
    window.open(url, "_system");
  } catch {}
  try {
    window.location.href = url;
    return;
  } catch {}
  if (browser?.open) {
    browser.open({ url }).catch(() => {});
  }
}

function openNativeDirections(lat, lon, opts = {}) {
  const latStr = String(lat);
  const lonStr = String(lon);
  const origin = opts?.origin;
  const originLat = Number(origin?.lat);
  const originLon = Number(origin?.lon ?? origin?.lng);
  const hasOrigin = Number.isFinite(originLat) && Number.isFinite(originLon);
  const originRaw = hasOrigin ? `${originLat},${originLon}` : "";
  const destRaw = `${latStr},${lonStr}`;
  const isIOS =
    typeof navigator !== "undefined" &&
    (/iPad|iPhone|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
  const isAndroid =
    typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

  const appleScheme = hasOrigin
    ? `maps://?saddr=${originRaw}&daddr=${destRaw}&dirflg=d`
    : `maps://?daddr=${destRaw}&dirflg=d`;
  const appleWeb = hasOrigin
    ? `https://maps.apple.com/?saddr=${originRaw}&daddr=${destRaw}&dirflg=d`
    : `https://maps.apple.com/?daddr=${destRaw}&dirflg=d`;
  const navIntent = `google.navigation:q=${destRaw}`;
  const geoUrl = `geo:0,0?q=${destRaw}`;
  const gmaps = hasOrigin
    ? `https://www.google.com/maps/dir/?api=1&origin=${originRaw}&destination=${destRaw}&travelmode=driving`
    : `https://www.google.com/maps/dir/?api=1&destination=${destRaw}&travelmode=driving`;

  if (isIOS) {
    const launcher = window?.Capacitor?.Plugins?.AppLauncher;
    if (launcher?.openUrl) {
      launcher.openUrl({ url: appleScheme }).catch(() => {
        openExternalUrl(appleWeb, { forceBrowser: true });
      });
      return;
    }
    openExternalUrl(appleWeb, { forceBrowser: true });
    return;
  }
  if (isAndroid) {
    openExternalUrl(navIntent);
    setTimeout(() => {
      openExternalUrl(geoUrl);
    }, 250);
    setTimeout(() => {
      openExternalUrl(gmaps, { forceBrowser: true });
    }, 600);
    return;
  }
  openExternalUrl(gmaps, { forceBrowser: true });
}

function findNearestGraphNode(graph, point) {
  return findNearestNodeInGraph(graph, point);
}

function pickConnectedEntryNode(graph, userPos, waypointPos, opts = {}) {
  return pickGridEntryNodeForWaypoint(graph, userPos, waypointPos, opts);
}

function findNearestRoutableNodeInGraph(graph, point) {
  const nearest = findNearestNodeInGraph(graph, point);
  const edges = graph?.edges && typeof graph.edges === "object" ? graph.edges : {};
  const nodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
  const hasDegree = (node) => {
    const id = String(node?.id || "");
    if (!id) return false;
    const neighbors = edges[id] && typeof edges[id] === "object" ? Object.keys(edges[id]) : [];
    return neighbors.length > 0;
  };
  if (nearest?.node && hasDegree(nearest.node)) return nearest;

  let best = null;
  let bestDist = Infinity;
  nodes.forEach((n) => {
    if (!n || !hasDegree(n)) return;
    if (!Number.isFinite(Number(n.lat)) || !Number.isFinite(Number(n.lon ?? n.lng))) return;
    const d = haversineMeters(
      { lat: Number(point?.lat), lon: Number(point?.lon ?? point?.lng) },
      { lat: Number(n.lat), lon: Number(n.lon ?? n.lng) }
    );
    if (d < bestDist) {
      bestDist = d;
      best = n;
    }
  });
  if (best) return { node: best, dist: bestDist };
  return nearest || null;
}

function createRegionalRoutingGrid(grid, routePoints, opts = {}) {
  const points = (Array.isArray(routePoints) ? routePoints : [])
    .map((point) => ({
      lat: Number(point?.lat),
      lon: Number(point?.lon ?? point?.lng),
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon));
  const segments = Array.isArray(grid?.segments) ? grid.segments : [];
  if (points.length < 2 || !segments.length) return grid;
  const minLat = Math.min(...points.map((point) => point.lat));
  const maxLat = Math.max(...points.map((point) => point.lat));
  const minLon = Math.min(...points.map((point) => point.lon));
  const maxLon = Math.max(...points.map((point) => point.lon));
  const diagonal = haversineMeters(
    { lat: minLat, lon: minLon },
    { lat: maxLat, lon: maxLon }
  );
  const lowAscent = opts?.preference === "low_ascent";
  const bufferMeters = Math.max(
    lowAscent ? 5000 : 2500,
    Math.min(lowAscent ? 20000 : 10000, diagonal * (lowAscent ? 0.28 : 0.18))
  );
  const centerLat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const latPadding = bufferMeters / 111320;
  const lonPadding =
    bufferMeters /
    Math.max(20000, 111320 * Math.cos((centerLat * Math.PI) / 180));
  const south = minLat - latPadding;
  const north = maxLat + latPadding;
  const west = minLon - lonPadding;
  const east = maxLon + lonPadding;
  const selected = segments.filter((segment) =>
    (Array.isArray(segment?.points) ? segment.points : []).some((point) => {
      const lat = Number(point?.lat);
      const lon = Number(point?.lon ?? point?.lng);
      return (
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        lat >= south &&
        lat <= north &&
        lon >= west &&
        lon <= east
      );
    })
  );
  const stamp = Math.abs(
    Math.round(south * 1e5) * 31 +
      Math.round(west * 1e5) * 17 +
      Math.round(north * 1e5) * 13 +
      Math.round(east * 1e5) * 7
  );
  return {
    ...grid,
    segments: selected,
    updatedAt: Number(grid?.updatedAt || 0) + stamp,
  };
}

function findConnectedComponentRoute(graph, fromPoint, toPoint, opts = {}) {
  const startNearest = findNearestNodeInGraph(graph, fromPoint);
  const startId = String(startNearest?.node?.id || "");
  const graphNodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
  const edges = graph?.edges && typeof graph.edges === "object" ? graph.edges : {};
  const nodesById =
    graph?.nodesById && typeof graph.nodesById === "object" ? graph.nodesById : {};
  if (!startId || !nodesById[startId] || !graphNodes.length) return null;

  const component = [];
  const seen = new Set([startId]);
  const queue = [startId];
  while (queue.length) {
    const cur = queue.shift();
    const node = nodesById[cur];
    if (
      node &&
      Number.isFinite(Number(node.lat)) &&
      Number.isFinite(Number(node.lon ?? node.lng))
    ) {
      component.push({
        id: String(node.id || cur),
        lat: Number(node.lat),
        lon: Number(node.lon ?? node.lng),
      });
    }
    const neighbors = edges[cur] && typeof edges[cur] === "object" ? edges[cur] : {};
    Object.keys(neighbors).forEach((nid) => {
      if (!nid || seen.has(nid) || !nodesById[nid]) return;
      seen.add(nid);
      queue.push(nid);
    });
  }
  if (!component.length) return null;

  const maxCandidates = Math.max(20, Math.min(Number(opts?.maxCandidates) || 80, 300));
  const candidates = component
    .map((n) => ({
      ...n,
      dToTarget: haversineMeters(
        { lat: Number(n.lat), lon: Number(n.lon) },
        { lat: Number(toPoint.lat), lon: Number(toPoint.lon) }
      ),
    }))
    .sort((a, b) => Number(a.dToTarget) - Number(b.dToTarget))
    .slice(0, maxCandidates);
  if (!candidates.length) return null;

  for (const cand of candidates) {
    const targetId = String(cand?.id || "");
    if (!targetId || targetId === startId || !nodesById[targetId]) continue;
    const parent = {};
    const seenPath = new Set([startId]);
    const q = [startId];
    let found = false;
    while (q.length && !found) {
      const cur = q.shift();
      const neighbors = edges[cur] && typeof edges[cur] === "object" ? edges[cur] : {};
      for (const nid of Object.keys(neighbors)) {
        if (!nid || seenPath.has(nid) || !nodesById[nid]) continue;
        seenPath.add(nid);
        parent[nid] = cur;
        if (nid === targetId) {
          found = true;
          break;
        }
        q.push(nid);
      }
    }
    if (!found) continue;
    const pathIds = [targetId];
    let cur = targetId;
    while (cur !== startId) {
      cur = parent[cur];
      if (!cur) break;
      pathIds.unshift(cur);
    }
    const path = pathIds
      .map((id) => nodesById[id])
      .filter(Boolean)
      .map((n) => ({ lat: Number(n.lat), lon: Number(n.lon ?? n.lng) }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    if (path.length > 1) {
      let distanceMeters = 0;
      for (let i = 1; i < path.length; i += 1) {
        distanceMeters +=
          haversineMeters(
            { lat: Number(path[i - 1].lat), lon: Number(path[i - 1].lon) },
            { lat: Number(path[i].lat), lon: Number(path[i].lon) }
          ) || 0;
      }
      return {
        path,
        distanceMeters,
        componentSize: component.length,
        startId,
        targetId,
        targetDistMeters: Number(cand.dToTarget || 0),
      };
    }
  }
  return null;
}

const ALERT_CATEGORY_LABELS = {
  fauna: "Fauna",
  trilho: "Trilho",
  seguranca: "Seguranca",
  criticos: "Alertas",
};

const ALERT_CATEGORY_ICONS = {
  fauna: "pawprints.svg",
  trilho: "trail.svg",
  seguranca: "security.svg",
  criticos: "alert.svg",
};

const ALERT_CATEGORY_COLORS = {
  fauna: "#1b6e40",
  trilho: "#0d6efd",
  seguranca: "#f59f0a",
  criticos: "#dc3545",
};

const ALERT_TTL_MS = 48 * 60 * 60 * 1000;

const ALERT_PROXIMITY_METERS = 30;
const ALERT_PROXIMITY_AUTO_CLOSE_MS = 30000;
const ALERT_PROXIMITY_COOLDOWN_MS = 5 * 60 * 1000;
const EXTRA_EVENT_ROUTE_COLORS = [
  "#ff006e",
  "#0077b6",
  "#ff8c00",
  "#00b894",
  "#6f42c1",
  "#198754",
  "#dc3545",
  "#f0ad4e",
];

function toAlertText(value, fallback = "") {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") return value.label || value.value || fallback;
  return fallback;
}

function getAlertSubLabel(entry) {
  if (!entry) return "";
  if (entry.category === "fauna") {
    return entry.animal || entry.animalKey || "";
  }
  return toAlertText(entry.detail, "");
}

function getAlertKey(entry) {
  const category = entry?.category || "all";
  const id = entry?.id || entry?.ts || "unknown";
  return `${category}:${id}`;
}

function colorForEventId(eventId) {
  const str = String(eventId || "");
  let sum = 0;
  for (let i = 0; i < str.length; i++) {
    sum += str.charCodeAt(i);
  }
  return EXTRA_EVENT_ROUTE_COLORS[sum % EXTRA_EVENT_ROUTE_COLORS.length];
}

// -------------------------------------------------------------
// Bearing seguro (Math.* em tudo)
// -------------------------------------------------------------
function bearingDeg(a, b) {
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// -------------------------------------------------------------
// Compass helper
// -------------------------------------------------------------
function getCompassHeading(e) {
  if (typeof e.webkitCompassHeading === "number") return e.webkitCompassHeading;
  if (typeof e.alpha === "number") return 360 - e.alpha;
  return null;
}

// -------------------------------------------------------------
// 🧭 ROTAÇÃO DO MAPA — WRAPPER EXTERNO (ÚNICO)
// -------------------------------------------------------------
const MAP_ROTATE_SUFFIX_RE = /\s*rotate\(-?\d+(?:\.\d+)?deg\)\s*$/i;

function applyRotationAwarePopupTransform(el, counterDeg) {
  if (!el) return;
  const currentTransform = String(el.style.transform || "");
  const leafletTransform = currentTransform.replace(MAP_ROTATE_SUFFIX_RE, "").trim();
  el.style.transform = leafletTransform
    ? `${leafletTransform} rotate(${counterDeg}deg)`
    : `rotate(${counterDeg}deg)`;
  el.style.transformOrigin = "50% 100%";
  el.style.willChange = "transform";
}

function applyMapRotation(map, deg) {
  const target = map?._rotateTarget;
  if (!target) return;

  const rotationDeg = Number.isFinite(Number(deg)) ? Number(deg) : 0;
  map._rotationDeg = rotationDeg;
  target.style.transform = `translate(-50%, -50%) rotate(${rotationDeg}deg)`;
  target.style.transformOrigin = "50% 50%";
  target.style.willChange = "transform";

  // Mantém labels de membros legíveis quando o mapa roda.
  const memberLabels = target.querySelectorAll(".tk-member-label");
  const rotationAwarePopups = target.querySelectorAll(
    ".leaflet-popup.tk-waypoint-popup, " +
      ".leaflet-popup.tk-navsegment-popup, " +
      ".leaflet-popup.tk-segment-popup"
  );
  const waypointMarkerVisuals = target.querySelectorAll(".tk-waypoint-marker-visual");
  const counterDeg = -rotationDeg;
  memberLabels.forEach((el) => {
    el.style.transform = `rotate(${counterDeg}deg)`;
    el.style.transformOrigin = "50% 50%";
    el.style.willChange = "transform";
  });
  waypointMarkerVisuals.forEach((el) => {
    el.style.transform = `rotate(${counterDeg}deg)`;
    el.style.transformOrigin = "50% 50%";
    el.style.willChange = "transform";
  });
  // Preserva o translate3d do Leaflet no root do popup e contra-roda o popup
  // inteiro. Rodar body e tip em separado desalinha a ponta do balão.
  rotationAwarePopups.forEach((el) => {
    applyRotationAwarePopupTransform(el, counterDeg);
    el.querySelectorAll(
      ".leaflet-popup-content-wrapper, .leaflet-popup-tip-container"
    ).forEach((child) => {
      child.style.removeProperty("transform");
      child.style.removeProperty("transform-origin");
      child.style.removeProperty("will-change");
    });
  });
}

function getEventClientPoint(event) {
  const source = event?.originalEvent || event;
  if (
    Number.isFinite(Number(source?.clientX)) &&
    Number.isFinite(Number(source?.clientY))
  ) {
    return { x: Number(source.clientX), y: Number(source.clientY) };
  }
  const touch = source?.changedTouches?.[0] || source?.touches?.[0];
  if (
    Number.isFinite(Number(touch?.clientX)) &&
    Number.isFinite(Number(touch?.clientY))
  ) {
    return { x: Number(touch.clientX), y: Number(touch.clientY) };
  }
  return null;
}

function getRotationAwareContainerPoint(map, event) {
  const target = map?._rotateTarget || map?.getContainer?.();
  if (!target || typeof target.getBoundingClientRect !== "function") return null;
  const clientPoint = getEventClientPoint(event);
  if (!clientPoint) return null;

  const rect = target.getBoundingClientRect();
  const width = Number(target.clientWidth || target.offsetWidth || 0);
  const height = Number(target.clientHeight || target.offsetHeight || 0);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  const rotationDeg = Number(map?._rotationDeg || 0);
  const radians = (-rotationDeg * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const dx = clientPoint.x - centerX;
  const dy = clientPoint.y - centerY;
  const x = width / 2 + dx * cos - dy * sin;
  const y = height / 2 + dx * sin + dy * cos;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return L.point(x, y);
}

function getRotationAwareLatLng(map, event) {
  const point = getRotationAwareContainerPoint(map, event);
  if (!point || typeof map?.containerPointToLatLng !== "function") {
    return event?.latlng || null;
  }
  try {
    const latlng = map.containerPointToLatLng(point);
    if (
      Number.isFinite(Number(latlng?.lat)) &&
      Number.isFinite(Number(latlng?.lng))
    ) {
      return latlng;
    }
  } catch {}
  return event?.latlng || null;
}

function touchPairAngleDeg(touches) {
  if (!touches || touches.length < 2) return null;
  const first = touches[0];
  const second = touches[1];
  return (
    Math.atan2(second.clientY - first.clientY, second.clientX - first.clientX) *
    (180 / Math.PI)
  );
}

function shortestAngleDeltaDeg(fromDeg, toDeg) {
  return ((toDeg - fromDeg + 540) % 360) - 180;
}

function enableTwoFingerMapRotation(map) {
  const container = map?.getContainer?.();
  if (!container) return;

  let previousAngle = null;

  const begin = (event) => {
    if (event.touches?.length !== 2 || map._manualRotationEnabled === false) {
      previousAngle = null;
      return;
    }
    previousAngle = touchPairAngleDeg(event.touches);
  };

  const rotate = (event) => {
    if (event.touches?.length !== 2 || map._manualRotationEnabled === false) {
      previousAngle = null;
      return;
    }

    const nextAngle = touchPairAngleDeg(event.touches);
    if (!Number.isFinite(previousAngle) || !Number.isFinite(nextAngle)) {
      previousAngle = nextAngle;
      return;
    }

    const delta = shortestAngleDeltaDeg(previousAngle, nextAngle);
    previousAngle = nextAngle;
    if (Math.abs(delta) < 0.05) return;

    applyMapRotation(map, Number(map._rotationDeg || 0) + delta);
  };

  const end = (event) => {
    if (event.touches?.length === 2 && map._manualRotationEnabled !== false) {
      previousAngle = touchPairAngleDeg(event.touches);
    } else {
      previousAngle = null;
    }
  };

  container.addEventListener("touchstart", begin, { capture: true, passive: true });
  container.addEventListener("touchmove", rotate, { capture: true, passive: true });
  container.addEventListener("touchend", end, { capture: true, passive: true });
  container.addEventListener("touchcancel", end, { capture: true, passive: true });
}

function enableRotationAwareMapDragging(map) {
  const draggable = map?.dragging?._draggable;
  if (!draggable || draggable._trekkinRotationAware) return;

  draggable._trekkinRotationAware = true;
  draggable.on("predrag", () => {
    const rotationDeg = Number(map._rotationDeg || 0);
    if (!rotationDeg || !draggable._startPos || !draggable._newPos) return;

    const delta = draggable._newPos.subtract(draggable._startPos);
    const radians = (-rotationDeg * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    draggable._newPos = draggable._startPos.add(
      L.point(
        delta.x * cos - delta.y * sin,
        delta.x * sin + delta.y * cos
      )
    );
  });
}

// -------------------------------------------------------------
// Helper: obter evento ativo de um grupo + flag LIVE
// -------------------------------------------------------------
function getActiveEventForGroupSafe(groups, gidOrGroup, events) {
  if (!gidOrGroup) return { group: null, activeEvent: null, isLive: false };

  const groupHint =
    gidOrGroup && typeof gidOrGroup === "object" ? gidOrGroup : null;
  const gid =
    groupHint?.id ||
    groupHint?.groupId ||
    groupHint?.firebaseId ||
    groupHint?.key ||
    (typeof gidOrGroup === "string" ? gidOrGroup : null);
  if (!gid && !groupHint) {
    return { group: null, activeEvent: null, isLive: false };
  }

  const groupsArr = Array.isArray(groups)
    ? groups
    : groups && typeof groups === "object"
      ? Object.values(groups)
      : [];

  let group =
    groupsArr.find(
      (g) =>
        g.id === gid || g.groupId === gid || g.firebaseId === gid || g.key === gid
    ) || null;

  if (!group && groupHint) {
    group = groupHint;
  }
  if (!group) return { group: null, activeEvent: null, isLive: false };

  let activeEventId =
    group.activeEventId || group.activeEvent || group.eventId || null;

  const eventsArr = Array.isArray(events)
    ? events
    : events && typeof events === "object"
      ? Object.values(events)
      : [];

  let activeEventFromList =
    activeEventId && eventsArr.length
      ? eventsArr.find(
          (ev) =>
            ev?.id === activeEventId ||
            ev?.id === String(activeEventId) ||
            ev?.eventId === activeEventId
        ) || null
      : null;
  const activeEventFromMap =
    activeEventId && events && typeof events === "object"
      ? events[activeEventId] || null
      : null;
  const liveMatch =
    eventsArr.length
      ? (() => {
          const gidCandidates = new Set(
            [group?.id, group?.groupId, group?.firebaseId, group?.key].filter(Boolean)
          );
          if (!gidCandidates.size) return null;
          return (
            eventsArr.find(
              (ev) => ev?.isLive && ev?.groupId && gidCandidates.has(ev?.groupId)
            ) || null
          );
        })()
      : null;

  let activeEvent =
    (activeEventFromList?.isLive === true ? activeEventFromList : null) ||
    (activeEventFromMap?.isLive === true ? activeEventFromMap : null) ||
    liveMatch ||
    activeEventFromList ||
    activeEventFromMap ||
    (typeof group === "object" && group?.event ? group.event : null);

  // Se o grupo tem activeEventId mas ainda não há payload em events/eventsLive,
  // mantemos apenas o id. Não marcamos como LIVE: isso cria markers temporários
  // que são removidos quando o payload real chega, causando flicker no mapa.
  if (!activeEvent && activeEventId) {
    activeEvent = {
      id: activeEventId,
      eventId: activeEventId,
      groupId: group?.id || group?.groupId || group?.firebaseId || group?.key || null,
      isLive: false,
      __fallback: true,
    };
  }

  if (!activeEvent && eventsArr.length) {
    const gidCandidates = new Set(
      [group?.id, group?.groupId, group?.firebaseId, group?.key].filter(Boolean)
    );
    if (gidCandidates.size) {
      const fallbackLiveMatch = eventsArr.find(
        (ev) => ev?.isLive && gidCandidates.has(ev?.groupId)
      );
      if (fallbackLiveMatch) {
        activeEvent = fallbackLiveMatch;
        activeEventId = fallbackLiveMatch?.id || fallbackLiveMatch?.eventId || activeEventId;
      }
    }
  }

  const isLive = !!activeEvent?.isLive;
  return { group, activeEvent, isLive };
}

// -------------------------------------------------------------
// 🧭 Resolver direção do mapa (bússola vs movimento)
// -------------------------------------------------------------
const SPEED_THRESHOLD = 0.8; // m/s ≈ 3 km/h

function resolveMapBearing({ speed, gpsBearing, compassHeading, lastBearing }) {
  if (speed < SPEED_THRESHOLD) {
    if (Number.isFinite(compassHeading)) return compassHeading;
    if (Number.isFinite(gpsBearing)) return gpsBearing;
    return lastBearing ?? null;
  }

  if (Number.isFinite(gpsBearing)) return gpsBearing;
  return lastBearing ?? null;
}

// -------------------------------------------------------------
// 🧭 Suavização de rotação (SEM EPS, SEM BLOQUEIOS)
// -------------------------------------------------------------
function smoothBearing(prev, next, factor = 0.18) {
  if (prev == null) return next;

  let delta = ((next - prev + 540) % 360) - 180;
  return (prev + delta * factor + 360) % 360;
}

export default function MapView({ hideTopControls }) {
  const language = useLanguageStore((state) => state.language);
  const nativePlatform = (() => {
    try {
      return String(window?.Capacitor?.getPlatform?.() || "").toLowerCase();
    } catch {
      return "";
    }
  })();
  const isIOS =
    nativePlatform === "ios" ||
    (typeof navigator !== "undefined" &&
      (/iPad|iPhone|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)));
  const isAndroid =
    nativePlatform === "android" ||
    (typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent));
  const [mapStyleEpoch, setMapStyleEpoch] = useState(0);
  const [nativeMapPrimedEpoch] = useState(() => primeNativeMapLocalKeysFromStorage());
  const [nativeMapProbeDone, setNativeMapProbeDone] = useState(
    () => !isNativeMapRuntimeNow() || nativeMapPrimedEpoch > 0
  );
  const [networkOnline, setNetworkOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine !== false
  );
  const backgroundIdle = useBackgroundIdle();
  const maxEditDragMarkers = isIOS ? 260 : isAndroid ? 500 : 900;
  const maxEditPointsLoad = isIOS ? 6000 : isAndroid ? 10000 : 18000;

  // -------------------------------------------------------------
  // 🧭 SMOOTH COMPASS (iOS) — anti-solavancos
  // -------------------------------------------------------------
  const smoothHeadingRef = useRef(null);
  const lastRawHeadingRef = useRef(null);
  const base = import.meta.env.BASE_URL || "/";

  const can = useCan();
  const activityMode = useActivityModeStore((s) => s.activityMode);
  const canUsePremiumSegments = can("PanelNavGridAccess", "use_premium_segments");
  const canManageWaypoints = can("PanelWaypointsAdmin", "open");
  const canNavigateWaypoints = can("PanelWaypoints", "navigate");
  const canManageNavGrid = can("PanelNavGridAdmin", "open");
  const canPlanRoute = can("PanelNav", "plan_route");
  const canUseMapbox = false;
  const canUseLite = can("PanelMap", "use_lite");
  const canUsePro = can("PanelMap", "use_pro");
  const canViewFaunaAlerts = can("PanelAlerts", "view_alerts_fauna");
  const canViewTrailAlerts = can("PanelAlerts", "view_alerts_trilho");
  const canViewSafetyAlerts = can("PanelAlerts", "view_alerts_seguranca");
  const canViewCriticalAlerts = can("PanelAlerts", "view_alerts_criticos");
  const canViewMapAlerts =
    canViewFaunaAlerts ||
    canViewTrailAlerts ||
    canViewSafetyAlerts ||
    canViewCriticalAlerts;

function smoothAngle(prev, next, factor = 0.12) {
  if (prev == null) return next;
  let delta = ((next - prev + 540) % 360) - 180;
  return (prev + delta * factor + 360) % 360;
}

function bearingDegrees(a, b) {
  if (!a || !b) return 0;
  const lat1 = (Number(a.lat) * Math.PI) / 180;
  const lat2 = (Number(b.lat) * Math.PI) / 180;
  const dLon = ((Number(b.lon) - Number(a.lon)) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const brng = (Math.atan2(y, x) * 180) / Math.PI;
  return (brng + 360) % 360;
}

const LIVE_WAYPOINT_DISTANCE_METERS = 300;
const LIVE_WAYPOINT_FETCH_INTERVAL_MS = 5 * 60 * 1000;

function cleanupOrphanMapLibreBaseLayers(map, { reason = "unknown", platform = "" } = {}) {
  if (!map) return { removedLayers: 0, removedDom: 0 };
  let removedLayers = 0;
  let removedDom = 0;
  const staleBaseLayerPattern =
    /\b(tk-base-lite-layer|leaflet-maplibre-layer|maplibregl-map|tk-osm-base-tile|tk-lite-base-tile)\b/;
  try {
    map.eachLayer?.((layer) => {
      try {
        const cls = [
          layer?.options?.className,
          layer?.getContainer?.()?.className,
          layer?._container?.className,
        ]
          .filter(Boolean)
          .join(" ");
        if (!staleBaseLayerPattern.test(String(cls))) {
          return;
        }
        map.removeLayer(layer);
        removedLayers += 1;
      } catch {}
    });
  } catch {}
  try {
    const tilePane = map.getPane?.("tilePane") || map.getPanes?.()?.tilePane;
    tilePane
      ?.querySelectorAll?.(
        ".tk-base-lite-layer, .leaflet-maplibre-layer, .maplibregl-map, .maplibregl-canvas-container, .maplibregl-canvas, .tk-osm-base-tile, .tk-lite-base-tile"
      )
      ?.forEach((el) => {
        try {
          const target =
            el.closest?.(".tk-base-lite-layer, .leaflet-maplibre-layer") ||
            el.closest?.(".leaflet-layer") ||
            el;
          if (target?.parentNode) {
            target.parentNode.removeChild(target);
            removedDom += 1;
          }
        } catch {}
      });
  } catch {}
  if (removedLayers || removedDom) {
    logEvent("warn", "MAP_PRO_REMOTE_ORPHAN_LAYER_CLEANUP", {
      platform,
      reason,
      removedLayers,
      removedDom,
    });
  }
  return { removedLayers, removedDom };
}

function cleanupProHillshadeTiles(map, { reason = "unknown", platform = "" } = {}) {
  if (!map) return 0;
  let removedDom = 0;
  try {
    const tilePane = map.getPane?.("tilePane") || map.getPanes?.()?.tilePane;
    tilePane?.querySelectorAll?.(".tk-pro-hillshade")?.forEach((el) => {
      try {
        if (el?.parentNode) {
          el.parentNode.removeChild(el);
          removedDom += 1;
        }
      } catch {}
    });
  } catch {}
  if (removedDom) {
    logEvent("warn", "MAP_PRO_HILLSHADE_DOM_CLEANUP", {
      platform,
      reason,
      removedDom,
    });
  }
  return removedDom;
}

  // Viewer mode
  const isViewerMode = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.has("share");
    } catch {
      return false;
    }
  })();

  useNavGridSync({ enabled: !isViewerMode });

  const gps = useGPSHybrid();
  const {
    lastFix,
    lastLiveFix,
    path,
    startTracking,
    stopTracking,
    requestAndroidAlwaysPermission,
    requestAndroidForegroundPermission,
  } = gps;

  const mapRef = useRef(null);
  const userMarkerRef = useRef(null);
  const guideHaloRef = useRef(null);
  const isZoomingRef = useRef(false);
  const isDraggingRef = useRef(false);

  const accuracyRef = useRef(null);
  const baseLayerRef = useRef(null);
  const trailPreviewLayerRef = useRef(null);
  const trailStagesLayerRef = useRef(null);
  const trailStartAssistLayerRef = useRef(null);
  const baseMapLayerRef = useRef(null);
  const fallbackRasterRef = useRef(null);
  const liteRasterRef = useRef(null);
  const proRasterBaseRef = useRef(null);
  const proRasterShadeRef = useRef(null);
  const baseDirectionLayerRef = useRef(null);
  const eventDirectionLayerRef = useRef(null);
  const recordLayerRef = useRef(null);
  const trailBaseRenderSeqRef = useRef(0);
  const trailBaseHadPointsRef = useRef(false);
  const lineAnomalyRef = useRef({ ts: 0, key: null });
  const pathLatLngsRef = useRef([]);
  const androidRenderWindowRef = useRef([]);
  const androidRenderLastPointRef = useRef(null);
  const androidWasRecordingRef = useRef(false);
  const androidMapBaseVisibleAtRef = useRef(0);
  const androidMapBaseRefreshAtRef = useRef(0);
  const androidMapPaintNudgeAtRef = useRef(0);
  const androidTrackBaseRepaintAtRef = useRef(0);
  const androidTrackRenderSignatureRef = useRef("");
  const androidTrackRenderSignatureTsRef = useRef(0);
  const appResumePerfRef = useRef(null);
  const lastUiGpsTsRef = useRef(0);
  const lastAndroidResumeCameraTsRef = useRef(0);
  const lastAndroidResumeCameraSkipLogRef = useRef(0);
  const eventRouteLayerRef = useRef(null);
  const lastEventRouteKeyRef = useRef(null);
  const altEventRoutesRef = useRef({});
  const lastRegistrationEventIdRef = useRef(null);
  const extraEventRoutesRef = useRef({});
  const extraEventFetchRef = useRef({});
  const firstFixRef = useRef(false);
  const lastInteractionRef = useRef(Date.now());
  const viewerPathRef = useRef(null);
  const viewerCenteredOnWalkerRef = useRef(false);
  const smoothBearingRef = useRef(null);
  const lastRotationTsRef = useRef(0);
  // ANDROID: impede replay visual de fixes antigos ao sair de BG
  const lastMarkerFixTsRef = useRef(0);
  const [directionArrowSpacingM, setDirectionArrowSpacingM] = useState(null);
  const [directionZoomTick, setDirectionZoomTick] = useState(0);
  const [zoomLevelLabel, setZoomLevelLabel] = useState(null);
  const [showZoomLevelLabel, setShowZoomLevelLabel] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapCenterOutsidePmtilesBounds, setMapCenterOutsidePmtilesBounds] =
    useState(false);
  const zoomLevelHideTimeoutRef = useRef(null);

  const renderDirectionMarkers = useCallback((layerRef, sourcePoints, options = {}) => {
    const layer = layerRef?.current;
    if (!layer) return;
    layer.clearLayers();

    const normalized = (Array.isArray(sourcePoints) ? sourcePoints : [])
      .map((pt) => {
        if (Array.isArray(pt) && pt.length >= 2) {
          const lat = Number(pt[0]);
          const lon = Number(pt[1]);
          if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
          return null;
        }
        const lat = Number(pt?.lat);
        const lon = Number(pt?.lon ?? pt?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        return { lat, lon };
      })
      .filter(Boolean);

    if (normalized.length < 2) return;

    const cumulative = new Array(normalized.length).fill(0);
    let totalMeters = 0;
    for (let i = 1; i < normalized.length; i++) {
      const segMeters = haversineMeters(normalized[i - 1], normalized[i]);
      totalMeters += segMeters;
      cumulative[i] = totalMeters;
    }
    if (!Number.isFinite(totalMeters) || totalMeters < 80) return;

    const mapZoom = mapRef.current?.getZoom?.() ?? 13;
    const zoomSpacingMeters = getArrowSpacingByZoom(mapZoom);
    const targetArrows = Math.max(6, Number(options?.targetArrows) || 44);
    const autoSpacingMeters = totalMeters / (targetArrows + 1);
    const rawSpacingMeters = Number(options?.spacingMeters);
    const spacingMeters =
      Number.isFinite(rawSpacingMeters) && rawSpacingMeters > 0
        ? rawSpacingMeters
        : zoomSpacingMeters || autoSpacingMeters;
    const offsetMeters = Math.max(30, Math.min(spacingMeters * 0.7, 180));
    const maxArrows = Math.max(
      Math.ceil(totalMeters / Math.max(1, spacingMeters)) + 2,
      Number(options?.maxArrows) || 180
    );
    const color = String(options?.color || "#0d6efd");
    const pane = String(options?.pane || "trailDirectionPane");
    const size = Math.max(13, Number(options?.size) || 16);

    let segIdx = 0;
    for (
      let dist = offsetMeters;
      dist < totalMeters && layer.getLayers().length < maxArrows;
      dist += spacingMeters
    ) {
      while (
        segIdx < normalized.length - 2 &&
        cumulative[segIdx + 1] < dist
      ) {
        segIdx += 1;
      }
      const a = normalized[segIdx];
      const b = normalized[segIdx + 1];
      if (!a || !b) continue;
      const segStartDist = cumulative[segIdx];
      const segLen = Math.max(0, cumulative[segIdx + 1] - segStartDist);
      if (!Number.isFinite(segLen) || segLen < 2) continue;
      const t = Math.min(1, Math.max(0, (dist - segStartDist) / segLen));
      const lat = a.lat + (b.lat - a.lat) * t;
      const lon = a.lon + (b.lon - a.lon) * t;
      const bearing = bearingDegrees(a, b);
      const icon = L.divIcon({
        className: "tk-route-direction-arrow",
        html: `<div style="color:${color};font-size:${size}px;line-height:${size}px;font-weight:700;transform:rotate(${bearing.toFixed(1)}deg);transform-origin:center center;opacity:.9;filter:drop-shadow(0 0 1px rgba(0,0,0,.65));">▲</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
      L.marker([lat, lon], {
        icon,
        pane,
        interactive: false,
        keyboard: false,
      }).addTo(layer);
    }
    if (options?.updateSpacingLabel !== false) {
      setDirectionArrowSpacingM(Math.round(spacingMeters));
    }
  }, []);

  const { user } = useAuthStore();
  const isAdminUser = String(user?.role || "").toLowerCase() === "admin";
  const canViewPrivateWaypoints = (() => {
    const role = String(user?.role || "").toLowerCase();
    return role === "admin" || role === "guide";
  })();
  const { userPosition, points, trailBase, isRecording, setActivePanel, currentPanel } =
    usePanelsStore();
  const recordingMode = usePanelsStore((s) => s.recordingMode || "normal");
  const isExpeditionMode = recordingMode === "expedition";
  const navWaypointTarget = usePanelsStore((s) => s.navWaypointTarget);
  const navWaypointQueue = usePanelsStore((s) => s.navWaypointQueue);
  const navWaypointPending = usePanelsStore((s) => s.navWaypointPending);
  const navWaypointAddMode = usePanelsStore((s) => s.navWaypointAddMode);
  const enqueueNavWaypoint = usePanelsStore((s) => s.enqueueNavWaypoint);
  const setNavWaypointAddMode = usePanelsStore((s) => s.setNavWaypointAddMode);
  const startRecording = usePanelsStore((s) => s.startRecording);
  const setNavWaypointTarget = usePanelsStore((s) => s.setNavWaypointTarget);
  const setNavWaypointPending = usePanelsStore((s) => s.setNavWaypointPending);
  const clearNavWaypointPending = usePanelsStore((s) => s.clearNavWaypointPending);
  const clearNavWaypoint = usePanelsStore((s) => s.clearNavWaypoint);
  const planPickMode = usePanelsStore((s) => s.planPickMode);
  const setPlanStart = usePanelsStore((s) => s.setPlanStart);
  const setPlanEnd = usePanelsStore((s) => s.setPlanEnd);
  const setPlanPickMode = usePanelsStore((s) => s.setPlanPickMode);
  const setPlanFinalizeRequested = usePanelsStore(
    (s) => s.setPlanFinalizeRequested
  );
  const planActive = usePanelsStore((s) => s.planActive);
  const planWaypoints = usePanelsStore((s) => s.planWaypoints);
  const planStart = usePanelsStore((s) => s.planStart);
  const planEnd = usePanelsStore((s) => s.planEnd);
  const planRoutePreference = usePanelsStore((s) => s.planRoutePreference);
  const planRouteType = usePanelsStore((s) => s.planRouteType || "linear");
  const setPlanRoutePoints = usePanelsStore((s) => s.setPlanRoutePoints);
  const addPlanWaypoint = usePanelsStore((s) => s.addPlanWaypoint);
  const setTrailData = usePanelsStore((s) => s.setTrailData);
  const { currentGroup, memberLocations } = useGroupStore();
  const events = useEventsStore((s) => s.events);
  const userEvents = useEventsStore((s) => s.userEvents);
  const eventRegistrations = useEventsStore((s) => s.eventRegistrations);
  const listenEventRegistrations = useEventsStore((s) => s.listenEventRegistrations);
  const addAlternativeRoute = useEventsStore((s) => s.addAlternativeRoute);
  const addTourRoute = useEventsStore((s) => s.addTourRoute);
  const updateEvent = useEventsStore((s) => s.updateEvent);
  const groups = useGroupStore((s) => s.groups);
  const [liveEventOverride, setLiveEventOverride] = useState(null);
  const [followDirectionReversedForMap, setFollowDirectionReversedForMap] = useState(() => {
    try {
      return localStorage.getItem("trekkin_follow_direction_reversed") === "1";
    } catch {
      return false;
    }
  });
  const liveEventFetchRef = useRef({ eventId: null, ts: 0 });
  const pendingNavRouteCacheRef = useRef({ key: "", path: null });
  const currentGroupId =
    typeof currentGroup === "string"
      ? currentGroup
      : currentGroup?.id ||
        currentGroup?.groupId ||
        currentGroup?.firebaseId ||
        currentGroup?.key ||
        null;
  // live status calculado dentro dos efeitos para evitar TDZ
  const { liveData, livePath, isSharing, updateShareData } = useShareStore();
  const mapStylePref = useMemo(
    () => readMapStylePref(user),
    [user?.mapStyle, mapStyleEpoch]
  );
  const resolvedMapStyle = useMemo(
    () => resolveMapStyle(mapStylePref, canUseLite, canUsePro, isAdminUser),
    [mapStylePref, canUseLite, canUsePro, isAdminUser]
  );
  const effectiveMapStyle = resolvedMapStyle;
  const hasDownloadedProMap = useMemo(() => {
    return hasUsableNativeLocalMapHint("pro");
  }, [mapStyleEpoch, nativeMapPrimedEpoch]);
  const hasDownloadedLiteMap = useMemo(() => {
    return hasUsableNativeLocalMapHint("lite");
  }, [mapStyleEpoch, nativeMapPrimedEpoch]);
  const nativeMapRuntime = useMemo(() => {
    return isNativeMapRuntimeNow();
  }, []);
  const mapSourceMode = useMemo(
    () => readMapSourceModePref(),
    [mapStyleEpoch]
  );
  const localNativeStyleAvailable =
    effectiveMapStyle === "pro"
      ? hasDownloadedProMap
      : effectiveMapStyle === "lite"
        ? hasDownloadedLiteMap
        : false;
  const effectiveRemoteOnlyMaps = mapSourceMode === "remote";

  const navWaypointHasUserPosition =
    Number.isFinite(Number(userPosition?.lat)) &&
    Number.isFinite(Number(userPosition?.lon));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!nativeMapRuntime || effectiveRemoteOnlyMaps || effectiveMapStyle === "openstreet") {
      setNativeMapProbeDone(true);
      return undefined;
    }
    let cancelled = false;

    const hydrateFile = async (fileName, storageKey, bytesKey) => {
      try {
        const stat = await statNativePmtiles(fileName);
        const bytes = Number(stat?.fileBytes || stat?.fileSize || 0);
        const ok = stat?.exists === true || (Number.isFinite(bytes) && bytes > 0);
        if (!ok) return { ok: false, bytes: 0, changed: false };
        const changed = ensureNativeLocalKey(storageKey, fileName);
        if (Number.isFinite(bytes) && bytes > 0) writeStoredMapBytes(bytesKey, bytes);
        return { ok: true, bytes: Number.isFinite(bytes) ? bytes : 0, changed };
      } catch {
        return { ok: false, bytes: 0, changed: false };
      }
    };

    (async () => {
      try {
        const [lite, pro, contours] = await Promise.all([
          hydrateFile(NATIVE_LITE_FILE, LOCAL_PM_LITE_KEY, NATIVE_LITE_BYTES_KEY),
          hydrateFile(NATIVE_PRO_FILE, LOCAL_PM_PRO_KEY, NATIVE_PRO_BYTES_KEY),
          hydrateFile(
            NATIVE_CONTOURS_FILE,
            LOCAL_PM_CONTOURS_KEY,
            NATIVE_CONTOURS_BYTES_KEY
          ),
        ]);
        if (cancelled) return;
        setNativeMapProbeDone(true);
        if (!contours.ok) return;
        let changed = !!(lite.changed || pro.changed || contours.changed);
        const markNativeReady = (readyKey, forceKey) => {
          let localChanged = false;
          try {
            if (localStorage.getItem(readyKey) !== "1") {
              localStorage.setItem(readyKey, "1");
              localChanged = true;
            }
            if (localStorage.getItem(forceKey) === "1") {
              localStorage.removeItem(forceKey);
              localChanged = true;
            }
          } catch {}
          return localChanged;
        };
        try {
          if (pro.ok) {
            changed = markNativeReady(NATIVE_PRO_READY_KEY, FORCE_REMOTE_PRO_KEY) || changed;
          }
          if (lite.ok) {
            changed = markNativeReady(NATIVE_LITE_READY_KEY, FORCE_REMOTE_LITE_KEY) || changed;
          }
        } catch {}
        if (changed) {
          logEvent("system", "MAP_NATIVE_PMFILES_REHYDRATED", {
            style: effectiveMapStyle,
            liteBytes: Number(lite.bytes || 0),
            proBytes: Number(pro.bytes || 0),
            contoursBytes: Number(contours.bytes || 0),
          });
          setMapStyleEpoch((value) => value + 1);
          window.dispatchEvent(new CustomEvent("trekkin:map-style-changed"));
        }
      } catch (error) {
        if (!cancelled) {
          setNativeMapProbeDone(true);
          logEvent("warn", "MAP_NATIVE_PMFILES_REHYDRATE_FAILED", {
            style: effectiveMapStyle,
            message: String(error?.message || error),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveMapStyle, nativeMapRuntime, effectiveRemoteOnlyMaps]);

  const useLocalProPmtiles =
    effectiveMapStyle === "pro" &&
    hasDownloadedProMap &&
    !effectiveRemoteOnlyMaps;
  const useLocalLitePmtiles =
    effectiveMapStyle === "lite" &&
    hasDownloadedLiteMap &&
    !effectiveRemoteOnlyMaps;
  const useLocalBasePmtiles = useLocalProPmtiles || useLocalLitePmtiles;
  const currentMapZoom = Number(zoomLevelLabel);
  const useLiteGlobalOverview =
    zoomLevelLabel != null &&
    Number.isFinite(currentMapZoom) &&
    currentMapZoom <= 7;
  const useOnlineFallbackUnderLocalMap =
    mapSourceMode !== "local" &&
    (effectiveMapStyle === "pro" || effectiveMapStyle === "lite");
  const useTransparentPmtilesBackground =
    effectiveMapStyle === "pro" ||
    (effectiveMapStyle === "lite" &&
      (useLiteGlobalOverview || mapCenterOutsidePmtilesBounds));
  const localOnlyMapUnavailable =
    mapSourceMode === "local" &&
    effectiveMapStyle !== "openstreet" &&
    !localNativeStyleAvailable;
  const shouldDeferNativeBaseMap =
    localOnlyMapUnavailable ||
    (nativeMapRuntime &&
      mapSourceMode !== "remote" &&
      (effectiveMapStyle === "pro" || effectiveMapStyle === "lite") &&
      !nativeMapProbeDone &&
      !localNativeStyleAvailable);
  const mapBaseSourceKey =
    effectiveMapStyle === "openstreet"
      ? "openstreet"
      : effectiveMapStyle === "pro"
        ? useLocalProPmtiles
          ? "pro-local-pmtiles"
          : shouldDeferNativeBaseMap
            ? "pro-native-pending"
          : "pro-remote"
        : useLocalLitePmtiles
          ? "lite-local-pmtiles"
          : shouldDeferNativeBaseMap
            ? "lite-native-pending"
            : "lite-remote";
  // Keep enabled: images are lazy-loaded only when popup opens.
  const disableWaypointImagesForStability = false;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const bump = () => setMapStyleEpoch((v) => v + 1);
    window.addEventListener("storage", bump);
    window.addEventListener("trekkin:map-style-changed", bump);
    return () => {
      window.removeEventListener("storage", bump);
      window.removeEventListener("trekkin:map-style-changed", bump);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let lastOnline = navigator.onLine !== false;
    let pendingOnlineTimer = null;
    const applyNetwork = (online) => {
      lastOnline = online;
      setNetworkOnline(online);
      logEvent("info", "MAP_NETWORK_SOURCE_SWITCH", {
        online,
        style: effectiveMapStyle,
        sourceMode: mapSourceMode,
      });
    };
    const syncNetwork = () => {
      // Some Android WebViews report offline while the screen is off. Switching
      // sources in that state discards the mounted raster and its loaded tiles.
      if (isAndroid && document?.visibilityState !== "visible") {
        if (pendingOnlineTimer) {
          window.clearTimeout(pendingOnlineTimer);
          pendingOnlineTimer = null;
        }
        return;
      }
      const online = navigator.onLine !== false;
      if (online === lastOnline) return;
      if (pendingOnlineTimer) {
        window.clearTimeout(pendingOnlineTimer);
        pendingOnlineTimer = null;
      }
      if (isAndroid && online) {
        pendingOnlineTimer = window.setTimeout(() => {
          pendingOnlineTimer = null;
          if (document?.visibilityState !== "visible") return;
          applyNetwork(true);
        }, 500);
        return;
      }
      applyNetwork(online);
    };
    const interval = window.setInterval(syncNetwork, 1500);
    window.addEventListener("online", syncNetwork);
    window.addEventListener("offline", syncNetwork);
    document?.addEventListener?.("visibilitychange", syncNetwork);
    return () => {
      if (pendingOnlineTimer) window.clearTimeout(pendingOnlineTimer);
      window.clearInterval(interval);
      window.removeEventListener("online", syncNetwork);
      window.removeEventListener("offline", syncNetwork);
      document?.removeEventListener?.("visibilitychange", syncNetwork);
    };
  }, [effectiveMapStyle, isAndroid, mapSourceMode]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let appStateHandle = null;
    let lastStartedAt = 0;
    let lastHiddenAt = 0;

    const platform = isIOS ? "ios" : isAndroid ? "android" : "web";

    const startResumePerf = (reason) => {
      const now = Date.now();
      if (now - lastStartedAt < 800) return;
      const activeResume = appResumePerfRef.current;
      if (
        isAndroid &&
        activeResume?.startedAt &&
        now - activeResume.startedAt < 5000
      ) {
        logEvent("debug", "APP_RESUME_PERF_COALESCED", {
          platform,
          reason,
          originalReason: activeResume.reason,
          elapsedMs: now - activeResume.startedAt,
        });
        return;
      }
      lastStartedAt = now;
      const pointsNow = (() => {
        try {
          const pts = usePanelsStore.getState()?.points;
          return Array.isArray(pts) ? pts.length : 0;
        } catch {
          return 0;
        }
      })();
      const isRecordingNow = (() => {
        try {
          return !!usePanelsStore.getState()?.isRecording;
        } catch {
          return false;
        }
      })();
      const perf = {
        startedAt: now,
        reason,
        platform,
        firstTileLogged: false,
        baseVisibleLogged: false,
        trackVisibleLogged: !isAndroid || !isRecordingNow || pointsNow === 0,
        processingId: `app-resume-${now}`,
        processingEnded: false,
      };
      appResumePerfRef.current = perf;
      window.dispatchEvent(
        new CustomEvent("trekkin:processing", {
          detail: {
            active: true,
            id: perf.processingId,
            text: isRecordingNow
              ? "A retomar o mapa e o trilho..."
              : "A retomar o mapa...",
            minimumMs: 650,
          },
        })
      );
      perf.processingTimeout = window.setTimeout(() => {
        if (perf.processingEnded) return;
        perf.processingEnded = true;
        window.dispatchEvent(
          new CustomEvent("trekkin:processing", {
            detail: { active: false, id: perf.processingId },
          })
        );
      }, 8000);
      if (isAndroid) {
        window.__TREKKIN_ANDROID_RESUME_PERF = {
          ...(window.__TREKKIN_ANDROID_RESUME_PERF || {}),
          startedAt: now,
          pointsBefore: pointsNow,
          firstRenderLogged: false,
          reason,
        };
      }
      if (isIOS) window.__TREKKIN_IOS_RESUME_PERF = perf;
      window.__TREKKIN_APP_RESUME_PERF = perf;
      logEvent("info", "APP_RESUME_PERF_START", {
        platform,
        reason,
        style: effectiveMapStyle,
        source: mapBaseSourceKey,
        mapReady: !!mapRef.current,
        isRecording: isRecordingNow,
        pointsNow,
        sinceHiddenMs: lastHiddenAt ? now - lastHiddenAt : null,
      });
      window.setTimeout(() => {
        logEvent("info", "APP_RESUME_MAIN_THREAD_TICK", {
          platform,
          reason,
          elapsedMs: Date.now() - now,
        });
      }, 0);
      window.requestAnimationFrame?.(() => {
        const map = mapRef.current;
        const proRasterMounted =
          effectiveMapStyle === "pro" &&
          map &&
          proRasterBaseRef.current &&
          map.hasLayer?.(proRasterBaseRef.current);
        const skipInvalidate =
          !!proRasterMounted || (isAndroid && isRecordingNow && !!map);
        try {
          if (!skipInvalidate) map?.invalidateSize?.(false);
        } catch {}
        logEvent("info", "APP_RESUME_MAP_INVALIDATE", {
          platform,
          reason,
          elapsedMs: Date.now() - now,
          hasMap: !!map,
          skipped: skipInvalidate,
          skipReason: proRasterMounted
            ? "pro_raster_mounted"
            : skipInvalidate
              ? "android_recording_map_preserved"
              : null,
        });
        window.requestAnimationFrame?.(() => {
          try {
            const container = map?.getContainer?.();
            const hasLoadedRaster = !!container?.querySelector?.(
              ".leaflet-tile-loaded"
            );
            const mapCanvas = container?.querySelector?.(
              ".maplibregl-canvas, .mapboxgl-canvas"
            );
            const hasRenderedCanvas =
              Number(mapCanvas?.width || 0) > 0 &&
              Number(mapCanvas?.height || 0) > 0;
            if (hasLoadedRaster || hasRenderedCanvas) {
              window.dispatchEvent(
                new CustomEvent("trekkin:map-base-visible", {
                  detail: { source: "resume-preserved-base" },
                })
              );
            }

            if (isAndroid && isRecordingNow) {
              const renderedTrack = recordLayerRef.current?.getLatLngs?.();
              const hasRenderedTrack =
                Array.isArray(renderedTrack) && renderedTrack.flat(2).length > 0;
              if (hasRenderedTrack) {
                window.dispatchEvent(
                  new CustomEvent("trekkin:android-track-resume-ready", {
                    detail: { source: "resume-preserved-track" },
                  })
                );
              }
            }
          } catch {}
        });
      });
    };

    const markHidden = () => {
      lastHiddenAt = Date.now();
      logEvent("info", "APP_BACKGROUND_ENTER", {
        platform,
        style: effectiveMapStyle,
        source: mapBaseSourceKey,
      });
    };

    const handleVisible = () => {
      if (document?.visibilityState === "visible") {
        startResumePerf("visibility");
      } else if (document?.visibilityState === "hidden") {
        markHidden();
      }
    };

    const handleBaseVisible = () => {
      const perf = appResumePerfRef.current;
      if (!perf?.startedAt || perf.baseVisibleLogged) return;
      perf.baseVisibleLogged = true;
      logEvent("info", "APP_RESUME_MAP_BASE_VISIBLE", {
        platform,
        reason: perf.reason,
        style: effectiveMapStyle,
        source: mapBaseSourceKey,
        resumeToBaseMs: Date.now() - perf.startedAt,
      });
      if (perf.trackVisibleLogged && !perf.processingEnded) {
        perf.processingEnded = true;
        window.clearTimeout(perf.processingTimeout);
        window.dispatchEvent(
          new CustomEvent("trekkin:processing", {
            detail: { active: false, id: perf.processingId },
          })
        );
      }
    };

    const handleTrackVisible = () => {
      const perf = appResumePerfRef.current;
      if (!perf?.startedAt || perf.trackVisibleLogged) return;
      perf.trackVisibleLogged = true;
      if (perf.baseVisibleLogged && !perf.processingEnded) {
        perf.processingEnded = true;
        window.clearTimeout(perf.processingTimeout);
        window.dispatchEvent(
          new CustomEvent("trekkin:processing", {
            detail: { active: false, id: perf.processingId },
          })
        );
      }
    };

    document?.addEventListener?.("visibilitychange", handleVisible);
    window.addEventListener("trekkin:map-base-visible", handleBaseVisible);
    window.addEventListener("trekkin:android-track-resume-ready", handleTrackVisible);
    try {
      const app = window?.Capacitor?.Plugins?.App;
      app?.addListener?.("appStateChange", ({ isActive }) => {
        if (isActive) startResumePerf("app_state");
        else markHidden();
      })?.then?.((handle) => {
        appStateHandle = handle;
      });
    } catch {}

    return () => {
      document?.removeEventListener?.("visibilitychange", handleVisible);
      window.removeEventListener("trekkin:map-base-visible", handleBaseVisible);
      window.removeEventListener("trekkin:android-track-resume-ready", handleTrackVisible);
      const perf = appResumePerfRef.current;
      if (perf?.processingTimeout) window.clearTimeout(perf.processingTimeout);
      try {
        appStateHandle?.remove?.();
      } catch {}
    };
  }, [effectiveMapStyle, isAndroid, isIOS, mapBaseSourceKey]);

  useEffect(() => {
    const onFollowDirectionChanged = (event) => {
      const raw = event?.detail?.reversed;
      const next = raw === true || raw === "1" || raw === 1;
      setFollowDirectionReversedForMap(next);
    };
    window.addEventListener(
      "trekkin:follow-direction-changed",
      onFollowDirectionChanged
    );
    return () => {
      window.removeEventListener(
        "trekkin:follow-direction-changed",
        onFollowDirectionChanged
      );
    };
  }, []);

  const [navGridVisible, setNavGridVisible] = useState(() =>
    isAdminUser ? readLocalFlag("navGridLayerOn") : false
  );
  // A camada administrativa é uma ferramenta de edição: nela mostramos os
  // trechos de caminhada, bicicleta e ambos. Na navegação normal continua a
  // aplicar-se o modo escolhido pelo utilizador.
  const navGridDisplayActivityMode =
    isAdminUser && navGridVisible ? null : activityMode;
  const [navGridNodesVisible, setNavGridNodesVisible] = useState(() =>
    isAdminUser ? readLocalFlag("navGridNodesOn") : false
  );
  const [navGridNodesFilter, setNavGridNodesFilter] = useState(() =>
    readNavGridNodesFilter()
  );
  const [navGridNodesGraph, setNavGridNodesGraph] = useState(null);
  const navGridGraphStampRef = useRef(0);
  const navGridGraphModeRef = useRef(null);
  const navGridConfigRef = useRef(loadNavGrid());
  const navGridRouteLoadPromiseRef = useRef(null);
  const navGridRecordUserPosRef = useRef({ lat: null, lon: null });
  const navGridRecordActiveRef = useRef(false);
  const navGridRecordPointsRef = useRef([]);
  const navGridRecordMetaRef = useRef({
    name: "Trecho gravado",
    dedupeEnabled: true,
    dedupeMeters: 15,
    premium: false,
  });
  const [navGridSegments, setNavGridSegments] = useState([]);
  const [navGridPreviewSegmentId, setNavGridPreviewSegmentId] = useState(null);
  const navGridEndpointPreviewLayerRef = useRef(null);
  const navGridEndpointAuditActiveRef = useRef(false);
  const [classifySegmentDraft, setClassifySegmentDraft] = useState(null);
  const [adminQuickCreatePoint, setAdminQuickCreatePoint] = useState(null);
  const [mapPickPointActive, setMapPickPointActive] = useState(false);
  const [navGridRoutePath, setNavGridRoutePath] = useState([]);
  const [navGridRouteExternalSegments, setNavGridRouteExternalSegments] = useState([]);
  const [navGridRouteColoredSegments, setNavGridRouteColoredSegments] = useState([]);
  const navGridLayerRef = useRef(null);
  const canvasRendererRef = useRef(null);
  const [cachedWaypoints, setCachedWaypoints] = useState(() => loadCachedWaypoints());
  const summitsInsideRef = useRef(new Set());
  const summitWriteLocksRef = useRef(new Set());
  const [summitCategoryIds, setSummitCategoryIds] = useState(() => new Set());

  useEffect(() => onValue(ref(db, "waypointCategories"), (snapshot) => {
    const ids = new Set(Object.entries(snapshot.val() || {})
      .filter(([, value]) => /cume|summit|peak/i.test(String(value?.name || "")))
      .map(([id]) => id));
    setSummitCategoryIds(ids);
  }), []);

  useEffect(() => {
    const uid = user?.uid;
    const lat = Number(userPosition?.lat);
    const lon = Number(userPosition?.lon ?? userPosition?.lng);
    if (!uid || !Number.isFinite(lat) || !Number.isFinite(lon) || !Array.isArray(cachedWaypoints)) return;

    cachedWaypoints.forEach((waypoint) => {
      const category = String(
        waypoint?.categoryId || waypoint?.category || waypoint?.categoryName || ""
      ).toLowerCase();
      if (!category.includes("cume") && !category.includes("summit") && !category.includes("peak") && !summitCategoryIds.has(String(waypoint?.categoryId || ""))) return;
      const waypointLat = Number(waypoint?.lat ?? waypoint?.latitude);
      const waypointLon = Number(waypoint?.lon ?? waypoint?.lng ?? waypoint?.longitude);
      if (!Number.isFinite(waypointLat) || !Number.isFinite(waypointLon)) return;
      const waypointId = String(waypoint?.id || waypoint?.uid || `${waypointLat}_${waypointLon}`);
      const distance = haversineMeters({ lat, lon }, { lat: waypointLat, lon: waypointLon });

      if (distance > 100) {
        summitsInsideRef.current.delete(waypointId);
        return;
      }
      if (distance > 50 || summitsInsideRef.current.has(waypointId) || summitWriteLocksRef.current.has(waypointId)) return;

      summitsInsideRef.current.add(waypointId);
      summitWriteLocksRef.current.add(waypointId);
      const conqueredAt = Date.now();
      runTransaction(ref(db, `users/${uid}/summitConquests/${waypointId}`), (current) => ({
        ...(current || {}),
        waypointId,
        name: waypoint?.name || waypoint?.title || (language === "en" ? "Summit" : "Cume"),
        altitude: waypoint?.ele ?? waypoint?.altitude ?? waypoint?.elevation ?? current?.altitude ?? null,
        imageUrl: waypoint?.coverImage || waypoint?.imageUrl || waypoint?.galleryImages?.[0] || current?.imageUrl || null,
        icon: waypoint?.icon || current?.icon || null,
        count: Math.max(0, Number(current?.count || 0)) + 1,
        firstConqueredAt: Number(current?.firstConqueredAt || conqueredAt),
        lastConqueredAt: conqueredAt,
      })).then(() => {
        showToast(language === "en" ? `Summit conquered: ${waypoint?.name || "Summit"}` : `Cume conquistado: ${waypoint?.name || "Cume"}`, "success");
      }).catch((error) => {
        summitsInsideRef.current.delete(waypointId);
        console.warn("Falha ao registar cume conquistado:", error);
      }).finally(() => summitWriteLocksRef.current.delete(waypointId));
    });
  }, [cachedWaypoints, language, summitCategoryIds, user?.uid, userPosition?.lat, userPosition?.lon, userPosition?.lng]);
  const [queuedWaypointOps, setQueuedWaypointOps] = useState(() => loadWaypointOpsQueue());
  const routePointsRef = useRef({ main: [], alts: {} });
  const [routePointsVersion, setRoutePointsVersion] = useState(0);
  const [planRouteRecalcTick, setPlanRouteRecalcTick] = useState(0);
  const [eventRouteReady, setEventRouteReady] = useState(false);
  const liveWaypointsLayerRef = useRef(null);
  const liveWaypointsEventIdRef = useRef(null);
  const liveWaypointFetchRef = useRef({ eventId: null, ts: 0 });

  useEffect(() => {
    if (!currentGroupId) {
      if (liveEventOverride) setLiveEventOverride(null);
      return;
    }

    const groupsArr = Array.isArray(groups)
      ? groups
      : groups && typeof groups === "object"
        ? Object.values(groups)
        : [];

    const group =
      groupsArr.find(
        (g) =>
          g.id === currentGroupId ||
          g.groupId === currentGroupId ||
          g.firebaseId === currentGroupId ||
          g.key === currentGroupId
      ) || currentGroup || null;

    if (!group) {
      if (liveEventOverride) setLiveEventOverride(null);
      return;
    }

    const activeEventId =
      group.activeEventId || group.activeEvent || group.eventId || null;

    if (!activeEventId) {
      if (liveEventOverride) setLiveEventOverride(null);

      const now = Date.now();
      const last = liveEventFetchRef.current;
      const groupKey = `group:${currentGroupId}`;
      if (last.eventId === groupKey && now - last.ts < 3000) return;
      liveEventFetchRef.current = { eventId: groupKey, ts: now };

      get(ref(db, "/eventsLive"))
        .then((snap) => {
          if (!snap.exists()) return;
          const payload = snap.val() || {};
          const matchId = Object.keys(payload).find(
            (id) =>
              payload?.[id]?.groupId === currentGroupId &&
              payload?.[id]?.isLive === true
          );
          if (!matchId) return;
          const livePayload = payload[matchId] || {};
          setLiveEventOverride({ id: matchId, ...livePayload });

          // Atualiza apenas em memória (não escreve no DB) para destravar live share.
          useGroupStore.setState((state) => {
            const nextGroups = Array.isArray(state.groups)
              ? state.groups.map((g) =>
                  g?.id === currentGroupId || g?.groupId === currentGroupId
                    ? { ...g, activeEventId: matchId }
                    : g
                )
              : state.groups;
            const nextCurrent =
              state.currentGroup &&
              (state.currentGroup?.id === currentGroupId ||
                state.currentGroup?.groupId === currentGroupId)
                ? { ...state.currentGroup, activeEventId: matchId }
                : state.currentGroup;
            return { groups: nextGroups, currentGroup: nextCurrent };
          });
        })
        .catch((err) => {
          console.warn("[LIVE] erro ao ler eventsLive por grupo", err);
        });

      return;
    }

    const eventsArr = Array.isArray(events)
      ? events
      : events && typeof events === "object"
        ? Object.values(events)
        : [];
    const userEventsMap = userEvents || useEventsStore.getState().userEvents || {};
    const hasActiveUserEvent = (eventId) => {
      if (!eventId) return false;
      const reg = userEventsMap?.[eventId] || null;
      const st = String(reg?.status || reg?.state || reg?.registrationStatus || reg || "")
        .trim()
        .toLowerCase();
      return (
        st === "confirmed" ||
        st === "active" ||
        st === "paid" ||
        st === "registered" ||
        st === "approved"
      );
    };

    const matchedEvent =
      eventsArr.find(
        (ev) =>
          ev?.id === activeEventId ||
          ev?.id === String(activeEventId) ||
          ev?.eventId === activeEventId
      ) || null;
    const hasFreshLiveData =
      matchedEvent &&
      matchedEvent.isLive === true &&
      !!(
        matchedEvent.routeGPXUrl ||
        matchedEvent.routeGpxUrl ||
        matchedEvent.gpxUrl ||
        matchedEvent.routeUrl ||
        matchedEvent.alternativeRoutes ||
        matchedEvent.altRoutes ||
        matchedEvent.tourRoutes
      );

    if (hasFreshLiveData) {
      if (liveEventOverride) setLiveEventOverride(null);
      return;
    }

    if (liveEventOverride?.id === activeEventId) return;

    const now = Date.now();
    const last = liveEventFetchRef.current;
    if (last.eventId === activeEventId && now - last.ts < 1500) return;

    liveEventFetchRef.current = { eventId: activeEventId, ts: now };

    // Prioriza sempre /events/{id} como source-of-truth.
    // Isto evita inconsistências temporárias em eventsPublic/eventsLive para alguns utilizadores.
    get(ref(db, `/events/${activeEventId}`))
      .then((eventSnap) => {
        const eventPayload = eventSnap.exists() ? eventSnap.val() || {} : null;
        if (eventPayload && eventPayload.isLive === true) {
          setLiveEventOverride({ id: activeEventId, ...eventPayload });
          return;
        }

        return get(ref(db, `/eventsLive/${activeEventId}`)).then((liveSnap) => {
          if (!liveSnap.exists()) return;
          const livePayload = liveSnap.val() || {};
          setLiveEventOverride({ id: activeEventId, ...livePayload });
        });
      })
      .catch((err) => {
        console.warn("[LIVE] erro ao resolver evento ativo", err);
      });
  }, [currentGroupId, currentGroup, groups, events, liveEventOverride]);

  const eventsWithLive = useMemo(() => {
    const eventsArr = Array.isArray(events)
      ? events
      : events && typeof events === "object"
        ? Object.values(events)
        : [];
    if (!liveEventOverride) return eventsArr;
    let merged = false;
    const next = eventsArr.map((ev) => {
      if (
        ev?.id === liveEventOverride.id ||
        ev?.eventId === liveEventOverride.id
      ) {
        merged = true;
        return {
          ...ev,
          ...liveEventOverride,
          id: ev?.id || liveEventOverride.id,
        };
      }
      return ev;
    });
    if (merged) return next;
    return [...next, liveEventOverride];
  }, [events, liveEventOverride]);

  useEffect(() => {
    navGridRecordUserPosRef.current = {
      lat: Number(userPosition?.lat),
      lon: Number(userPosition?.lon),
    };
  }, [userPosition?.lat, userPosition?.lon]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const publishRecordState = (active) => {
      try {
        window.__trekkinNavGridRecActive = !!active;
      } catch {}
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-record-state", {
          detail: { active: !!active },
        })
      );
    };

    const handleRecordToggle = (event) => {
      const detail = event?.detail || {};
      const nextActive = !!detail.active;

      if (nextActive) {
        navGridRecordMetaRef.current = {
          name: String(detail.name || "Trecho gravado").trim() || "Trecho gravado",
          dedupeEnabled: detail.dedupeEnabled !== false,
          dedupeMeters: Math.max(1, Number(detail.dedupeMeters) || 15),
          premium: detail.premium === true,
          activityMode: normalizeActivityMode(detail.activityMode, ACTIVITY_MODE_WALK),
        };
        navGridRecordPointsRef.current = [];
        navGridRecordActiveRef.current = true;

        const lat = Number(navGridRecordUserPosRef.current?.lat);
        const lon = Number(navGridRecordUserPosRef.current?.lon);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          navGridRecordPointsRef.current.push({ lat, lon, ts: Date.now() });
        }

        publishRecordState(true);
        showToast("Gravação de trecho iniciada.", "success");
        return;
      }

      if (!navGridRecordActiveRef.current) {
        publishRecordState(false);
        return;
      }

      navGridRecordActiveRef.current = false;
      const rawPoints = Array.isArray(navGridRecordPointsRef.current)
        ? navGridRecordPointsRef.current.slice()
        : [];
      navGridRecordPointsRef.current = [];
      publishRecordState(false);
      const opId = startPendingOp("navgrid_record_stop_save", {
        rawPoints: rawPoints.length,
        name: String(navGridRecordMetaRef.current?.name || ""),
      });

      if (rawPoints.length < 2) {
        finishPendingOp(opId, "aborted_points", { rawPoints: rawPoints.length });
        showToast("Trecho não guardado: pontos insuficientes.", "warning");
        return;
      }

      try {
        updatePendingOp(opId, "build_grid");
        const current = loadNavGrid();
        const safeSpacing = Math.max(5, Number(current?.spacingMeters) || 50);
        const safeMerge = Math.max(5, Number(current?.mergeMeters) || 15);
        const safeRouteMerge = Math.max(
          safeMerge,
          Number(current?.routeMergeMeters) || 30
        );
        const nextGrid = {
          ...current,
          spacingMeters: safeSpacing,
          mergeMeters: safeMerge,
          routeMergeMeters: safeRouteMerge,
        };

        const meta = navGridRecordMetaRef.current || {};
        updatePendingOp(opId, "dedupe_points", { dedupeEnabled: meta.dedupeEnabled !== false });
        let filtered = rawPoints;
        if (meta.dedupeEnabled) {
          const segmentCount = Array.isArray(nextGrid?.segments)
            ? nextGrid.segments.length
            : 0;
          const shouldSkipHeavyDedupe = isIOS && segmentCount >= 300;
          if (shouldSkipHeavyDedupe) {
            updatePendingOp(opId, "dedupe_skipped_ios_heavy_grid", {
              segments: segmentCount,
            });
          } else {
            const graph = buildNavGridGraph(nextGrid);
            filtered = filterPointsByNodes(
              graph.nodes,
              rawPoints,
              meta.dedupeMeters
            );
          }
        }

        if (!Array.isArray(filtered) || filtered.length < 2) {
          finishPendingOp(opId, "aborted_dedupe", { filtered: Array.isArray(filtered) ? filtered.length : 0 });
          showToast("Trecho não guardado: removido por duplicação.", "warning");
          return;
        }

        updatePendingOp(opId, "create_segment", { filtered: filtered.length });
        const segment = createGridSegment({
          name: meta.name || "Trecho gravado",
          points: filtered,
          spacingMeters: nextGrid.spacingMeters,
          premium: meta.premium === true,
          activityMode: meta.activityMode,
        });
        updatePendingOp(opId, "append_segment", { segmentId: segment?.id || null });
        const updated = addSegmentToGrid(withUpdatedAt(nextGrid), segment);
        queueSaveNavGridPersistent(updated, { delayMs: 0 }).catch((err) => {
          logEvent("error", "NAVGRID_RECORD_LOCAL_PERSIST_FAIL", {
            message: String(err?.message || err),
            segmentId: segment?.id || null,
          });
        });
        navGridConfigRef.current = updated;
        navGridGraphStampRef.current = 0;
        setNavGridSegments(Array.isArray(updated?.segments) ? updated.segments : []);
        window.dispatchEvent(new CustomEvent("trekkin:navgrid-updated"));
        updatePendingOp(opId, "sync_cloud_deferred");
        setTimeout(() => {
          saveNavGridSegmentToCloud(db, segment, updated).catch((err) => {
            console.warn("Erro a sincronizar rede:", err);
            logEvent("error", "NAVGRID_RECORD_CLOUD_UPSERT_FAIL", {
              message: String(err?.message || err),
              segmentId: segment?.id || null,
            });
          });
        }, 250);
        finishPendingOp(opId, "ok", { segmentId: segment?.id || null });
        showToast("Trecho gravado e guardado.", "success");
      } catch (err) {
        updatePendingOp(opId, "failed_exception", {
          message: String(err?.message || err),
        });
        logEvent("error", "NAVGRID_RECORD_SAVE_CRASH_GUARD", {
          message: String(err?.message || err),
          stack: String(err?.stack || ""),
          opId,
        });
        showToast("Falha ao guardar trecho gravado.", "error");
      }
    };

    window.addEventListener("trekkin:navgrid-record-toggle", handleRecordToggle);
    return () => {
      window.removeEventListener("trekkin:navgrid-record-toggle", handleRecordToggle);
      navGridRecordActiveRef.current = false;
      navGridRecordPointsRef.current = [];
      publishRecordState(false);
    };
  }, []);

  useEffect(() => {
    if (!navGridRecordActiveRef.current) return;
    const lat = Number(userPosition?.lat);
    const lon = Number(userPosition?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

    const list = navGridRecordPointsRef.current;
    const now = Date.now();
    const last = list[list.length - 1];
    if (last) {
      const dt = now - Number(last.ts || 0);
      const d = haversineMeters({ lat, lon }, { lat: last.lat, lon: last.lon });
      if (dt < 1200 && d < 2) return;
    }
    list.push({ lat, lon, ts: now });
  }, [userPosition?.lat, userPosition?.lon]);

  const [segmentDefinitionsRaw, setSegmentDefinitionsRaw] = useState({});
  const [activeSegmentUi, setActiveSegmentUi] = useState(null);
  const [activeSegmentLeaderboard, setActiveSegmentLeaderboard] = useState(null);
  const [segmentHudPos, setSegmentHudPos] = useState(null);
  const [segmentFinishToast, setSegmentFinishToast] = useState(null);
  const [nearSegmentPreview, setNearSegmentPreview] = useState(null);
  const [autoAlertVibrate, setAutoAlertVibrate] = useState(() =>
    readBoolPref("autoAlertVibrate", true)
  );
  const [autoAlertSound, setAutoAlertSound] = useState(() =>
    readBoolPref("autoAlertSound", true)
  );
  const segmentTrackingRef = useRef({ activeAttempt: null });
  const segmentProximityRuntimeRef = useRef({
    inside: false,
    lastAlertTs: 0,
    lastSegmentId: null,
  });
  const segmentPreviewLayerRef = useRef(null);
  const nearbySegmentLastIdRef = useRef("");
  const segmentsRuntimeEnabled = (() => {
    if (!isRecording) return false;
    if (!SEGMENTS_RUNTIME_DEFAULT_ON) {
      try {
        return window?.localStorage?.getItem("trekkin.segments.runtime") === "1";
      } catch {
        return false;
      }
    }
    return true;
  })();

  useEffect(() => {
    if (!segmentsRuntimeEnabled) {
      setSegmentDefinitionsRaw({});
      return;
    }
    const unsub = onValue(ref(db, "segments/definitions"), (snap) => {
      setSegmentDefinitionsRaw(snap.val() || {});
    });
    return () => {
      try {
        unsub?.();
      } catch {}
    };
  }, [segmentsRuntimeEnabled]);

  const segmentDefinitions = useMemo(
    () =>
      normalizeSegmentDefinitions(segmentDefinitionsRaw, navGridConfigRef.current, {
        activityMode,
      }),
    [segmentDefinitionsRaw, activityMode]
  );

  useEffect(() => {
    if (!segmentsRuntimeEnabled) {
      setActiveSegmentLeaderboard(null);
      return;
    }
    const sid = String(activeSegmentUi?.segmentId || nearSegmentPreview?.segmentId || "").trim();
    if (!sid) {
      setActiveSegmentLeaderboard(null);
      return;
    }
    const unsub = onValue(ref(db, `segments/leaderboards/${sid}`), (snap) => {
      setActiveSegmentLeaderboard(snap.val() || null);
    });
    return () => {
      try {
        unsub?.();
      } catch {}
    };
  }, [activeSegmentUi?.segmentId, nearSegmentPreview?.segmentId, segmentsRuntimeEnabled]);

  useEffect(() => {
    try {
      if (!segmentsRuntimeEnabled) {
        segmentTrackingRef.current.activeAttempt = null;
        setActiveSegmentUi(null);
        setNearSegmentPreview(null);
        return;
      }
      if (!isRecording) {
        segmentTrackingRef.current.activeAttempt = null;
        setActiveSegmentUi(null);
        return;
      }
      if (!segmentDefinitions.length) return;

      const pos = { lat: userPosition?.lat, lon: userPosition?.lon ?? userPosition?.lng };
      const activeAttempt = segmentTrackingRef.current.activeAttempt;
      const transition = detectSegmentTransition(pos, segmentDefinitions, activeAttempt);

      if (transition.type === "enter" && transition.segment) {
        const now = Date.now();
        segmentTrackingRef.current.activeAttempt = {
          segmentId: transition.segment.id,
          startedAt: now,
        };
        setActiveSegmentUi({
          mode: "active",
          segmentId: transition.segment.id,
          name: transition.segment.name,
          startedAt: now,
          elapsedSec: 0,
        });
        showToast(`Segmento iniciado: ${transition.segment.name || "Segmento"}`, "success");
        return;
      }

      if (transition.type === "finish" && transition.segment && activeAttempt) {
        const startedAt = Number(activeAttempt.startedAt || 0);
        const finishedAt = Date.now();
        segmentTrackingRef.current.activeAttempt = null;
        setActiveSegmentUi(null);
        persistSegmentAttempt({
          db,
          uid: user?.uid,
          userName: user?.name || user?.displayName || user?.username || user?.email,
          userSex: user?.sex || user?.gender || null,
          segment: transition.segment,
          startedAt,
          finishedAt,
        })
          .then((res) => {
            if (!res?.ok) return;
            const durationSec = Math.max(0, (finishedAt - startedAt) / 1000);
            setSegmentFinishToast({
              segmentName: transition.segment.name || "Segmento",
              durationSec,
              rankOverall: Number(res?.rankOverall) || null,
            });
          })
          .catch((err) => {
            console.warn("Falha ao guardar tentativa de segmento:", err);
          });
        return;
      }

      if (transition.type === "cancel_offpath" && activeAttempt) {
        segmentTrackingRef.current.activeAttempt = null;
        setActiveSegmentUi(null);
        showToast("Segmento cancelado: saíste mais de 50m do traçado.", "warning");
        return;
      }
    } catch (err) {
      console.warn("Segment tracking runtime error:", err);
      segmentTrackingRef.current.activeAttempt = null;
      setActiveSegmentUi(null);
    }
  }, [
    segmentsRuntimeEnabled,
    isRecording,
    userPosition?.lat,
    userPosition?.lon,
    userPosition?.lng,
    segmentDefinitions,
    user?.uid,
    user?.name,
    user?.displayName,
    user?.username,
    user?.email,
    user?.sex,
    user?.gender,
  ]);

  useEffect(() => {
    if (!segmentsRuntimeEnabled) return;
    if (!activeSegmentUi?.startedAt) return;
    const id = setInterval(() => {
      setActiveSegmentUi((prev) => {
        if (!prev?.startedAt) return prev;
        const elapsedSec = Math.max(0, (Date.now() - Number(prev.startedAt || 0)) / 1000);
        return { ...prev, elapsedSec };
      });
    }, 500);
    return () => clearInterval(id);
  }, [activeSegmentUi?.startedAt, segmentsRuntimeEnabled]);

  useEffect(() => {
    if (!segmentsRuntimeEnabled) {
      setSegmentHudPos(null);
      return;
    }
    if (!mapReady || !activeSegmentUi?.segmentId) {
      setSegmentHudPos(null);
      return;
    }
    const map = mapRef.current;
    const lat = Number(userPosition?.lat);
    const lon = Number(userPosition?.lon ?? userPosition?.lng);
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lon)) {
      setSegmentHudPos(null);
      return;
    }
    const pt = map.latLngToContainerPoint([lat, lon]);
    setSegmentHudPos({
      left: Number(pt?.x || 0),
      top: Number(pt?.y || 0) + 26,
    });
  }, [
    segmentsRuntimeEnabled,
    mapReady,
    userPosition?.lat,
    userPosition?.lon,
    userPosition?.lng,
    activeSegmentUi?.segmentId,
  ]);

  useEffect(() => {
    if (!segmentsRuntimeEnabled) {
      setNearSegmentPreview(null);
      return;
    }
    const lat = Number(userPosition?.lat);
    const lon = Number(userPosition?.lon ?? userPosition?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || !segmentDefinitions.length) {
      setNearSegmentPreview(null);
      return;
    }
    let best = null;
    let bestDist = Infinity;
    const pos = { lat, lon };
    for (const seg of segmentDefinitions) {
      const d = haversineMeters(pos, seg.start);
      if (d <= 50 && d < bestDist) {
        best = seg;
        bestDist = d;
      }
    }
    if (!best) {
      setNearSegmentPreview(null);
      return;
    }
    const path = (Array.isArray(best.path) && best.path.length >= 2 ? best.path : [best.start, best.end])
      .map((p) => ({ lat: Number(p?.lat), lon: Number(p?.lon), ele: Number(p?.ele) }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    if (path.length < 2) {
      setNearSegmentPreview(null);
      return;
    }
    const distanceMeters =
      Math.max(0, Number(best.distanceMeters || 0)) ||
      haversineMeters(path[0], path[1]) ||
      0;
    setNearSegmentPreview({
      segmentId: best.id,
      name: best.name || "Segmento",
      path,
      distanceMeters,
      ascentMeters: Math.max(0, Math.round(Number(best.ascentMeters || 0))),
      descentMeters: Math.max(0, Math.round(Number(best.descentMeters || 0))),
    });
  }, [
    segmentsRuntimeEnabled,
    userPosition?.lat,
    userPosition?.lon,
    userPosition?.lng,
    segmentDefinitions,
  ]);

  useEffect(() => {
    if (!segmentsRuntimeEnabled) return;
    const runtime = segmentProximityRuntimeRef.current || {
      inside: false,
      lastAlertTs: 0,
      lastSegmentId: null,
    };
    const segmentId = String(nearSegmentPreview?.segmentId || "");
    if (!segmentId) {
      segmentProximityRuntimeRef.current = {
        inside: false,
        lastAlertTs: Number(runtime.lastAlertTs || 0),
        lastSegmentId: null,
      };
      return;
    }
    const now = Date.now();
    const isNewEntry = !runtime.inside || runtime.lastSegmentId !== segmentId;
    const canRepeat =
      now - Number(runtime.lastAlertTs || 0) >= SEGMENT_PROX_ALERT_INTERVAL_MS;
    if (isNewEntry || canRepeat) {
      if (autoAlertSound) {
        try {
          playPing();
        } catch {}
      }
      if (autoAlertVibrate) {
        try {
          triggerAlertVibration([200, 80, 200]);
        } catch {}
      }
    }
    segmentProximityRuntimeRef.current = {
      inside: true,
      lastAlertTs: isNewEntry || canRepeat ? now : Number(runtime.lastAlertTs || 0),
      lastSegmentId: segmentId,
    };
  }, [nearSegmentPreview?.segmentId, autoAlertSound, autoAlertVibrate, segmentsRuntimeEnabled]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!segmentPreviewLayerRef.current) {
      segmentPreviewLayerRef.current = L.layerGroup().addTo(map);
    }
    const layer = segmentPreviewLayerRef.current;
    layer.clearLayers();
    if (!segmentsRuntimeEnabled) return;
    const activeSegId = String(activeSegmentUi?.segmentId || "").trim();
    const activeDef = activeSegId
      ? (segmentDefinitions || []).find((s) => String(s?.id || "") === activeSegId)
      : null;
    const sourceSegment = activeDef
      ? {
          segmentId: activeDef.id,
          name: activeDef.name || "Segmento",
          path: Array.isArray(activeDef.path) ? activeDef.path : [],
          distanceMeters: Number(activeDef.distanceMeters || 0),
          ascentMeters: Number(activeDef.ascentMeters || 0),
          descentMeters: Number(activeDef.descentMeters || 0),
        }
      : nearSegmentPreview;
    if (!sourceSegment || !Array.isArray(sourceSegment.path)) {
      nearbySegmentLastIdRef.current = "";
      return;
    }
    const coords = sourceSegment.path
      .map((p) => [Number(p?.lat), Number(p?.lon)])
      .filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));
    if (coords.length < 2) return;
    L.polyline(coords, {
      color: "#16a34a",
      weight: 5,
      opacity: 0.95,
    }).addTo(layer);
    const start = coords[0];
    const km = Number(sourceSegment.distanceMeters || 0) / 1000;
    const ascent = Math.max(0, Math.round(Number(sourceSegment.ascentMeters || 0)));
    const descent = Math.max(0, Math.round(Number(sourceSegment.descentMeters || 0)));
    const popupHtml = `
      <div style="min-width:160px">
        <div style="font-weight:700;color:#166534">${sourceSegment.name || "Segmento"}</div>
        <div style="font-size:12px;color:#1f2937">Distância: ${km.toFixed(2)} km</div>
        <div style="font-size:12px;color:#1f2937">Acumulado+: ${ascent} m</div>
        <div style="font-size:12px;color:#1f2937">Acumulado-: ${descent} m</div>
      </div>
    `;
    const marker = L.circleMarker(start, {
      radius: 8,
      color: "#16a34a",
      fillColor: "#22c55e",
      fillOpacity: 0.9,
      weight: 2,
    })
      .addTo(layer)
      .bindPopup(popupHtml, {
        autoClose: true,
        closeOnClick: true,
        closeButton: false,
        className: "tk-segment-popup",
      });
    if (nearbySegmentLastIdRef.current !== sourceSegment.segmentId) {
      nearbySegmentLastIdRef.current = sourceSegment.segmentId;
      try {
        marker.openPopup();
      } catch {}
    }
  }, [mapReady, nearSegmentPreview, activeSegmentUi?.segmentId, segmentDefinitions, segmentsRuntimeEnabled]);

  const liveWaypointsCacheEventRef = useRef(null);
  const navGridRouteLayerRef = useRef(null);
  const navGridRouteExternalLayerRef = useRef(null);
  const navGridRouteDirectionLayerRef = useRef(null);
  const navGridNodesLayerRef = useRef(null);
  const [navGridEditNodesActive, setNavGridEditNodesActive] = useState(false);
  const [drawContext, setDrawContext] = useState("navgrid");
  const [navGridAddNodeActive, setNavGridAddNodeActive] = useState(false);
  const [navGridDeleteNodesActive, setNavGridDeleteNodesActive] = useState(false);
  const [navGridSplitNodeActive, setNavGridSplitNodeActive] = useState(false);
  const [navGridNodeMode, setNavGridNodeMode] = useState(null);
  const [alertsLayerOn, setAlertsLayerOn] = useState(() =>
    readLocalFlag("alertsLayerOn")
  );
  const [autoAlertsEnabled, setAutoAlertsEnabled] = useState(() =>
    readBoolPref("autoAlertsEnabled", true)
  );
  const [autoAlertFauna, setAutoAlertFauna] = useState(() =>
    readBoolPref("autoAlertFauna", true)
  );
  const [autoAlertTrilho, setAutoAlertTrilho] = useState(() =>
    readBoolPref("autoAlertTrilho", true)
  );
  const [autoAlertSeguranca, setAutoAlertSeguranca] = useState(() =>
    readBoolPref("autoAlertSeguranca", true)
  );
  const [autoAlertCriticos, setAutoAlertCriticos] = useState(() =>
    readBoolPref("autoAlertCriticos", true)
  );
  const [offTrailAlertEnabled, setOffTrailAlertEnabled] = useState(() =>
    readBoolPref("offTrailAlert", true)
  );
  const [offTrailThresholdM, setOffTrailThresholdM] = useState(() =>
    readOffTrailThresholdPref(40)
  );
  const [alertsFilter, setAlertsFilter] = useState(() => ({
    category: "all",
    type: "",
    sub: "",
    adminAll: false,
    fromTs: null,
    toTs: null,
  }));
  const [mapAlerts, setMapAlerts] = useState([]);
  const alertsLayerRef = useRef(null);
  const [proximityAlert, setProximityAlert] = useState(null);
  const proximityTimerRef = useRef(null);
  const offTrailRuntimeRef = useRef({ outside: false, lastAlertTs: 0 });
  const [waypointsVisible, setWaypointsVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("waypointsLayerOn") === "1";
    } catch {
      return false;
    }
  });
  const [waypointNavResolving, setWaypointNavResolving] = useState(false);
  const [exclusiveFocusedWaypoint, setExclusiveFocusedWaypoint] = useState(null);

  useEffect(() => {
    if (!mapReady || !exclusiveFocusedWaypoint) return undefined;
    const map = mapRef.current;
    if (!map) return undefined;
    const restoreWaypointLayer = () => setExclusiveFocusedWaypoint(null);
    map.on("popupclose", restoreWaypointLayer);
    return () => map.off("popupclose", restoreWaypointLayer);
  }, [mapReady, exclusiveFocusedWaypoint]);
  const [partnerRecommendationsPanel, setPartnerRecommendationsPanel] = useState(null);
  const [partnerRecommendationAdminTarget, setPartnerRecommendationAdminTarget] = useState(null);
  const [adminUserView, setAdminUserView] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("trekkin_admin_user_view_v1") === "1";
    } catch {
      return false;
    }
  });
  const isAdminMode = isAdminUser && !adminUserView;
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncAdminUserView = (event) => {
      if (typeof event?.detail?.enabled === "boolean") {
        setAdminUserView(event.detail.enabled);
        if (event.detail.enabled) setPartnerRecommendationAdminTarget(null);
        return;
      }
      try {
        const enabled = window.localStorage.getItem("trekkin_admin_user_view_v1") === "1";
        setAdminUserView(enabled);
        if (enabled) setPartnerRecommendationAdminTarget(null);
      } catch {
        setAdminUserView(false);
      }
    };
    window.addEventListener("storage", syncAdminUserView);
    window.addEventListener("trekkin:admin-user-view", syncAdminUserView);
    return () => {
      window.removeEventListener("storage", syncAdminUserView);
      window.removeEventListener("trekkin:admin-user-view", syncAdminUserView);
    };
  }, []);
  const [partnerRecommendations, setPartnerRecommendations] = useState([]);
  const [partnerRecommendationCategory, setPartnerRecommendationCategory] = useState("all");
  const [partnerRecommendationIndex, setPartnerRecommendationIndex] = useState(0);
  const [waypointCategories, setWaypointCategories] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem("waypointsSelectedCategories");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.filter((cat) => typeof cat === "string" && cat)
        : [];
    } catch {
      return [];
    }
  });
  const [waypointCategoryPalette, setWaypointCategoryPalette] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem("waypointsCategoryPalette");
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });
  const [liveWaypointPalette, setLiveWaypointPalette] = useState(null);
  const liveWaypointPaletteRef = useRef({ eventId: null });
  const waypointsLayerRef = useRef(null);
  const waypointsLayerProcessingIdRef = useRef(null);
  const navGridLayerProcessingIdRef = useRef(null);
  const segmentStartsLayerRef = useRef(null);
  const waypointsMarkersRef = useRef(new Map());
  const waypointViewportDebounceRef = useRef(null);
  const [waypointViewportTick, setWaypointViewportTick] = useState(0);
  const [segmentStartsPreview, setSegmentStartsPreview] = useState([]);
  const userPositionRef = useRef(null);
  const publicTrailsLayerRef = useRef(null);
  const publicTrailsMarkersRef = useRef(new Map());
  const publicTrailsCatalogFetchRef = useRef(null);
  const navTrailsLayerRef = useRef(null);
  const navTrailsMarkersRef = useRef(new Map());
  const navTrailsRenderSeqRef = useRef(0);
  const trailRatingsCacheRef = useRef(new Map());
  const [publicTrailsMapState, setPublicTrailsMapState] = useState(() => {
    const restored = typeof window !== "undefined"
      ? window.__trekkinPublicTrailsMapState
      : null;
    if (restored && typeof restored === "object") {
      return {
        on: restored.on === true,
        items: Array.isArray(restored.items) ? restored.items : [],
      };
    }
    try {
      const cached = JSON.parse(
        window.localStorage.getItem(PUBLIC_TRAILS_MAP_CACHE_KEY) || "[]"
      );
      return { on: false, items: Array.isArray(cached) ? cached : [] };
    } catch {
      return { on: false, items: [] };
    }
  });
  const [navTrailsMapState, setNavTrailsMapState] = useState({
    on: false,
    items: [],
  });
  const [trailPreviewMapState, setTrailPreviewMapState] = useState(() => {
    if (typeof window === "undefined") return [];
    return Object.values(window.__trekkinMapOnlyTrails || {}).filter(
      (item) =>
        String(item?.trailId || "").trim() &&
        Array.isArray(item?.points) &&
        item.points.length > 1
    );
  });
  const [trailPreviewFocusPoints, setTrailPreviewFocusPoints] = useState([]);
  const [trailStagesMapState, setTrailStagesMapState] = useState({
    on: false,
    trailId: "",
    segments: [],
  });
  const [stageStartWaypointPickActive, setStageStartWaypointPickActive] =
    useState(false);
  const [navGridDrawActive, setNavGridDrawActive] = useState(false);
  const [trailEditActive, setTrailEditActiveState] = useState(false);
  const drawLayerRef = useRef(null);
  const drawDotsLayerRef = useRef(null);
  const drawPointsRef = useRef([]);
  const eventAltMetaRef = useRef(null);
  const eventMainMetaRef = useRef(null);
  const editLayerRef = useRef(null);
  const editMarkersRef = useRef([]);
  const editPointsRef = useRef([]);
  const editOriginalRef = useRef([]);
  const editHistoryRef = useRef([]);
  const editMetaRef = useRef(null);
  const editActiveRef = useRef(false);
  const navGridDrawActiveRef = useRef(navGridDrawActive);
  const planPickModeRef = useRef(planPickMode);
  const planActiveRef = useRef(planActive);
  const canNavigateWaypointsRef = useRef(canNavigateWaypoints);
  const waypointNavResolvingRef = useRef(waypointNavResolving);
  const waypointNavResolvingTimerRef = useRef(null);
  const isRecordingRef = useRef(isRecording);
  const canUsePremiumSegmentsRef = useRef(canUsePremiumSegments);
  const pickReturnPanelRef = useRef({ name: "waypoints", props: {} });
  const planRouteErrorRef = useRef(null);
  const navWaypointRouteErrorRef = useRef(null);
  const navWaypointDebugStampRef = useRef("");
  const navGridMutationBusyRef = useRef(false);
  const trailEditPrevLayersRef = useRef(null);

  useEffect(() => {
    if (!isAdminUser) {
      setNavGridVisible(false);
      return;
    }

    const shouldShow = readLocalFlag("navGridLayerOn");
    setNavGridVisible(shouldShow);
    if (!shouldShow || typeof window === "undefined") return;

    let cancelled = false;
    const restoreVisibleGrid = async () => {
      try {
        await window.__TREKKIN_LOAD_NAVGRID_ON_DEMAND?.(false);
      } catch {}
      if (cancelled) return;
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-updated", {
          detail: { context: "visibility_restore" },
        })
      );
    };
    restoreVisibleGrid();
    return () => {
      cancelled = true;
    };
  }, [isAdminUser]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      if (!isAdminUser) return;
      const segmentId = String(event?.detail?.segmentId || "");
      if (!segmentId) return;
      const segment =
        navGridSegments.find((item) => String(item?.id || "") === segmentId) ||
        (Array.isArray(loadNavGrid()?.segments) ? loadNavGrid().segments : []).find(
          (item) => String(item?.id || "") === segmentId
        );
      const points = (Array.isArray(segment?.points) ? segment.points : [])
        .map((point) => [Number(point?.lat), Number(point?.lon ?? point?.lng)])
        .filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));
      if (points.length < 2) {
        showToast("Não foi possível localizar o trecho.", "warning");
        return;
      }
      setNavGridVisible(true);
      if (!navGridSegments.some((item) => String(item?.id || "") === segmentId)) {
        setNavGridSegments((current) => [...current, segment]);
      }
      setNavGridPreviewSegmentId(segmentId);
      const map = mapRef.current;
      if (map) {
        map.fitBounds(L.latLngBounds(points), {
          padding: [36, 36],
          maxZoom: 17,
          animate: !isIOS,
        });
      }
      showToast("Toca no trecho destacado para decidir se queres otimizá-lo.", "info");
    };
    window.addEventListener("trekkin:navgrid-preview-segment", handler);
    return () => window.removeEventListener("trekkin:navgrid-preview-segment", handler);
  }, [isAdminUser, isIOS, navGridSegments]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const clearEndpointPreview = () => {
      const map = mapRef.current;
      const layer = navGridEndpointPreviewLayerRef.current;
      if (map && layer) map.removeLayer(layer);
      navGridEndpointPreviewLayerRef.current = null;
    };
    const handler = (event) => {
      if (!isAdminUser) return;
      const endpoints = (Array.isArray(event?.detail?.endpoints)
        ? event.detail.endpoints
        : []
      ).filter(
        (endpoint) =>
          Number.isFinite(Number(endpoint?.lat)) &&
          Number.isFinite(Number(endpoint?.lon))
      );
      if (endpoints.length !== 2) {
        showToast("Não foi possível localizar as extremidades.", "warning");
        return;
      }
      const map = mapRef.current;
      if (!map) return;
      navGridEndpointAuditActiveRef.current = true;
      clearEndpointPreview();
      setNavGridVisible(true);
      setNavGridPreviewSegmentId(null);

      const layer = L.layerGroup().addTo(map);
      navGridEndpointPreviewLayerRef.current = layer;
      const colors = ["#ef4444", "#2563eb"];
      const endpointPositions = endpoints.map((endpoint) =>
        L.latLng(Number(endpoint.lat), Number(endpoint.lon))
      );
      const endpointPixels = endpointPositions.map((position) =>
        map.latLngToContainerPoint(position)
      );
      const deltaX = endpointPixels[1].x - endpointPixels[0].x;
      const deltaY = endpointPixels[1].y - endpointPixels[0].y;
      const horizontalLayout = Math.abs(deltaX) >= Math.abs(deltaY);
      const tooltipPlacements = horizontalLayout
        ? deltaX >= 0
          ? [
              { direction: "left", offset: [-14, 0] },
              { direction: "right", offset: [14, 0] },
            ]
          : [
              { direction: "right", offset: [14, 0] },
              { direction: "left", offset: [-14, 0] },
            ]
        : deltaY >= 0
          ? [
              { direction: "top", offset: [0, -14] },
              { direction: "bottom", offset: [0, 14] },
            ]
          : [
              { direction: "bottom", offset: [0, 14] },
              { direction: "top", offset: [0, -14] },
            ];
      endpoints.forEach((endpoint, index) => {
        const latLng = [Number(endpoint.lat), Number(endpoint.lon)];
        const tooltip = document.createElement("div");
        const title = document.createElement("strong");
        title.textContent = `${endpoint.key || (index === 0 ? "A" : "B")} · ${endpoint.segmentName || "Trecho"}`;
        tooltip.appendChild(title);
        tooltip.appendChild(document.createElement("br"));
        tooltip.appendChild(
          document.createTextNode(`Extremidade de ${endpoint.side || "trecho"}`)
        );
        L.circleMarker(latLng, {
          radius: 11,
          color: "#ffffff",
          weight: 4,
          fillColor: colors[index],
          fillOpacity: 1,
        })
          .bindTooltip(tooltip, {
            permanent: true,
            className: "tk-navgrid-endpoint-tooltip",
            direction: tooltipPlacements[index].direction,
            offset: tooltipPlacements[index].offset,
          })
          .addTo(layer);
      });
      const positions = endpoints.map((endpoint) => [Number(endpoint.lat), Number(endpoint.lon)]);
      L.polyline(positions, {
        color: "#f59e0b",
        weight: 4,
        dashArray: "7 7",
        opacity: 0.95,
        interactive: false,
      }).addTo(layer);
      map.fitBounds(L.latLngBounds(positions), {
        padding: [90, 90],
        maxZoom: 20,
        animate: !isIOS,
      });
      const distance = Number(event?.detail?.distance);
      showToast(
        Number.isFinite(distance)
          ? `Extremidades A e B separadas por ${distance.toFixed(1)} m.`
          : "Extremidades A e B assinaladas no mapa.",
        "info"
      );
    };
    window.addEventListener("trekkin:navgrid-preview-endpoints", handler);
    return () => {
      window.removeEventListener("trekkin:navgrid-preview-endpoints", handler);
      clearEndpointPreview();
    };
  }, [isAdminUser, isIOS]);

  useEffect(() => {
    userPositionRef.current = userPosition || null;
  }, [userPosition]);

  useEffect(() => {
    if (!trailEditActive) {
      const prev = trailEditPrevLayersRef.current;
      if (prev) {
        setNavGridVisible(isAdminUser && !!prev.navGridVisible);
        setNavGridNodesVisible(isAdminUser && !!prev.navGridNodesVisible);
        trailEditPrevLayersRef.current = null;
      }
      return;
    }
    if (!trailEditPrevLayersRef.current) {
      trailEditPrevLayersRef.current = {
        navGridVisible,
        navGridNodesVisible,
      };
    }
    // Isola a edição ao trecho ativo: sem pontos/camadas globais concorrentes.
    setNavGridVisible(false);
    setNavGridNodesVisible(false);
    try {
      navGridLayerRef.current?.clearLayers?.();
      navGridNodesLayerRef.current?.clearLayers?.();
      navGridRouteLayerRef.current?.setLatLngs?.([]);
      navGridRouteExternalLayerRef.current?.clearLayers?.();
      navGridRouteDirectionLayerRef.current?.clearLayers?.();
    } catch {}
  }, [trailEditActive, navGridVisible, navGridNodesVisible, isAdminUser]);
  useEffect(() => {
    planActiveRef.current = !!planActive;
    canNavigateWaypointsRef.current = !!canNavigateWaypoints;
    waypointNavResolvingRef.current = !!waypointNavResolving;
    isRecordingRef.current = !!isRecording;
    canUsePremiumSegmentsRef.current = !!canUsePremiumSegments;
  }, [
    planActive,
    canNavigateWaypoints,
    waypointNavResolving,
    isRecording,
    canUsePremiumSegments,
  ]);
  const planRouteAnchorsRef = useRef([]);
  const planRouteSegmentsRef = useRef([]);
  const planRouteNoAltNoticeRef = useRef("");
  const planRoutePreferenceRef = useRef("shortest");
  const mapBaseErrorToastRef = useRef({ key: "", ts: 0 });
  const mapBaseDecodeFallbackRef = useRef({ active: false, ts: 0, reason: "" });
  const planLastRef = useRef(null);
  const drawContextRef = useRef("navgrid");
  const mergeSegmentsRef = useRef([]);
  const stopPlanPickRef = useRef({
    active: false,
    trailId: "",
    trailName: "",
    points: [],
  });
  const [stopPlanPickActive, setStopPlanPickActive] = useState(false);
  const groupMarkersRef = useRef({});
  const lastElevTsRef = useRef(0);
  const liveDiagRef = useRef({ ts: 0, key: "" });
  const liveAutoJoinRef = useRef({ gid: null, ts: 0 });
  const liveEventsListenRef = useRef(false);
  const liveWaypointsComputeRef = useRef({ key: "", ts: 0 });
  const liveWaypointsDrawRef = useRef({ key: "", ts: 0 });
  const liveRenderStateDiagRef = useRef({ key: "", ts: 0 });
  const liveMembersRenderDiagRef = useRef({ key: "", ts: 0 });
  const liveForceSwitchRef = useRef({ key: "", ts: 0 });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const detail = event?.detail || {};
      const on = detail?.on === true;
      const items = Array.isArray(detail?.items) ? detail.items : [];
      window.__trekkinPublicTrailsMapState = { on, items };
      setPublicTrailsMapState({ on, items });
    };
    window.addEventListener("trekkin:public-trails-map", handler);
    return () => window.removeEventListener("trekkin:public-trails-map", handler);
  }, []);

  // O mapa não deve depender do ciclo de vida do Painel Nav para obter os
  // pontos dos trilhos públicos. Ao abrir o mapa, recupera autonomamente o
  // catálogo se o botão foi ativado sem itens (primeira utilização/cache
  // vazia). Isto também cobre a troca imediata de painel em iOS/Android.
  useEffect(() => {
    if (!publicTrailsMapState?.on) return;
    if (Array.isArray(publicTrailsMapState?.items) && publicTrailsMapState.items.length) {
      return;
    }
    if (publicTrailsCatalogFetchRef.current) return;

    const request = (async () => {
      try {
        const response = await fetch(PUBLIC_TRAILS_LIST_URL, { cache: "no-store" });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const items = (Array.isArray(data?.items) ? data.items : []).map((item) => {
          const rawLat = item?.startLat;
          const rawLon = item?.startLon;
          const lat = rawLat === null || rawLat === "" ? null : Number(rawLat);
          const lon = rawLon === null || rawLon === "" ? null : Number(rawLon);
          return {
            filename: item?.filename || null,
            name: item?.name || item?.filename || "Trilho disponível",
            url: item?.url || null,
            mtime: item?.mtime || null,
            size: item?.size || null,
            shortDescription: String(item?.shortDescription || ""),
            gallery: Array.isArray(item?.gallery) ? item.gallery : [],
            startLat: Number.isFinite(lat) ? lat : null,
            startLon: Number.isFinite(lon) ? lon : null,
          };
        });
        try {
          window.localStorage.setItem(
            PUBLIC_TRAILS_MAP_CACHE_KEY,
            JSON.stringify(items)
          );
        } catch {}
        const current = window.__trekkinPublicTrailsMapState;
        if (current?.on !== true) return;
        window.__trekkinPublicTrailsMapState = { on: true, items };
        setPublicTrailsMapState({ on: true, items });
      } catch (error) {
        console.warn("Falha ao recuperar os pontos dos trilhos públicos:", error);
      } finally {
        publicTrailsCatalogFetchRef.current = null;
      }
    })();
    publicTrailsCatalogFetchRef.current = request;
  }, [publicTrailsMapState?.on, publicTrailsMapState?.items]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const list = Array.isArray(event?.detail?.segments) ? event.detail.segments : [];
      setSegmentStartsPreview(list);
      try {
        window.dispatchEvent(
          new CustomEvent("trekkin:segments-map-preview-state", {
            detail: { on: list.length > 0 },
          })
        );
        window.localStorage.setItem("trekkin.segments.preview.on", list.length > 0 ? "1" : "0");
      } catch {}
      if (!list.length) return;
      const map = mapRef.current;
      if (!map) return;
      const boundsPts = list
        .map((it) => [Number(it?.lat), Number(it?.lon)])
        .filter((pt) => Number.isFinite(pt[0]) && Number.isFinite(pt[1]));
      if (boundsPts.length >= 1) {
        try {
          const bounds = L.latLngBounds(boundsPts);
          map.fitBounds(bounds.pad(0.2), { maxZoom: 17, animate: true });
        } catch {}
      }
    };
    window.addEventListener("trekkin:segments-map-preview", handler);
    return () => window.removeEventListener("trekkin:segments-map-preview", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const on = event?.detail?.on === true;
      setStageStartWaypointPickActive(on);
    };
    window.addEventListener("trekkin:stage-start-waypoint-pick", handler);
    return () =>
      window.removeEventListener("trekkin:stage-start-waypoint-pick", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const nearestTrailIndex = (trailPoints, lat, lon) => {
      const list = Array.isArray(trailPoints) ? trailPoints : [];
      if (!list.length) return -1;
      let bestIdx = -1;
      let bestDist = Number.POSITIVE_INFINITY;
      for (let i = 0; i < list.length; i += 1) {
        const p = list[i];
        const pLat = Number(p?.lat);
        const pLon = Number(p?.lon);
        if (!Number.isFinite(pLat) || !Number.isFinite(pLon)) continue;
        const d = haversineMeters(
          { lat: Number(lat), lon: Number(lon) },
          { lat: pLat, lon: pLon }
        );
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    };
    const cumulativeTrailMeters = (trailPoints) => {
      const list = Array.isArray(trailPoints) ? trailPoints : [];
      if (!list.length) return [];
      const cum = [0];
      for (let i = 1; i < list.length; i += 1) {
        const a = list[i - 1];
        const b = list[i];
        const seg = haversineMeters(
          { lat: Number(a?.lat), lon: Number(a?.lon) },
          { lat: Number(b?.lat), lon: Number(b?.lon) }
        );
        cum[i] = cum[i - 1] + (Number.isFinite(seg) ? seg : 0);
      }
      return cum;
    };
    const distanceAlongTrailMeters = (trailPoints, fromPoint, toPoint) => {
      const list = Array.isArray(trailPoints) ? trailPoints : [];
      if (list.length < 2) return NaN;
      const fromIdx = nearestTrailIndex(list, fromPoint?.lat, fromPoint?.lon);
      const toIdx = nearestTrailIndex(list, toPoint?.lat, toPoint?.lon);
      if (fromIdx < 0 || toIdx < 0) return NaN;
      const cum = cumulativeTrailMeters(list);
      if (!Array.isArray(cum) || cum.length <= Math.max(fromIdx, toIdx)) return NaN;
      const hi = Math.max(fromIdx, toIdx);
      const lo = Math.min(fromIdx, toIdx);
      return Math.max(0, Number(cum[hi] || 0) - Number(cum[lo] || 0));
    };
    const safePoints = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map((p) => ({ lat: Number(p?.lat), lon: Number(p?.lon) }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    const readStagesLocal = (trailId) => {
      const uid = useAuthStore.getState()?.user?.uid || "guest";
      const key = `trekkin.trailStopPlan.${uid}.${String(trailId || "").trim()}`;
      try {
        const raw = window.localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };
    const saveStagesLocal = (trailId, stages) => {
      const uid = useAuthStore.getState()?.user?.uid || "guest";
      const key = `trekkin.trailStopPlan.${uid}.${String(trailId || "").trim()}`;
      try {
        window.localStorage.setItem(key, JSON.stringify(Array.isArray(stages) ? stages : []));
      } catch {}
    };
    const appendStageWithConfirm = (candidate) => {
      const ctx = stopPlanPickRef.current || {};
      const trailId = String(ctx?.trailId || "").trim();
      if (!trailId) return false;
      const lat = Number(candidate?.lat);
      const lon = Number(candidate?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
      const list = readStagesLocal(trailId);
      const prev =
        (Array.isArray(list) && list.length ? list[list.length - 1] : null) ||
        (Array.isArray(ctx?.points) && ctx.points.length ? ctx.points[0] : null) ||
        null;
      let distKm = 0;
      if (prev && Number.isFinite(Number(prev?.lat)) && Number.isFinite(Number(prev?.lon))) {
        const byTrail = distanceAlongTrailMeters(
          ctx?.points,
          { lat: Number(prev.lat), lon: Number(prev.lon) },
          { lat, lon }
        );
        if (Number.isFinite(byTrail) && byTrail > 0) {
          distKm = byTrail / 1000;
        } else {
          distKm =
            haversineMeters(
              { lat: Number(prev.lat), lon: Number(prev.lon) },
              { lat, lon }
            ) / 1000;
        }
      }
      const ok = window.confirm(
        `Distância da nova etapa: ${Number(distKm || 0).toFixed(2)} km\nDefinir este waypoint como final da etapa?`
      );
      if (!ok) return false;
      const nextStage = {
        lat,
        lon,
        name: String(candidate?.name || `Etapa ${list.length + 1}`).trim(),
        waypointId: String(candidate?.waypointId || "").trim() || null,
        startDate: "",
        startTime: "",
        notes: "",
        desc: String(candidate?.desc || candidate?.description || candidate?.details || "").trim(),
        tel: String(candidate?.tel || candidate?.phone || candidate?.telefone || "").trim(),
        email: String(candidate?.email || candidate?.mail || candidate?.eMail || "").trim(),
        www: String(candidate?.www || candidate?.web || candidate?.website || candidate?.url || "").trim(),
        imageUrl: String(candidate?.imageUrl || candidate?.image || candidate?.photoUrl || candidate?.photo || "").trim(),
      };
      const next = [...list, nextStage];
      saveStagesLocal(trailId, next);
      window.dispatchEvent(
        new CustomEvent("trekkin:trail-stop-plan-stages", {
          detail: { trailId, stages: next },
        })
      );
      showToast("Paragem definida como final da etapa.", "success");
      setActivePanel("trailDetails");
      return true;
    };
    window.__trekkinAppendStageFromWaypoint = appendStageWithConfirm;

    const onStopPlan = (event) => {
      const detail = event?.detail || {};
      const on = detail?.on === true;
      const trailId = String(detail?.trailId || "").trim();
      if (!on) {
        stopPlanPickRef.current = {
          active: false,
          trailId: "",
          trailName: "",
          points: [],
        };
        setStopPlanPickActive(false);
        return;
      }
      stopPlanPickRef.current = {
        active: stopPlanPickRef.current?.active === true,
        trailId,
        trailName: String(detail?.trailName || "").trim(),
        points: safePoints(detail?.points),
      };
      setStopPlanPickActive(stopPlanPickRef.current?.active === true);
    };
    const onStopPlanPick = (event) => {
      const detail = event?.detail || {};
      stopPlanPickRef.current = {
        active: true,
        trailId: String(detail?.trailId || "").trim(),
        trailName: String(detail?.trailName || "").trim(),
        points: safePoints(detail?.points),
      };
      setStopPlanPickActive(true);
    };
    const onStopPlanClear = () => {
      stopPlanPickRef.current = {
        active: false,
        trailId: "",
        trailName: "",
        points: [],
      };
      setStopPlanPickActive(false);
    };
    window.addEventListener("trekkin:trail-stop-plan", onStopPlan);
    window.addEventListener("trekkin:trail-stop-plan-pick", onStopPlanPick);
    window.addEventListener("trekkin:trail-stop-plan-clear", onStopPlanClear);
    return () => {
      delete window.__trekkinAppendStageFromWaypoint;
      window.removeEventListener("trekkin:trail-stop-plan", onStopPlan);
      window.removeEventListener("trekkin:trail-stop-plan-pick", onStopPlanPick);
      window.removeEventListener("trekkin:trail-stop-plan-clear", onStopPlanClear);
    };
  }, [setActivePanel]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const detail = event?.detail || {};
      const on = detail?.on === true;
      const items = Array.isArray(detail?.items) ? detail.items : [];
      setNavTrailsMapState({ on, items });
    };
    window.addEventListener("trekkin:nav-trails-map", handler);
    return () => window.removeEventListener("trekkin:nav-trails-map", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const detail = event?.detail || {};
      const on = detail?.on === true;
      const points = Array.isArray(detail?.points) ? detail.points : [];
      const trailId = String(detail?.trailId || "").trim();
      if (on && points.length > 1) setTrailPreviewFocusPoints(points);
      if (trailId) {
        const active = { ...(window.__trekkinMapOnlyTrails || {}) };
        if (on && points.length > 1) {
          active[trailId] = {
            trailId,
            trailName: String(detail?.trailName || "Trilho").trim() || "Trilho",
            points,
          };
        } else if (!on) {
          delete active[trailId];
        }
        window.__trekkinMapOnlyTrails = active;
      } else if (!on) {
        window.__trekkinMapOnlyTrails = {};
      }
      setTrailPreviewMapState((current) => {
        const rows = Array.isArray(current) ? current : [];
        if (!on) {
          return trailId ? rows.filter((item) => item.trailId !== trailId) : [];
        }
        if (!trailId) return rows;
        const next = {
          trailId,
          trailName: String(detail?.trailName || "Trilho").trim() || "Trilho",
          points,
        };
        return [...rows.filter((item) => item.trailId !== trailId), next];
      });
    };
    window.addEventListener("trekkin:trail-preview-map", handler);
    return () => window.removeEventListener("trekkin:trail-preview-map", handler);
  }, []);

  useEffect(() => {
    if (!mapReady || trailPreviewFocusPoints.length < 2) return;
    const map = mapRef.current;
    if (!map) return;
    const boundsPoints = trailPreviewFocusPoints
      .map((point) => [Number(point?.lat), Number(point?.lon ?? point?.lng)])
      .filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));
    if (boundsPoints.length < 2) return;
    const bounds = L.latLngBounds(boundsPoints);
    if (bounds.isValid()) {
      map.fitBounds(bounds.pad(0.08), {
        padding: [28, 28],
        maxZoom: 17,
        animate: true,
      });
    }
  }, [mapReady, trailPreviewFocusPoints]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const detail = event?.detail || {};
      const on = detail?.on === true;
      const trailId = String(detail?.trailId || "");
      const segments = Array.isArray(detail?.segments) ? detail.segments : [];
      setTrailStagesMapState({ on, trailId, segments });
    };
    window.addEventListener("trekkin:trail-stop-plan-visualize", handler);
    return () =>
      window.removeEventListener("trekkin:trail-stop-plan-visualize", handler);
  }, []);

  const openPublicTrailFromMap = useCallback(
    async (item) => {
      try {
        const rawUrl = String(item?.url || "").trim();
        if (!rawUrl) {
          showToast("Trilho sem URL válida.", "warning");
          return;
        }
        const absoluteUrl = rawUrl.startsWith("http")
          ? rawUrl
          : `https://www.trekkin.pt${rawUrl}`;
        const res = await fetch(absoluteUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const xml = await res.text();
        const parsed = await parseGPX(xml);
        const points = Array.isArray(parsed?.points) ? parsed.points : [];
        if (points.length < 2) {
          showToast("Trilho sem pontos suficientes.", "warning");
          return;
        }
        const trail = {
          id: `public-${item?.filename || Date.now()}`,
          nome: item?.name || item?.filename || "Trilho público",
          name: item?.name || item?.filename || "Trilho público",
          shortDescription: String(item?.shortDescription || ""),
          galleryImages: Array.isArray(item?.gallery) ? item.gallery : [],
          points,
          cloud: {
            file: item?.filename || null,
            url: rawUrl,
          },
          __source: "public-gpx",
          public: true,
        };
        setTrailData(trail);
        setActivePanel("trailDetails", { trail });
      } catch (err) {
        console.warn("Falha ao abrir trilho público do mapa:", err);
        showToast("Não foi possível abrir este trilho.", "error");
      }
    },
    [setTrailData, setActivePanel]
  );

  const resolveNavTrailMapUrl = useCallback((item) => {
    const raw =
      item?.cloud?.url ||
      item?.url ||
      item?.cloud?.path ||
      item?.cloud?.file ||
      null;
    if (!raw) return null;
    const normalized = normalizeTrekkinUrl(String(raw));
    return buildGpxFetchUrl(normalized);
  }, []);

  const openNavTrailFromMap = useCallback(
    async (item) => {
      try {
        let points = Array.isArray(item?.points) ? item.points : [];
        if ((!Array.isArray(points) || points.length < 2) && typeof item?.file === "string" && item.file) {
          const parsed = await parseGPX(item.file);
          points = Array.isArray(parsed?.points) ? parsed.points : [];
        }
        if ((!Array.isArray(points) || points.length < 2) && typeof item?.gpx === "string" && item.gpx) {
          const parsed = await parseGPX(item.gpx);
          points = Array.isArray(parsed?.points) ? parsed.points : [];
        }
        if (!Array.isArray(points) || points.length < 2) {
          const url = resolveNavTrailMapUrl(item);
          if (!url) throw new Error("missing_nav_url");
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const xml = await res.text();
          const parsed = await parseGPX(xml);
          points = Array.isArray(parsed?.points) ? parsed.points : [];
        }
        if (!Array.isArray(points) || points.length < 2) {
          showToast("Trilho sem pontos suficientes.", "warning");
          return;
        }
        const trail = {
          id: item?.id || `nav-${Date.now()}`,
          name: item?.name || item?.id || "Trilho",
          nome: item?.name || item?.id || "Trilho",
          shortDescription: String(item?.shortDescription || ""),
          galleryImages: Array.isArray(item?.galleryImages) ? item.galleryImages : [],
          points,
          __source: "nav",
          cloud: item?.cloud || null,
          gpx: typeof item?.gpx === "string" ? item.gpx : null,
        };
        setTrailData(trail);
        setActivePanel("trailDetails", { trail });
      } catch (err) {
        console.warn("Falha ao abrir trilho do Nav no mapa:", err);
        showToast("Não foi possível abrir este trilho.", "error");
      }
    },
    [resolveNavTrailMapUrl, setTrailData, setActivePanel]
  );

  const bindTrailMarkerPopup = useCallback((entry, item, openFn, fallbackTitle) => {
    if (!entry?.marker || typeof openFn !== "function") return;
    const name = String(
      item?.name ||
        item?.nome ||
        item?.title ||
        item?.filename ||
        item?.id ||
        fallbackTitle ||
        "Trilho"
    ).trim();
    const subtitle = String(item?.shortDescription || item?.date || "").trim();
    const coverCandidate =
      item?.coverImage ||
      item?.imageUrl ||
      item?.image ||
      (Array.isArray(item?.galleryImages) && item.galleryImages[0]) ||
      (Array.isArray(item?.gallery) && item.gallery[0]) ||
      null;
    const coverUrl = typeof coverCandidate === "string" ? coverCandidate.trim() : "";
    const popup = L.DomUtil.create("div", "tk-waypoint-popup tk-trail-marker-popup");
    popup.style.minWidth = "200px";
    popup.style.display = "flex";
    popup.style.flexDirection = "column";
    popup.style.gap = "8px";

    const titleEl = L.DomUtil.create("div", "tk-trail-marker-popup-title", popup);
    titleEl.textContent = name;
    titleEl.style.fontWeight = "700";
    titleEl.style.color = "#0f5132";
    titleEl.style.fontSize = "14px";
    titleEl.style.lineHeight = "1.2";

    if (coverUrl) {
      const img = L.DomUtil.create("img", "", popup);
      img.src = coverUrl;
      img.alt = "Capa do trilho";
      img.loading = "lazy";
      img.style.width = "100%";
      img.style.maxHeight = "120px";
      img.style.objectFit = "cover";
      img.style.borderRadius = "8px";
      img.style.border = "1px solid rgba(0,0,0,0.08)";
      img.style.background = "#f4f4f4";
    }

    if (subtitle) {
      const subtitleEl = L.DomUtil.create("div", "tk-trail-marker-popup-description", popup);
      subtitleEl.textContent = subtitle;
      subtitleEl.style.color = "#556";
      subtitleEl.style.fontSize = "12px";
      subtitleEl.style.lineHeight = "1.3";
      subtitleEl.style.whiteSpace = "pre-wrap";
    }

    const ratingAvgRaw =
      item?.ratingAvg ??
      item?.ratingsAvg ??
      item?.rating?.avg ??
      item?.trailRatingAvg ??
      null;
    const ratingCountRaw =
      item?.ratingCount ??
      item?.ratingsCount ??
      item?.rating?.count ??
      item?.trailRatingCount ??
      null;
    const ratingAvg = Number(ratingAvgRaw);
    const ratingCount = Number(ratingCountRaw);
    const starsLine = L.DomUtil.create("div", "tk-trail-marker-popup-rating", popup);
    starsLine.style.color = "#6b5200";
    starsLine.style.fontSize = "12px";
    starsLine.style.lineHeight = "1.2";
    if (Number.isFinite(ratingAvg) && Number.isFinite(ratingCount) && ratingCount > 0) {
      const rounded = Math.max(1, Math.min(5, Math.round(ratingAvg)));
      starsLine.textContent = `${"★".repeat(rounded)}${"☆".repeat(5 - rounded)} · ${ratingAvg.toFixed(
        1
      )} (${ratingCount})`;
    } else {
      starsLine.textContent = "Sem avaliações";
    }

    const trailRatingKey = toFirebaseSafeKey(
      item?.cloud?.file || item?.filename || item?.cloud?.url || item?.url || item?.id || name
    );
    const applyRatingLine = (avgValue, countValue) => {
      const avgNum = Number(avgValue);
      const countNum = Number(countValue);
      if (Number.isFinite(avgNum) && Number.isFinite(countNum) && countNum > 0) {
        const rounded = Math.max(1, Math.min(5, Math.round(avgNum)));
        starsLine.textContent = `${"★".repeat(rounded)}${"☆".repeat(5 - rounded)} · ${avgNum.toFixed(
          1
        )} (${countNum})`;
        return;
      }
      starsLine.textContent = "Sem avaliações";
    };
    const refreshRatingLine = async () => {
      if (!trailRatingKey) return;
      const cached = trailRatingsCacheRef.current.get(trailRatingKey);
      const now = Date.now();
      if (cached && now - Number(cached.ts || 0) < 60000) {
        applyRatingLine(cached.avg, cached.count);
        return;
      }
      try {
        const snap = await get(ref(db, `publicTrailRatings/${trailRatingKey}/byUser`));
        const raw = snap.exists() ? snap.val() || {} : {};
        const rows = Object.values(raw || {});
        const stars = rows
          .map((r) => Number(r?.stars || 0))
          .filter((n) => Number.isFinite(n) && n >= 1 && n <= 5);
        const count = stars.length;
        const avg = count ? stars.reduce((a, b) => a + b, 0) / count : 0;
        trailRatingsCacheRef.current.set(trailRatingKey, { avg, count, ts: now });
        applyRatingLine(avg, count);
      } catch {}
    };
    refreshRatingLine();

    const button = L.DomUtil.create("button", "tk-trail-marker-popup-action", popup);
    button.type = "button";
    button.textContent = "Ver detalhes do trilho";
    button.style.border = "1px solid #198754";
    button.style.background = "#fff";
    button.style.color = "#198754";
    button.style.borderRadius = "8px";
    button.style.padding = "6px 10px";
    button.style.fontSize = "13px";
    button.style.fontWeight = "700";
    button.style.cursor = "pointer";

    L.DomEvent.on(button, "click", (event) => {
      L.DomEvent.stop(event);
      entry.marker.closePopup();
      entry.hitMarker?.closePopup?.();
      openFn(item);
    });

    L.DomEvent.disableClickPropagation(popup);
    if (entry.onTrailPopupOpen) {
      try {
        entry.marker.off("popupopen", entry.onTrailPopupOpen);
        entry.hitMarker?.off?.("popupopen", entry.onTrailPopupOpen);
      } catch {}
    }
    entry.onTrailPopupOpen = () => {
      refreshRatingLine();
    };
    entry.marker.on("popupopen", entry.onTrailPopupOpen);
    entry.hitMarker?.on?.("popupopen", entry.onTrailPopupOpen);
    entry.marker.bindPopup(popup, {
      minWidth: 200,
      className: "tk-waypoint-popup",
    });
    entry.hitMarker?.bindPopup?.(popup, {
      minWidth: 200,
      className: "tk-waypoint-popup",
    });
  }, []);

  const bindPublicTrailCardPopup = useCallback((entry, item) => {
    if (!entry?.marker) return;
    const fullName = String(item?.name || item?.filename || "Trilho disponível").trim();
    const colonIndex = fullName.indexOf(":");
    const officialRoute = fullName.match(/^((?:GR|PR)\s*[A-Z0-9.-]+)\s*[-–—]\s*(.+)$/i);
    const code = colonIndex >= 0
      ? fullName.slice(0, colonIndex).trim()
      : officialRoute?.[1]?.trim() || "Trilho";
    const title = colonIndex >= 0
      ? fullName.slice(colonIndex + 1).trim() || fullName
      : officialRoute?.[2]?.trim() || fullName;
    const images = [...new Set([
      ...(Array.isArray(item?.gallery) ? item.gallery : []),
      ...(Array.isArray(item?.galleryImages) ? item.galleryImages : []),
      item?.coverImage,
      item?.imageUrl,
      item?.image,
    ].map((value) => String(value || "").trim()).filter(Boolean))].slice(0, 4);

    const card = L.DomUtil.create("button", "tk-public-trail-card tk-public-trail-map-card");
    card.type = "button";
    card.setAttribute("aria-label", `Ver ${fullName}`);

    if (images.length) {
      const media = L.DomUtil.create("span", "tk-public-trail-card-media", card);
      media.style.gridTemplateColumns = `repeat(${images.length}, minmax(0, 1fr))`;
      media.style.height = images.length === 1 ? "150px" : "104px";
      images.forEach((url) => {
        const image = L.DomUtil.create("img", "", media);
        image.src = url;
        image.alt = "";
        image.loading = "lazy";
        image.decoding = "async";
      });
    }

    const body = L.DomUtil.create("span", "tk-public-trail-card-body", card);
    const icon = L.DomUtil.create("span", "tk-public-trail-card-icon", body);
    const iconImage = L.DomUtil.create("img", "", icon);
    iconImage.src = `${base}icons/boot.svg`;
    iconImage.alt = "";
    const copy = L.DomUtil.create("span", "tk-public-trail-card-copy", body);
    const codeEl = L.DomUtil.create("b", "", copy);
    codeEl.textContent = code;
    const titleEl = L.DomUtil.create("strong", "", copy);
    titleEl.textContent = title;
    const metricsEl = L.DomUtil.create("span", "tk-public-trail-card-metrics", copy);
    const chevron = L.DomUtil.create("span", "tk-public-trail-card-chevron", body);
    chevron.textContent = "›";
    chevron.setAttribute("aria-hidden", "true");

    const firstValue = (...values) =>
      values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");
    const renderMetrics = (source) => {
      metricsEl.replaceChildren();
      const distance = firstValue(source?.lengthKm, source?.distanceKm, source?.distance);
      const ascent = firstValue(source?.elevationGain, source?.ascent, source?.dPlus);
      const duration = firstValue(source?.durationLabel, source?.estimatedDuration, source?.duration);
      const region = firstValue(source?.region, source?.municipality, source?.location);
      const values = [];
      if (distance !== undefined) values.push(`${Number(distance).toFixed(1)} km`);
      if (typeof source?.circular === "boolean") {
        values.push(source.circular ? "↻ Circular" : "↔ Linear");
      }
      if (duration) values.push(String(duration));
      if (ascent !== undefined) values.push(`D+ ${Math.round(Number(ascent))} m`);
      if (region) values.push(String(region));
      values.filter((value) => !value.includes("NaN")).forEach((value) => {
        const chip = L.DomUtil.create("span", "", metricsEl);
        chip.textContent = value;
      });
      metricsEl.hidden = metricsEl.childElementCount === 0;
    };
    renderMetrics(item);

    L.DomEvent.on(card, "click", (event) => {
      L.DomEvent.stop(event);
      entry.marker.closePopup();
      openPublicTrailFromMap(item);
    });
    L.DomEvent.disableClickPropagation(card);

    if (entry.onPublicCardOpen) {
      entry.marker.off("popupopen", entry.onPublicCardOpen);
    }
    entry.onPublicCardOpen = async () => {
      if (entry.publicMetricsLoaded || entry.publicMetricsLoading) return;
      entry.publicMetricsLoading = true;
      try {
        const rawUrl = String(item?.url || "").trim();
        if (!rawUrl) return;
        const absoluteUrl = rawUrl.startsWith("http")
          ? rawUrl
          : `https://www.trekkin.pt${rawUrl}`;
        const response = await fetch(absoluteUrl);
        if (!response.ok) return;
        const parsed = await parseGPX(await response.text());
        const points = Array.isArray(parsed?.points) ? parsed.points : [];
        let distanceKm = 0;
        for (let index = 1; index < points.length; index += 1) {
          distanceKm += haversineMeters(points[index - 1], points[index]) / 1000;
        }
        const endGapMeters = points.length > 1
          ? haversineMeters(points[0], points[points.length - 1])
          : null;
        const metrics = {
          ...item,
          ...(distanceKm > 0 ? { distanceKm } : {}),
          ...(Number.isFinite(Number(parsed?.elevStats?.totalGain))
            ? { elevationGain: Number(parsed.elevStats.totalGain) }
            : {}),
          ...(Number.isFinite(endGapMeters) && distanceKm > 0
            ? { circular: endGapMeters <= Math.max(120, distanceKm * 1000 * 0.05) }
            : {}),
        };
        renderMetrics(metrics);
        entry.publicMetricsLoaded = true;
      } catch (error) {
        console.warn("Falha ao carregar métricas do card do trilho:", error);
      } finally {
        entry.publicMetricsLoading = false;
      }
    };
    entry.marker.on("popupopen", entry.onPublicCardOpen);
    entry.marker.bindPopup(card, {
      minWidth: 300,
      maxWidth: 520,
      className: "tk-public-trail-map-popup",
    });
  }, [base, openPublicTrailFromMap]);

  // ⏳ Defer heavy live route rendering until UI is stable
  useEffect(() => {
    if (!mapReady || isViewerMode) {
      if (eventRouteReady) setEventRouteReady(false);
      return;
    }
    const t = setTimeout(() => setEventRouteReady(true), 900);
    return () => clearTimeout(t);
  }, [mapReady, isViewerMode, eventRouteReady]);

  // Android: diagnóstico leve do LIVE (para detectar ausência de evento/locs)
  useEffect(() => {
    if (!isAndroid || !mapReady || isViewerMode) return;

    const gid = currentGroupId || null;
    const { activeEvent, isLive } = getActiveEventForGroupSafe(
      groups,
      currentGroup || gid,
      eventsWithLive
    );
    const membersObj =
      gid && memberLocations && memberLocations[gid] ? memberLocations[gid] : {};
    const membersCount = Object.keys(membersObj).length;
    const diagKey = `${gid || "nogroup"}:${activeEvent?.id || activeEvent?.eventId || "noevent"}:${isLive}:${membersCount}`;

    const now = Date.now();
    if (diagKey !== liveDiagRef.current.key || now - liveDiagRef.current.ts > 12000) {
      liveDiagRef.current = { key: diagKey, ts: now };
      const payload = {
        gid,
        activeEventId: activeEvent?.id || activeEvent?.eventId || null,
        isLive,
        membersCount,
        eventsCount: Array.isArray(eventsWithLive)
          ? eventsWithLive.length
          : eventsWithLive && typeof eventsWithLive === "object"
            ? Object.keys(eventsWithLive).length
            : 0,
      };
      mapDebug("[LIVE-ANDROID]", JSON.stringify(payload));
    }
  }, [
    isAndroid,
    mapReady,
    isViewerMode,
    currentGroup,
    groups,
    eventsWithLive,
    memberLocations,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let active = true;
    const normalizePoints = (rawPoints) =>
      rawPoints
        .map((pt, idx) => {
          const lat = Number(pt?.lat);
          const lon = Number(pt?.lon);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
          const timeValue =
            pt?.time ||
            pt?.ts ||
            pt?.timestamp ||
            Date.now() + idx * 1000;
          return {
            lat,
            lon,
            ele: pt?.ele ?? pt?.alt ?? pt?.altitude ?? pt?.elevation ?? null,
            time: Number.isFinite(timeValue) ? Number(timeValue) : Date.now(),
          };
        })
        .filter(Boolean);
    const sanitizeName = (value) => {
      const safe = typeof value === "string" && value.trim() ? value.trim() : "trilho";
      return safe
        .normalize("NFKD")
        .replace(/[^\w.-]/g, "_")
        .replace(/^[-_.]+/, "")
        .slice(0, 32);
    };
    const handleEventAltSave = async (detail) => {
      if (!active || !detail?.eventId) return;
      const normalized = normalizePoints(Array.isArray(detail.points) ? detail.points : []);
      if (!normalized.length) {
        showToast("⚠️ Percurso inválido.");
        return;
      }
      try {
        const baseName = `${detail.eventName || "Trilho"} (Alternativo desenhado)`;
        const gpx = exportGPX(normalized, {
          name: baseName,
          date: new Date().toISOString(),
        });
        if (!gpx) {
          throw new Error("GPX inválido");
        }
        const blob = new Blob([gpx], {
          type: "application/gpx+xml;charset=utf-8",
        });
        const fileName = `${sanitizeName(baseName)}-${Date.now()}.gpx`;
        const payload =
          typeof File === "function"
            ? new File([blob], fileName, { type: "application/gpx+xml" })
            : blob;
        const url = await uploadGPX(payload);
        await addAlternativeRoute(detail.eventId, {
          name: baseName,
          routeGPXUrl: url,
        });
        showToast("✅ Trilho alternativo criado.");
      } catch (err) {
        console.error("Erro ao criar trilho alternativo:", err);
        showToast("❌ Não foi possível criar o trilho alternativo.");
      }
    };
    const handleEventMainSave = async (detail) => {
      if (!active || !detail?.eventId) return;
      const normalized = normalizePoints(Array.isArray(detail.points) ? detail.points : []);
      if (!normalized.length) {
        showToast("⚠️ Percurso inválido.");
        return;
      }
      try {
        const baseName = detail.eventName || "Trilho principal";
        const gpx = exportGPX(normalized, {
          name: baseName,
          date: new Date().toISOString(),
        });
        if (!gpx) {
          throw new Error("GPX inválido");
        }
        const blob = new Blob([gpx], {
          type: "application/gpx+xml;charset=utf-8",
        });
        const fileName = `${sanitizeName(baseName)}-${Date.now()}.gpx`;
        const payload =
          typeof File === "function"
            ? new File([blob], fileName, { type: "application/gpx+xml" })
            : blob;
        const url = await uploadGPX(payload);
        await updateEvent(detail.eventId, { routeGPXUrl: url });
        showToast("✅ Trilho principal criado.");
      } catch (err) {
        console.error("Erro ao criar trilho principal:", err);
        showToast("❌ Não foi possível criar o trilho principal.");
      }
    };
    const handleWaypointsPlanSave = async (detail) => {
      if (!active) return;
      if (!detail || detail.__handled) return;
      const planTarget = detail?.planTarget || null;
      const targetEventId = detail?.planTargetData?.eventId || detail?.eventId || null;
      if (!targetEventId) return;
      const isAltTarget = planTarget === "eventAlt";
      const isMainTarget = planTarget === "eventMain";
      const isTourTarget = planTarget === "eventTour";
      const isDayTarget = planTarget === "eventDay";
      if (!isAltTarget && !isMainTarget && !isTourTarget && !isDayTarget) return;
      const normalized = normalizePoints(Array.isArray(detail.points) ? detail.points : []);
      if (!normalized.length) {
        showToast("⚠️ Percurso inválido.");
        return;
      }
      detail.__handled = true;
      try {
        const baseName =
          detail.planName ||
          `${detail.eventName || "Trilho"} (${
            isAltTarget ? "Alternativo" : isMainTarget ? "Principal" : isTourTarget ? "Tour" : "Dia"
          } por waypoints)`;
        const gpx = exportGPX(normalized, {
          name: baseName,
          date: new Date().toISOString(),
        });
        if (!gpx) {
          throw new Error("GPX inválido");
        }
        const blob = new Blob([gpx], {
          type: "application/gpx+xml;charset=utf-8",
        });
        const fileName = `${sanitizeName(baseName)}-${Date.now()}.gpx`;
        const payload =
          typeof File === "function"
            ? new File([blob], fileName, { type: "application/gpx+xml" })
            : blob;
        const url = await uploadGPX(payload);
        if (isAltTarget) {
          await addAlternativeRoute(targetEventId, {
            name: baseName,
            routeGPXUrl: url,
          });
          showToast("✅ Trilho alternativo criado a partir dos waypoints.");
        } else if (isMainTarget) {
          await updateEvent(targetEventId, { routeGPXUrl: url });
          showToast("✅ Trilho principal criado a partir dos waypoints.");
        } else if (isTourTarget) {
          await addTourRoute(targetEventId, {
            name: baseName,
            routeGPXUrl: url,
          });
          showToast("✅ Rota tour criada a partir dos waypoints.");
        } else if (isDayTarget) {
          const dayIndex = Number(detail?.planTargetData?.dayIndex);
          const allEvents = Array.isArray(events) ? events : [];
          const event = allEvents.find((ev) => String(ev?.id || "") === String(targetEventId || ""));
          const meetingPoints = Array.isArray(event?.meetingPoints)
            ? event.meetingPoints.map((mp) => ({ ...(mp || {}) }))
            : [];
          if (!Number.isInteger(dayIndex) || dayIndex < 0 || dayIndex >= meetingPoints.length) {
            throw new Error("day_index_invalid");
          }
          meetingPoints[dayIndex] = {
            ...(meetingPoints[dayIndex] || {}),
            dayGpxUrl: url,
            dayRouteName: baseName,
          };
          await updateEvent(targetEventId, { meetingPoints });
          showToast(`✅ Trilho do dia ${dayIndex + 1} criado a partir dos waypoints.`);
        }
      } catch (err) {
        console.error("Erro ao criar trilho por waypoints:", err);
        showToast("❌ Não foi possível criar o trilho.");
      }
    };
    const handleAlt = (ev) => handleEventAltSave(ev?.detail);
    const handleMain = (ev) => handleEventMainSave(ev?.detail);
    const handleWaypoints = (ev) => handleWaypointsPlanSave(ev?.detail);
    window.addEventListener("trekkin:event-alt-draw-save", handleAlt);
    window.addEventListener("trekkin:event-main-draw-save", handleMain);
    window.addEventListener("trekkin:waypoints-plan-saved", handleWaypoints);
    return () => {
      active = false;
      window.removeEventListener("trekkin:event-alt-draw-save", handleAlt);
      window.removeEventListener("trekkin:event-main-draw-save", handleMain);
      window.removeEventListener("trekkin:waypoints-plan-saved", handleWaypoints);
    };
  }, [addAlternativeRoute, addTourRoute, updateEvent, uploadGPX, events]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    get(ref(db, "waypointCategories"))
      .then((snap) => {
        if (cancelled) return;
        const raw = snap.val() || {};
        const merged = { ...(waypointCategoryPalette || {}) };
        let changed = false;
        Object.entries(raw).forEach(([id, data]) => {
          if (!data || typeof data !== "object") return;
          if (!data.color && !data.icon) return;
          if (!merged[id]) {
            merged[id] = {
              color: data.color || "#28a745",
              name: data.name || "",
              icon: data.icon || "",
            };
            changed = true;
            return;
          }
          const nextColor = data.color || merged[id].color || "#28a745";
          const nextName = data.name || merged[id].name || "";
          const nextIcon = data.icon || merged[id].icon || "";
          if (
            merged[id].color !== nextColor ||
            merged[id].name !== nextName ||
            merged[id].icon !== nextIcon
          ) {
            merged[id] = { color: nextColor, name: nextName, icon: nextIcon };
            changed = true;
          }
        });
        if (!changed) return;
        setWaypointCategoryPalette(merged);
        try {
          window.localStorage.setItem(
            "waypointsCategoryPalette",
            JSON.stringify(merged)
          );
        } catch {}
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user, waypointCategoryPalette]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (!isAdminUser) {
        setNavGridVisible(false);
        return;
      }
      const next = event?.detail?.on;
      if (event?.detail?.processingId) {
        navGridLayerProcessingIdRef.current = String(
          event.detail.processingId
        );
      }
      if (next === false && navGridLayerProcessingIdRef.current) {
        const processingId = navGridLayerProcessingIdRef.current;
        navGridLayerProcessingIdRef.current = null;
        window.dispatchEvent(
          new CustomEvent("trekkin:processing", {
            detail: { active: false, id: processingId },
          })
        );
      }
      if (typeof next === "boolean") {
        setNavGridVisible(next);
      } else {
        setNavGridVisible(readLocalFlag("navGridLayerOn"));
      }
    };
    window.addEventListener("trekkin:navgrid-toggle", handler);
    return () => window.removeEventListener("trekkin:navgrid-toggle", handler);
  }, [isAdminUser]);

  useEffect(() => {
    const loadSegments = () => {
      const grid = loadNavGrid();
      const displayGrid = filterNavGridByAccess(grid, {
        allowPremium: true,
        activityMode: navGridDisplayActivityMode,
      });
      const segments = Array.isArray(displayGrid.segments) ? displayGrid.segments : [];
      setNavGridSegments(segments);
      navGridConfigRef.current = grid;
      navGridGraphStampRef.current = 0;
      setNavGridNodesGraph(null);
    };
    loadSegments();
    if (typeof window === "undefined") return;
    window.addEventListener("trekkin:navgrid-updated", loadSegments);
    return () => {
      window.removeEventListener("trekkin:navgrid-updated", loadSegments);
    };
  }, [navGridDisplayActivityMode]);

  const requestNavGridForRouting = useCallback(
    (reason = "route") => {
      const applyLoadedGrid = () => {
        const grid = loadNavGrid();
        const segments = Array.isArray(grid?.segments) ? grid.segments : [];
        if (!segments.length) return false;
        const displayGrid = filterNavGridByAccess(grid, {
          allowPremium: true,
          activityMode,
        });
        navGridConfigRef.current = grid;
        navGridGraphStampRef.current = 0;
        setNavGridNodesGraph(null);
        setNavGridSegments(
          Array.isArray(displayGrid?.segments) ? displayGrid.segments : []
        );
        setPlanRouteRecalcTick((value) => value + 1);
        return true;
      };

      if (applyLoadedGrid()) return Promise.resolve(true);
      if (navGridRouteLoadPromiseRef.current) {
        return navGridRouteLoadPromiseRef.current;
      }

      const promise = (async () => {
        const startedAt = Date.now();
        try {
          logEvent("info", "NAVGRID_ROUTE_DEMAND_START", { reason });
        } catch {}

        let loader = window.__TREKKIN_LOAD_NAVGRID_ON_DEMAND;
        if (typeof loader !== "function") {
          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-demand", { detail: { reason } })
          );
          // The sync hook may still be mounting on the first app render.
          for (let attempt = 0; attempt < 4 && typeof loader !== "function"; attempt += 1) {
            await new Promise((resolve) => window.setTimeout(resolve, 100));
            loader = window.__TREKKIN_LOAD_NAVGRID_ON_DEMAND;
          }
        }
        if (typeof loader === "function") {
          await loader(false);
        }

        const loaded = applyLoadedGrid();
        try {
          logEvent(loaded ? "info" : "warn", loaded
            ? "NAVGRID_ROUTE_DEMAND_READY"
            : "NAVGRID_ROUTE_DEMAND_EMPTY", {
            reason,
            elapsedMs: Date.now() - startedAt,
            segments: Number(loadNavGrid()?.segments?.length || 0),
          });
        } catch {}
        return loaded;
      })()
        .catch((error) => {
          try {
            logEvent("error", "NAVGRID_ROUTE_DEMAND_FAILED", {
              reason,
              message: String(error?.message || error || "unknown"),
            });
          } catch {}
          return false;
        })
        .finally(() => {
          navGridRouteLoadPromiseRef.current = null;
        });

      navGridRouteLoadPromiseRef.current = promise;
      return promise;
    },
    [activityMode]
  );

  useEffect(() => {
    if (!planActive) return;
    if (navGridSegments.length && navGridConfigRef.current?.segments?.length) return;
    requestNavGridForRouting("plan_open");
  }, [planActive, navGridSegments.length, requestNavGridForRouting]);

  useEffect(() => {
    if (isAndroid || (!navWaypointTarget && !navWaypointPending)) return;
    if (navGridSegments.length && navGridConfigRef.current?.segments?.length) return;
    requestNavGridForRouting("waypoint_navigation");
  }, [
    isAndroid,
    navWaypointTarget,
    navWaypointPending,
    navGridSegments.length,
    requestNavGridForRouting,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (!isAdminUser) {
        setNavGridNodesVisible(false);
        return;
      }
      if (isIosSafeModeActive()) {
        setNavGridNodesVisible(false);
        return;
      }
      const next = event?.detail?.on;
      if (typeof next === "boolean") {
        setNavGridNodesVisible(next);
      } else {
        setNavGridNodesVisible(readLocalFlag("navGridNodesOn"));
      }
    };
    window.addEventListener("trekkin:navgrid-nodes-toggle", handler);
    return () =>
      window.removeEventListener("trekkin:navgrid-nodes-toggle", handler);
  }, [isAdminUser]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const ids = Array.isArray(event?.detail?.ids)
        ? event.detail.ids
            .map((id) => (typeof id === "string" ? id : String(id)))
            .filter((id) => typeof id === "string" && id)
        : [];
      setNavGridNodesFilter(ids);
    };
    window.addEventListener("trekkin:navgrid-nodes-filter", handler);
    return () =>
      window.removeEventListener("trekkin:navgrid-nodes-filter", handler);
  }, []);

  useEffect(() => {
    if (isIosSafeModeActive()) return;
    const needsGraph =
      navGridNodesVisible ||
      navGridEditNodesActive ||
      navGridAddNodeActive ||
      navGridDeleteNodesActive ||
      navGridSplitNodeActive ||
      !!navWaypointTarget ||
      (!isAndroid && !!navWaypointPending);
    if (!needsGraph) return;
    const gridFiltered = navGridConfigRef.current;
    const gridFull = loadNavGrid();
    const grid =
      gridFull?.segments?.length
        ? gridFull
        : gridFiltered;
    const stamp = Number(grid?.updatedAt || 0) || 0;
    if (
      navGridNodesGraph?.nodes?.length &&
      navGridGraphStampRef.current === stamp &&
      navGridGraphModeRef.current === (navGridDisplayActivityMode || "all")
    ) {
      return;
    }
    const build = () => {
      const nextGraph = buildNavGridGraph(grid, {
        activityMode: navGridDisplayActivityMode,
      });
      navGridGraphStampRef.current = stamp;
      navGridGraphModeRef.current = navGridDisplayActivityMode || "all";
      setNavGridNodesGraph(nextGraph);
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(build, { timeout: 1200 });
    } else {
      window.setTimeout(build, 0);
    }
  }, [
    navGridNodesVisible,
    navGridEditNodesActive,
    navGridAddNodeActive,
    navGridDeleteNodesActive,
    navGridSplitNodeActive,
    navGridSegments,
    navGridNodesGraph,
    navWaypointTarget,
    navWaypointPending,
    navGridDisplayActivityMode,
    isAndroid,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (!isAdminUser) {
        setNavGridNodesVisible(false);
        setNavGridEditNodesActive(false);
        setNavGridNodeMode(null);
        return;
      }
      if (isIosSafeModeActive()) {
        setNavGridNodesVisible(false);
        setNavGridEditNodesActive(false);
        setNavGridNodeMode(null);
        return;
      }
      const nextId = event?.detail?.segmentId;
      if (!nextId) return;
      setNavGridNodesFilter([nextId]);
      setNavGridNodesVisible(true);
      setNavGridEditNodesActive(true);
      setNavGridNodeMode("move");
    };
    window.addEventListener("trekkin:navgrid-focus-segment", handler);
    return () =>
      window.removeEventListener("trekkin:navgrid-focus-segment", handler);
  }, [isAdminUser]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (isIosSafeModeActive()) {
        setNavGridRoutePath([]);
        setNavGridRouteExternalSegments([]);
        setNavGridRouteColoredSegments([]);
        return;
      }
      const nextPath = Array.isArray(event?.detail?.path)
        ? event.detail.path
        : [];
      const nextExternalSegments = Array.isArray(event?.detail?.externalSegments)
        ? event.detail.externalSegments
        : [];
      const nextColoredSegments = Array.isArray(event?.detail?.coloredSegments)
        ? event.detail.coloredSegments
        : [];
      setNavGridRoutePath(nextPath);
      setNavGridRouteExternalSegments(nextExternalSegments);
      setNavGridRouteColoredSegments(nextColoredSegments);
      if (event?.detail?.fit === true && nextPath.length >= 2) {
        const map = mapRef.current;
        const coords = nextPath
          .map((point) =>
            point && Number.isFinite(point.lat) && Number.isFinite(point.lon)
              ? [point.lat, point.lon]
              : null
          )
          .filter(Boolean);
        if (map && coords.length >= 2) {
          try {
            map.fitBounds(coords, { padding: [36, 36], maxZoom: 18 });
          } catch {}
        }
      }
    };
    window.addEventListener("trekkin:navgrid-route", handler);
    return () => window.removeEventListener("trekkin:navgrid-route", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (!isAdminUser) {
        setNavGridEditNodesActive(false);
        return;
      }
      if (isIosSafeModeActive()) {
        setNavGridEditNodesActive(false);
        return;
      }
      const next = typeof event?.detail?.on === "boolean" ? event.detail.on : false;
      setNavGridEditNodesActive(next);
    };
    window.addEventListener("trekkin:navgrid-edit-nodes-toggle", handler);
    return () =>
      window.removeEventListener("trekkin:navgrid-edit-nodes-toggle", handler);
  }, [isAdminUser]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (!isAdminUser) {
        setNavGridAddNodeActive(false);
        return;
      }
      if (isIosSafeModeActive()) {
        setNavGridAddNodeActive(false);
        return;
      }
      const next = typeof event?.detail?.on === "boolean" ? event.detail.on : false;
      setNavGridAddNodeActive(next);
    };
    window.addEventListener("trekkin:navgrid-add-node-toggle", handler);
    return () =>
      window.removeEventListener("trekkin:navgrid-add-node-toggle", handler);
  }, [isAdminUser]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (!isAdminUser) {
        setNavGridDeleteNodesActive(false);
        return;
      }
      if (isIosSafeModeActive()) {
        setNavGridDeleteNodesActive(false);
        return;
      }
      const next = typeof event?.detail?.on === "boolean" ? event.detail.on : false;
      setNavGridDeleteNodesActive(next);
    };
    window.addEventListener("trekkin:navgrid-delete-nodes-toggle", handler);
    return () =>
      window.removeEventListener("trekkin:navgrid-delete-nodes-toggle", handler);
  }, [isAdminUser]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (!isAdminUser) {
        setNavGridSplitNodeActive(false);
        setNavGridEditNodesActive(false);
        return;
      }
      if (isIosSafeModeActive()) {
        setNavGridSplitNodeActive(false);
        return;
      }
      const next = typeof event?.detail?.on === "boolean" ? event.detail.on : false;
      if (next) {
        setNavGridEditNodesActive(true);
      }
      setNavGridSplitNodeActive(next);
    };
    window.addEventListener("trekkin:navgrid-split-node-toggle", handler);
    return () =>
      window.removeEventListener("trekkin:navgrid-split-node-toggle", handler);
  }, [isAdminUser]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (isIosSafeModeActive()) {
        setWaypointsVisible(false);
        setWaypointCategories([]);
        return;
      }
      const detail = event?.detail || {};
      if (detail.processingId) {
        waypointsLayerProcessingIdRef.current = String(detail.processingId);
      }
      if (!detail.on && waypointsLayerProcessingIdRef.current) {
        const processingId = waypointsLayerProcessingIdRef.current;
        waypointsLayerProcessingIdRef.current = null;
        window.dispatchEvent(
          new CustomEvent("trekkin:processing", {
            detail: { active: false, id: processingId },
          })
        );
      }
      setWaypointsVisible(!!detail.on);
      const categories = Array.isArray(detail.categories)
        ? detail.categories.filter((cat) => typeof cat === "string" && cat)
        : [];
      setWaypointCategories(categories);
      if (detail.categoryPalette && typeof detail.categoryPalette === "object") {
        setWaypointCategoryPalette(detail.categoryPalette);
        try {
          window.localStorage.setItem(
            "waypointsCategoryPalette",
            JSON.stringify(detail.categoryPalette)
          );
        } catch {}
      }
    };
    window.addEventListener("trekkin:waypoints-map-filter", handler);
    return () =>
      window.removeEventListener("trekkin:waypoints-map-filter", handler);
  }, []);

  useEffect(() => {
    if (!waypointsVisible) return;
    const hasLocal =
      Array.isArray(cachedWaypoints) &&
      cachedWaypoints.some(
        (wp) =>
          Number.isFinite(Number(wp?.lat ?? wp?.coords?.lat)) &&
          Number.isFinite(Number(wp?.lon ?? wp?.lng ?? wp?.coords?.lon ?? wp?.coords?.lng))
      );
    if (hasLocal) return;
    let cancelled = false;
    const run = async () => {
      let loadedCount = 0;
      try {
        const res = await fetchAndCacheWaypoints({ force: true });
        if (cancelled) return;
        const list = Array.isArray(res?.list) ? res.list : loadCachedWaypoints();
        loadedCount = Array.isArray(list) ? list.length : 0;
        setCachedWaypoints(Array.isArray(list) ? list : []);
      } catch {
        loadedCount = 0;
      } finally {
        if (!cancelled && loadedCount === 0) {
          const processingId = waypointsLayerProcessingIdRef.current;
          waypointsLayerProcessingIdRef.current = null;
          if (processingId) {
            window.dispatchEvent(
              new CustomEvent("trekkin:processing", {
                detail: { active: false, id: processingId },
              })
            );
          }
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [waypointsVisible, mapReady, user?.uid, cachedWaypoints]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const explicit = event?.detail?.on;
      setAlertsLayerOn((prev) => {
        const next = typeof explicit === "boolean" ? explicit : !prev;
        try {
          window.localStorage.setItem("alertsLayerOn", next ? "1" : "0");
        } catch {}
        window.dispatchEvent(
          new CustomEvent("trekkin:alerts-layer-changed", { detail: { on: next } })
        );
        return next;
      });
    };
    window.addEventListener("trekkin:alerts-view-map", handler);
    return () => window.removeEventListener("trekkin:alerts-view-map", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      if (typeof event?.detail?.on === "boolean") {
        setAlertsLayerOn(event.detail.on);
        return;
      }
      setAlertsLayerOn(readLocalFlag("alertsLayerOn"));
    };
    window.addEventListener("trekkin:alerts-layer-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("trekkin:alerts-layer-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncPrefs = () => {
      setAutoAlertsEnabled(readBoolPref("autoAlertsEnabled", true));
      setAutoAlertFauna(readBoolPref("autoAlertFauna", true));
      setAutoAlertTrilho(readBoolPref("autoAlertTrilho", true));
      setAutoAlertSeguranca(readBoolPref("autoAlertSeguranca", true));
      setAutoAlertCriticos(readBoolPref("autoAlertCriticos", true));
      setAutoAlertVibrate(readBoolPref("autoAlertVibrate", true));
      setAutoAlertSound(readBoolPref("autoAlertSound", true));
      setOffTrailAlertEnabled(readBoolPref("offTrailAlert", true));
      setOffTrailThresholdM(readOffTrailThresholdPref(40));
    };
    syncPrefs();
    window.addEventListener("trekkin:alerts-auto-changed", syncPrefs);
    window.addEventListener("storage", syncPrefs);
    return () => {
      window.removeEventListener("trekkin:alerts-auto-changed", syncPrefs);
      window.removeEventListener("storage", syncPrefs);
    };
  }, []);

  const followRouteForOffTrail = useMemo(() => {
    const localRoute = Array.isArray(trailBase)
      ? trailBase.filter(
          (p) =>
            Number.isFinite(Number(p?.lat)) &&
            Number.isFinite(Number(p?.lon ?? p?.lng))
        )
      : [];
    if (localRoute.length > 1) return localRoute;

    const gid =
      typeof currentGroup === "string"
        ? currentGroup
        : currentGroup?.id || currentGroup?.groupId || null;
    const { activeEvent, isLive } = getActiveEventForGroupSafe(
      groups,
      currentGroup || gid,
      eventsWithLive
    );
    if (!activeEvent || !isLive) return [];

    const mainRoute = Array.isArray(routePointsRef.current?.main)
      ? routePointsRef.current.main.filter(
          (p) =>
            Number.isFinite(Number(p?.lat)) &&
            Number.isFinite(Number(p?.lon ?? p?.lng))
        )
      : [];
    if (mainRoute.length > 1) return mainRoute;
    return [];
  }, [trailBase, routePointsVersion, currentGroup, groups, eventsWithLive]);

  const isNativeAndroid = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const capacitor = window?.Capacitor;
      return (
        !!capacitor?.isNativePlatform?.() &&
        capacitor?.getPlatform?.() === "android"
      );
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (isViewerMode) return;

    const resetRuntime = () => {
      offTrailRuntimeRef.current = { outside: false, lastAlertTs: 0 };
    };

    // Android nativo já trata alertas no serviço (FG+BG).
    // Evita duplicação de alertas no JS.
    if (isNativeAndroid) {
      resetRuntime();
      return;
    }

    const routePoints = followRouteForOffTrail;
    const hasFollowRoute =
      Array.isArray(routePoints) && routePoints.length > 1;
    if (!hasFollowRoute || !offTrailAlertEnabled) {
      resetRuntime();
      return;
    }

    const lastPoint = Array.isArray(points) && points.length
      ? points[points.length - 1]
      : null;
    const posLat = Number(userPosition?.lat ?? lastPoint?.lat);
    const posLon = Number(
      userPosition?.lon ?? userPosition?.lng ?? lastPoint?.lon ?? lastPoint?.lng
    );
    if (!Number.isFinite(posLat) || !Number.isFinite(posLon)) {
      return;
    }

    const probe = { lat: posLat, lon: posLon };
    const minDist = minDistanceToRouteMeters(probe, routePoints);
    if (!Number.isFinite(minDist)) return;

    const threshold = Number(offTrailThresholdM || 40);
    const prev = offTrailRuntimeRef.current || { outside: false, lastAlertTs: 0 };
    const exitThreshold = Math.max(8, threshold * 0.82);
    const outside = prev.outside
      ? minDist > exitThreshold
      : minDist > threshold;
    const now = Date.now();
    const enteringOutside = outside && !prev.outside;
    const minEnterGapMs = 8000;
    const shouldRepeat =
      outside &&
      ((enteringOutside && now - Number(prev.lastAlertTs || 0) >= minEnterGapMs) ||
        now - Number(prev.lastAlertTs || 0) >= OFF_TRAIL_ALERT_INTERVAL_MS);

    if (shouldRepeat) {
      if (autoAlertSound) {
        playPing();
      }
      if (autoAlertVibrate) {
        triggerAlertVibration([200, 80, 200]);
      }
    }

    offTrailRuntimeRef.current = {
      outside,
      lastAlertTs: outside
        ? shouldRepeat
          ? now
          : Number(prev.lastAlertTs || 0)
        : 0,
    };
  }, [
    isViewerMode,
    followRouteForOffTrail,
    points,
    userPosition?.lat,
    userPosition?.lon,
    userPosition?.lng,
    offTrailAlertEnabled,
    offTrailThresholdM,
    autoAlertSound,
    autoAlertVibrate,
    isNativeAndroid,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const capacitor = window?.Capacitor;
    if (!capacitor?.isNativePlatform?.()) return;
    if (capacitor?.getPlatform?.() !== "android") return;

    const plugin =
      capacitor?.Plugins?.GPSNativePlugin ||
      capacitor?.Plugins?.GPSNative;
    if (!plugin?.setOffTrailConfig) return;

    const routePoints = Array.isArray(followRouteForOffTrail)
      ? followRouteForOffTrail
          .map((p) => ({
            lat: Number(p?.lat),
            lon: Number(p?.lon ?? p?.lng),
          }))
          .filter(
            (p) => Number.isFinite(p.lat) && Number.isFinite(p.lon)
          )
      : [];

    const hasRoute = offTrailAlertEnabled && routePoints.length > 1;
    let sampled = routePoints;
    const MAX_NATIVE_ROUTE_POINTS = 1600;
    if (sampled.length > MAX_NATIVE_ROUTE_POINTS) {
      const step = Math.ceil(sampled.length / MAX_NATIVE_ROUTE_POINTS);
      const next = [];
      for (let i = 0; i < sampled.length; i += step) {
        next.push(sampled[i]);
      }
      const last = sampled[sampled.length - 1];
      if (
        last &&
        (!next.length ||
          next[next.length - 1].lat !== last.lat ||
          next[next.length - 1].lon !== last.lon)
      ) {
        next.push(last);
      }
      sampled = next;
    }

    plugin
      .setOffTrailConfig({
        enabled: hasRoute,
        thresholdM: Number(offTrailThresholdM || 40),
        repeatMs: OFF_TRAIL_ALERT_INTERVAL_MS,
        sound: !!autoAlertSound,
        vibrate: !!autoAlertVibrate,
        route: hasRoute ? sampled : [],
      })
      .catch(() => {});
  }, [
    followRouteForOffTrail,
    offTrailAlertEnabled,
    offTrailThresholdM,
    autoAlertSound,
    autoAlertVibrate,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event) => {
      const detail = event?.detail || {};
      setAlertsFilter({
        category: detail.category || "all",
        type: detail.type || "",
        sub: detail.sub || "",
        adminAll: Boolean(detail.adminAll),
        fromTs: Number.isFinite(detail.fromTs) ? detail.fromTs : null,
        toTs: Number.isFinite(detail.toTs) ? detail.toTs : null,
      });
    };
    window.addEventListener("trekkin:alerts-map-filter", handler);
    return () => window.removeEventListener("trekkin:alerts-map-filter", handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const list = Array.isArray(event?.detail?.list)
        ? event.detail.list
        : null;
      if (list) {
        setCachedWaypoints(list);
        return;
      }
      setCachedWaypoints(loadCachedWaypoints());
    };
    handler({ detail: { list: loadCachedWaypoints() } });
    if (typeof window === "undefined") return;
    window.addEventListener("trekkin:waypoints-cache-updated", handler);
    return () =>
      window.removeEventListener("trekkin:waypoints-cache-updated", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      setQueuedWaypointOps(
        Array.isArray(event?.detail?.queue)
          ? event.detail.queue
          : loadWaypointOpsQueue()
      );
    };
    window.addEventListener(WAYPOINTS_OPS_EVENT, handler);
    return () => window.removeEventListener(WAYPOINTS_OPS_EVENT, handler);
  }, []);

  const normalizedWaypoints = useMemo(() => {
    return applyWaypointOpsToList(cachedWaypoints, queuedWaypointOps)
      .map((wp) => {
        const lat = Number(wp?.lat ?? wp?.coords?.lat);
        const lon = Number(wp?.lon ?? wp?.lng ?? wp?.coords?.lon ?? wp?.coords?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        const status = wp?.status || "published";
        if (status !== "published") return null;
        if (
          wp?.accommodationPartner !== true &&
          wp?.visibleOnMap === false &&
          !(isAdminMode && partnerRecommendationAdminTarget?.id)
        ) return null;
        if (
          wp?.accommodationPartner !== true &&
          String(wp?.visibilityScope || "public") === "partner_recommendation" &&
          !isAdminMode
        ) {
          return null;
        }
        if (
          String(wp?.visibilityScope || "public") === "private" &&
          !canViewPrivateWaypoints
        ) {
          return null;
        }
        return { ...wp, lat, lon };
      })
      .filter(Boolean);
  }, [
    cachedWaypoints,
    queuedWaypointOps,
    canViewPrivateWaypoints,
    isAdminMode,
    partnerRecommendationAdminTarget?.id,
  ]);

  useEffect(() => {
    const partnerId = String(
      partnerRecommendationsPanel?.id || partnerRecommendationAdminTarget?.id || ""
    ).trim();
    if (!partnerId) {
      setPartnerRecommendations([]);
      return undefined;
    }
    let cancelled = false;
    const unsubscribe = onValue(ref(db, `waypoints/${partnerId}/recommendedWaypoints`), async (snapshot) => {
      const value = snapshot.val() || {};
      const waypointById = new Map(
        applyWaypointOpsToList(cachedWaypoints, queuedWaypointOps).map((waypoint) => [
          String(waypoint?.id || ""),
          waypoint,
        ])
      );
      const missingIds = Object.keys(value).filter(
        (waypointId) => !waypointById.has(String(waypointId))
      );
      if (missingIds.length) {
        const missingSnapshots = await Promise.all(
          missingIds.map((waypointId) => get(ref(db, `waypoints/${waypointId}`)))
        );
        missingSnapshots.forEach((waypointSnapshot, index) => {
          const waypoint = waypointSnapshot.val();
          if (waypoint) {
            waypointById.set(String(missingIds[index]), {
              ...waypoint,
              id: waypoint?.id || missingIds[index],
            });
          }
        });
      }
      if (cancelled) return;
      const list = Object.entries(value)
        .map(([waypointId, relation]) => {
          const waypoint = waypointById.get(String(waypointId));
          if (!waypoint) return null;
          return {
            ...waypoint,
            recommendationCategory: normalizePartnerRecommendationCategory(
              waypointCategoryPalette?.[waypoint?.categoryId]?.name ||
                waypoint?.categoryName ||
                relation?.category ||
                waypoint?.categoryId ||
                "Outros"
            ) || "Outros",
          };
        })
        .filter(Boolean)
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), language === "en" ? "en" : "pt"));
      setPartnerRecommendations(list);
      setPartnerRecommendationIndex(0);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [partnerRecommendationsPanel?.id, partnerRecommendationAdminTarget?.id, cachedWaypoints, queuedWaypointOps, waypointCategoryPalette, language]);

  useEffect(() => {
    setPartnerRecommendationIndex(0);
  }, [partnerRecommendationCategory]);

  const computeLiveWaypoints = useCallback(() => {
    const mainPoints = routePointsRef.current?.main || [];
    const altPointsById = routePointsRef.current?.alts || {};
    const altPoints = Object.values(altPointsById).flat();
    const routePoints = [...mainPoints, ...altPoints];
    if (!routePoints.length || !normalizedWaypoints.length) return [];

    const routeLatLngs = routePoints
      .map((pt) => {
        const lat = Number(pt?.lat ?? pt?.coords?.lat);
        const lon = Number(pt?.lon ?? pt?.lng ?? pt?.coords?.lon ?? pt?.coords?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        return { lat, lon };
      })
      .filter(Boolean);

    if (!routeLatLngs.length) return [];

    // Android: reduzir carga sem perder waypoints em trilhos longos.
    const maxSamples = isAndroid ? 1200 : 900;
    const step = Math.max(1, Math.floor(routeLatLngs.length / maxSamples));
    const sampledRoute = step > 1 ? routeLatLngs.filter((_, idx) => idx % step === 0) : routeLatLngs;
    let nearby = normalizedWaypoints.filter((wp) =>
      sampledRoute.some(
        (pt) =>
          haversineMeters({ lat: pt.lat, lon: pt.lon }, { lat: wp.lat, lon: wp.lon }) <=
          LIVE_WAYPOINT_DISTANCE_METERS
      )
    );

    // Fallback: se a amostragem não encontrou nenhum waypoint,
    // tenta a rota completa (com teto para evitar custo excessivo).
    if (!nearby.length && sampledRoute.length < routeLatLngs.length && routeLatLngs.length <= 22000) {
      nearby = normalizedWaypoints.filter((wp) =>
        routeLatLngs.some(
          (pt) =>
            haversineMeters({ lat: pt.lat, lon: pt.lon }, { lat: wp.lat, lon: wp.lon }) <=
            LIVE_WAYPOINT_DISTANCE_METERS
        )
      );
    }

    return nearby;
  }, [normalizedWaypoints, isAndroid]);

  const liveWaypointsCacheKey = useCallback(
    (eventId) => `trekkin_live_waypoints_${eventId}`,
    []
  );

  const persistLiveWaypointsCache = useCallback(
    (eventId, list) => {
      if (!eventId) return;
      try {
        const payload = {
          ts: Date.now(),
          list: Array.isArray(list) ? list : [],
        };
        localStorage.setItem(liveWaypointsCacheKey(eventId), JSON.stringify(payload));
      } catch {}
    },
    [liveWaypointsCacheKey]
  );

  const clearLiveWaypointsCache = useCallback(
    (eventId) => {
      if (!eventId) return;
      try {
        localStorage.removeItem(liveWaypointsCacheKey(eventId));
      } catch {}
    },
    [liveWaypointsCacheKey]
  );

  const readLiveWaypointsCache = useCallback(
    (eventId) => {
      if (!eventId) return [];
      try {
        const raw = localStorage.getItem(liveWaypointsCacheKey(eventId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        const list = Array.isArray(parsed?.list) ? parsed.list : [];
        return list.filter((wp) => {
          const lat = Number(wp?.lat ?? wp?.coords?.lat);
          const lon = Number(wp?.lon ?? wp?.lng ?? wp?.coords?.lon ?? wp?.coords?.lng);
          return Number.isFinite(lat) && Number.isFinite(lon);
        });
      } catch {
        return [];
      }
    },
    [liveWaypointsCacheKey]
  );

  const ensureLiveLayer = useCallback(() => {
    const map = mapRef.current;
    if (!map) return null;
    const waypointPane = "waypointsPane";
    const layerOptions = waypointPane ? { pane: waypointPane } : undefined;
    if (!liveWaypointsLayerRef.current) {
      liveWaypointsLayerRef.current = L.layerGroup([], layerOptions).addTo(map);
    }
    return liveWaypointsLayerRef.current;
  }, []);

  const clearLiveWaypointsLayer = useCallback(() => {
    if (liveWaypointsLayerRef.current) {
      liveWaypointsLayerRef.current.clearLayers();
    }
  }, []);

  const navigateToWaypoint = useCallback(
    (wp) => {
      if (planActiveRef.current) {
        showToast("Termina o planeamento antes de navegar.", "error");
        return;
      }
      if (!canNavigateWaypointsRef.current) {
        showPlanUpgradeToast(language === "en"
          ? "❗ Your plan does not allow navigation to this waypoint."
          : "❗ O teu plano não permite navegar para este waypoint.");
        return;
      }
      if (!wp?.lat || !wp?.lon) {
        showToast("Coordenadas inválidas.", "error");
        return;
      }
      if (waypointNavResolvingRef.current) return;
      const userPos = userPositionRef.current || null;
      try {
        logEvent("info", "NAV_WAYPOINT_TAP_NAVIGATE", {
          waypointId: String(wp?.id || ""),
          waypointName: String(wp?.name || ""),
          waypointLat: Number(wp?.lat),
          waypointLon: Number(wp?.lon),
          userLat: Number(userPos?.lat),
          userLon: Number(userPos?.lon),
          hadTarget: !!usePanelsStore.getState()?.navWaypointTarget,
          queueCount: Array.isArray(usePanelsStore.getState()?.navWaypointQueue)
            ? usePanelsStore.getState().navWaypointQueue.length
            : 0,
        });
      } catch {}
      if (!userPos?.lat || !userPos?.lon) {
        openNativeDirections(wp.lat, wp.lon);
        return;
      }
      const processingId = `waypoint-route-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      window.dispatchEvent(
        new CustomEvent("trekkin:processing", {
          detail: {
            active: true,
            id: processingId,
            text: "A calcular o melhor acesso à rede...",
            minimumMs: 650,
          },
        })
      );
      waypointNavResolvingRef.current = true;
      setWaypointNavResolving(true);
      try {
        if (waypointNavResolvingTimerRef.current) {
          window.clearTimeout(waypointNavResolvingTimerRef.current);
        }
        waypointNavResolvingTimerRef.current = window.setTimeout(() => {
          if (isAndroid) {
            // A operacao continua ativa. Libertar o lock aqui permitia iniciar
            // uma segunda hidratacao/grafo e podia bloquear o WebView.
            showToast("A rede ainda está a carregar.", "info");
            return;
          }
          waypointNavResolvingRef.current = false;
          setWaypointNavResolving(false);
          showToast("Demorou demasiado a calcular acesso à rede.", "warning");
        }, isAndroid ? 30000 : 12000);
      } catch {}
      window.setTimeout(async () => {
        try {
          const cachedGrid = loadNavGrid();
          const hasCachedGrid =
            Array.isArray(cachedGrid?.segments) && cachedGrid.segments.length > 0;
          if (!isAndroid) {
            await window.__TREKKIN_LOAD_NAVGRID_ON_DEMAND?.(false);
          }
          if (!isRecordingRef.current) {
            try {
              const mode = usePanelsStore.getState().recordingMode === "expedition"
                ? "expedition"
                : "normal";
              startRecording({ mode });
              showToast("Gravação iniciada automaticamente.", "success");
            } catch {}
          }
          try {
            setActivePanel("nav");
          } catch {}
          if (isAndroid && !hasCachedGrid) {
            const nearest = await findNearestAndroidNavEntry(userPos, {
              allowPremium: canUsePremiumSegmentsRef.current,
              activityMode,
            });
            if (!nearest) {
              showToast("Sem rede de navegação disponível.", "error");
              openNativeDirections(wp.lat, wp.lon, { origin: userPos });
              return;
            }
            const threshold =
              Number(navGridConfigRef.current?.entryPromptMeters) || 200;
            const payload = {
              id: wp.id,
              name: wp.name || "Waypoint",
              lat: wp.lat,
              lon: wp.lon,
            };
            clearNavWaypoint();
            window.dispatchEvent(
              new CustomEvent("trekkin:navgrid-route", {
                detail: { path: [], externalSegments: [], coloredSegments: [] },
              })
            );
            setNavWaypointPending({
              target: payload,
              entry: { lat: nearest.node.lat, lon: nearest.node.lon },
              thresholdMeters: threshold,
              createdAt: Date.now(),
            });
            const prefetchStartedAt = Date.now();
            prefetchAndroidNavRouteGrid(nearest.node, payload, {
              allowPremium: canUsePremiumSegmentsRef.current,
              activityMode,
              baseGrid: navGridConfigRef.current,
            })
              .then((routeGrid) => {
                try {
                  logEvent("info", "NAV_WAYPOINT_ROUTE_GRID_READY", {
                    waypointId: String(payload?.id || ""),
                    segments: Number(routeGrid?.segments?.length || 0),
                    elapsedMs: Date.now() - prefetchStartedAt,
                  });
                } catch {}
              })
              .catch((error) => {
                try {
                  logEvent("warn", "NAV_WAYPOINT_ROUTE_GRID_FAILED", {
                    waypointId: String(payload?.id || ""),
                    error: String(error?.message || error || "unknown"),
                  });
                } catch {}
              });
            try {
              logEvent("info", "NAV_WAYPOINT_PENDING_SET", {
                waypointId: String(payload?.id || ""),
                entryLat: Number(nearest.node.lat),
                entryLon: Number(nearest.node.lon),
                nearestDist: Number(nearest.dist),
                threshold,
                lightweightIndex: true,
              });
            } catch {}
            showToast("A caminho da rede. Segue a linha até ao ponto de entrada.");
            return;
          }
          const fullGrid = hasCachedGrid ? cachedGrid : loadNavGrid();
          const grid = filterNavGridByAccess(fullGrid, {
            allowPremium: canUsePremiumSegmentsRef.current,
            activityMode,
          });
          if (!grid?.segments?.length) {
            showToast("Rede de navegação vazia.", "error");
            openNativeDirections(wp.lat, wp.lon, { origin: userPos });
            return;
          }
          let nearest = null;
          if (isAndroid) {
            // Encontrar apenas o ponto de entrada e barato e suficiente enquanto
            // o utilizador ainda esta fora da rede. Montar o grafo nacional aqui
            // bloqueava o WebView durante varios segundos.
            for (const segment of grid.segments) {
              const points = Array.isArray(segment?.points) ? segment.points : [];
              for (const point of points) {
                const lat = Number(point?.lat);
                const lon = Number(point?.lon ?? point?.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
                const dist = haversineMeters(
                  { lat: Number(userPos.lat), lon: Number(userPos.lon) },
                  { lat, lon }
                );
                if (!nearest || dist < nearest.dist) {
                  nearest = { node: { ...point, lat, lon }, dist };
                }
              }
            }
          } else {
            const graph = buildNavGridGraph(grid, {
              mergeMeters: grid.mergeMeters ?? grid.routeMergeMeters,
            });
            nearest = findNearestGraphNode(graph, {
              lat: userPos.lat,
              lon: userPos.lon,
            });
          }
          try {
            logEvent("info", "NAV_WAYPOINT_ENTRY_CHECK", {
              waypointId: String(wp?.id || ""),
              threshold: Number(grid?.entryPromptMeters) || 200,
              nearestDist: Number(nearest?.dist),
              nearestNodeLat: Number(nearest?.node?.lat),
              nearestNodeLon: Number(nearest?.node?.lon),
            });
          } catch {}
          const threshold = Number(grid?.entryPromptMeters) || 200;
          if (!nearest) {
            showToast("Sem rede de navegação disponível.", "error");
            openNativeDirections(wp.lat, wp.lon, { origin: userPos });
            return;
          }
          if (isAndroid && nearest.dist > threshold) {
            const payload = {
              id: wp.id,
              name: wp.name || "Waypoint",
              lat: wp.lat,
              lon: wp.lon,
            };
            clearNavWaypoint();
            window.dispatchEvent(
              new CustomEvent("trekkin:navgrid-route", {
                detail: { path: [], externalSegments: [], coloredSegments: [] },
              })
            );
            setNavWaypointPending({
              target: payload,
              entry: { lat: nearest.node.lat, lon: nearest.node.lon },
              thresholdMeters: threshold,
              createdAt: Date.now(),
            });
            try {
              logEvent("info", "NAV_WAYPOINT_PENDING_SET", {
                waypointId: String(payload?.id || ""),
                entryLat: Number(nearest.node.lat),
                entryLon: Number(nearest.node.lon),
                nearestDist: Number(nearest.dist),
                threshold,
                lightweight: true,
              });
            } catch {}
            showToast("A caminho da rede. Segue a linha até ao ponto de entrada.");
            return;
          }
          if (!canUsePremiumSegmentsRef.current) {
            const mergeMeters = Number(grid.mergeMeters ?? grid.routeMergeMeters) || 30;
            const routeWithoutPremium = computeNavGridRoute(
              grid,
              { lat: userPos.lat, lon: userPos.lon },
              { lat: Number(wp.lat), lon: Number(wp.lon) },
              { mergeMeters, maxSnapMeters: 400 }
            );
            if (!routeWithoutPremium?.ok) {
              const routeWithPremium = computeNavGridRoute(
                fullGrid,
                { lat: userPos.lat, lon: userPos.lon },
                { lat: Number(wp.lat), lon: Number(wp.lon) },
                { mergeMeters, maxSnapMeters: 400 }
              );
              if (routeWithPremium?.ok) {
                showToast(
                  "Para aceder a este local apenas com guia presencial ou guia online.",
                  "warning"
                );
                return;
              }
            }
          }
          const payload = {
            id: wp.id,
            name: wp.name || "Waypoint",
            lat: wp.lat,
            lon: wp.lon,
          };
          // Sempre reiniciar a navegação ao usar "Navegar" no popup de waypoint.
          // Evita queue/target stale a puxar rota para corredor antigo.
          clearNavWaypoint();
          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-route", {
              detail: { path: [], externalSegments: [], coloredSegments: [] },
            })
          );
          if (nearest.dist > threshold) {
            // Evita bloqueio em iOS: usa entrada imediata pelo nó mais próximo.
            const entryNode = nearest.node;
            setNavWaypointPending({
              target: payload,
              entry: { lat: entryNode.lat, lon: entryNode.lon },
              thresholdMeters: threshold,
              createdAt: Date.now(),
            });
            try {
              logEvent("info", "NAV_WAYPOINT_PENDING_SET", {
                waypointId: String(payload?.id || ""),
                entryLat: Number(entryNode?.lat),
                entryLon: Number(entryNode?.lon),
                nearestDist: Number(nearest?.dist),
                threshold,
              });
            } catch {}
            showToast("A caminho da rede. Segue a linha até ao ponto de entrada.");
            return;
          }
          try {
            localStorage.setItem("navGridLayerOn", "0");
            localStorage.setItem("navGridNodesOn", "0");
          } catch {}
          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-toggle", { detail: { on: false } })
          );
          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-nodes-toggle", { detail: { on: false } })
          );
          setNavWaypointTarget(payload);
          try {
            logEvent("info", "NAV_WAYPOINT_TARGET_SET", {
              waypointId: String(payload?.id || ""),
              waypointLat: Number(payload?.lat),
              waypointLon: Number(payload?.lon),
              mode: "direct",
            });
          } catch {}
          showToast("Navegação iniciada.", "success");
        } finally {
          try {
            if (waypointNavResolvingTimerRef.current) {
              window.clearTimeout(waypointNavResolvingTimerRef.current);
              waypointNavResolvingTimerRef.current = null;
            }
          } catch {}
          waypointNavResolvingRef.current = false;
          setWaypointNavResolving(false);
          window.dispatchEvent(
            new CustomEvent("trekkin:processing", {
              detail: { active: false, id: processingId },
            })
          );
          try {
            logEvent("info", "NAV_WAYPOINT_RESOLVING_END", {
              waypointId: String(wp?.id || ""),
              at: Date.now(),
            });
          } catch {}
        }
      }, 16);
    },
    [
      startRecording,
      setNavWaypointPending,
      setNavWaypointTarget,
      clearNavWaypoint,
      setActivePanel,
      setWaypointNavResolving,
      showToast,
      isAndroid,
      language,
    ]
  );

  const drawLiveWaypoints = useCallback(
    (list) => {
      const layer = ensureLiveLayer();
      if (!layer) return;
      layer.clearLayers();
      const userLat = userPosition?.lat;
      const userLon = userPosition?.lon ?? userPosition?.lng;
      const hasUser = Number.isFinite(userLat) && Number.isFinite(userLon);
      list.forEach((wp) => {
        if (!wp || !Number.isFinite(wp.lat) || !Number.isFinite(wp.lon)) return;
        const paletteSource =
          liveWaypointPalette && typeof liveWaypointPalette === "object"
            ? liveWaypointPalette
            : waypointCategoryPalette;
        const paletteEntry =
          wp.categoryId && paletteSource && typeof paletteSource === "object"
            ? paletteSource[wp.categoryId]
            : null;
        const markerColor = paletteEntry?.color || "#28a745";
        const markerIconName = String(
          wp?.icon || paletteEntry?.icon || "waypoints.svg"
        ).trim();
        const iconUrl = `${base}icons/${markerIconName}`;
        const waypointPane = "waypointsPane";
        const marker = L.marker([wp.lat, wp.lon], {
          pane: waypointPane || undefined,
          icon: L.divIcon({
            className: "",
            html: `
              <div class="tk-waypoint-marker-visual" style="
                width:18px;height:18px;border-radius:50%;
                border:2px solid ${markerColor};
                background:#ffffff;
                display:flex;align-items:center;justify-content:center;
                box-shadow:0 1px 2px rgba(0,0,0,0.25);
              ">
                <img src="${iconUrl}" alt="" draggable="false"
                  style="width:11px;height:11px;display:block;object-fit:contain;pointer-events:none;-webkit-user-drag:none;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none;" />
              </div>`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            popupAnchor: [0, -10],
          }),
        });
        const closePopupHard = () => {
          try {
            marker?.closePopup?.();
          } catch {}
          try {
            mapRef.current?.closePopup?.();
          } catch {}
        };
        // Android: popup leve (sem imagem) para garantir abertura
        const popup = L.DomUtil.create("div", "tk-waypoint-popup");

        if (popup) {
          popup.style.display = "flex";
          popup.style.flexDirection = "column";
          popup.style.gap = "6px";
          popup.style.width = "100%";

          const title = L.DomUtil.create("div", "", popup);
          title.textContent = language === "en"
            ? (wp.translations?.en?.name || wp.name || "Waypoint")
            : (wp.name || "Waypoint");
          title.style.fontWeight = "600";
          title.style.color = "#2c6b3f";
          title.style.fontSize = "13px";
          title.style.whiteSpace = "nowrap";
          title.style.textOverflow = "ellipsis";
          title.style.overflow = "hidden";

          const imageUrlRaw =
            wp.imageUrl ||
            wp.image ||
            wp.photoUrl ||
            wp.photo ||
            wp.coverImage ||
            (Array.isArray(wp.images) ? wp.images[0] : null) ||
            (Array.isArray(wp.photos) ? wp.photos[0] : null) ||
            null;
          const imageCandidates = disableWaypointImagesForStability
            ? []
            : buildWaypointImageCandidates(imageUrlRaw, isIOS);
          if (imageCandidates.length) {
            const img = L.DomUtil.create("img", "", popup);
            let imageIndex = 0;
            img.src = imageCandidates[imageIndex] || "";
            img.alt = "";
            img.loading = "lazy";
            img.decoding = "async";
            img.style.width = "280px";
            img.style.height = "280px";
            img.style.objectFit = "cover";
            img.style.borderRadius = "8px";
            img.style.alignSelf = "center";
            img.onerror = () => {
              imageIndex += 1;
              if (imageIndex < imageCandidates.length) {
                img.src = imageCandidates[imageIndex];
                return;
              }
              try {
                img.remove();
              } catch {}
            };
          }

          const meta = L.DomUtil.create("div", "", popup);
          meta.style.display = "flex";
          meta.style.flexDirection = "column";
          meta.style.gap = "4px";

          const coordsLine = L.DomUtil.create("div", "", meta);
          coordsLine.style.fontSize = "12px";
          coordsLine.style.color = "#666";
          coordsLine.textContent = `${wp.lat?.toFixed?.(5) ?? wp.lat}, ${wp.lon?.toFixed?.(5) ?? wp.lon}`;

          const descText = language === "en"
            ? (wp.translations?.en?.desc || wp.translations?.en?.description || wp.translations?.en?.details || wp.translations?.en?.text || wp.translations?.en?.note || "")
            : (wp.desc || wp.description || wp.details || wp.text || wp.note || "");
          if (descText) {
            const descLine = L.DomUtil.create("div", "", meta);
            descLine.style.fontSize = "12px";
            descLine.style.color = "#555";
            descLine.style.whiteSpace = "pre-wrap";
            descLine.style.overflowWrap = "anywhere";
            descLine.textContent = descText;
          }

          const telText = readWaypointContactValue(wp, ["tel", "phone", "telefone"]);
          const emailText = readWaypointContactValue(wp, ["email", "mail", "eMail"]);
          const webText = readWaypointContactValue(wp, ["www", "web", "website", "url"]);
          createWaypointContactButtons(meta, {
            tel: telText,
            email: emailText,
            web: webText,
          }, language);

          const categoryLabel = wp.categoryName || wp.categoryId;
          if (categoryLabel) {
            const categoryLine = L.DomUtil.create("div", "", meta);
            categoryLine.style.fontSize = "12px";
            categoryLine.style.color = "#666";
            categoryLine.textContent = categoryLabel;
          }

          if (navWaypointPending?.target?.id && navWaypointPending.target.id === wp.id) {
            const pendingLine = L.DomUtil.create("div", "", meta);
            pendingLine.style.fontSize = "12px";
            pendingLine.style.color = "#f59f0a";
            pendingLine.style.fontWeight = "600";
            pendingLine.textContent = "A caminho da rede...";
          }

          if (hasUser) {
            const dist = haversineMeters(
              { lat: userLat, lon: userLon },
              { lat: wp.lat, lon: wp.lon }
            );
            const distanceLine = L.DomUtil.create("div", "", meta);
            distanceLine.style.fontSize = "12px";
            distanceLine.style.color = "#666";
            distanceLine.textContent = `${language === "en" ? "Current distance" : "Distância atual"}: ${(dist / 1000).toFixed(2)} km`;
          }

          if (canNavigateWaypoints) {
            const row = L.DomUtil.create("div", "", popup);
            row.style.display = "flex";
            row.style.gap = "6px";
            row.style.justifyContent = "space-between";
            row.style.flexWrap = "wrap";
            const navBtn = L.DomUtil.create("button", "", row);
            const navIcon = L.DomUtil.create("img", "", navBtn);
            navIcon.src = `${base}icons/${waypointStartIcon(activityMode)}`;
            navIcon.alt = "";
            navIcon.style.width = "18px";
            navIcon.style.height = "18px";
            navIcon.style.filter = "brightness(0) invert(1)";
            const navText = L.DomUtil.create("span", "", navBtn);
            navText.textContent = waypointStartLabel(activityMode, language);
            navBtn.style.flex = "1 1 100px";
            navBtn.style.padding = "6px 8px";
            navBtn.style.borderRadius = "6px";
            navBtn.style.border = "none";
            navBtn.style.color = "white";
            navBtn.style.fontSize = "12px";
            navBtn.style.cursor = "pointer";
            navBtn.style.background = "#ff9800";
            navBtn.style.display = "inline-flex";
            navBtn.style.alignItems = "center";
            navBtn.style.justifyContent = "center";
            navBtn.style.gap = "7px";
            L.DomEvent.on(navBtn, "click", (event) => {
              L.DomEvent.stopPropagation(event);
              navigateToWaypoint(wp);
              closePopupHard();
            });
          }

          L.DomEvent.disableClickPropagation(popup);
          marker.bindPopup(popup, {
            minWidth: 180,
            className: "tk-waypoint-popup",
          });
        }
        layer.addLayer(marker);
      });
    },
    [
      ensureLiveLayer,
      liveWaypointPalette,
      waypointCategoryPalette,
      userPosition,
      canNavigateWaypoints,
      navigateToWaypoint,
      navWaypointPending,
      isAndroid,
      activityMode,
      language,
    ]
  );

  const currentGroupIdSafe = useMemo(() => {
    if (typeof currentGroup === "string") return currentGroup;
    return (
      currentGroup?.id ||
      currentGroup?.groupId ||
      currentGroup?.firebaseId ||
      currentGroup?.key ||
      currentGroupId ||
      null
    );
  }, [
    currentGroupId,
    currentGroup?.id,
    currentGroup?.groupId,
    currentGroup?.firebaseId,
    currentGroup?.key,
  ]);

  const currentGroupActiveEventId = useMemo(() => {
    if (!currentGroup || typeof currentGroup !== "object") return null;
    return (
      currentGroup.activeEventId || currentGroup.activeEvent || currentGroup.eventId || null
    );
  }, [currentGroup?.activeEventId, currentGroup?.activeEvent, currentGroup?.eventId]);

  const liveEventState = useMemo(() => {
    const eventsArr = Array.isArray(eventsWithLive)
      ? eventsWithLive
      : eventsWithLive && typeof eventsWithLive === "object"
        ? Object.values(eventsWithLive)
        : [];

    let activeEventById = null;
    if (currentGroupActiveEventId && eventsArr.length) {
      activeEventById =
        eventsArr.find(
          (ev) =>
            ev?.id === currentGroupActiveEventId ||
            ev?.eventId === currentGroupActiveEventId ||
            ev?.id === String(currentGroupActiveEventId)
        ) || null;
    }

    let liveEventByGroup = null;
    if (currentGroupIdSafe && eventsArr.length) {
      liveEventByGroup =
        eventsArr.find(
          (ev) =>
            ev?.isLive &&
            ev?.groupId &&
            (ev.groupId === currentGroupIdSafe ||
              ev.groupId === String(currentGroupIdSafe))
        ) || null;
    }

    // Se activeEventId estiver desatualizado, preferimos sempre o LIVE real do grupo.
    const activeEvent = liveEventByGroup || activeEventById || null;
    const isLive = !!(liveEventByGroup?.isLive || activeEventById?.isLive);

    return { activeEvent, isLive };
  }, [eventsWithLive, currentGroupActiveEventId, currentGroupIdSafe]);

  useEffect(() => {
    if (!mapReady || isViewerMode) return;
    const uid = user?.uid || null;
    const gid = currentGroupIdSafe || null;
    const activeEventId =
      liveEventState?.activeEvent?.id ||
      liveEventState?.activeEvent?.eventId ||
      liveEventState?.activeEvent?.key ||
      null;
    const canSeeFn = useEventsStore.getState().canUserSeeEventLive;
    const canSeeEvent =
      activeEventId && typeof canSeeFn === "function" ? !!canSeeFn(activeEventId) : null;
    const membersObj =
      gid && memberLocations && memberLocations[gid] ? memberLocations[gid] : {};
    const membersCount = Object.keys(membersObj || {}).length;
    const markersCount = Object.keys(groupMarkersRef.current || {}).length;
    const routeMainCount = Array.isArray(routePointsRef.current?.main)
      ? routePointsRef.current.main.length
      : 0;
    const routeAltPoints = Object.values(routePointsRef.current?.alts || {}).reduce(
      (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
      0
    );
    const payload = {
      uid,
      gid,
      activeEventId,
      isLive: !!liveEventState?.isLive,
      canSeeEvent,
      membersCount,
      markersCount,
      routeMainCount,
      routeAltPoints,
      cachedWaypointsCount: Array.isArray(cachedWaypoints) ? cachedWaypoints.length : 0,
    };
    const diagKey = `${gid || "nogid"}:${activeEventId || "noevent"}:${payload.isLive}:${canSeeEvent}:${membersCount}:${markersCount}:${routeMainCount}:${routeAltPoints}:${payload.cachedWaypointsCount}`;
    const now = Date.now();
    if (
      liveRenderStateDiagRef.current.key !== diagKey ||
      now - liveRenderStateDiagRef.current.ts > 10000
    ) {
      liveRenderStateDiagRef.current = { key: diagKey, ts: now };
      logEvent("info", "LIVE_MAP_RENDER_STATE", payload);
      console.info("[LIVE_MAP_RENDER_STATE]", payload);
    }
  }, [
    mapReady,
    isViewerMode,
    user?.uid,
    currentGroupIdSafe,
    memberLocations,
    liveEventState,
    routePointsVersion,
    cachedWaypoints,
  ]);

  const canCurrentUserUseResolvedLiveEvent = useCallback(
    (event) => {
      const authUser = user || useAuthStore.getState().user;
      const uid = authUser?.uid || null;
      if (!uid || !event) return false;

      const role = String(authUser?.role || "free").toLowerCase();
      const isAdminOrGuide = role === "admin" || role === "guide";
      if (isAdminOrGuide) return true;

      if (
        event?.createdByUid === uid ||
        event?.guideId === uid ||
        event?.guideUid === uid ||
        event?.guide === uid ||
        event?.coGuideId === uid ||
        event?.coGuideUid === uid
      ) {
        return true;
      }

      const groupId = event?.groupId || event?.group || null;
      if (groupId) {
        const groupsArr = Array.isArray(groups)
          ? groups
          : groups && typeof groups === "object"
            ? Object.values(groups)
            : [];
        const groupMatch =
          groupsArr.find(
            (g) =>
              g?.id === groupId ||
              g?.groupId === groupId ||
              String(g?.id || "") === String(groupId) ||
              String(g?.groupId || "") === String(groupId)
          ) || null;
        if (groupMatch?.members?.[uid] === true || !!groupMatch?.members?.[uid]) {
          return true;
        }
      }

      // Fallback adicional por grupo atual ativo (troca de conta / caches atrasados).
      const currentGroupState = useGroupStore.getState();
      const cg =
        currentGroupState?.currentGroup &&
        typeof currentGroupState.currentGroup === "object"
          ? currentGroupState.currentGroup
          : null;
      const cgId =
        cg?.id || cg?.groupId || cg?.firebaseId || cg?.key || currentGroupIdSafe || null;
      const cgActiveEventId = cg?.activeEventId || cg?.activeEvent || cg?.eventId || null;
      if (
        cgId &&
        cgActiveEventId &&
        String(cgActiveEventId) ===
          String(event?.id || event?.eventId || event?.key || "")
      ) {
        const groupsArr = Array.isArray(groups)
          ? groups
          : groups && typeof groups === "object"
            ? Object.values(groups)
            : [];
        const cgMatch =
          groupsArr.find(
            (g) =>
              g?.id === cgId ||
              g?.groupId === cgId ||
              g?.firebaseId === cgId ||
              g?.key === cgId
          ) || cg;
        if (cgMatch?.members?.[uid] === true || !!cgMatch?.members?.[uid]) {
          return true;
        }
      }

      const eventId = event?.id || event?.eventId || event?.key || null;
      if (!eventId) return false;

      const regs =
        eventRegistrations?.[eventId] ||
        useEventsStore.getState().eventRegistrations?.[eventId] ||
        {};
      const reg = regs?.[uid] || null;
      if (reg && typeof reg === "object") {
        const regStatus = String(
          reg?.status || reg?.state || reg?.registrationStatus || ""
        )
          .trim()
          .toLowerCase();
        if (
          regStatus &&
          regStatus !== "cancelled" &&
          regStatus !== "canceled" &&
          regStatus !== "declined" &&
          regStatus !== "rejected" &&
          regStatus !== "removed" &&
          regStatus !== "expired"
        ) {
          return true;
        }
      }
      let status = reg?.status || reg?.state || reg?.registrationStatus || null;

      if (!status) {
        const ue =
          userEvents?.[eventId] ||
          useEventsStore.getState().userEvents?.[eventId] ||
          null;
        status = ue?.status || ue?.state || ue?.registrationStatus || ue || null;
      }

      if (status === true) return true;
      const normalized = String(status || "").toLowerCase();
      return (
        normalized === "confirmed" ||
        normalized === "active" ||
        normalized === "paid" ||
        normalized === "registered" ||
        normalized === "approved"
      );
    },
    [currentGroupIdSafe, eventRegistrations, groups, user, userEvents]
  );

  useEffect(() => {
    if (!mapReady || isViewerMode) return;
    if (liveEventState?.isLive) return;

    const authUser = user || useAuthStore.getState().user;
    const uid = authUser?.uid || null;
    if (!uid) return;

    const normalizeStatus = (raw) => String(raw || "").trim().toLowerCase();
    const isActiveStatus = (status) =>
      status === "confirmed" ||
      status === "active" ||
      status === "paid" ||
      status === "registered" ||
      status === "approved";

    const ueMap = userEvents || useEventsStore.getState().userEvents || {};
    const confirmedEventIds = Object.entries(ueMap || {})
      .filter(([, reg]) =>
        isActiveStatus(
          normalizeStatus(
            reg?.status || reg?.state || reg?.registrationStatus || reg || ""
          )
        )
      )
      .map(([eventId]) => String(eventId));

    if (!confirmedEventIds.length) return;

    const currentGid = currentGroupIdSafe || null;
    const key = `${uid}:${currentGid || "nogid"}:${confirmedEventIds.sort().join(",")}`;
    const now = Date.now();
    if (
      liveForceSwitchRef.current.key === key &&
      now - liveForceSwitchRef.current.ts < 12000
    ) {
      return;
    }
    liveForceSwitchRef.current = { key, ts: now };

    Promise.all(
      confirmedEventIds.map(async (eventId) => {
        try {
          const snap = await get(ref(db, `/eventsLive/${eventId}`));
          return snap.exists()
            ? { eventId: String(eventId), ...(snap.val() || {}) }
            : null;
        } catch {
          return null;
        }
      })
    )
      .then((liveEntries) => {
        const target = liveEntries.find((ev) => ev?.isLive === true && ev?.groupId);
        if (!target?.groupId) return;

        const nextGid = String(target.groupId);
        if (currentGid && String(currentGid) === nextGid) return;

        const { joinGroup, _startLocationListener } = useGroupStore.getState();
        if (typeof joinGroup === "function") {
          joinGroup({
            id: nextGid,
            groupId: nextGid,
            activeEventId: String(target.eventId),
            eventId: String(target.eventId),
          });
        }
        if (typeof _startLocationListener === "function") {
          _startLocationListener(nextGid);
        }

        logEvent("info", "LIVE_FORCE_SWITCH_GROUP", {
          uid,
          fromGroupId: currentGid || null,
          toGroupId: nextGid,
          eventId: String(target.eventId),
        });
      })
      .catch((err) => {
        console.warn("[LIVE] force switch group falhou:", err?.message || String(err));
      });
  }, [
    mapReady,
    isViewerMode,
    liveEventState?.isLive,
    user,
    userEvents,
    currentGroupIdSafe,
  ]);

  useEffect(() => {
    if (!mapReady || isViewerMode) return;
    const gid = currentGroupId || null;
    if (!gid) return;
    try {
      const { _startLocationListener } = useGroupStore.getState();
      if (typeof _startLocationListener === "function") {
        _startLocationListener(gid);
      }
    } catch (e) {
      console.warn("[MapView] falha ao iniciar listener de memberLocations:", e);
    }
  }, [mapReady, isViewerMode, currentGroupId]);

  // Android: garantir listener de eventos cedo quando LIVE liga (evita reabrir app)
  useEffect(() => {
    if (!isAndroid || !mapReady || isViewerMode) return;
    const eventsCount = Array.isArray(eventsWithLive)
      ? eventsWithLive.length
      : eventsWithLive && typeof eventsWithLive === "object"
        ? Object.keys(eventsWithLive).length
        : 0;
    if (eventsCount > 0 || liveEventsListenRef.current) return;

    const listen = useEventsStore.getState().listenEvents;
    if (typeof listen !== "function") return;

    liveEventsListenRef.current = true;
    try {
      mapDebug("[LIVE-ANDROID] start listenEvents");
      listen();
    } catch (e) {
      liveEventsListenRef.current = false;
      console.warn("[LIVE-ANDROID] listenEvents falhou:", e?.message || String(e));
    }
  }, [isAndroid, mapReady, isViewerMode, eventsWithLive]);

  // Android: garantir joinGroup imediato quando LIVE liga (sem precisar reabrir app)
  useEffect(() => {
    if (!isAndroid || !mapReady || isViewerMode) return;
    const { activeEvent, isLive } = liveEventState;
    if (!isLive || !activeEvent) return;

    const gidFromEvent = activeEvent.groupId || activeEvent.group || null;
    if (!gidFromEvent) return;

    const currentId =
      typeof currentGroup === "string"
        ? currentGroup
        : currentGroup?.id || currentGroup?.groupId || null;

    if (currentId && String(currentId) === String(gidFromEvent)) return;

    const now = Date.now();
    if (
      liveAutoJoinRef.current.gid === gidFromEvent &&
      now - liveAutoJoinRef.current.ts < 5000
    ) {
      return;
    }
    liveAutoJoinRef.current = { gid: gidFromEvent, ts: now };

    try {
      const { joinGroup, _startLocationListener } = useGroupStore.getState();
      if (typeof joinGroup === "function") {
        mapDebug("[LIVE-ANDROID] auto-joinGroup", gidFromEvent);
        joinGroup({ id: gidFromEvent, groupId: gidFromEvent });
      }
      if (typeof _startLocationListener === "function") {
        _startLocationListener(gidFromEvent);
      }
    } catch (e) {
      console.warn("[LIVE-ANDROID] auto-joinGroup falhou:", e?.message || String(e));
    }
  }, [isAndroid, mapReady, isViewerMode, liveEventState, currentGroup]);

  useEffect(() => {
    const { activeEvent, isLive } = liveEventState;
    const activeEventId =
      activeEvent?.id || activeEvent?.eventId || activeEvent?.key || null;
    const liveKey = activeEventId || "live";

    if (!isLive) return;
    const lastFetch = liveWaypointFetchRef.current || { eventId: null, ts: 0 };
    const sameEvent = lastFetch.eventId === liveKey;
    const hasUsefulCache =
      Array.isArray(cachedWaypoints) && cachedWaypoints.length > 0;
    if (sameEvent && hasUsefulCache && Date.now() - Number(lastFetch.ts || 0) < 15000) {
      return;
    }

    liveWaypointFetchRef.current = { eventId: liveKey, ts: Date.now() };
    fetchAndCacheWaypoints({ force: !hasUsefulCache })
      .then((res) => {
        if (Array.isArray(res?.list)) {
          setCachedWaypoints(res.list);
          if (isAndroid) {
            mapDebug(
              "[LIVE-WAYPOINTS]",
              JSON.stringify({
                eventId: activeEventId || null,
                listCount: res.list.length,
              })
            );
          }
        }
      })
      .catch((err) => {
        console.warn(
          "⚠️ Falha ao sincronizar waypoints para o LIVE:",
          err?.message || String(err)
        );
      });
  }, [liveEventState, cachedWaypoints.length]);

  useEffect(() => {
    const { activeEvent, isLive } = liveEventState;
    const activeEventId =
      activeEvent?.id || activeEvent?.eventId || activeEvent?.key || null;
    const liveKey = activeEventId || "live";

    const authUser = user || useAuthStore.getState().user;
    const uid = authUser?.uid || null;
    const role = String(authUser?.role || "free").toLowerCase();
    const isAdmin = role === "admin";
    const isGuideRole = role === "guide" || isAdmin;
    const eventsArr = Array.isArray(eventsWithLive)
      ? eventsWithLive
      : eventsWithLive && typeof eventsWithLive === "object"
        ? Object.values(eventsWithLive)
        : [];

    const extraLiveEventIds =
      uid && isGuideRole
        ? eventsArr
            .map((ev) => {
              if (!ev || ev.isLive !== true) return null;
              const eventId = ev.id || ev.eventId || ev.key || null;
              if (!eventId) return null;
              if (activeEventId && String(eventId) === String(activeEventId)) return null;
              const isGuideHere =
                ev.guideId === uid ||
                ev.guideUid === uid ||
                ev.guide === uid ||
                ev.coGuideId === uid ||
                ev.coGuideUid === uid;
              if (!isAdmin && !isGuideHere) return null;
              return String(eventId);
            })
            .filter(Boolean)
        : [];

    const hasRoutes =
      (routePointsRef.current?.main?.length || 0) > 0 ||
      Object.values(routePointsRef.current?.alts || {}).some(
        (points) => Array.isArray(points) && points.length > 0
      );

    const extrasFromCache = extraLiveEventIds.flatMap((eventId) =>
      readLiveWaypointsCache(eventId).map((wp) => ({
        ...wp,
        __liveEventId: eventId,
      }))
    );

    if ((!isLive || !hasRoutes) && !extrasFromCache.length) {
      clearLiveWaypointsLayer();
      liveWaypointsEventIdRef.current = null;
      if (liveWaypointPaletteRef.current.eventId) {
        liveWaypointPaletteRef.current.eventId = null;
        setLiveWaypointPalette(null);
        try {
          localStorage.removeItem("trekkin_live_waypoints_palette");
        } catch {}
      }
      if (!isLive && liveWaypointsCacheEventRef.current) {
        clearLiveWaypointsCache(liveWaypointsCacheEventRef.current);
        liveWaypointsCacheEventRef.current = null;
      }
      return;
    }

    if (liveWaypointPaletteRef.current.eventId !== liveKey) {
      liveWaypointPaletteRef.current.eventId = liveKey;
      try {
        const raw = localStorage.getItem("trekkin_live_waypoints_palette");
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && typeof parsed === "object") {
          setLiveWaypointPalette(parsed);
        }
      } catch {}
      get(ref(db, "waypointCategories"))
        .then((snap) => {
          const raw = snap.val() || {};
          const next = {};
          Object.entries(raw).forEach(([id, data]) => {
            if (!data || typeof data !== "object") return;
            if (!data.color) return;
            next[id] = { color: data.color, name: data.name || "" };
          });
          setLiveWaypointPalette(next);
          try {
            localStorage.setItem(
              "trekkin_live_waypoints_palette",
              JSON.stringify(next)
            );
          } catch {}
        })
        .catch(() => {});
    }

    const computeKey = `${activeEventId || "live"}:${routePointsVersion}:${normalizedWaypoints.length}:${extraLiveEventIds.join("|")}`;
    const now = Date.now();
    if (
      liveWaypointsComputeRef.current.key === computeKey &&
      now - liveWaypointsComputeRef.current.ts < 4000
    ) {
      return;
    }
    liveWaypointsComputeRef.current = { key: computeKey, ts: now };

    const nearby =
      isLive && hasRoutes
        ? computeLiveWaypoints().map((wp) => ({
            ...wp,
            __liveEventId: activeEventId || "live",
          }))
        : [];

    const mergedMap = new Map();
    const pushMerged = (list, fallbackEventId) => {
      (Array.isArray(list) ? list : []).forEach((wp) => {
        if (!wp) return;
        const lat = Number(wp?.lat ?? wp?.coords?.lat);
        const lon = Number(wp?.lon ?? wp?.lng ?? wp?.coords?.lon ?? wp?.coords?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        const eventIdForKey = String(wp.__liveEventId || fallbackEventId || "live");
        const baseId =
          String(wp.id || "").trim() ||
          `${lat.toFixed(5)}:${lon.toFixed(5)}`;
        const key = `${eventIdForKey}:${baseId}`;
        if (mergedMap.has(key)) return;
        mergedMap.set(key, {
          ...wp,
          lat,
          lon,
          __liveEventId: eventIdForKey,
        });
      });
    };
    pushMerged(nearby, activeEventId || "live");
    pushMerged(extrasFromCache, "extra");
    const mergedNearby = Array.from(mergedMap.values());

    const drawKey =
      `${activeEventId || "live"}:${extraLiveEventIds.join("|")}:` +
      `${mergedNearby.length}:` +
      `${mergedNearby
        .slice(0, 60)
        .map((w) => `${w.__liveEventId || "live"}:${w.id || `${w.lat}:${w.lon}`}`)
        .join("|")}`;
    const drawNow = Date.now();
    if (
      isAndroid &&
      liveWaypointsDrawRef.current.key === drawKey &&
      drawNow - liveWaypointsDrawRef.current.ts < 6000
    ) {
      return;
    }
    liveWaypointsDrawRef.current = { key: drawKey, ts: drawNow };
    if (isAndroid) {
      mapDebug(
        "[LIVE-WAYPOINTS]",
        JSON.stringify({
          eventId: activeEventId || null,
          cachedCount: normalizedWaypoints.length,
          routeMainCount: routePointsRef.current?.main?.length || 0,
          routeAltCount: Object.values(routePointsRef.current?.alts || {}).reduce(
            (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
            0
          ),
          nearbyCount: nearby.length,
          mergedNearbyCount: mergedNearby.length,
          extraLiveEvents: extraLiveEventIds.length,
        })
      );
    }
    if (!mergedNearby.length) {
      clearLiveWaypointsLayer();
      liveWaypointsEventIdRef.current = liveKey;
      if (activeEventId) {
        liveWaypointsCacheEventRef.current = activeEventId;
        persistLiveWaypointsCache(activeEventId, []);
      }
      return;
    }

    drawLiveWaypoints(mergedNearby);
    liveWaypointsEventIdRef.current = liveKey;
    if (activeEventId) {
      liveWaypointsCacheEventRef.current = activeEventId;
      persistLiveWaypointsCache(activeEventId, nearby);
    }
  }, [
    liveEventState,
    routePointsVersion,
    eventsWithLive,
    cachedWaypoints,
    persistLiveWaypointsCache,
    clearLiveWaypointsCache,
    readLiveWaypointsCache,
    computeLiveWaypoints,
    drawLiveWaypoints,
    user,
    currentGroup,
    groups,
  ]);

  useEffect(() => {
    if (!canViewMapAlerts || (!alertsLayerOn && !autoAlertsEnabled)) return;
    const categories = [];
    if (canViewFaunaAlerts) categories.push("fauna");
    if (canViewTrailAlerts) categories.push("trilho");
    if (canViewSafetyAlerts) categories.push("seguranca");
    if (canViewCriticalAlerts) categories.push("criticos");
    if (!categories.length) return;

    const buffers = {};
    const ignoreTtl = alertsFilter.adminAll;
    const unsubs = categories.map((category) => {
      const baseRef = ref(db, `alerts/${category}`);
      const q = ignoreTtl
        ? query(baseRef, orderByChild("ts"))
        : query(baseRef, orderByChild("ts"), limitToLast(300));
      return onValue(q, (snap) => {
        const raw = snap.val() || {};
        const now = Date.now();
        buffers[category] = Object.entries(raw)
          .map(([id, data]) => ({
            id,
            category,
            ...(data || {}),
          }))
          .filter((entry) => {
            if (!entry || typeof entry !== "object") return false;
            if (entry.adminOnly && !isAdminUser) return false;
            if (entry.targetUid && String(entry.targetUid) !== String(user?.uid || "")) return false;
            if (entry.status && entry.status !== "active") return false;
            const tsValue = Number(entry.ts || 0) || 0;
            if (alertsFilter.fromTs && tsValue && tsValue < alertsFilter.fromTs) {
              return false;
            }
            if (alertsFilter.toTs && tsValue && tsValue > alertsFilter.toTs) {
              return false;
            }
            if (ignoreTtl) return true;
            const expiresValue = Number(entry.expiresAt || 0) || 0;
            const confirmedValue = Number(entry.confirmedAt || 0) || 0;
            const confirmationExpire = confirmedValue
              ? confirmedValue + ALERT_TTL_MS
              : 0;
            const fallbackExpire = tsValue ? tsValue + ALERT_TTL_MS : 0;
            const exp = expiresValue || confirmationExpire || fallbackExpire;
            if (exp && now > exp) return false;
            return true;
          });
        const merged = Object.values(buffers)
          .flat()
          .sort((a, b) => (b.ts || 0) - (a.ts || 0));
        setMapAlerts(merged);
      });
    });

    return () => unsubs.forEach((u) => u && u());
  }, [
    canViewMapAlerts,
    isAdminUser,
    alertsLayerOn,
    autoAlertsEnabled,
    canViewFaunaAlerts,
    canViewTrailAlerts,
    canViewSafetyAlerts,
    canViewCriticalAlerts,
    alertsFilter.adminAll,
    alertsFilter.fromTs,
    alertsFilter.toTs,
  ]);

  useEffect(() => {
    if (!autoAlertsEnabled || !canViewMapAlerts) return;
    if (proximityAlert) return;
    const lat = userPosition?.lat;
    const lon = userPosition?.lon ?? userPosition?.lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    if (!mapAlerts.length) return;

    const allowed = new Set();
    if (autoAlertFauna) allowed.add("fauna");
    if (autoAlertTrilho) allowed.add("trilho");
    if (autoAlertSeguranca) allowed.add("seguranca");
    if (autoAlertCriticos) allowed.add("criticos");
    if (!allowed.size) return;

    const now = Date.now();
    let best = null;
    mapAlerts.forEach((entry) => {
      if (!entry?.coords || !Number.isFinite(entry.coords.lat) || !Number.isFinite(entry.coords.lon)) {
        return;
      }
      if (!allowed.has(entry.category)) return;
      const dist = haversineMeters(
        { lat, lon },
        { lat: entry.coords.lat, lon: entry.coords.lon }
      );
      if (dist > ALERT_PROXIMITY_METERS) return;
      const key = getAlertKey(entry);
      try {
        const voted = window.localStorage.getItem(`alertVote.${key}`);
        if (voted) return;
        const lastPrompt = Number(window.localStorage.getItem(`alertPrompt.${key}`) || 0);
        if (lastPrompt && now - lastPrompt < ALERT_PROXIMITY_COOLDOWN_MS) return;
      } catch {}
      if (!best || dist < best.dist) {
        best = { entry, dist };
      }
    });

    if (best) {
      setProximityAlert({
        entry: best.entry,
        distance: Math.round(best.dist),
      });
    }
  }, [
    autoAlertsEnabled,
    autoAlertFauna,
    autoAlertTrilho,
    autoAlertSeguranca,
    autoAlertCriticos,
    canViewMapAlerts,
    mapAlerts,
    userPosition?.lat,
    userPosition?.lon,
    userPosition?.lng,
    proximityAlert,
  ]);

  useEffect(() => {
    if (!proximityAlert) return;
    // O aviso automático é a apresentação principal. Fechar qualquer
    // popup Leaflet aberto impede que o mesmo alerta apareça duas vezes,
    // sobreposto, quando o marcador também foi tocado.
    try {
      mapRef.current?.closePopup?.();
    } catch {}
    const key = getAlertKey(proximityAlert.entry);
    try {
      window.localStorage.setItem(`alertPrompt.${key}`, String(Date.now()));
    } catch {}

    if (autoAlertSound) {
      playPing();
    }
    if (autoAlertVibrate) {
      triggerAlertVibration([160, 80, 160]);
    }

    if (proximityTimerRef.current) {
      clearTimeout(proximityTimerRef.current);
    }
    proximityTimerRef.current = setTimeout(() => {
      setProximityAlert(null);
    }, ALERT_PROXIMITY_AUTO_CLOSE_MS);

    return () => {
      if (proximityTimerRef.current) {
        clearTimeout(proximityTimerRef.current);
        proximityTimerRef.current = null;
      }
    };
  }, [proximityAlert, autoAlertSound, autoAlertVibrate]);

  useEffect(() => {
    return () => {
      navGridLayerRef.current?.remove();
      navGridRouteLayerRef.current?.remove();
      navGridRouteExternalLayerRef.current?.remove();
      waypointsLayerRef.current?.remove();
      navGridNodesLayerRef.current?.remove();
      alertsLayerRef.current?.remove();
    };
  }, []);

  // -------------------------------------------------------------
  // 🟩 MAP ROTATION — SQUARE SIZE
  // -------------------------------------------------------------
  function getMapSquareSize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    return Math.ceil(Math.sqrt(w * w + h * h));
  }

  const [squareSize, setSquareSize] = useState(() => getMapSquareSize());

  useEffect(() => {
    function handleResize() {
      const next = getMapSquareSize();
      setSquareSize(next);

      const map = mapRef.current;
      if (map) {
        requestAnimationFrame(() => {
          map.invalidateSize(true);
        });
      }
    }

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);



  useEffect(() => {
    if (!navGridEditNodesActive) {
      setNavGridNodeMode(null);
      return;
    }
    if (navGridSplitNodeActive) {
      setNavGridNodeMode("split");
    } else if (navGridAddNodeActive) {
      setNavGridNodeMode("add");
    } else if (navGridDeleteNodesActive) {
      setNavGridNodeMode("delete");
    } else {
      setNavGridNodeMode("move");
    }
  }, [
    navGridEditNodesActive,
    navGridAddNodeActive,
    navGridDeleteNodesActive,
    navGridSplitNodeActive,
  ]);

  useEffect(() => {
    navGridDrawActiveRef.current = navGridDrawActive;
  }, [navGridDrawActive]);

  useEffect(() => {
    planPickModeRef.current = planPickMode;
  }, [planPickMode]);

  useEffect(() => {
    planLastRef.current =
      (Array.isArray(planWaypoints) && planWaypoints.length
        ? planWaypoints[planWaypoints.length - 1]
        : planStart) || null;
  }, [planWaypoints, planStart]);

  useEffect(() => {
    if (planActive) return;
    if (!navWaypointPending || navWaypointTarget) return;
    if (!userPosition?.lat || !userPosition?.lon) return;
    const threshold =
      Number(navWaypointPending?.thresholdMeters) ||
      Number(navGridConfigRef.current?.entryPromptMeters) ||
      200;
    let nearAnyGridNode = false;
    if (isAndroid) {
      const loadedGrid = loadNavGrid();
      if (!Array.isArray(loadedGrid?.segments) || !loadedGrid.segments.length) return;
      const entry = navWaypointPending?.entry;
      const distanceToEntry = haversineMeters(
        { lat: Number(userPosition.lat), lon: Number(userPosition.lon) },
        { lat: Number(entry?.lat), lon: Number(entry?.lon) }
      );
      nearAnyGridNode =
        Number.isFinite(distanceToEntry) && distanceToEntry <= threshold;
    } else {
      const grid = navGridConfigRef.current;
      const graph = buildNavGridGraph(grid, {
        mergeMeters: grid?.mergeMeters ?? grid?.routeMergeMeters,
        activityMode,
      });
      const nearest = findNearestGraphNode(graph, {
        lat: userPosition.lat,
        lon: userPosition.lon,
      });
      nearAnyGridNode =
        Number.isFinite(Number(nearest?.dist)) && Number(nearest.dist) <= threshold;
    }
    if (nearAnyGridNode) {
      clearNavWaypointPending();
      setNavWaypointTarget(navWaypointPending.target);
      showToast("Ligado à rede. A iniciar navegação.");
    }
  }, [
    planActive,
    navWaypointPending,
    navWaypointTarget,
    userPosition?.lat,
    userPosition?.lon,
    clearNavWaypointPending,
    setNavWaypointTarget,
    isAndroid,
    activityMode,
    navGridSegments,
  ]);

  useEffect(() => {
    try {
      if (!planActive) {
        if (!navWaypointTarget) {
          if (navWaypointPending?.entry?.lat && navWaypointPending?.entry?.lon) {
            return;
          }
          setPlanRoutePoints([]);
          planRouteAnchorsRef.current = [];
          planRouteSegmentsRef.current = [];
          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-route", { detail: { path: [] } })
          );
        }
        return;
      }
    const planStartLonRaw = Number(planStart?.lon ?? planStart?.lng);
    const planEndLonRaw = Number(planEnd?.lon ?? planEnd?.lng);
    const hasManualPlanStart =
      Number.isFinite(Number(planStart?.lat)) && Number.isFinite(planStartLonRaw);
    const hasLiveUserStart =
      Number.isFinite(Number(userPosition?.lat)) && Number.isFinite(Number(userPosition?.lon));
    const effectivePlanStart = hasManualPlanStart
      ? { lat: Number(planStart.lat), lon: planStartLonRaw }
      : hasLiveUserStart
      ? { lat: Number(userPosition.lat), lon: Number(userPosition.lon) }
      : null;
    const usingLiveUserAsPlanStart = !hasManualPlanStart && hasLiveUserStart;
    if (!effectivePlanStart?.lat || !effectivePlanStart?.lon) {
      // Em criação de rota, não apagar preview existente por estado transitório.
      return;
    }
    const normalizedPlanWaypoints = (Array.isArray(planWaypoints) ? planWaypoints : [])
      .map((wp) => ({
        ...wp,
        lat: Number(wp?.lat),
        lon: Number(wp?.lon ?? wp?.lng),
      }))
      .filter((wp) => Number.isFinite(wp.lat) && Number.isFinite(wp.lon));
    const anchors = [effectivePlanStart, ...normalizedPlanWaypoints];
    if (planRouteType === "circular" && normalizedPlanWaypoints.length > 0) {
      anchors.push({ ...effectivePlanStart, source: "circular" });
    } else if (Number.isFinite(Number(planEnd?.lat)) && Number.isFinite(planEndLonRaw)) {
      anchors.push({ ...planEnd, lat: Number(planEnd.lat), lon: planEndLonRaw });
    }
    if (anchors.length < 2) {
      // Só limpar ao cancelar/parar criação; manter preview atual.
      return;
    }
    if (!navGridSegments.length) {
      requestNavGridForRouting("plan_anchors_ready");
      return;
    }
    const grid = navGridConfigRef.current;
    const rawRoutingMerge =
      Number(grid?.routeMergeMeters) || Number(grid?.mergeMeters) || 8;
    // No criador de rota, priorizar fidelidade geométrica da grelha
    // (evitar "atalhos" visuais em curvas por fusão excessiva).
    // Só unir nós realmente coincidentes. Uma tolerância larga ligava
    // indevidamente trilhos paralelos/próximos e fazia a rota "saltar" entre
    // eles. As fusões explícitas da NavGrid ficam normalmente abaixo de 1 m.
    const mergeMeters = Math.max(0.5, Math.min(rawRoutingMerge, 1.5));
    // O fallback serve apenas para pequenas imprecisões de gravação, nunca
    // para criar atalhos entre ramais visualmente próximos.
    // As fusões reais desta zona têm até cerca de 5 m de diferença entre os
    // pontos gravados. Abaixo disso, Tasca Picão → Porto d'Olho permanecia
    // artificialmente ligada apenas pela volta de 12 km.
    const fallbackMergeMeters = Math.max(5, Math.min(rawRoutingMerge, 5));
    const currentPreference =
      planRoutePreference === "low_ascent" ? "low_ascent" : "shortest";
    const anchorLats = anchors.map((point) => Number(point.lat));
    const anchorLons = anchors.map((point) => Number(point.lon));
    const routeDiagonalMeters = haversineMeters(
      { lat: Math.min(...anchorLats), lon: Math.min(...anchorLons) },
      { lat: Math.max(...anchorLats), lon: Math.max(...anchorLons) }
    );
    // Grafo regional: o custo de memória depende do percurso em edição e não
    // dos mais de 440 mil pontos (e crescimento futuro) da NavGrid completa.
    const routeBufferMeters = Math.max(
      currentPreference === "low_ascent" ? 5000 : 2500,
      Math.min(
        currentPreference === "low_ascent" ? 20000 : 12000,
        Number(routeDiagonalMeters || 0) *
          (currentPreference === "low_ascent" ? 1.25 : 0.75)
      )
    );
    const centerLat = anchorLats.reduce((sum, value) => sum + value, 0) / anchorLats.length;
    const latPadding = routeBufferMeters / 111320;
    const lonPadding =
      routeBufferMeters /
      Math.max(20000, 111320 * Math.cos((centerLat * Math.PI) / 180));
    const minRouteLat = Math.min(...anchorLats) - latPadding;
    const maxRouteLat = Math.max(...anchorLats) + latPadding;
    const minRouteLon = Math.min(...anchorLons) - lonPadding;
    const maxRouteLon = Math.max(...anchorLons) + lonPadding;
    const routingSegments = (Array.isArray(grid?.segments) ? grid.segments : []).filter(
      (segment) =>
        (Array.isArray(segment?.points) ? segment.points : []).some((point) => {
          const lat = Number(point?.lat);
          const lon = Number(point?.lon ?? point?.lng);
          return (
            Number.isFinite(lat) &&
            Number.isFinite(lon) &&
            lat >= minRouteLat &&
            lat <= maxRouteLat &&
            lon >= minRouteLon &&
            lon <= maxRouteLon
          );
        })
    );
    const routeRegionStamp = Math.abs(
      Math.round(minRouteLat * 1e5) * 31 +
        Math.round(minRouteLon * 1e5) * 17 +
        Math.round(maxRouteLat * 1e5) * 13 +
        Math.round(maxRouteLon * 1e5) * 7
    );
    const routingGrid = {
      ...grid,
      segments: routingSegments,
      updatedAt: Number(grid?.updatedAt || 0) + routeRegionStamp,
    };
    // Manter apenas um grafo em memória. No iOS a NavGrid é suficientemente
    // grande para duas variantes simultâneas fazerem o WebContent exceder o
    // limite de memória e terminar durante a criação da rota.
    const graph = buildNavGridGraph(routingGrid, {
      mergeMeters: fallbackMergeMeters,
      mergeEndpointsOnly: false,
      activityMode,
    });
    const graphAllNodes = graph;
    if (!graph?.nodes?.length) {
      return;
    }
    const firstLegMeters = haversineMeters(
      { lat: Number(anchors?.[0]?.lat), lon: Number(anchors?.[0]?.lon) },
      { lat: Number(anchors?.[1]?.lat), lon: Number(anchors?.[1]?.lon) }
    );
    const lastLegMeters = haversineMeters(
      {
        lat: Number(anchors?.[anchors.length - 2]?.lat),
        lon: Number(anchors?.[anchors.length - 2]?.lon),
      },
      {
        lat: Number(anchors?.[anchors.length - 1]?.lat),
        lon: Number(anchors?.[anchors.length - 1]?.lon),
      }
    );
    const closeAnchorMode =
      (Number.isFinite(firstLegMeters) && firstLegMeters <= 500) ||
      (Number.isFinite(lastLegMeters) && lastLegMeters <= 500);
    const strictSnapMeters = closeAnchorMode
      ? 25
      : Math.max(
          20,
          Math.min(Number(navGridConfigRef.current?.entryPromptMeters) || 200, 60)
        );
    const prevAnchors = Array.isArray(planRouteAnchorsRef.current)
      ? planRouteAnchorsRef.current
      : [];
    const prevSegments = Array.isArray(planRouteSegmentsRef.current)
      ? planRouteSegmentsRef.current
      : [];
    const previousPreference = String(planRoutePreferenceRef.current || "shortest");
    const preferenceChanged = previousPreference !== currentPreference;
    planRoutePreferenceRef.current = currentPreference;
    if (currentPreference === "low_ascent") {
      const startNode = findNearestNodeInGraph(graph, anchors[0]);
      const endNode = findNearestNodeInGraph(graph, anchors[anchors.length - 1]);
      if (
        !graph.__lowAscentElevationReady &&
        !graph.__lowAscentElevationLoading &&
        startNode?.node?.id &&
        endNode?.node?.id
      ) {
        graph.__lowAscentElevationLoading = true;
        ensureGraphElevationForLowAscent(
          graph,
          startNode.node.id,
          endNode.node.id,
          { maxNodes: 4500 }
        )
          .then((changed) => {
            graph.__lowAscentElevationReady = true;
            if (changed) {
              setPlanRouteRecalcTick((n) => n + 1);
            }
          })
          .catch(() => {})
          .finally(() => {
            graph.__lowAscentElevationLoading = false;
          });
      }
    }
    const anchorsMatch = (a, b) =>
      !!a &&
      !!b &&
      Number.isFinite(a.lat) &&
      Number.isFinite(a.lon) &&
      Number.isFinite(b.lat) &&
      Number.isFinite(b.lon) &&
      a.lat === b.lat &&
      a.lon === b.lon;
    const anchorsNearlySame = (a, b, toleranceMeters = 35) =>
      !!a &&
      !!b &&
      Number.isFinite(a.lat) &&
      Number.isFinite(a.lon) &&
      Number.isFinite(b.lat) &&
      Number.isFinite(b.lon) &&
      haversineMeters(
        { lat: Number(a.lat), lon: Number(a.lon) },
        { lat: Number(b.lat), lon: Number(b.lon) }
      ) <= Number(toleranceMeters || 35);
    const routeSignature = (path) => {
      const list = Array.isArray(path) ? path : [];
      if (!list.length) return "empty";
      const stride = Math.max(1, Math.floor(list.length / 12));
      const chunks = [];
      for (let idx = 0; idx < list.length; idx += stride) {
        const p = list[idx];
        if (!p) continue;
        chunks.push(
          `${Number(p.lat).toFixed(5)},${Number(p.lon).toFixed(5)}`
        );
      }
      const last = list[list.length - 1];
      if (last) {
        chunks.push(
          `${Number(last.lat).toFixed(5)},${Number(last.lon).toFixed(5)}`
        );
      }
      return chunks.join("|");
    };
    let firstDiff = 0;
    if (!preferenceChanged) {
      const minLen = Math.min(prevAnchors.length, anchors.length);
      for (; firstDiff < minLen; firstDiff += 1) {
        if (!anchorsMatch(prevAnchors[firstDiff], anchors[firstDiff])) break;
      }
    }
    // Recalcula sempre todos os troços para evitar reutilização stale
    // quando o utilizador fecha no mesmo ponto inicial fora da grid.
    const startLeg = 0;
    const nextSegments = [];
    const previewSegments = [];
    const externalSegments = [];
    let firstExternalStart = null;
    let lastExternalEnd = null;
    for (let i = 0; i < startLeg; i += 1) {
      nextSegments[i] = prevSegments[i];
    }

    for (let i = startLeg; i < anchors.length - 1; i += 1) {
      const anchorStart = anchors[i];
      const anchorEnd = anchors[i + 1];

      // Um circuito com apenas um ponto intermédio é uma ida e volta.
      // Recalcular o regresso de forma independente podia escolher outro
      // ramo da rede e produzir uma volta enorme. Reutiliza exatamente o
      // percurso de ida em sentido inverso.
      const mirrorsSingleWaypointRoute =
        planRouteType === "circular" &&
        currentPreference === "shortest" &&
        normalizedPlanWaypoints.length === 1 &&
        i === 1 &&
        Array.isArray(nextSegments[0]) &&
        nextSegments[0].length > 0;
      if (mirrorsSingleWaypointRoute) {
        nextSegments[i] = [...nextSegments[0]].reverse().map((point) => ({
          ...point,
        }));
        previewSegments[i] = Array.isArray(previewSegments[0])
          ? [...previewSegments[0]].reverse().map((point) => ({ ...point }))
          : nextSegments[i];
        if (Array.isArray(firstExternalStart) && firstExternalStart.length > 1) {
          lastExternalEnd = [...firstExternalStart]
            .reverse()
            .map((point) => ({ ...point }));
        }
        continue;
      }

      let segmentStart = anchorStart;
      let segmentEnd = anchorEnd;
      let connectorStart = [];
      let connectorEnd = [];

      const nearestStart = findNearestRoutableNodeInGraph(graphAllNodes, anchorStart);
      if (nearestStart?.node) {
        const entry = {
          lat: Number(nearestStart.node.lat),
          lon: Number(nearestStart.node.lon),
        };
        if (Number.isFinite(entry.lat) && Number.isFinite(entry.lon)) {
          segmentStart = entry;
          connectorStart = [
            { lat: Number(anchorStart.lat), lon: Number(anchorStart.lon) },
            entry,
          ];
        }
      }

      const nearestEnd = findNearestRoutableNodeInGraph(graphAllNodes, anchorEnd);
      if (nearestEnd?.node) {
        const exitNode = {
          lat: Number(nearestEnd.node.lat),
          lon: Number(nearestEnd.node.lon),
        };
        if (Number.isFinite(exitNode.lat) && Number.isFinite(exitNode.lon)) {
          segmentEnd = exitNode;
          connectorEnd = [
            exitNode,
            { lat: Number(anchorEnd.lat), lon: Number(anchorEnd.lon) },
          ];

          // Fecho de loop (início=fim fora da grid):
          // se já estamos mais perto do fim no ponto atual da grid,
          // não voltar ao nó inicial; fecha em linha reta a partir daqui.
          const isLastLeg = i === anchors.length - 2;
          const closesAtStart = anchorsNearlySame(
            anchors[0],
            anchorEnd,
            Math.max(30, strictSnapMeters)
          );
          if (
            isLastLeg &&
            closesAtStart &&
            Number.isFinite(Number(segmentStart?.lat)) &&
            Number.isFinite(Number(segmentStart?.lon))
          ) {
            const endAnchorPoint = {
              lat: Number(anchorEnd.lat),
              lon: Number(anchorEnd.lon),
            };
            const distFromCurrentGrid = haversineMeters(
              { lat: Number(segmentStart.lat), lon: Number(segmentStart.lon) },
              endAnchorPoint
            );
            const distFromExitNode = haversineMeters(exitNode, endAnchorPoint);
            if (distFromCurrentGrid + 8 < distFromExitNode) {
              segmentEnd = {
                lat: Number(segmentStart.lat),
                lon: Number(segmentStart.lon),
              };
              connectorEnd = [
                segmentEnd,
                { lat: Number(anchorEnd.lat), lon: Number(anchorEnd.lon) },
              ];
            }
          }
        }
      }

      // No fecho "terminar no mesmo local", tenta sempre pela Grid.
      // Conector em linha reta só deve existir para ligar ponto fora da rede ao nó mais próximo.

      const sameSnappedNode =
        Number.isFinite(Number(segmentStart?.lat)) &&
        Number.isFinite(Number(segmentStart?.lon)) &&
        Number.isFinite(Number(segmentEnd?.lat)) &&
        Number.isFinite(Number(segmentEnd?.lon)) &&
        Math.abs(Number(segmentStart.lat) - Number(segmentEnd.lat)) < 1e-8 &&
        Math.abs(Number(segmentStart.lon) - Number(segmentEnd.lon)) < 1e-8;
      if (sameSnappedNode) {
        if ((!Array.isArray(connectorStart) || connectorStart.length < 2) && segmentStart) {
          const startDist = haversineMeters(
            { lat: Number(anchorStart?.lat), lon: Number(anchorStart?.lon) },
            { lat: Number(segmentStart?.lat), lon: Number(segmentStart?.lon) }
          );
          if (Number.isFinite(startDist) && startDist > 6) {
            connectorStart = [
              { lat: Number(anchorStart.lat), lon: Number(anchorStart.lon) },
              { lat: Number(segmentStart.lat), lon: Number(segmentStart.lon) },
            ];
          }
        }
        if ((!Array.isArray(connectorEnd) || connectorEnd.length < 2) && segmentEnd) {
          const endDist = haversineMeters(
            { lat: Number(anchorEnd?.lat), lon: Number(anchorEnd?.lon) },
            { lat: Number(segmentEnd?.lat), lon: Number(segmentEnd?.lon) }
          );
          if (Number.isFinite(endDist) && endDist > 6) {
            connectorEnd = [
              { lat: Number(segmentEnd.lat), lon: Number(segmentEnd.lon) },
              { lat: Number(anchorEnd.lat), lon: Number(anchorEnd.lon) },
            ];
          }
        }
        nextSegments[i] = [
          { lat: Number(segmentStart.lat), lon: Number(segmentStart.lon) },
        ];
        const previewSegment = [];
        const pushPreviewPoint = (pt) => {
          if (!pt || !Number.isFinite(Number(pt.lat)) || !Number.isFinite(Number(pt.lon))) return;
          const last = previewSegment[previewSegment.length - 1];
          if (
            last &&
            Math.abs(Number(last.lat) - Number(pt.lat)) < 1e-8 &&
            Math.abs(Number(last.lon) - Number(pt.lon)) < 1e-8
          ) {
            return;
          }
          previewSegment.push({ lat: Number(pt.lat), lon: Number(pt.lon) });
        };
        connectorStart.forEach(pushPreviewPoint);
        pushPreviewPoint({ lat: Number(segmentStart.lat), lon: Number(segmentStart.lon) });
        connectorEnd.forEach(pushPreviewPoint);
        previewSegments[i] = previewSegment;
        if (
          i === 0 &&
          Array.isArray(connectorStart) &&
          connectorStart.length > 1
        ) {
          firstExternalStart = connectorStart;
        }
        if (
          i === anchors.length - 2 &&
          Array.isArray(connectorEnd) &&
          connectorEnd.length > 1
        ) {
          lastExternalEnd = connectorEnd;
        }
        continue;
      }
      const resultStrict = computeNavGridRoute(
        routingGrid,
        segmentStart,
        segmentEnd,
        {
          graph,
          mergeMeters: fallbackMergeMeters,
          maxSnapMeters: strictSnapMeters,
          preference: currentPreference,
        }
      );
      let result = resultStrict;
      if (!result?.ok) {
        const connectedFallback = findConnectedComponentRoute(
          graph,
          { lat: Number(segmentStart?.lat), lon: Number(segmentStart?.lon) },
          { lat: Number(segmentEnd?.lat), lon: Number(segmentEnd?.lon) },
          { maxCandidates: 140 }
        );
        if (connectedFallback?.path?.length > 1) {
          result = {
            ok: true,
            path: connectedFallback.path,
            distanceMeters: Number(connectedFallback.distanceMeters || 0),
          };
        }
      }
      if (!result?.ok) {
        // Fallback igual ao fluxo de waypoint:
        // tenta extrair troço na mesma segment polyline para manter
        // conector reta->GRID + GRID + conector GRID->reta.
        const segs = Array.isArray(routingGrid?.segments) ? routingGrid.segments : [];
        const segmentPaths = segs
          .map((seg) =>
            (Array.isArray(seg?.points) ? seg.points : [])
              .map((p) => ({ lat: Number(p?.lat), lon: Number(p?.lon) }))
              .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
          )
          .filter((pts) => pts.length > 1);
        let bestLeg = null;
        for (const pts of segmentPaths) {
          let iFrom = -1;
          let iTo = -1;
          let dFrom = Infinity;
          let dTo = Infinity;
          for (let pi = 0; pi < pts.length; pi += 1) {
            const df = haversineMeters(
              { lat: Number(anchorStart.lat), lon: Number(anchorStart.lon) },
              pts[pi]
            );
            if (df < dFrom) {
              dFrom = df;
              iFrom = pi;
            }
            const dt = haversineMeters(
              { lat: Number(anchorEnd.lat), lon: Number(anchorEnd.lon) },
              pts[pi]
            );
            if (dt < dTo) {
              dTo = dt;
              iTo = pi;
            }
          }
          if (iFrom < 0 || iTo < 0 || iFrom === iTo) continue;
          const score = dFrom + dTo;
          if (!bestLeg || score < bestLeg.score) {
            bestLeg = { pts, iFrom, iTo, score };
          }
        }
        if (bestLeg) {
          const step = bestLeg.iTo >= bestLeg.iFrom ? 1 : -1;
          const forcedGridSegment = [];
          for (let pi = bestLeg.iFrom; ; pi += step) {
            forcedGridSegment.push(bestLeg.pts[pi]);
            if (pi === bestLeg.iTo) break;
          }
          if (forcedGridSegment.length > 1) {
            const firstGrid = forcedGridSegment[0];
            const lastGrid = forcedGridSegment[forcedGridSegment.length - 1];
            const startDist = haversineMeters(
              { lat: Number(anchorStart.lat), lon: Number(anchorStart.lon) },
              firstGrid
            );
            const endDist = haversineMeters(
              { lat: Number(anchorEnd.lat), lon: Number(anchorEnd.lon) },
              lastGrid
            );
            if (Number.isFinite(startDist) && startDist > 6) {
              connectorStart = [
                { lat: Number(anchorStart.lat), lon: Number(anchorStart.lon) },
                { lat: Number(firstGrid.lat), lon: Number(firstGrid.lon) },
              ];
            }
            if (Number.isFinite(endDist) && endDist > 6) {
              connectorEnd = [
                { lat: Number(lastGrid.lat), lon: Number(lastGrid.lon) },
                { lat: Number(anchorEnd.lat), lon: Number(anchorEnd.lon) },
              ];
            }
            nextSegments[i] = forcedGridSegment;
            const previewSegment = [];
            const pushPreviewPoint = (pt) => {
              if (!pt || !Number.isFinite(Number(pt.lat)) || !Number.isFinite(Number(pt.lon))) return;
              const last = previewSegment[previewSegment.length - 1];
              if (
                last &&
                Math.abs(Number(last.lat) - Number(pt.lat)) < 1e-8 &&
                Math.abs(Number(last.lon) - Number(pt.lon)) < 1e-8
              ) {
                return;
              }
              previewSegment.push({ lat: Number(pt.lat), lon: Number(pt.lon) });
            };
            connectorStart.forEach(pushPreviewPoint);
            forcedGridSegment.forEach(pushPreviewPoint);
            connectorEnd.forEach(pushPreviewPoint);
            previewSegments[i] = previewSegment;
            if (
              i === 0 &&
              Array.isArray(connectorStart) &&
              connectorStart.length > 1
            ) {
              firstExternalStart = connectorStart;
            }
            if (
              i === anchors.length - 2 &&
              Array.isArray(connectorEnd) &&
              connectorEnd.length > 1
            ) {
              lastExternalEnd = connectorEnd;
            }
            continue;
          }
        }
        if (planRouteErrorRef.current !== result.reason) {
          showToast("Sem rota disponível entre os pontos.", "error");
          planRouteErrorRef.current = result.reason || "no_route";
        }
        return;
      }
      if (currentPreference === "low_ascent") {
        const shortestStrict = computeNavGridRoute(
          routingGrid,
          segmentStart,
          segmentEnd,
          {
            graph,
            mergeMeters,
            maxSnapMeters: strictSnapMeters,
            preference: "shortest",
          }
        );
        const shortestResult = shortestStrict;
        if (shortestResult?.ok) {
          const lowSig = routeSignature(result?.path);
          const shortSig = routeSignature(shortestResult?.path);
          if (lowSig === shortSig) {
            const noticeKey = `${i}:${lowSig}`;
            if (planRouteNoAltNoticeRef.current !== noticeKey) {
              planRouteNoAltNoticeRef.current = noticeKey;
              showToast("Nesta zona não há alternativa com menos desnível.", "warning");
            }
          }
        }
      }
      const gridSegment = Array.isArray(result.path) ? result.path : [];

      if ((!Array.isArray(connectorStart) || connectorStart.length < 2) && gridSegment.length) {
        const firstGridPoint = gridSegment[0];
        const startDist = haversineMeters(
          { lat: Number(anchorStart?.lat), lon: Number(anchorStart?.lon) },
          { lat: Number(firstGridPoint?.lat), lon: Number(firstGridPoint?.lon) }
        );
        if (Number.isFinite(startDist) && startDist > 6) {
          connectorStart = [
            { lat: Number(anchorStart.lat), lon: Number(anchorStart.lon) },
            { lat: Number(firstGridPoint.lat), lon: Number(firstGridPoint.lon) },
          ];
        }
      }
      if ((!Array.isArray(connectorEnd) || connectorEnd.length < 2) && gridSegment.length) {
        const lastGridPoint = gridSegment[gridSegment.length - 1];
        const endDist = haversineMeters(
          { lat: Number(anchorEnd?.lat), lon: Number(anchorEnd?.lon) },
          { lat: Number(lastGridPoint?.lat), lon: Number(lastGridPoint?.lon) }
        );
        if (Number.isFinite(endDist) && endDist > 6) {
          connectorEnd = [
            { lat: Number(lastGridPoint.lat), lon: Number(lastGridPoint.lon) },
            { lat: Number(anchorEnd.lat), lon: Number(anchorEnd.lon) },
          ];
        }
      }

      nextSegments[i] = gridSegment;
      const previewSegment = [];
      const pushPreviewPoint = (pt) => {
        if (!pt || !Number.isFinite(Number(pt.lat)) || !Number.isFinite(Number(pt.lon))) return;
        const last = previewSegment[previewSegment.length - 1];
        if (
          last &&
          Math.abs(Number(last.lat) - Number(pt.lat)) < 1e-8 &&
          Math.abs(Number(last.lon) - Number(pt.lon)) < 1e-8
        ) {
          return;
        }
        previewSegment.push({ lat: Number(pt.lat), lon: Number(pt.lon) });
      };
      connectorStart.forEach(pushPreviewPoint);
      gridSegment.forEach(pushPreviewPoint);
      connectorEnd.forEach(pushPreviewPoint);
      previewSegments[i] = previewSegment;
      if (
        i === 0 &&
        Array.isArray(connectorStart) &&
        connectorStart.length > 1
      ) {
        firstExternalStart = connectorStart;
      }
      if (
        i === anchors.length - 2 &&
        Array.isArray(connectorEnd) &&
        connectorEnd.length > 1
      ) {
        lastExternalEnd = connectorEnd;
      }
    }

    if (Array.isArray(firstExternalStart) && firstExternalStart.length > 1) {
      externalSegments.push(firstExternalStart);
    }
    if (Array.isArray(lastExternalEnd) && lastExternalEnd.length > 1) {
      externalSegments.push(lastExternalEnd);
    }

    const mergedPath = [];
    nextSegments.forEach((segment) => {
      if (!Array.isArray(segment) || !segment.length) return;
      if (!mergedPath.length) {
        mergedPath.push(...segment);
        return;
      }
      const last = mergedPath[mergedPath.length - 1];
      const first = segment[0];
      if (last && first && last.lat === first.lat && last.lon === first.lon) {
        mergedPath.push(...segment.slice(1));
      } else {
        mergedPath.push(...segment);
      }
    });

    if (!mergedPath.length) {
      return;
    }

    planRouteErrorRef.current = null;
    planRouteAnchorsRef.current = anchors;
    planRouteSegmentsRef.current = nextSegments;
    const points = [];
    mergedPath.forEach((point) => {
      if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) return;
      if (!points.length) {
        points.push({ ...point, dist: 0 });
        return;
      }
      const prev = points[points.length - 1];
      const nextDist = (prev.dist || 0) + haversineMeters(prev, point);
      points.push({ ...point, dist: nextDist });
    });

    setPlanRoutePoints(points);
    const previewPath = points
      .map((pt) =>
        pt && Number.isFinite(Number(pt.lat)) && Number.isFinite(Number(pt.lon))
          ? { lat: Number(pt.lat), lon: Number(pt.lon) }
          : null
      )
      .filter(Boolean);

    if (
      usingLiveUserAsPlanStart &&
      Array.isArray(anchors) &&
      anchors.length > 1 &&
      (!Array.isArray(externalSegments) || externalSegments.length === 0)
    ) {
      const firstPreview = previewPath[0] || null;
      const secondPreview = previewPath[1] || null;
      const targetPoint = secondPreview || firstPreview;
      if (targetPoint) {
        const d = haversineMeters(
          { lat: Number(effectivePlanStart.lat), lon: Number(effectivePlanStart.lon) },
          { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) }
        );
        if (Number.isFinite(d) && d > 6) {
          externalSegments.push([
            { lat: Number(effectivePlanStart.lat), lon: Number(effectivePlanStart.lon) },
            { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) },
          ]);
        }
      }
    }

    window.dispatchEvent(
      new CustomEvent("trekkin:navgrid-route", {
        detail: { path: previewPath, externalSegments },
      })
    );
    } catch (err) {
      logEvent("error", "PLAN_ROUTE_CALC_CRASH", {
        message: String(err?.message || err || "unknown"),
        stack: String(err?.stack || "").slice(0, 1000),
        planActive: !!planActive,
        waypointsCount: Array.isArray(planWaypoints) ? planWaypoints.length : 0,
        hasPlanStart:
          Number.isFinite(Number(planStart?.lat)) &&
          Number.isFinite(Number(planStart?.lon)),
        hasPlanEnd:
          Number.isFinite(Number(planEnd?.lat)) &&
          Number.isFinite(Number(planEnd?.lon)),
      });
      showToast("Falha temporária ao calcular rota. Tenta novamente.", "error");
    }
  }, [
    planActive,
    planStart?.lat,
    planStart?.lon,
    userPosition?.lat,
    userPosition?.lon,
    planEnd?.lat,
    planEnd?.lon,
    planWaypoints,
    planRoutePreference,
    planRouteType,
    navWaypointTarget,
    canUsePremiumSegments,
    navGridSegments,
    planRouteRecalcTick,
    setPlanRoutePoints,
    navWaypointPending,
    navWaypointQueue,
    requestNavGridForRouting,
  ]);

  useEffect(() => {
    if (planActive) return;
    if (
      !navWaypointTarget &&
      navWaypointPending?.entry?.lat &&
      navWaypointPending?.entry?.lon &&
      userPosition?.lat &&
      userPosition?.lon
    ) {
      if (isAndroid) {
        const directPath = [
          { lat: Number(userPosition.lat), lon: Number(userPosition.lon) },
          {
            lat: Number(navWaypointPending.entry.lat),
            lon: Number(navWaypointPending.entry.lon),
          },
        ];
        window.dispatchEvent(
          new CustomEvent("trekkin:navgrid-route", {
            detail: { path: directPath, externalSegments: [] },
          })
        );
        try {
          window.__trekkinLastNavGridRoute = {
            path: directPath,
            externalSegments: [],
            ts: Date.now(),
          };
        } catch {}
        const routeGrid = loadNavGrid();
        if (!Array.isArray(routeGrid?.segments) || !routeGrid.segments.length) {
          return;
        }
      }
      const grid = isAndroid ? loadNavGrid() : navGridConfigRef.current;
      const gridSegments = Array.isArray(grid?.segments) ? grid.segments : [];
      const eleLookup = new Map();
      for (const seg of gridSegments) {
        const pts = Array.isArray(seg?.points) ? seg.points : [];
        for (const pt of pts) {
          const pLat = Number(pt?.lat);
          const pLon = Number(pt?.lon);
          const ele = Number(pt?.ele ?? pt?.elevation ?? pt?.alt ?? pt?.z);
          if (!Number.isFinite(pLat) || !Number.isFinite(pLon) || !Number.isFinite(ele)) continue;
          const key = `${pLat.toFixed(6)}|${pLon.toFixed(6)}`;
          if (!eleLookup.has(key)) eleLookup.set(key, ele);
        }
      }
      const withEle = (path) =>
        (Array.isArray(path) ? path : [])
          .map((p) => {
            const lat = Number(p?.lat);
            const lon = Number(p?.lon ?? p?.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
            const ownEle = Number(p?.ele ?? p?.elevation ?? p?.alt ?? p?.z);
            const key = `${lat.toFixed(6)}|${lon.toFixed(6)}`;
            const mapEle = eleLookup.get(key);
            const ele = Number.isFinite(ownEle)
              ? ownEle
              : Number.isFinite(Number(mapEle))
              ? Number(mapEle)
              : undefined;
            return {
              ...p,
              lat,
              lon,
              ele: Number.isFinite(ele) ? ele : undefined,
            };
          })
          .filter(Boolean);
      const straightPath = [
        { lat: Number(userPosition.lat), lon: Number(userPosition.lon) },
        {
          lat: Number(navWaypointPending.entry.lat),
          lon: Number(navWaypointPending.entry.lon),
        },
      ];
      const target = navWaypointPending?.target || null;
      if (target?.lat && target?.lon && grid?.segments?.length) {
        const cacheKey = [
          String(target?.id || ""),
          Number(navWaypointPending.entry.lat).toFixed(6),
          Number(navWaypointPending.entry.lon).toFixed(6),
          Number(target.lat).toFixed(6),
          Number(target.lon).toFixed(6),
          Number(grid?.segments?.length || 0),
        ].join("|");
        let plannedPath = null;
        if (pendingNavRouteCacheRef.current.key === cacheKey) {
          plannedPath = pendingNavRouteCacheRef.current.path;
        } else {
          const rawRoutingMerge =
            Number(grid?.routeMergeMeters) || Number(grid?.mergeMeters) || 8;
          const mergeMeters = Math.max(5, Math.min(rawRoutingMerge, 15));
          const routeGrid = createRegionalRoutingGrid(
            grid,
            [navWaypointPending.entry, target, ...(Array.isArray(navWaypointQueue) ? navWaypointQueue : [])],
            { preference: "shortest" }
          );
          const graph = buildNavGridGraph(routeGrid, {
            mergeMeters,
            mergeEndpointsOnly: false,
            activityMode,
          });
          const strictSnapMeters = Math.max(
            150,
            Math.min(Number(grid?.entryPromptMeters) || 200, 2000)
          );
          const nearestEntry = findNearestRoutableNodeInGraph(graph, {
            lat: Number(navWaypointPending.entry.lat),
            lon: Number(navWaypointPending.entry.lon),
          });
          const nearestTarget = findNearestRoutableNodeInGraph(graph, {
            lat: Number(target.lat),
            lon: Number(target.lon),
          });
          const routeStart =
            nearestEntry?.node &&
            Number.isFinite(Number(nearestEntry.node.lat)) &&
            Number.isFinite(Number(nearestEntry.node.lon))
              ? {
                  lat: Number(nearestEntry.node.lat),
                  lon: Number(nearestEntry.node.lon),
                }
              : {
                  lat: Number(navWaypointPending.entry.lat),
                  lon: Number(navWaypointPending.entry.lon),
                };
          const routeEnd =
            nearestTarget?.node &&
            Number.isFinite(Number(nearestTarget.node.lat)) &&
            Number.isFinite(Number(nearestTarget.node.lon))
              ? {
                  lat: Number(nearestTarget.node.lat),
                  lon: Number(nearestTarget.node.lon),
                }
              : { lat: Number(target.lat), lon: Number(target.lon) };
          const resultStrict = computeNavGridRoute(
            routeGrid,
            routeStart,
            routeEnd,
            {
              graph,
              mergeMeters,
              maxSnapMeters: strictSnapMeters,
            }
          );
          const resultAll = null;
          const result = resultStrict?.ok ? resultStrict : resultAll;
          plannedPath =
            result?.ok && Array.isArray(result.path) && result.path.length > 1
              ? result.path
              : null;
          if (!plannedPath) {
            const connectedFallback = findConnectedComponentRoute(
              graph,
              { lat: Number(routeStart.lat), lon: Number(routeStart.lon) },
              { lat: Number(target.lat), lon: Number(target.lon) },
              { maxCandidates: 180 }
            );
            if (Array.isArray(connectedFallback?.path) && connectedFallback.path.length > 1) {
              plannedPath = connectedFallback.path;
              try {
                logEvent("warn", "NAV_WAYPOINT_PENDING_COMPONENT_FALLBACK_OK", {
                  points: connectedFallback.path.length,
                  componentSize: Number(connectedFallback.componentSize || 0),
                  targetDistMeters: Number(connectedFallback.targetDistMeters || 0),
                });
              } catch {}
            }
          }
          pendingNavRouteCacheRef.current = { key: cacheKey, path: plannedPath };
        }
        if (plannedPath?.length > 1) {
          const queueTargets = (Array.isArray(navWaypointQueue) ? navWaypointQueue : [])
            .map((wp) => ({
              ...wp,
              lat: Number(wp?.lat),
              lon: Number(wp?.lon),
            }))
            .filter((wp) => Number.isFinite(wp.lat) && Number.isFinite(wp.lon));

          const routePalette = ["#007bff", "#7c3aed", "#0ea5e9", "#f59e0b", "#ef4444", "#10b981"];
          const pickResult = (strictRes, allRes) =>
            strictRes?.ok ? strictRes : allRes;
          const joinPath = (base, leg) => {
            const nextBase = Array.isArray(base) ? [...base] : [];
            const nextLeg = Array.isArray(leg) ? leg : [];
            if (!nextLeg.length) return nextBase;
            if (!nextBase.length) return [...nextLeg];
            const last = nextBase[nextBase.length - 1];
            const first = nextLeg[0];
            if (
              last &&
              first &&
              Math.abs(Number(last.lat) - Number(first.lat)) < 1e-8 &&
              Math.abs(Number(last.lon ?? last.lng) - Number(first.lon ?? first.lng)) < 1e-8
            ) {
              return [...nextBase, ...nextLeg.slice(1)];
            }
            return [...nextBase, ...nextLeg];
          };

          let mergedPath = Array.isArray(plannedPath) ? [...plannedPath] : [];
          const coloredSegmentsRaw = [{ path: plannedPath, color: routePalette[0] }];
          let failedQueueReason = null;
          let fromPoint = { lat: Number(target.lat), lon: Number(target.lon) };
          for (let i = 0; i < queueTargets.length; i += 1) {
            const toPoint = queueTargets[i];
            const resultStrict = computeNavGridRoute(
              routeGrid,
              fromPoint,
              { lat: Number(toPoint.lat), lon: Number(toPoint.lon) },
              {
                graph,
                mergeMeters,
                maxSnapMeters: strictSnapMeters,
              }
            );
            const resultAll = null;
            const result = pickResult(resultStrict, resultAll);
            if (!result?.ok) {
              failedQueueReason = result?.reason || "no_route";
              break;
            }
            const leg = Array.isArray(result.path) ? result.path : [];
            if (leg.length > 1) {
              mergedPath = joinPath(mergedPath, leg);
              coloredSegmentsRaw.push({
                path: leg,
                color: routePalette[(i + 1) % routePalette.length],
              });
            }
            fromPoint = { lat: Number(toPoint.lat), lon: Number(toPoint.lon) };
          }
          if (failedQueueReason) {
            if (navWaypointRouteErrorRef.current !== failedQueueReason) {
              showToast("Sem rota disponível para um waypoint da fila.", "error");
              navWaypointRouteErrorRef.current = failedQueueReason;
            }
          }

          const pathWithEle = withEle(mergedPath);
          const externalWithEle = [withEle(straightPath)];
          const coloredSegments = coloredSegmentsRaw
            .map((seg) => ({
              path: withEle(seg.path),
              color: seg.color,
            }))
            .filter((seg) => Array.isArray(seg.path) && seg.path.length > 1);
          try {
            window.__trekkinLastNavGridRoute = {
              path: pathWithEle,
              externalSegments: externalWithEle,
              coloredSegments,
              ts: Date.now(),
            };
          } catch {}
          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-route", {
              detail: {
                path: pathWithEle,
                externalSegments: externalWithEle,
                coloredSegments,
              },
            })
          );
          return;
        }
      }
      const straightWithEle = withEle(straightPath);
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-route", {
          detail: { path: straightWithEle, externalSegments: [] },
        })
      );
      try {
        window.__trekkinLastNavGridRoute = {
          path: straightWithEle,
          externalSegments: [],
          ts: Date.now(),
        };
      } catch {}
      return;
    }
    const target = navWaypointTarget;
    if (!target?.lat || !target?.lon) {
      navWaypointRouteErrorRef.current = null;
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-route", {
          detail: { path: [], externalSegments: [], coloredSegments: [] },
        })
      );
      try {
        window.__trekkinLastNavGridRoute = {
          path: [],
          externalSegments: [],
          coloredSegments: [],
          ts: Date.now(),
        };
      } catch {}
      return;
    }
    if (!userPosition?.lat || !userPosition?.lon) {
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-route", {
          detail: { path: [], externalSegments: [], coloredSegments: [] },
        })
      );
      try {
        window.__trekkinLastNavGridRoute = {
          path: [],
          externalSegments: [],
          coloredSegments: [],
          ts: Date.now(),
        };
      } catch {}
      return;
    }
    const grid = navGridConfigRef.current;
    if (!grid?.segments?.length) {
      requestNavGridForRouting("waypoint_route_empty");
      return;
    }
    const rawRoutingMerge =
      Number(grid?.routeMergeMeters) || Number(grid?.mergeMeters) || 8;
    const mergeMeters = Math.max(5, Math.min(rawRoutingMerge, 15));
    const waypointTargets = [target, ...(Array.isArray(navWaypointQueue) ? navWaypointQueue : [])]
      .map((wp) => ({
        ...wp,
        lat: Number(wp?.lat),
        lon: Number(wp?.lon),
      }))
      .filter((wp) => Number.isFinite(wp.lat) && Number.isFinite(wp.lon));
    const routeGrid = createRegionalRoutingGrid(
      grid,
      [userPosition, ...waypointTargets],
      { preference: "shortest" }
    );
    const graph = buildNavGridGraph(routeGrid, {
      mergeMeters,
      mergeEndpointsOnly: false,
      activityMode,
    });
    if (!waypointTargets.length) {
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-route", {
          detail: { path: [], externalSegments: [], coloredSegments: [] },
        })
      );
      try {
        window.__trekkinLastNavGridRoute = {
          path: [],
          externalSegments: [],
          coloredSegments: [],
          ts: Date.now(),
        };
      } catch {}
      return;
    }

    const pickResult = (strictRes, allRes) =>
      strictRes?.ok ? strictRes : allRes;

    const routePalette = ["#007bff", "#7c3aed", "#0ea5e9", "#f59e0b", "#ef4444", "#10b981"];

    let failedReason = null;
    let failedIndex = -1;
    let fromPoint = { lat: Number(userPosition.lat), lon: Number(userPosition.lon) };
    const allRoutePoints = [];
    const coloredSegments = [];
    const externalSegments = [];
    const legDiagnostics = [];

    for (let i = 0; i < waypointTargets.length; i += 1) {
      const toPoint = waypointTargets[i];
      const startNearest = findNearestRoutableNodeInGraph(graph, fromPoint);
      const endNearest = findNearestRoutableNodeInGraph(graph, toPoint);
      if (!startNearest?.node || !endNearest?.node) {
        try {
          logEvent("warn", "NAV_WAYPOINT_LEG_NODE_MISSING", {
            leg: i,
            fromLat: Number(fromPoint?.lat),
            fromLon: Number(fromPoint?.lon),
            toLat: Number(toPoint?.lat),
            toLon: Number(toPoint?.lon),
            startNearestDist: Number(startNearest?.dist),
            endNearestDist: Number(endNearest?.dist),
          });
        } catch {}
        legDiagnostics.push({
          leg: i,
          status: "fail",
          reason: "no_nearby_node",
          from: { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
          to: { lat: Number(toPoint.lat), lon: Number(toPoint.lon) },
          startDistMeters: Number.isFinite(Number(startNearest?.dist))
            ? Number(Number(startNearest.dist).toFixed(1))
            : null,
          endDistMeters: Number.isFinite(Number(endNearest?.dist))
            ? Number(Number(endNearest.dist).toFixed(1))
            : null,
        });
        failedReason = "no_nearby_node";
        failedIndex = i;
        break;
      }

      const routeFromPoint = {
        lat: Number(startNearest.node.lat),
        lon: Number(startNearest.node.lon),
      };
      const routeToPoint = {
        lat: Number(endNearest.node.lat),
        lon: Number(endNearest.node.lon),
      };

      const nodeRouteStrict = computeNavGridRoute(routeGrid, routeFromPoint, routeToPoint, {
        graph,
        mergeMeters,
        maxSnapMeters: 300,
      });
      const nodeRouteAll = null;
      let nodeRoute = pickResult(nodeRouteStrict, nodeRouteAll);
      const nodeRouteUltra = null;
      let nodeLegPath = Array.isArray(nodeRoute?.path) ? nodeRoute.path : [];
      if (!nodeRoute?.ok || nodeLegPath.length < 2) {
        const connectedFallback = findConnectedComponentRoute(
          graph,
          { lat: Number(routeFromPoint.lat), lon: Number(routeFromPoint.lon) },
          { lat: Number(routeToPoint.lat), lon: Number(routeToPoint.lon) },
          { maxCandidates: 180 }
        );
        if (connectedFallback?.path?.length > 1) {
          nodeRoute = {
            ok: true,
            path: connectedFallback.path,
            distanceMeters: Number(connectedFallback.distanceMeters || 0),
          };
          nodeLegPath = connectedFallback.path;
          try {
            logEvent("warn", "NAV_WAYPOINT_COMPONENT_LEG_FALLBACK_OK", {
              leg: i,
              startId: String(connectedFallback.startId || ""),
              targetId: String(connectedFallback.targetId || ""),
              componentSize: Number(connectedFallback.componentSize || 0),
              targetDistMeters: Number(connectedFallback.targetDistMeters || 0),
              pathPoints: connectedFallback.path.length,
            });
          } catch {}
        }
      }
      if (!nodeRoute?.ok || nodeLegPath.length < 2) {
        try {
          logEvent("warn", "NAV_WAYPOINT_LEG_ROUTE_FAIL", {
            leg: i,
            reason: String(nodeRoute?.reason || "no_route"),
            strictOk: !!nodeRouteStrict?.ok,
            fallbackOk: !!nodeRouteAll?.ok,
            ultraOk: !!nodeRouteUltra?.ok,
            fromLat: Number(fromPoint?.lat),
            fromLon: Number(fromPoint?.lon),
            toLat: Number(toPoint?.lat),
            toLon: Number(toPoint?.lon),
          });
        } catch {}
        legDiagnostics.push({
          leg: i,
          status: "fail",
          reason: nodeRoute?.reason || "no_route",
          from: { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
          to: { lat: Number(toPoint.lat), lon: Number(toPoint.lon) },
          startNode:
            nodeLegPath[0] && Number.isFinite(Number(nodeLegPath[0].lat)) && Number.isFinite(Number(nodeLegPath[0].lon ?? nodeLegPath[0].lng))
              ? {
                  lat: Number(nodeLegPath[0].lat),
                  lon: Number(nodeLegPath[0].lon ?? nodeLegPath[0].lng),
                }
              : null,
          endNode:
            nodeLegPath[nodeLegPath.length - 1] &&
            Number.isFinite(Number(nodeLegPath[nodeLegPath.length - 1].lat)) &&
            Number.isFinite(Number(nodeLegPath[nodeLegPath.length - 1].lon ?? nodeLegPath[nodeLegPath.length - 1].lng))
              ? {
                  lat: Number(nodeLegPath[nodeLegPath.length - 1].lat),
                  lon: Number(nodeLegPath[nodeLegPath.length - 1].lon ?? nodeLegPath[nodeLegPath.length - 1].lng),
                }
              : null,
          startDistMeters: Number.isFinite(Number(startNearest?.dist))
            ? Number(Number(startNearest.dist).toFixed(1))
            : null,
          endDistMeters: Number.isFinite(Number(endNearest?.dist))
            ? Number(Number(endNearest.dist).toFixed(1))
            : null,
          strictOk: !!nodeRouteStrict?.ok,
          fallbackOk: !!nodeRouteAll?.ok,
          ultraOk: !!nodeRouteUltra?.ok,
          strictReason: nodeRouteStrict?.reason || null,
          fallbackReason: nodeRouteAll?.reason || null,
          ultraReason: nodeRouteUltra?.reason || null,
          mergeMeters,
          fallbackMergeMeters: mergeMeters,
        });
        failedReason = nodeRoute?.reason || "no_route";
        failedIndex = i;
        break;
      }

      const firstNode = nodeLegPath[0];
      const lastNode = nodeLegPath[nodeLegPath.length - 1];
      const startNode =
        firstNode &&
        Number.isFinite(Number(firstNode.lat)) &&
        Number.isFinite(Number(firstNode.lon ?? firstNode.lng))
          ? { lat: Number(firstNode.lat), lon: Number(firstNode.lon ?? firstNode.lng) }
          : null;
      const endNode =
        lastNode &&
        Number.isFinite(Number(lastNode.lat)) &&
        Number.isFinite(Number(lastNode.lon ?? lastNode.lng))
          ? { lat: Number(lastNode.lat), lon: Number(lastNode.lon ?? lastNode.lng) }
          : null;
      if (!startNode || !endNode) {
        legDiagnostics.push({
          leg: i,
          status: "fail",
          reason: "invalid_leg_nodes",
          from: { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
          to: { lat: Number(toPoint.lat), lon: Number(toPoint.lon) },
        });
        failedReason = "no_route";
        failedIndex = i;
        break;
      }

      const startDistMeters = Number(
        haversineMeters(
          { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
          { lat: Number(startNode.lat), lon: Number(startNode.lon) }
        ) || 0
      );
      const endDistMeters = Number(
        haversineMeters(
          { lat: Number(toPoint.lat), lon: Number(toPoint.lon) },
          { lat: Number(endNode.lat), lon: Number(endNode.lon) }
        ) || 0
      );
      const startOffGrid = startDistMeters > 6;
      const endOffGrid = endDistMeters > 6;

      if (startOffGrid) {
        externalSegments.push([
          { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
          { lat: Number(startNode.lat), lon: Number(startNode.lon) },
        ]);
      }
      if (endOffGrid) {
        externalSegments.push([
          { lat: Number(endNode.lat), lon: Number(endNode.lon) },
          { lat: Number(toPoint.lat), lon: Number(toPoint.lon) },
        ]);
      }

      coloredSegments.push({
        path: nodeLegPath,
        color: routePalette[i % routePalette.length],
      });
      if (!allRoutePoints.length) {
        allRoutePoints.push(...nodeLegPath);
      } else {
        const first = nodeLegPath[0];
        const last = allRoutePoints[allRoutePoints.length - 1];
        if (
          first &&
          last &&
          Math.abs(Number(first.lat) - Number(last.lat)) < 1e-8 &&
          Math.abs(Number(first.lon ?? first.lng) - Number(last.lon ?? last.lng)) < 1e-8
        ) {
          allRoutePoints.push(...nodeLegPath.slice(1));
        } else {
          allRoutePoints.push(...nodeLegPath);
        }
      }
      legDiagnostics.push({
        leg: i,
        status: "ok",
        from: { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
        to: { lat: Number(toPoint.lat), lon: Number(toPoint.lon) },
        startNode,
        endNode,
        startDistMeters: Number.isFinite(Number(startNearest?.dist))
          ? Number(Number(startNearest.dist).toFixed(1))
          : null,
        endDistMeters: Number.isFinite(Number(endNearest?.dist))
          ? Number(Number(endNearest.dist).toFixed(1))
          : null,
        nodePoints: nodeLegPath.length,
      });
      try {
        logEvent("info", "NAV_WAYPOINT_LEG_ROUTE_OK", {
          leg: i,
          fromLat: Number(fromPoint?.lat),
          fromLon: Number(fromPoint?.lon),
          toLat: Number(toPoint?.lat),
          toLon: Number(toPoint?.lon),
          startNearestDist: Number(startNearest?.dist),
          endNearestDist: Number(endNearest?.dist),
          pathPoints: nodeLegPath.length,
          pathMeters: Number(nodeRoute?.distanceMeters || 0),
        });
      } catch {}
      fromPoint = { lat: Number(toPoint.lat), lon: Number(toPoint.lon) };
    }

    const diagPayload = {
      ts: Date.now(),
      targetCount: waypointTargets.length,
      failedReason: failedReason || null,
      failedIndex,
      user: {
        lat: Number(userPosition?.lat),
        lon: Number(userPosition?.lon),
      },
      legs: legDiagnostics,
    };
    try {
      window.__trekkinNavWaypointDiag = diagPayload;
    } catch {}

    if (failedReason) {
      try {
        logEvent("warn", "NAV_WAYPOINT_ROUTE_FAILED", {
          failedReason: String(failedReason || ""),
          failedIndex: Number(failedIndex),
          targetCount: waypointTargets.length,
          userLat: Number(userPosition?.lat),
          userLon: Number(userPosition?.lon),
          diag: diagPayload,
        });
      } catch {}
      // Resgate específico para 1 waypoint: evita cair num fallback parcial errado.
      if (failedReason === "no_route" && waypointTargets.length === 1) {
        try {
          const targetPoint = waypointTargets[0];
          const tryBfsGraph = (graphBase, tag = "base") => {
            const startNearest = findNearestNodeInGraph(graphBase, fromPoint);
            const graphNodes = Array.isArray(graphBase?.nodes) ? graphBase.nodes : [];
            const edges = graphBase?.edges && typeof graphBase.edges === "object" ? graphBase.edges : {};
            const nodesById =
              graphBase?.nodesById && typeof graphBase.nodesById === "object" ? graphBase.nodesById : {};
            if (!(startNearest?.node && graphNodes.length && nodesById && edges)) return null;
            const startId = String(startNearest.node?.id || "");
            const rankedEndNodes = graphNodes
              .map((n) => ({
                id: String(n?.id || ""),
                lat: Number(n?.lat),
                lon: Number(n?.lon ?? n?.lng),
                dist: haversineMeters(
                  { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) },
                  { lat: Number(n?.lat), lon: Number(n?.lon ?? n?.lng) }
                ),
              }))
              .filter(
                (x) =>
                  x.id &&
                  Number.isFinite(x.lat) &&
                  Number.isFinite(x.lon) &&
                  Number.isFinite(Number(x.dist))
              )
              .sort((a, b) => Number(a.dist) - Number(b.dist))
              .slice(0, 30);

            let best = null;
            for (const cand of rankedEndNodes) {
              const endId = String(cand.id || "");
              if (!startId || !endId || !nodesById[startId] || !nodesById[endId]) continue;
              const queue = [startId];
              const seen = new Set([startId]);
              const parent = {};
              let found = startId === endId;
              while (queue.length && !found) {
                const cur = queue.shift();
                const neighbors = edges[cur] && typeof edges[cur] === "object" ? edges[cur] : {};
                for (const nid of Object.keys(neighbors)) {
                  if (!nid || seen.has(nid) || !nodesById[nid]) continue;
                  seen.add(nid);
                  parent[nid] = cur;
                  if (nid === endId) {
                    found = true;
                    break;
                  }
                  queue.push(nid);
                }
              }
              if (!found) continue;
              const pathIds = [endId];
              let cur = endId;
              while (cur !== startId) {
                cur = parent[cur];
                if (!cur) break;
                pathIds.unshift(cur);
              }
              const graphPath = pathIds
                .map((id) => nodesById[id])
                .filter(Boolean)
                .map((n) => ({ lat: Number(n.lat), lon: Number(n.lon) }))
                .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
              if (graphPath.length < 2) continue;
              let routeMeters = 0;
              for (let pi = 1; pi < graphPath.length; pi += 1) {
                routeMeters +=
                  haversineMeters(
                    { lat: Number(graphPath[pi - 1].lat), lon: Number(graphPath[pi - 1].lon) },
                    { lat: Number(graphPath[pi].lat), lon: Number(graphPath[pi].lon) }
                  ) || 0;
              }
              const exitMeters = haversineMeters(
                { lat: Number(cand.lat), lon: Number(cand.lon) },
                { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) }
              );
              const score = Number(routeMeters || 0) + Number(exitMeters || 0) * 2;
              if (!best || score < best.score) {
                best = { score, path: graphPath };
              }
            }
            if (!best?.path?.length) {
              try {
                logEvent("warn", "NAV_WAYPOINT_BFS_1WP_NO_PATH", {
                  graphTag: tag,
                  startId,
                  candidates: rankedEndNodes.length,
                  startDistMeters: Number(startNearest?.dist),
                });
              } catch {}
              return null;
            }
            return best.path;
          };

          let bfsPath = tryBfsGraph(graph, "base");
          if (!bfsPath) {
            const graphLoose = buildNavGridGraph(grid, {
              mergeMeters: Math.max(80, Math.min(fallbackMergeMeters * 2, 320)),
              mergeEndpointsOnly: false,
              activityMode,
            });
            bfsPath = tryBfsGraph(graphLoose, "loose");
          }

          if (Array.isArray(bfsPath) && bfsPath.length > 1) {
            const extOut = [];
            const first = bfsPath[0];
            const last = bfsPath[bfsPath.length - 1];
            if (
              first &&
              (Math.abs(Number(fromPoint.lat) - Number(first.lat)) > 1e-8 ||
                Math.abs(Number(fromPoint.lon) - Number(first.lon ?? first.lng)) > 1e-8)
            ) {
              extOut.push([
                { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
                { lat: Number(first.lat), lon: Number(first.lon ?? first.lng) },
              ]);
            }
            extOut.push([
              { lat: Number(last.lat), lon: Number(last.lon ?? last.lng) },
              { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) },
            ]);
            window.dispatchEvent(
              new CustomEvent("trekkin:navgrid-route", {
                detail: {
                  path: bfsPath,
                  externalSegments: extOut,
                  coloredSegments: [{ path: bfsPath, color: routePalette[0] }],
                },
              })
            );
            try {
              window.__trekkinLastNavGridRoute = {
                path: bfsPath,
                externalSegments: extOut,
                coloredSegments: [{ path: bfsPath, color: routePalette[0] }],
                ts: Date.now(),
              };
              logEvent("warn", "NAV_WAYPOINT_BFS_1WP_OK", {
                points: bfsPath.length,
              });
            } catch {}
            navWaypointRouteErrorRef.current = null;
            return;
          }

          // Fallback extra para 1 waypoint: usa a componente do nó inicial
          // e liga ao nó dessa componente mais próximo do destino.
          const nearestFrom = findNearestNodeInGraph(graph, fromPoint);
          const startNodeId = String(nearestFrom?.node?.id || "");
          const edges = graph?.edges && typeof graph.edges === "object" ? graph.edges : {};
          const nodesById =
            graph?.nodesById && typeof graph.nodesById === "object" ? graph.nodesById : {};
          const componentNodes = [];
          if (startNodeId && nodesById[startNodeId]) {
            const seen = new Set([startNodeId]);
            const queue = [startNodeId];
            while (queue.length) {
              const current = queue.shift();
              const node = nodesById[current];
              if (
                node &&
                Number.isFinite(Number(node.lat)) &&
                Number.isFinite(Number(node.lon))
              ) {
                componentNodes.push({
                  id: String(node.id || current),
                  lat: Number(node.lat),
                  lon: Number(node.lon),
                });
              }
              const neighbors = edges[current] && typeof edges[current] === "object" ? edges[current] : {};
              Object.keys(neighbors).forEach((nid) => {
                if (!nid || seen.has(nid) || !nodesById[nid]) return;
                seen.add(nid);
                queue.push(nid);
              });
            }
          }
          const candidateNodes = componentNodes
            .map((n) => ({
              ...n,
              dToTarget: haversineMeters(
                { lat: Number(n.lat), lon: Number(n.lon) },
                { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) }
              ),
            }))
            .sort((a, b) => Number(a.dToTarget) - Number(b.dToTarget))
            .slice(0, 80);
          try {
            logEvent("warn", "NAV_WAYPOINT_COMPONENT_DIAG", {
              startNodeId,
              componentSize: componentNodes.length,
              candidateCount: candidateNodes.length,
              bestTargetDist: Number(candidateNodes?.[0]?.dToTarget),
            });
          } catch {}
          if (startNodeId && candidateNodes.length) {
            const targetNode = candidateNodes[0];
            const targetId = String(targetNode?.id || "");
            if (targetId && targetId !== startNodeId) {
              const parent = {};
              const seen = new Set([startNodeId]);
              const queue = [startNodeId];
              let found = false;
              while (queue.length && !found) {
                const cur = queue.shift();
                const neighbors = edges[cur] && typeof edges[cur] === "object" ? edges[cur] : {};
                for (const nid of Object.keys(neighbors)) {
                  if (!nid || seen.has(nid) || !nodesById[nid]) continue;
                  seen.add(nid);
                  parent[nid] = cur;
                  if (nid === targetId) {
                    found = true;
                    break;
                  }
                  queue.push(nid);
                }
              }
              if (found) {
                const pathIds = [targetId];
                let cur = targetId;
                while (cur !== startNodeId) {
                  cur = parent[cur];
                  if (!cur) break;
                  pathIds.unshift(cur);
                }
                const gridPath = pathIds
                  .map((id) => nodesById[id])
                  .filter(Boolean)
                  .map((n) => ({ lat: Number(n.lat), lon: Number(n.lon) }))
                  .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
                if (gridPath.length > 1) {
                  const extOut = [];
                  const first = gridPath[0];
                  const last = gridPath[gridPath.length - 1];
                  if (
                    Math.abs(Number(fromPoint.lat) - Number(first.lat)) > 1e-8 ||
                    Math.abs(Number(fromPoint.lon) - Number(first.lon)) > 1e-8
                  ) {
                    extOut.push([
                      { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
                      { lat: Number(first.lat), lon: Number(first.lon) },
                    ]);
                  }
                  extOut.push([
                    { lat: Number(last.lat), lon: Number(last.lon) },
                    { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) },
                  ]);
                  window.dispatchEvent(
                    new CustomEvent("trekkin:navgrid-route", {
                      detail: {
                        path: gridPath,
                        externalSegments: extOut,
                        coloredSegments: [{ path: gridPath, color: routePalette[0] }],
                      },
                    })
                  );
                  try {
                    window.__trekkinLastNavGridRoute = {
                      path: gridPath,
                      externalSegments: extOut,
                      coloredSegments: [{ path: gridPath, color: routePalette[0] }],
                      ts: Date.now(),
                    };
                    logEvent("warn", "NAV_WAYPOINT_COMPONENT_FALLBACK_OK", {
                      points: gridPath.length,
                      targetDist: Number(targetNode?.dToTarget),
                    });
                  } catch {}
                  navWaypointRouteErrorRef.current = null;
                  return;
                }
              }
            }
          }
        } catch {}
      }
      if (
        waypointTargets.length > 1 &&
        failedReason === "no_route" &&
        failedIndex >= 0 &&
        waypointTargets[failedIndex]
      ) {
        const targetPoint = waypointTargets[failedIndex];
        const userPoint = { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) };

        // Fallback prioritário multi-waypoint: user->grid e grid->waypoints em fila com cores por leg.
        {
          const segs = Array.isArray(grid?.segments) ? grid.segments : [];
          const segmentPaths = segs
            .map((seg) =>
              (Array.isArray(seg?.points) ? seg.points : [])
                .map((p) => ({ lat: Number(p?.lat), lon: Number(p?.lon) }))
                .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
            )
            .filter((pts) => pts.length > 1);
          const buildLeg = (from, to) => {
            let best = null;
            for (const pts of segmentPaths) {
              let iFrom = -1;
              let iTo = -1;
              let dFrom = Infinity;
              let dTo = Infinity;
              for (let i = 0; i < pts.length; i += 1) {
                const df = haversineMeters(from, pts[i]);
                if (df < dFrom) { dFrom = df; iFrom = i; }
                const dt = haversineMeters(to, pts[i]);
                if (dt < dTo) { dTo = dt; iTo = i; }
              }
              if (iFrom < 0 || iTo < 0 || iFrom === iTo) continue;
              const score = dFrom + dTo;
              if (!best || score < best.score) best = { pts, iFrom, iTo, dFrom, dTo, score };
            }
            if (!best) return null;
            const step = best.iTo >= best.iFrom ? 1 : -1;
            const gridPath = [];
            for (let i = best.iFrom; ; i += step) {
              gridPath.push(best.pts[i]);
              if (i === best.iTo) break;
            }
            if (gridPath.length <= 1) return null;
            return { gridPath, dFrom: best.dFrom, dTo: best.dTo };
          };

          let legFrom = userPoint;
          const merged = [];
          const extOut = [];
          const colored = [];
          let okLegs = 0;
          for (let li = 0; li < waypointTargets.length; li += 1) {
            const wp = waypointTargets[li];
            const leg = buildLeg(
              { lat: Number(legFrom.lat), lon: Number(legFrom.lon) },
              { lat: Number(wp.lat), lon: Number(wp.lon) }
            );
            if (!leg) break;
            const first = leg.gridPath[0];
            const last = leg.gridPath[leg.gridPath.length - 1];
            if (li === 0 && (Math.abs(Number(userPoint.lat) - Number(first.lat)) > 1e-8 || Math.abs(Number(userPoint.lon) - Number(first.lon)) > 1e-8)) {
              extOut.push([userPoint, first]);
            }
            extOut.push([last, { lat: Number(wp.lat), lon: Number(wp.lon) }]);
            colored.push({ path: leg.gridPath, color: routePalette[li % routePalette.length] });
            if (!merged.length) merged.push(...leg.gridPath);
            else {
              const mLast = merged[merged.length - 1];
              const lFirst = leg.gridPath[0];
              if (Math.abs(Number(mLast.lat) - Number(lFirst.lat)) < 1e-8 && Math.abs(Number(mLast.lon) - Number(lFirst.lon)) < 1e-8) {
                merged.push(...leg.gridPath.slice(1));
              } else merged.push(...leg.gridPath);
            }
            legFrom = { lat: Number(wp.lat), lon: Number(wp.lon) };
            okLegs += 1;
          }

          if (okLegs > 0 && merged.length > 1) {
            window.dispatchEvent(
              new CustomEvent("trekkin:navgrid-route", {
                detail: { path: merged, externalSegments: extOut, coloredSegments: colored },
              })
            );
            try {
              window.__trekkinLastNavGridRoute = {
                path: merged,
                externalSegments: extOut,
                coloredSegments: colored,
                ts: Date.now(),
              };
            } catch {}
            navWaypointRouteErrorRef.current = null;
            logger.warn("NAV_WAYPOINT", "Fallback segmento multi aplicado", {
              failedIndex,
              okLegs,
              targetCount: waypointTargets.length,
              routePoints: merged.length,
            });
            return;
          }
        }

        const nearestFrom = findNearestNodeInGraph(graph, fromPoint);
        const startNodeId = String(nearestFrom?.node?.id || "");
        const edges = graph?.edges && typeof graph.edges === "object" ? graph.edges : {};
        const nodesById =
          graph?.nodesById && typeof graph.nodesById === "object" ? graph.nodesById : {};
        const componentNodes = [];
        if (startNodeId && nodesById[startNodeId]) {
          const seen = new Set([startNodeId]);
          const queue = [startNodeId];
          while (queue.length) {
            const current = queue.shift();
            const node = nodesById[current];
            if (
              node &&
              Number.isFinite(Number(node.lat)) &&
              Number.isFinite(Number(node.lon))
            ) {
              componentNodes.push({
                lat: Number(node.lat),
                lon: Number(node.lon),
                id: String(node.id || current),
              });
            }
            const neighbors = edges[current] && typeof edges[current] === "object" ? edges[current] : {};
            Object.keys(neighbors).forEach((nid) => {
              if (!nid || seen.has(nid) || !nodesById[nid]) return;
              seen.add(nid);
              queue.push(nid);
            });
          }
        }
        const candidateNodes = (componentNodes.length ? componentNodes : Array.isArray(graph?.nodes) ? graph.nodes : [])
          .filter(
            (n) =>
              Number.isFinite(Number(n?.lat)) &&
              Number.isFinite(Number(n?.lon))
          )
          .map((n) => ({
            id: String(n?.id || ""),
            lat: Number(n.lat),
            lon: Number(n.lon),
            dToTarget: haversineMeters(
              { lat: Number(n.lat), lon: Number(n.lon) },
              { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) }
            ),
          }))
          .sort((a, b) => Number(a.dToTarget) - Number(b.dToTarget))
          .slice(0, 80);

        // Caminho garantido dentro da componente do user (sem depender do router/snap).
        if (startNodeId && componentNodes.length && candidateNodes.length) {
          const targetNode = candidateNodes[0];
          const targetId = String(targetNode?.id || "");
          if (targetId && targetId !== startNodeId) {
            const parent = {};
            const seen = new Set([startNodeId]);
            const queue = [startNodeId];
            let found = false;
            while (queue.length && !found) {
              const cur = queue.shift();
              const neighbors = edges[cur] && typeof edges[cur] === "object" ? edges[cur] : {};
              for (const nid of Object.keys(neighbors)) {
                if (!nid || seen.has(nid) || !nodesById[nid]) continue;
                seen.add(nid);
                parent[nid] = cur;
                if (nid === targetId) {
                  found = true;
                  break;
                }
                queue.push(nid);
              }
            }
            if (found) {
              const pathIds = [targetId];
              let cur = targetId;
              while (cur !== startNodeId) {
                cur = parent[cur];
                if (!cur) break;
                pathIds.unshift(cur);
              }
              const gridPath = pathIds
                .map((id) => nodesById[id])
                .filter(Boolean)
                .map((n) => ({ lat: Number(n.lat), lon: Number(n.lon) }))
                .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
              if (gridPath.length > 1) {
                const extOut = [];
                const first = gridPath[0];
                const last = gridPath[gridPath.length - 1];
                if (
                  Math.abs(Number(fromPoint.lat) - Number(first.lat)) > 1e-8 ||
                  Math.abs(Number(fromPoint.lon) - Number(first.lon)) > 1e-8
                ) {
                  extOut.push([
                    { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
                    { lat: Number(first.lat), lon: Number(first.lon) },
                  ]);
                }
                extOut.push([
                  { lat: Number(last.lat), lon: Number(last.lon) },
                  { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) },
                ]);
                const colored = [
                  { path: gridPath, color: routePalette[failedIndex % routePalette.length] },
                ];
                window.dispatchEvent(
                  new CustomEvent("trekkin:navgrid-route", {
                    detail: {
                      path: gridPath,
                      externalSegments: extOut,
                      coloredSegments: colored,
                    },
                  })
                );
                try {
                  window.__trekkinLastNavGridRoute = {
                    path: gridPath,
                    externalSegments: extOut,
                    coloredSegments: colored,
                    ts: Date.now(),
                  };
                } catch {}
                navWaypointRouteErrorRef.current = null;
                logger.warn("NAV_WAYPOINT", "Fallback componente aplicado (entrada user + grid + ligação final)", {
                  failedIndex,
                  startNodeId,
                  targetId,
                  routePoints: gridPath.length,
                });
                return;
              }
            }
          }
        }

        let bestPartial = null;
        for (const node of candidateNodes) {
          const candidateRoute = computeNavGridRoute(grid, fromPoint, node, {
            graph,
            mergeMeters,
            maxSnapMeters: 900,
          });
          if (!candidateRoute?.ok || !Array.isArray(candidateRoute.path) || candidateRoute.path.length < 1) {
            continue;
          }
          const score =
            Number(candidateRoute.distanceMeters || 0) + Number(node.dToTarget || 0) * 1.35;
          if (!bestPartial || score < bestPartial.score) {
            bestPartial = {
              score,
              node,
              route: candidateRoute.path,
            };
          }
        }

        if (bestPartial?.route?.length > 0) {
          let partialRoute = bestPartial.route;
          if (partialRoute.length <= 1) {
            const segs = Array.isArray(grid?.segments) ? grid.segments : [];
            let bestSeg = null;
            let bestStartIdx = -1;
            let bestStartDist = Infinity;
            for (const seg of segs) {
              const pts = (Array.isArray(seg?.points) ? seg.points : [])
                .map((p) => ({ lat: Number(p?.lat), lon: Number(p?.lon) }))
                .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
              if (pts.length < 2) continue;
              let localIdx = -1;
              let localDist = Infinity;
              for (let pi = 0; pi < pts.length; pi += 1) {
                const d = haversineMeters(
                  { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
                  pts[pi]
                );
                if (d < localDist) {
                  localDist = d;
                  localIdx = pi;
                }
              }
              if (localDist < bestStartDist) {
                bestStartDist = localDist;
                bestStartIdx = localIdx;
                bestSeg = pts;
              }
            }
            if (bestSeg && bestStartIdx >= 0) {
              let endIdx = -1;
              let endDist = Infinity;
              for (let pi = 0; pi < bestSeg.length; pi += 1) {
                const d = haversineMeters(
                  { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) },
                  bestSeg[pi]
                );
                if (d < endDist) {
                  endDist = d;
                  endIdx = pi;
                }
              }
              if (endIdx >= 0 && endIdx !== bestStartIdx) {
                const step = endIdx >= bestStartIdx ? 1 : -1;
                const forced = [];
                for (let pi = bestStartIdx; ; pi += step) {
                  forced.push(bestSeg[pi]);
                  if (pi === endIdx) break;
                }
                if (forced.length > 1) {
                  partialRoute = forced;
                }
              }
            }
          }
          const firstNode = partialRoute[0];
          const lastNode = partialRoute[partialRoute.length - 1];
          const fallbackExternal = [];
          if (
            firstNode &&
            (Math.abs(Number(fromPoint.lat) - Number(firstNode.lat)) > 1e-8 ||
              Math.abs(Number(fromPoint.lon) - Number(firstNode.lon ?? firstNode.lng)) > 1e-8)
          ) {
            fallbackExternal.push([
              { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
              { lat: Number(firstNode.lat), lon: Number(firstNode.lon ?? firstNode.lng) },
            ]);
          }
          fallbackExternal.push([
            { lat: Number(lastNode.lat), lon: Number(lastNode.lon ?? lastNode.lng) },
            { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) },
          ]);

          const fallbackColored = [
            {
              path: partialRoute,
              color: routePalette[failedIndex % routePalette.length],
            },
          ];
          const pathOut = partialRoute
            .map((p) => ({
              ...p,
              lat: Number(p?.lat),
              lon: Number(p?.lon ?? p?.lng),
            }))
            .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
          const extOut = fallbackExternal
            .map((seg) =>
              seg
                .map((p) => ({ lat: Number(p.lat), lon: Number(p.lon) }))
                .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
            )
            .filter((seg) => seg.length > 1);

          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-route", {
              detail: {
                path: pathOut,
                externalSegments: extOut,
                coloredSegments: fallbackColored,
              },
            })
          );
          try {
            window.__trekkinLastNavGridRoute = {
              path: pathOut,
              externalSegments: extOut,
              coloredSegments: fallbackColored,
              ts: Date.now(),
            };
          } catch {}
          navWaypointRouteErrorRef.current = null;
          logger.warn("NAV_WAYPOINT", "Fallback parcial aplicado (grid + ligação final)", {
            failedIndex,
            target: targetPoint,
            chosenNode: bestPartial.node,
            routePoints: pathOut.length,
          });
          return;
        }

        // Fallback de emergência: sem rota navegável, mas sem bloquear navegação.
        const emergencyEntry =
          nearestFrom?.node &&
          Number.isFinite(Number(nearestFrom.node.lat)) &&
          Number.isFinite(Number(nearestFrom.node.lon))
            ? { lat: Number(nearestFrom.node.lat), lon: Number(nearestFrom.node.lon) }
            : null;
        if (emergencyEntry) {
          const extOut = [
            [
              { lat: Number(fromPoint.lat), lon: Number(fromPoint.lon) },
              emergencyEntry,
            ],
            [
              emergencyEntry,
              { lat: Number(targetPoint.lat), lon: Number(targetPoint.lon) },
            ],
          ];
          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-route", {
              detail: {
                path: [],
                externalSegments: extOut,
                coloredSegments: [],
              },
            })
          );
          try {
            window.__trekkinLastNavGridRoute = {
              path: [],
              externalSegments: extOut,
              coloredSegments: [],
              ts: Date.now(),
            };
          } catch {}
          navWaypointRouteErrorRef.current = null;
          logger.warn("NAV_WAYPOINT", "Fallback emergência aplicado (sem bloqueio)", {
            failedIndex,
            target: targetPoint,
            emergencyEntry,
          });
          return;
        }
      }
      const failStamp = `${failedReason}|${failedIndex}|${navWaypointTarget?.id || "na"}|${waypointTargets.length}`;
      if (navWaypointDebugStampRef.current !== failStamp) {
        navWaypointDebugStampRef.current = failStamp;
        logger.warn("NAV_WAYPOINT", "Falha no cálculo da rota para waypoint", diagPayload);
      }
      if (navWaypointRouteErrorRef.current !== failedReason) {
        const hasPartialRoute =
          (Array.isArray(allRoutePoints) && allRoutePoints.length > 1) ||
          (Array.isArray(externalSegments) &&
            externalSegments.some((seg) => Array.isArray(seg) && seg.length > 1));
        if (hasPartialRoute) {
          const normalizeSimple = (list) =>
            (Array.isArray(list) ? list : [])
              .map((p) => {
                const lat = Number(p?.lat);
                const lon = Number(p?.lon ?? p?.lng);
                if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
                return { ...p, lat, lon };
              })
              .filter(Boolean);
          const partialPath = normalizeSimple(allRoutePoints);
          const partialExternal = (Array.isArray(externalSegments) ? externalSegments : [])
            .map(normalizeSimple)
            .filter((seg) => seg.length > 1);
          const partialColored = (Array.isArray(coloredSegments) ? coloredSegments : [])
            .map((seg) => ({
              ...seg,
              path: normalizeSimple(seg.path),
            }))
            .filter((seg) => Array.isArray(seg.path) && seg.path.length > 1);
          window.dispatchEvent(
            new CustomEvent("trekkin:navgrid-route", {
              detail: {
                path: partialPath,
                externalSegments: partialExternal,
                coloredSegments: partialColored,
              },
            })
          );
          try {
            window.__trekkinLastNavGridRoute = {
              path: partialPath,
              externalSegments: partialExternal,
              coloredSegments: partialColored,
              ts: Date.now(),
            };
          } catch {}
          navWaypointRouteErrorRef.current = null;
          return;
        }
        const msg =
          failedReason === "no_nearby_node"
            ? "Aproxima-te da rede de navegação."
            : failedReason === "no_route"
            ? "Sem rota disponível para o waypoint."
            : "Falha ao calcular rota.";
        showToast(msg, "error");
        navWaypointRouteErrorRef.current = failedReason;
      }
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-route", {
          detail: { path: [], externalSegments: [], coloredSegments: [] },
        })
      );
      try {
        window.__trekkinLastNavGridRoute = {
          path: [],
          externalSegments: [],
          coloredSegments: [],
          ts: Date.now(),
        };
      } catch {}
      return;
    }
    navWaypointRouteErrorRef.current = null;
    navWaypointDebugStampRef.current = "";
    const rawPath = Array.isArray(allRoutePoints) ? allRoutePoints : [];
    const gridSegments = Array.isArray(grid?.segments) ? grid.segments : [];
    const eleLookup = new Map();
    for (const seg of gridSegments) {
      const pts = Array.isArray(seg?.points) ? seg.points : [];
      for (const pt of pts) {
        const pLat = Number(pt?.lat);
        const pLon = Number(pt?.lon);
        const ele = Number(pt?.ele ?? pt?.elevation ?? pt?.alt ?? pt?.z);
        if (!Number.isFinite(pLat) || !Number.isFinite(pLon) || !Number.isFinite(ele)) continue;
        const key = `${pLat.toFixed(6)}|${pLon.toFixed(6)}`;
        if (!eleLookup.has(key)) eleLookup.set(key, ele);
      }
    }
    let path = rawPath
      .map((p) => {
        const lat = Number(p?.lat);
        const lon = Number(p?.lon ?? p?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        const ownEle = Number(p?.ele ?? p?.elevation ?? p?.alt ?? p?.z);
        const key = `${lat.toFixed(6)}|${lon.toFixed(6)}`;
        const mapEle = eleLookup.get(key);
        const ele = Number.isFinite(ownEle)
          ? ownEle
          : Number.isFinite(Number(mapEle))
          ? Number(mapEle)
          : undefined;
        return {
          ...p,
          lat,
          lon,
          ele: Number.isFinite(ele) ? ele : undefined,
        };
      })
      .filter(Boolean);

    const routeRenderLimit = isIOS ? 3200 : 8000;
    const originalPathPoints = path.length;
    path = limitRoutePointsForRender(path, routeRenderLimit);
    const renderColoredSegments = coloredSegments.map((segment) => ({
      ...segment,
      path: limitRoutePointsForRender(segment?.path, routeRenderLimit),
    }));
    if (originalPathPoints > path.length) {
      try {
        logEvent("info", "NAV_WAYPOINT_ROUTE_RENDER_LIMITED", {
          platform: isIOS ? "ios" : isAndroid ? "android" : "web",
          originalPoints: originalPathPoints,
          renderedPoints: path.length,
          limit: routeRenderLimit,
          targetId: String(navWaypointTarget?.id || ""),
        });
      } catch {}
    }

    window.dispatchEvent(
      new CustomEvent("trekkin:navgrid-route", {
        detail: { path, externalSegments, coloredSegments: renderColoredSegments },
      })
    );
    try {
      window.__trekkinLastNavGridRoute = {
        path,
        externalSegments,
        coloredSegments: renderColoredSegments,
        ts: Date.now(),
      };
    } catch {}
  }, [
    planActive,
    navWaypointTarget?.id,
    navWaypointTarget?.lat,
    navWaypointTarget?.lon,
    navWaypointQueue,
    navWaypointPending,
    navWaypointHasUserPosition,
    navGridSegments,
    isAndroid,
    isIOS,
    requestNavGridForRouting,
  ]);

  useEffect(() => {
    const handler = (event) => {
      const panel = event?.detail?.panel;
      if (!panel?.name) return;
      pickReturnPanelRef.current = {
        name: panel.name,
        props: panel.props || {},
      };
    };
    if (typeof window !== "undefined") {
      window.addEventListener("trekkin:pickpoint-return-panel", handler);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("trekkin:pickpoint-return-panel", handler);
      }
    };
  }, []);

  const handleEditWaypoint = useCallback(
    (wp) => {
      if (!wp?.id) return;
      setActivePanel("adminWaypoints", { focusWaypointId: wp.id });
    },
    [setActivePanel]
  );

  const handleDeleteWaypoint = useCallback(async (wp) => {
    if (!wp?.id) return;
    const label = wp.name ? `"${wp.name}"` : "este waypoint";
    if (!window.confirm(`Remover ${label}?`)) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      enqueueRemoveWaypoint(wp.id);
      showToast("Sem rede. Remoção guardada para sincronizar depois.", "info");
      return;
    }
    try {
      await remove(ref(db, `waypoints/${wp.id}`));
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("trekkin:waypoints-refresh"));
      }
      showToast("Waypoint removido.", "success");
    } catch (err) {
      if (shouldQueueWaypointWrite(err)) {
        enqueueRemoveWaypoint(wp.id);
        showToast("Sem rede. Remoção guardada para sincronizar depois.", "info");
        return;
      }
      console.warn("Erro ao remover waypoint:", err);
      showToast("Falha ao remover waypoint.", "error");
    }
  }, []);

  const closeProximityAlert = useCallback(() => {
    if (!proximityAlert) return;
    const key = getAlertKey(proximityAlert.entry);
    try {
      window.localStorage.setItem(`alertPrompt.${key}`, String(Date.now()));
    } catch {}
    setProximityAlert(null);
  }, [proximityAlert]);

  const voteProximityAlert = useCallback(
    async (vote) => {
      if (!proximityAlert?.entry) return;
      const entry = proximityAlert.entry;
      const key = getAlertKey(entry);
      try {
        window.localStorage.setItem(`alertVote.${key}`, vote);
        window.localStorage.setItem(`alertPrompt.${key}`, String(Date.now()));
      } catch {}
      setProximityAlert(null);
      if (!entry?.category || !entry?.id) return;
      try {
        await runTransaction(
          ref(db, `alerts/${entry.category}/${entry.id}/votes/${vote}`),
          (current) => (Number(current) || 0) + 1
        );
      } catch (err) {
        console.warn("Falha ao registar voto no alerta:", err);
      }
    },
    [proximityAlert]
  );

  const confirmAlert = useCallback(
    async (entry) => {
      if (!entry?.category || !entry?.id) return;
      if (!user?.uid) {
        showToast("Inicia sessão para confirmar alertas.", "info");
        return;
      }
      const now = Date.now();
      try {
        await update(ref(db, `alerts/${entry.category}/${entry.id}`), {
          confirmedAt: now,
          confirmedByUid: user.uid,
          confirmedByName: user.name || user.email || null,
          expiresAt: now + ALERT_TTL_MS,
        });
        showToast("Alerta confirmado e prolongado por mais 48h.", "success");
        return true;
      } catch (err) {
        console.warn("Falha ao confirmar alerta:", err);
        showToast("Falha ao confirmar o alerta.", "error");
        return false;
      }
    },
    [user]
  );

  const dispatchNavGridToggle = useCallback((eventName, next) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(eventName, { detail: { on: !!next } }));
  }, []);

  const handleEditSegment = useCallback(
    (segment) => {
      if (!segment?.id || !canManageNavGrid) return;
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-focus-segment", {
          detail: { segmentId: segment.id },
        })
      );
      dispatchNavGridToggle("trekkin:navgrid-edit-nodes-toggle", true);
    },
    [canManageNavGrid, dispatchNavGridToggle]
  );

  const handleDeleteSegment = useCallback(
    async (segment) => {
      if (!segment?.id || !canManageNavGrid) return;
      const label = segment.name ? `"${segment.name}"` : "este trecho";
      if (!window.confirm(`Remover ${label} da grelha?`)) return;
      const currentGrid = loadNavGrid();
      const updated = removeSegmentFromGrid(withUpdatedAt(currentGrid), segment.id);
      saveNavGrid(updated, { skipPersistentQueue: true });
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("trekkin:navgrid-updated"));
      }
      try {
        await saveNavGridDiffToCloud(db, currentGrid, updated);
      } catch (err) {
        console.warn("Erro a sincronizar rede:", err);
      }
      showToast("Trecho removido.", "success");
    },
    [canManageNavGrid]
  );

  const handleOptimizeSegment = useCallback(
    async (segment) => {
      if (!segment?.id || !canManageNavGrid) return;
      const originalPoints = Array.isArray(segment?.points) ? segment.points : [];
      if (originalPoints.length < 3) {
        showToast("Este trecho já está otimizado.", "info");
        return;
      }
      const optimizedPoints = optimizeStraightSegmentPoints(originalPoints, 1.5);
      const removedPoints = originalPoints.length - optimizedPoints.length;
      if (removedPoints <= 0) {
        logEvent("info", "NAVGRID_SEGMENT_OPTIMIZE_NO_CHANGE", {
          segmentId: segment.id,
          points: originalPoints.length,
          toleranceMeters: 1.5,
        });
        showToast("Este trecho já está otimizado.", "info");
        return;
      }
      const label = String(segment.name || "Trecho");
      if (!window.confirm(`Otimizar “${label}”?\n\nSerão removidos ${removedPoints} pontos redundantes em retas. As extremidades e curvas serão preservadas.`)) return;

      const updatedAt = Date.now();
      const optimizedSegment = {
        ...segment,
        points: optimizedPoints,
        straightGeometrySimplified: true,
        updatedAt,
      };
      const currentGrid = loadNavGrid();
      const updated = {
        ...currentGrid,
        updatedAt,
        segments: (Array.isArray(currentGrid?.segments) ? currentGrid.segments : []).map((item) =>
          String(item?.id || "") === String(segment.id) ? optimizedSegment : item
        ),
      };
      saveNavGrid(updated, { skipPersistentQueue: true });
      navGridConfigRef.current = updated;
      navGridGraphStampRef.current = 0;
      setNavGridSegments(
        filterNavGridByAccess(updated, {
          allowPremium: true,
          activityMode: navGridDisplayActivityMode,
        })?.segments || []
      );
      setNavGridPreviewSegmentId(null);
      window.dispatchEvent(new CustomEvent("trekkin:navgrid-updated"));
      try {
        await saveNavGridSegmentToCloud(db, optimizedSegment, updated);
        logEvent("info", "NAVGRID_SEGMENT_OPTIMIZED", {
          segmentId: segment.id,
          originalPoints: originalPoints.length,
          storedPoints: optimizedPoints.length,
          removedPoints,
          toleranceMeters: 1.5,
          routingSpacingMeters: 10,
        });
        showToast(`Trecho otimizado: ${removedPoints} pontos removidos.`, "success");
      } catch (err) {
        console.warn("Erro a sincronizar trecho otimizado:", err);
        showToast("Trecho otimizado localmente. Falhou a sincronização.", "warning");
      }
    },
    [canManageNavGrid, navGridDisplayActivityMode]
  );

  const handleSaveSegmentClassification = useCallback(async () => {
    if (!canManageNavGrid || !classifySegmentDraft?.id) return;
    const nextMode = normalizeActivityMode(
      classifySegmentDraft.activityMode,
      ACTIVITY_MODE_WALK
    );
    const updatedAt = Date.now();
    let classifiedSegment = null;
    const currentGrid = loadNavGrid();
    const updatedSegments = (Array.isArray(currentGrid?.segments)
      ? currentGrid.segments
      : []
    ).map((segment) => {
      if (String(segment?.id || "") !== String(classifySegmentDraft.id)) {
        return segment;
      }
      classifiedSegment = { ...segment, activityMode: nextMode, updatedAt };
      return classifiedSegment;
    });
    if (!classifiedSegment) {
      showToast("Trecho não encontrado.", "warning");
      return;
    }
    const updated = { ...currentGrid, segments: updatedSegments, updatedAt };
    saveNavGrid(updated, { skipPersistentQueue: true });
    navGridConfigRef.current = updated;
    setNavGridSegments(
      filterNavGridByAccess(updated, {
        allowPremium: true,
        activityMode: navGridDisplayActivityMode,
      })?.segments || []
    );
    setClassifySegmentDraft(null);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("trekkin:navgrid-updated"));
    }
    try {
      await saveNavGridSegmentToCloud(db, classifiedSegment, updated);
      showToast(`Trecho classificado como ${getActivityModeLabel(nextMode)}.`, "success");
    } catch (err) {
      console.warn("Erro a sincronizar rede:", err);
      showToast("Classificação guardada localmente. Falhou sincronização.", "warning");
    }
  }, [canManageNavGrid, classifySegmentDraft, navGridDisplayActivityMode]);

  const handleMergeSegments = useCallback(
    (segmentIds) => {
      const ids = [...new Set(
        (Array.isArray(segmentIds) ? segmentIds : [])
          .map((id) => String(id || "").trim())
          .filter(Boolean)
      )];
      if (ids.length < 2) return;
      if (!canManageNavGrid) return;
      try {
        const grid = loadNavGrid();
        const threshold = Math.max(
          Number(navGridConfigRef.current?.routeMergeMeters) || 0,
          Number(navGridConfigRef.current?.mergeMeters) || 0
        );
        logEvent("info", "NAVGRID_MERGE_START", {
          segmentIds: ids,
          selectedCount: ids.length,
          threshold,
          segments: Array.isArray(grid?.segments) ? grid.segments.length : 0,
          isIOS,
          isAndroid,
        });
        if (!threshold) {
          showToast("Define a distância de fusão na grelha.");
          return;
        }
        const res = fuseSegmentEndpoints(grid, ids, threshold);
        if (!res?.updatedEndpoints) {
          logEvent("warn", "NAVGRID_MERGE_NO_ENDPOINTS", {
            segmentIds: ids,
            selectedCount: ids.length,
            threshold,
          });
          showToast("Sem extremidades próximas para fundir.");
          return;
        }
        const updated = withUpdatedAt(res.grid);
        saveNavGrid(updated, { skipPersistentQueue: true });
        window.dispatchEvent(new CustomEvent("trekkin:navgrid-updated"));
        // Os marcadores A/B e respetivos labels servem apenas para confirmar a
        // ligação antes da fusão. Depois de fundida, removê-los mesmo que o
        // utilizador continue no modo de edição da grelha.
        const endpointPreviewLayer = navGridEndpointPreviewLayerRef.current;
        if (endpointPreviewLayer && mapRef.current) {
          try {
            mapRef.current.removeLayer(endpointPreviewLayer);
          } catch {}
        }
        navGridEndpointPreviewLayerRef.current = null;
        saveNavGridDiffToCloud(db, grid, updated).catch((err) => {
          console.warn("Erro a sincronizar rede:", err);
        });
        logEvent("info", "NAVGRID_MERGE_DONE", {
          segmentIds: ids,
          selectedCount: ids.length,
          updatedEndpoints: Number(res?.updatedEndpoints || 0),
          clusterCount: Number(res?.clusterCount || 0),
          updatedSegments: Array.isArray(updated?.segments) ? updated.segments.length : 0,
        });
        showToast(
          `Fundidos ${res.updatedEndpoints} pontos em ${res.clusterCount} ligações.`
        );
        // Quando a fusão começou na auditoria de extremidades, regressar ao
        // painel e recalcular a lista para continuar pela próxima ligação.
        if (navGridEndpointAuditActiveRef.current) {
          navGridEndpointAuditActiveRef.current = false;
          setActivePanel("adminNavGrid", { autoAuditEndpoints: true });
        }
      } catch (err) {
        logEvent("error", "NAVGRID_MERGE_CRASH_GUARD", {
          segmentIds: ids,
          selectedCount: ids.length,
          message: String(err?.message || err),
        });
        showToast("Falha ao fundir pontos.", "error");
      }
    },
    [canManageNavGrid, isAndroid, isIOS, setActivePanel]
  );

  const closeNavGridEditMode = useCallback(() => {
    dispatchNavGridToggle("trekkin:navgrid-edit-nodes-toggle", false);
    dispatchNavGridToggle("trekkin:navgrid-add-node-toggle", false);
    dispatchNavGridToggle("trekkin:navgrid-delete-nodes-toggle", false);
    dispatchNavGridToggle("trekkin:navgrid-split-node-toggle", false);
    // Ao sair da edição, esconder imediatamente os pontos/nós da grelha.
    setNavGridNodesVisible(false);
    try {
      window.localStorage.setItem("navGridNodesOn", "0");
    } catch {}
    dispatchNavGridToggle("trekkin:navgrid-nodes-toggle", false);
    try {
      navGridNodesLayerRef.current?.clearLayers?.();
    } catch {}
  }, [dispatchNavGridToggle]);

  const setNodeMode = useCallback(
    (mode) => {
      dispatchNavGridToggle("trekkin:navgrid-edit-nodes-toggle", true);
      dispatchNavGridToggle("trekkin:navgrid-add-node-toggle", mode === "add");
      dispatchNavGridToggle("trekkin:navgrid-delete-nodes-toggle", mode === "delete");
      dispatchNavGridToggle("trekkin:navgrid-split-node-toggle", mode === "split");
    },
    [dispatchNavGridToggle]
  );

  const persistNavGridUpdate = useCallback(
    async (nextGrid, message, explicitChanges = null, options = {}) => {
      if (!nextGrid) return;
      const previousGrid = loadNavGrid();
      const normalized = withUpdatedAt(nextGrid);
      const cloudFirst = options?.cloudFirst === true;
      const isEditSession =
        navGridEditNodesActive ||
        navGridAddNodeActive ||
        navGridDeleteNodesActive ||
        navGridSplitNodeActive;
      const syncEditPatch = async () => {
        if (!(canManageNavGrid && isEditSession)) return;
        try {
          if (explicitChanges) {
            await saveNavGridChangesToCloud(db, {
              upserts: explicitChanges.upserts,
              removedIds: explicitChanges.removedIds,
              grid: normalized,
            });
          } else {
            await saveNavGridDiffToCloud(db, previousGrid, normalized);
          }
        } catch (err) {
          console.warn("Erro a sincronizar edição da rede:", err);
          showToast("Falha ao sincronizar a edição.", "error");
        }
      };

      // No iOS, alterações em segmentos grandes não devem serializar no Firebase
      // ao mesmo tempo que o mapa reconstrói a rede completa.
      if (cloudFirst) await syncEditPatch();

      setNavGridSegments(Array.isArray(normalized?.segments) ? normalized.segments : []);
      saveNavGrid(normalized, {
        skipPersistentQueue: isEditSession,
        persistDelayMs: isEditSession ? 0 : 40,
      });
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-updated", { detail: { context: "edit" } })
      );
      if (canManageNavGrid && isEditSession && !cloudFirst) {
        await syncEditPatch();
      } else if (canManageNavGrid && !isEditSession) {
        saveNavGridToCloud(db, normalized).catch((err) => {
          console.warn("Erro a sincronizar rede:", err);
          showToast("Falha ao sincronizar a rede.", "error");
        });
      }
      if (isEditSession) {
        logEvent("info", "NAVGRID_EDIT_PATCHED", {
          segments: Array.isArray(normalized?.segments) ? normalized.segments.length : 0,
          platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        });
      }
      if (message) showToast(message, "success");
    },
    [
      canManageNavGrid,
      isIOS,
      isAndroid,
      navGridAddNodeActive,
      navGridDeleteNodesActive,
      navGridEditNodesActive,
      navGridSplitNodeActive,
    ]
  );

  const refreshDrawLayer = useCallback(() => {
    const layer = drawLayerRef.current;
    if (!layer) return;
    const coords = drawPointsRef.current
      .filter((pt) => pt && Number.isFinite(pt.lat) && Number.isFinite(pt.lon))
      .map((pt) => [pt.lat, pt.lon]);
    layer.setLatLngs(coords);
    const dotsLayer = drawDotsLayerRef.current;
    if (!dotsLayer) return;
    dotsLayer.clearLayers();
    if (
      drawContextRef.current !== "trail" &&
      drawContextRef.current !== "eventAlt" &&
      drawContextRef.current !== "eventMain" &&
      drawContextRef.current !== "navgrid"
    )
      return;
    coords.forEach((coord) => {
      L.circleMarker(coord, {
        radius: 4,
        color: "#0d6efd",
        weight: 2,
        fillColor: "#ffffff",
        fillOpacity: 1,
      }).addTo(dotsLayer);
    });
  }, []);

  const emitDrawUpdate = useCallback(() => {
    if (typeof window === "undefined") return;
    const payload = {
      context: drawContextRef.current || "navgrid",
      active: navGridDrawActiveRef.current,
      points: drawPointsRef.current.map((pt) => ({
        lat: pt?.lat,
        lon: pt?.lon,
      })),
    };
    if (payload.context === "navgrid") {
      window.__trekkinNavDraw = payload;
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-draw-updated", { detail: payload })
      );
    } else if (payload.context === "trail") {
      window.__trekkinTrailDraw = payload;
      window.dispatchEvent(
        new CustomEvent("trekkin:trail-draw-updated", { detail: payload })
      );
    } else if (payload.context === "eventAlt") {
      window.__trekkinEventAltDraw = payload;
      window.dispatchEvent(
        new CustomEvent("trekkin:event-alt-draw-updated", { detail: payload })
      );
    } else if (payload.context === "eventMain") {
      window.__trekkinEventMainDraw = payload;
      window.dispatchEvent(
        new CustomEvent("trekkin:event-main-draw-updated", { detail: payload })
      );
    }
  }, []);

  const updateDrawActive = useCallback(
    (next) => {
      navGridDrawActiveRef.current = next;
      setNavGridDrawActive(next);
      const mapContainer = mapRef.current?.getContainer?.();
      if (mapContainer) {
        const isNavGridDrawing = Boolean(next && drawContextRef.current === "navgrid");
        mapContainer.classList.toggle("tk-navgrid-draw-cursor", isNavGridDrawing);
      }
      emitDrawUpdate();
    },
    [emitDrawUpdate]
  );

  const clearDrawPoints = useCallback(() => {
    drawPointsRef.current = [];
    refreshDrawLayer();
    emitDrawUpdate();
  }, [emitDrawUpdate, refreshDrawLayer]);

  const undoDrawPoint = useCallback(() => {
    if (!drawPointsRef.current.length) return;
    drawPointsRef.current = drawPointsRef.current.slice(0, -1);
    refreshDrawLayer();
    emitDrawUpdate();
  }, [emitDrawUpdate, refreshDrawLayer]);

  const addDrawPoint = useCallback(
    (latlng) => {
      if (!latlng) return;
      const { lat, lng } = latlng;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      drawPointsRef.current = [
        ...drawPointsRef.current,
        { lat, lon: lng },
      ];
      refreshDrawLayer();
      emitDrawUpdate();
    },
    [emitDrawUpdate, refreshDrawLayer]
  );

  const pushEditHistory = useCallback(() => {
    const snapshot = editPointsRef.current.map((pt) => ({
      lat: pt.lat,
      lon: pt.lon,
      time: pt.time,
      ele: pt.ele,
    }));
    editHistoryRef.current.push(snapshot);
    if (editHistoryRef.current.length > 20) {
      editHistoryRef.current.shift();
    }
  }, []);

  const refreshEditLayer = useCallback(() => {
    const layer = editLayerRef.current;
    if (!layer) return;
    const coords = editPointsRef.current
      .filter((pt) => pt && Number.isFinite(pt.lat) && Number.isFinite(pt.lon))
      .map((pt) => [pt.lat, pt.lon]);
    layer.setLatLngs(coords);
  }, []);

  const clearEditMarkers = useCallback(() => {
    const markers = editMarkersRef.current;
    markers.forEach((marker) => {
      try {
        marker.remove();
      } catch {}
    });
    editMarkersRef.current = [];
  }, []);

  const rebuildEditMarkers = useCallback(() => {
    logEvent("info", "TRAIL_EDIT_MARKERS_REBUILD_START", {
      points: Array.isArray(editPointsRef.current) ? editPointsRef.current.length : 0,
      isIOS,
      isAndroid,
    });
    const map = mapRef.current;
    if (!map) return;
    clearEditMarkers();
    const icon = L.divIcon({
      className: "tk-edit-point",
      html:
        '<div style="width:10px;height:10px;border-radius:50%;background:#0d6efd;border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,0.2);"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });
    const pts = Array.isArray(editPointsRef.current) ? editPointsRef.current : [];
    const total = pts.length;
    const step = total > maxEditDragMarkers ? Math.ceil(total / maxEditDragMarkers) : 1;
    if (step > 1) {
      logEvent("warn", "TRAIL_EDIT_MARKERS_THROTTLED", {
        totalPoints: total,
        maxMarkers: maxEditDragMarkers,
        step,
        isIOS,
        isAndroid,
      });
    }
    const markerIndexes = [];
    for (let i = 0; i < total; i += step) markerIndexes.push(i);
    if (total > 1 && markerIndexes[markerIndexes.length - 1] !== total - 1) {
      markerIndexes.push(total - 1);
    }

    markerIndexes.forEach((idx) => {
      const pt = pts[idx];
      if (!pt || !Number.isFinite(pt.lat) || !Number.isFinite(pt.lon)) return;
      const marker = L.marker([pt.lat, pt.lon], {
        draggable: true,
        icon,
        pane: "navGridRoutePane",
      });
      marker.on("dragstart", () => {
        pushEditHistory();
      });
      marker.on("dragend", (event) => {
        try {
          const latlng = event?.target?.getLatLng?.();
          const lat = Number(latlng?.lat);
          const lon = Number(latlng?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            logEvent("warn", "TRAIL_EDIT_DRAG_INVALID_COORDS", { idx, lat, lon });
            return;
          }
          const prev = editPointsRef.current[idx];
          if (!prev) {
            logEvent("warn", "TRAIL_EDIT_DRAG_MISSING_POINT", { idx });
            return;
          }
          editPointsRef.current[idx] = {
            ...prev,
            lat,
            lon,
          };
          refreshEditLayer();
        } catch (err) {
          logEvent("error", "TRAIL_EDIT_DRAG_CRASH_GUARD", {
            idx,
            message: String(err?.message || err),
          });
        }
      });
      marker.addTo(map);
      editMarkersRef.current.push(marker);
    });
    logEvent("info", "TRAIL_EDIT_MARKERS_REBUILD_END", {
      points: total,
      markers: markerIndexes.length,
      step,
      isIOS,
      isAndroid,
    });
  }, [
    clearEditMarkers,
    isAndroid,
    isIOS,
    maxEditDragMarkers,
    pushEditHistory,
    refreshEditLayer,
  ]);

  const setTrailEditActive = useCallback((next) => {
    editActiveRef.current = next;
    setTrailEditActiveState(next);
  }, []);

  const applyEditPoints = useCallback(
    (points) => {
      editPointsRef.current = points;
      refreshEditLayer();
      rebuildEditMarkers();
    },
    [refreshEditLayer, rebuildEditMarkers]
  );

  const undoEditChange = useCallback(() => {
    const history = editHistoryRef.current;
    if (!history.length) return;
    const prev = history.pop();
    if (!prev) return;
    applyEditPoints(prev);
  }, [applyEditPoints]);

  const clearEditPoints = useCallback(() => {
    pushEditHistory();
    applyEditPoints([]);
  }, [applyEditPoints, pushEditHistory]);

  const resetEditPoints = useCallback(() => {
    const original = editOriginalRef.current || [];
    applyEditPoints(original.map((pt) => ({ ...pt })));
  }, [applyEditPoints]);

  const exitEditMode = useCallback(() => {
    setTrailEditActive(false);
    editMetaRef.current = null;
    editHistoryRef.current = [];
    editOriginalRef.current = [];
    clearEditMarkers();
    const layer = editLayerRef.current;
    if (layer) {
      layer.setLatLngs([]);
    }
  }, [clearEditMarkers, setTrailEditActive]);

  const undoTrailDraw = useCallback(() => {
    if (drawContextRef.current === "trailEdit") {
      undoEditChange();
      return;
    }
    if (drawContextRef.current === "eventAlt") {
      window.dispatchEvent(new CustomEvent("trekkin:event-alt-draw-undo"));
      return;
    }
    if (drawContextRef.current === "eventMain") {
      window.dispatchEvent(new CustomEvent("trekkin:event-main-draw-undo"));
      return;
    }
    window.dispatchEvent(new CustomEvent("trekkin:trail-draw-undo"));
  }, [undoEditChange]);

  const clearTrailDraw = useCallback(() => {
    if (drawContextRef.current === "trailEdit") {
      clearEditPoints();
      return;
    }
    if (drawContextRef.current === "eventAlt") {
      window.dispatchEvent(new CustomEvent("trekkin:event-alt-draw-clear"));
      return;
    }
    if (drawContextRef.current === "eventMain") {
      window.dispatchEvent(new CustomEvent("trekkin:event-main-draw-clear"));
      return;
    }
    window.dispatchEvent(new CustomEvent("trekkin:trail-draw-clear"));
  }, [clearEditPoints]);

  const cancelTrailDraw = useCallback(() => {
    if (drawContextRef.current === "trailEdit") {
      resetEditPoints();
      exitEditMode();
      setActivePanel("nav");
      return;
    }
    if (drawContextRef.current === "eventAlt") {
      window.dispatchEvent(new CustomEvent("trekkin:event-alt-draw-clear"));
      window.dispatchEvent(new CustomEvent("trekkin:event-alt-draw-stop"));
      setActivePanel("eventManage", { eventId: eventAltMetaRef.current?.eventId });
      return;
    }
    if (drawContextRef.current === "eventMain") {
      window.dispatchEvent(new CustomEvent("trekkin:event-main-draw-clear"));
      window.dispatchEvent(new CustomEvent("trekkin:event-main-draw-stop"));
      setActivePanel("eventManage", { eventId: eventMainMetaRef.current?.eventId });
      return;
    }
    window.dispatchEvent(new CustomEvent("trekkin:trail-draw-clear"));
    window.dispatchEvent(new CustomEvent("trekkin:trail-draw-stop"));
    setActivePanel("nav");
  }, [exitEditMode, resetEditPoints, setActivePanel]);

  const calcDistanceKm = useCallback((pts) => {
    if (!Array.isArray(pts) || pts.length < 2) return 0;
    const toRad = (v) => (v * Math.PI) / 180;
    let dist = 0;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const dLat = toRad(b.lat - a.lat);
      const dLon = toRad(b.lon - a.lon);
      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);
      const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
      dist += 2 * 6371000 * Math.asin(Math.sqrt(h));
    }
    return dist / 1000;
  }, []);

  const calcElevationGain = useCallback((pts) => {
    if (!Array.isArray(pts) || pts.length < 2) return 0;
    let gain = 0;
    for (let i = 1; i < pts.length; i++) {
      const prev = Number(pts[i - 1]?.ele || 0);
      const next = Number(pts[i]?.ele || 0);
      const diff = next - prev;
      if (diff > 0 && diff < 15) gain += diff;
    }
    return gain;
  }, []);

  const updateTrailStorage = useCallback(
    (key, meta, points, gpx) => {
      try {
        const raw = localStorage.getItem(key) || "[]";
        const list = JSON.parse(raw);
        if (!Array.isArray(list)) return false;
        const next = list.map((item) => {
          if (!item) return item;
          const matchId = meta?.id && item.id === meta.id;
          const matchFile =
            meta?.cloud?.file && item?.cloud?.file === meta.cloud.file;
          const matchUrl =
            meta?.cloud?.url && item?.cloud?.url === meta.cloud.url;
          if (!matchId && !matchFile && !matchUrl) return item;
          const distKm = calcDistanceKm(points);
          const elevGain = calcElevationGain(points);
          return {
            ...item,
            points,
            gpx: gpx || item.gpx,
            dist: distKm ? `${distKm.toFixed(1)} km` : item.dist,
            elev: elevGain ? `${Math.round(elevGain)} m D+` : item.elev,
          };
        });
        localStorage.setItem(key, JSON.stringify(next));
        return true;
      } catch {
        return false;
      }
    },
    [calcDistanceKm, calcElevationGain]
  );

  const saveEditedTrail = useCallback(async () => {
    const pushTrailEditLog = (message, extra = {}) => {
      try { logEvent("info", message, extra); } catch {}
    };
    try {
      const rawPoints = editPointsRef.current;
      const points = (Array.isArray(rawPoints) ? rawPoints : [])
        .map((pt) => ({
          lat: Number(pt?.lat),
          lon: Number(pt?.lon ?? pt?.lng),
          ele: Number.isFinite(Number(pt?.ele)) ? Number(pt.ele) : undefined,
          time: Number(pt?.time ?? pt?.ts),
          ts: Number(pt?.ts),
        }))
        .filter((pt) => Number.isFinite(pt.lat) && Number.isFinite(pt.lon));
      pushTrailEditLog("saveEditedTrail_start", {
        rawCount: Array.isArray(rawPoints) ? rawPoints.length : 0,
        cleanCount: points.length,
        source: String(editMetaRef.current?.source || ""),
      });
      if (points.length < 2) {
        pushTrailEditLog("saveEditedTrail_abort_insufficient_points", {
          cleanCount: points.length,
        });
        showToast("Sem pontos suficientes.");
        return;
      }
      const meta = editMetaRef.current || {};
      const now = Date.now();
      const recorded = points.map((pt, idx) => ({
        lat: pt.lat,
        lon: pt.lon,
        time: Number.isFinite(pt.time) ? pt.time : Number.isFinite(pt.ts) ? pt.ts : now + idx * 1000,
      }));
      const gpx = exportGPX(recorded, {
        name: meta?.name || "Trilho",
        date: new Date().toISOString(),
      });
      pushTrailEditLog("saveEditedTrail_gpx_ready", {
        gpxBytes: typeof gpx === "string" ? gpx.length : 0,
      });
      const updatedHistory =
        meta?.source === "history"
          ? updateTrailStorage("historygps", meta, points, gpx)
          : false;
      const updatedTrails =
        meta?.source === "trails"
          ? updateTrailStorage("trilhos", meta, points, gpx)
          : false;
      if (updatedHistory) {
        window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
      }
      if (updatedTrails) {
        window.dispatchEvent(new CustomEvent("trekkin:trails-updated"));
      }
      pushTrailEditLog("saveEditedTrail_local_saved", {
        updatedHistory,
        updatedTrails,
        hasCloudFile: !!meta?.cloud?.file,
      });
      if (meta?.cloud?.file && user?.uid && gpx) {
      const updateCloudLocal = (key, cloud) => {
        try {
          const raw = localStorage.getItem(key) || "[]";
          const list = JSON.parse(raw);
          if (!Array.isArray(list)) return;
          const next = list.map((item) => {
            if (!item) return item;
            const matchId = meta?.id && item.id === meta.id;
            const matchFile =
              meta?.cloud?.file && item?.cloud?.file === meta.cloud.file;
            const matchUrl =
              meta?.cloud?.url && item?.cloud?.url === meta.cloud.url;
            if (!matchId && !matchFile && !matchUrl) return item;
            return {
              ...item,
              cloud: {
                ...(item.cloud || {}),
                ...cloud,
                synced: true,
                syncedAt: Date.now(),
              },
            };
          });
          localStorage.setItem(key, JSON.stringify(next));
        } catch {}
      };

      if (
        meta.source === "history" &&
        (can("PanelHistory", "upload_gpx") || can("PanelNav", "upload_gpx"))
      ) {
        try {
          const blob = new Blob([gpx], { type: "application/gpx+xml" });
          const form = new FormData();
          form.append("uid", user.uid);
          form.append("filename", meta.cloud.file);
          form.append("file", blob, meta.cloud.file);
          const res = await fetch(
            "https://www.trekkin.pt/app/app-upload/upload-history-gpx.php",
            { method: "POST", body: form }
          );
          const data = await res.json();
          if (data?.ok) {
            const file = data.file || meta.cloud.file;
            const url =
              data.url ||
              (data.path ? `/app/app-upload/${data.path}` : meta.cloud.url);
            updateCloudLocal("historygps", { file, url });
            window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
          }
        } catch {}
      }
      if (meta.source === "trails" && can("PanelNav", "upload_gpx")) {
        try {
          const blob = new Blob([gpx], { type: "application/gpx+xml" });
          const form = new FormData();
          form.append("uid", user.uid);
          form.append("type", "trails");
          form.append("filename", meta.cloud.file);
          form.append("file", blob, meta.cloud.file);
          const res = await fetch(
            "https://www.trekkin.pt/app/app-upload/upload-user-gpx.php",
            { method: "POST", body: form }
          );
          const data = await res.json();
          if (data?.ok) {
            const file = data.filename || meta.cloud.file;
            const url = data.url || meta.cloud.url;
            updateCloudLocal("trilhos", { file, url });
            window.dispatchEvent(new CustomEvent("trekkin:trails-updated"));
          }
        } catch {}
      }
      }
      pushTrailEditLog("saveEditedTrail_done", {
        source: String(meta?.source || ""),
      });
      showToast("✅ Trilho atualizado.");
      exitEditMode();
      setActivePanel("nav");
    } catch (err) {
      console.error("❌ saveEditedTrail crash guard:", err);
      try {
        logEvent("error", "saveEditedTrail_exception", {
          error: String(err?.message || err || "unknown"),
          stack: String(err?.stack || ""),
        });
      } catch {}
      showToast("Falha ao guardar edição do trecho.", "error");
    }
  }, [can, exitEditMode, setActivePanel, updateTrailStorage, user?.uid]);

  const saveTrailDraw = useCallback(() => {
    if (drawContextRef.current === "trailEdit") {
      void saveEditedTrail();
      return;
    }
    const isNavGridDraw =
      drawContextRef.current === "navgrid" || drawContext === "navgrid";
    if (isNavGridDraw) {
      updateDrawActive(false);
      setActivePanel("adminNavGrid");
      return;
    }
    if (drawContextRef.current === "eventAlt") {
      const payload = {
        eventId: eventAltMetaRef.current?.eventId || null,
        eventName: eventAltMetaRef.current?.eventName || null,
        points: drawPointsRef.current.map((pt) => ({ lat: pt.lat, lon: pt.lon })),
      };
      updateDrawActive(false);
      clearDrawPoints();
      window.dispatchEvent(
        new CustomEvent("trekkin:event-alt-draw-save", { detail: payload })
      );
      setActivePanel("eventManage", { eventId: eventAltMetaRef.current?.eventId });
      return;
    }
    if (drawContextRef.current === "eventMain") {
      const payload = {
        eventId: eventMainMetaRef.current?.eventId || null,
        eventName: eventMainMetaRef.current?.eventName || null,
        points: drawPointsRef.current.map((pt) => ({ lat: pt.lat, lon: pt.lon })),
      };
      updateDrawActive(false);
      clearDrawPoints();
      window.dispatchEvent(
        new CustomEvent("trekkin:event-main-draw-save", { detail: payload })
      );
      setActivePanel("eventManage", { eventId: eventMainMetaRef.current?.eventId });
      return;
    }
    if (typeof window !== "undefined") {
      const payload = {
        points: drawPointsRef.current.map((pt) => ({ lat: pt.lat, lon: pt.lon })),
      };
      window.__trekkinTrailDrawSave = payload;
      window.dispatchEvent(new CustomEvent("trekkin:trail-draw-save", { detail: payload }));
      window.dispatchEvent(new CustomEvent("trekkin:trail-draw-clear"));
      window.dispatchEvent(new CustomEvent("trekkin:trail-draw-stop"));
      clearDrawPoints();
      updateDrawActive(false);
    }
    setActivePanel("nav");
  }, [clearDrawPoints, drawContext, saveEditedTrail, setActivePanel, updateDrawActive]);

  const addEditPointAt = useCallback(
    (latlng) => {
      const map = mapRef.current;
      if (!map) return;
      const points = editPointsRef.current;
      if (!Array.isArray(points) || points.length < 2) return;
      const clickPt = map.latLngToLayerPoint(latlng);
      let bestIdx = -1;
      let bestDist = Infinity;
      const distToSegment = (p, a, b) => {
        const ax = a.x;
        const ay = a.y;
        const bx = b.x;
        const by = b.y;
        const dx = bx - ax;
        const dy = by - ay;
        if (dx === 0 && dy === 0) {
          const px = p.x - ax;
          const py = p.y - ay;
          return Math.hypot(px, py);
        }
        const t = ((p.x - ax) * dx + (p.y - ay) * dy) / (dx * dx + dy * dy);
        const clamped = Math.max(0, Math.min(1, t));
        const projX = ax + clamped * dx;
        const projY = ay + clamped * dy;
        return Math.hypot(p.x - projX, p.y - projY);
      };
      for (let i = 0; i < points.length - 1; i++) {
        const a = map.latLngToLayerPoint([points[i].lat, points[i].lon]);
        const b = map.latLngToLayerPoint([points[i + 1].lat, points[i + 1].lon]);
        const d = distToSegment(clickPt, a, b);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      if (bestIdx < 0 || bestDist > 12) return;
      pushEditHistory();
      const next = [...points];
      next.splice(bestIdx + 1, 0, {
        lat: latlng.lat,
        lon: latlng.lng,
        time: Date.now(),
      });
      applyEditPoints(next);
    },
    [applyEditPoints, pushEditHistory]
  );

  useEffect(() => {
    emitDrawUpdate();
  }, [emitDrawUpdate]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStart = (event) => {
      const keep = !!event?.detail?.keep;
      drawContextRef.current = "navgrid";
      setDrawContext("navgrid");
      if (!keep) {
        clearDrawPoints();
      }
      updateDrawActive(true);
    };
    const handleStop = () => updateDrawActive(false);
    const handleClear = () => clearDrawPoints();
    const handleUndo = () => undoDrawPoint();
    const handleSeed = (event) => {
      const lat = Number(event?.detail?.lat);
      const lon = Number(event?.detail?.lon ?? event?.detail?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      addDrawPoint({ lat, lng: lon });
    };
    window.addEventListener("trekkin:navgrid-draw-start", handleStart);
    window.addEventListener("trekkin:navgrid-draw-stop", handleStop);
    window.addEventListener("trekkin:navgrid-draw-clear", handleClear);
    window.addEventListener("trekkin:navgrid-draw-undo", handleUndo);
    window.addEventListener("trekkin:navgrid-draw-seed", handleSeed);
    const handleTrailStart = (event) => {
      const keep = !!event?.detail?.keep;
      drawContextRef.current = "trail";
      setDrawContext("trail");
      if (!keep) {
        clearDrawPoints();
      }
      updateDrawActive(true);
    };
    const handleTrailStop = () => updateDrawActive(false);
    const handleTrailClear = () => clearDrawPoints();
    const handleTrailUndo = () => undoDrawPoint();
    window.addEventListener("trekkin:trail-draw-start", handleTrailStart);
    window.addEventListener("trekkin:trail-draw-stop", handleTrailStop);
    window.addEventListener("trekkin:trail-draw-clear", handleTrailClear);
    window.addEventListener("trekkin:trail-draw-undo", handleTrailUndo);
    const handleTrailEditStart = (event) => {
      const detail = event?.detail || {};
      const raw = Array.isArray(detail.points) ? detail.points : [];
      logEvent("info", "TRAIL_EDIT_START_EVT", {
        rawPoints: raw.length,
        source: detail?.source || null,
        trailId: detail?.id || detail?.trail?.id || null,
        isIOS,
        isAndroid,
      });
      const normalized = raw
        .map((pt) => {
          const lat = Number(pt?.lat);
          const lon = Number(pt?.lon ?? pt?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
          return {
            lat,
            lon,
            time: pt?.time || pt?.ts || Date.now(),
            ele: pt?.ele,
          };
        })
        .filter(Boolean);
      if (normalized.length < 2) {
        showToast("Sem pontos suficientes para editar.");
        return;
      }
      let pointsToEdit = normalized;
      if (normalized.length > maxEditPointsLoad) {
        const step = Math.ceil(normalized.length / maxEditPointsLoad);
        const sampled = [];
        for (let i = 0; i < normalized.length; i += step) sampled.push(normalized[i]);
        if (sampled[sampled.length - 1] !== normalized[normalized.length - 1]) {
          sampled.push(normalized[normalized.length - 1]);
        }
        pointsToEdit = sampled;
        logEvent("warn", "TRAIL_EDIT_POINTS_THROTTLED", {
          originalPoints: normalized.length,
          loadedPoints: sampled.length,
          step,
          maxEditPointsLoad,
          isIOS,
          isAndroid,
        });
        showToast(
          `Trecho muito grande: edição em modo leve (${sampled.length}/${normalized.length} pontos).`,
          "warning"
        );
      }
      editMetaRef.current = {
        id: detail?.id || detail?.trail?.id || null,
        name: detail?.name || detail?.trail?.nome || detail?.trail?.name || "Trilho",
        cloud: detail?.cloud || detail?.trail?.cloud || null,
        source: detail?.source || null,
      };
      editOriginalRef.current = pointsToEdit.map((pt) => ({ ...pt }));
      editHistoryRef.current = [];
      logEvent("info", "TRAIL_EDIT_BEFORE_APPLY", {
        pointsToEdit: pointsToEdit.length,
        originalPoints: normalized.length,
      });
      applyEditPoints(pointsToEdit);
      logEvent("info", "TRAIL_EDIT_AFTER_APPLY", {
        pointsToEdit: pointsToEdit.length,
      });
      drawContextRef.current = "trailEdit";
      setDrawContext("trailEdit");
      setTrailEditActive(true);
      const map = mapRef.current;
      if (map && pointsToEdit.length > 1) {
        const bounds = L.latLngBounds(pointsToEdit.map((pt) => [pt.lat, pt.lon]));
        map.fitBounds(bounds, { padding: [40, 40] });
      }
      logEvent("info", "TRAIL_EDIT_START_READY", {
        pointsToEdit: pointsToEdit.length,
      });
    };
    const handleTrailEditStop = () => {
      exitEditMode();
      drawContextRef.current = "navgrid";
      setDrawContext("navgrid");
    };
    const handleTrailEditClear = () => clearEditPoints();
    const handleTrailEditUndo = () => undoEditChange();
    window.addEventListener("trekkin:trail-edit-start", handleTrailEditStart);
    window.addEventListener("trekkin:trail-edit-stop", handleTrailEditStop);
    window.addEventListener("trekkin:trail-edit-clear", handleTrailEditClear);
    window.addEventListener("trekkin:trail-edit-undo", handleTrailEditUndo);
    const handleEventAltStart = (event) => {
      const keep = !!event?.detail?.keep;
      eventAltMetaRef.current = {
        eventId: event?.detail?.eventId || null,
        eventName: event?.detail?.eventName || null,
      };
      drawContextRef.current = "eventAlt";
      setDrawContext("eventAlt");
      if (!keep) {
        clearDrawPoints();
      }
      updateDrawActive(true);
    };
    const handleEventAltStop = () => updateDrawActive(false);
    const handleEventAltClear = () => clearDrawPoints();
    const handleEventAltUndo = () => undoDrawPoint();
    window.addEventListener("trekkin:event-alt-draw-start", handleEventAltStart);
    window.addEventListener("trekkin:event-alt-draw-stop", handleEventAltStop);
    window.addEventListener("trekkin:event-alt-draw-clear", handleEventAltClear);
    window.addEventListener("trekkin:event-alt-draw-undo", handleEventAltUndo);
    const handleEventMainStart = (event) => {
      const keep = !!event?.detail?.keep;
      eventMainMetaRef.current = {
        eventId: event?.detail?.eventId || null,
        eventName: event?.detail?.eventName || null,
      };
      drawContextRef.current = "eventMain";
      setDrawContext("eventMain");
      if (!keep) {
        clearDrawPoints();
      }
      updateDrawActive(true);
    };
    const handleEventMainStop = () => updateDrawActive(false);
    const handleEventMainClear = () => clearDrawPoints();
    const handleEventMainUndo = () => undoDrawPoint();
    window.addEventListener("trekkin:event-main-draw-start", handleEventMainStart);
    window.addEventListener("trekkin:event-main-draw-stop", handleEventMainStop);
    window.addEventListener("trekkin:event-main-draw-clear", handleEventMainClear);
    window.addEventListener("trekkin:event-main-draw-undo", handleEventMainUndo);
    return () => {
      window.removeEventListener("trekkin:navgrid-draw-start", handleStart);
      window.removeEventListener("trekkin:navgrid-draw-stop", handleStop);
      window.removeEventListener("trekkin:navgrid-draw-clear", handleClear);
      window.removeEventListener("trekkin:navgrid-draw-undo", handleUndo);
      window.removeEventListener("trekkin:navgrid-draw-seed", handleSeed);
      window.removeEventListener("trekkin:trail-draw-start", handleTrailStart);
      window.removeEventListener("trekkin:trail-draw-stop", handleTrailStop);
      window.removeEventListener("trekkin:trail-draw-clear", handleTrailClear);
      window.removeEventListener("trekkin:trail-draw-undo", handleTrailUndo);
      window.removeEventListener("trekkin:trail-edit-start", handleTrailEditStart);
      window.removeEventListener("trekkin:trail-edit-stop", handleTrailEditStop);
      window.removeEventListener("trekkin:trail-edit-clear", handleTrailEditClear);
      window.removeEventListener("trekkin:trail-edit-undo", handleTrailEditUndo);
      window.removeEventListener("trekkin:event-alt-draw-start", handleEventAltStart);
      window.removeEventListener("trekkin:event-alt-draw-stop", handleEventAltStop);
      window.removeEventListener("trekkin:event-alt-draw-clear", handleEventAltClear);
      window.removeEventListener("trekkin:event-alt-draw-undo", handleEventAltUndo);
      window.removeEventListener("trekkin:event-main-draw-start", handleEventMainStart);
      window.removeEventListener("trekkin:event-main-draw-stop", handleEventMainStop);
      window.removeEventListener("trekkin:event-main-draw-clear", handleEventMainClear);
      window.removeEventListener("trekkin:event-main-draw-undo", handleEventMainUndo);
    };
  }, [
    applyEditPoints,
    clearDrawPoints,
    clearEditPoints,
    exitEditMode,
    isAndroid,
    isIOS,
    maxEditPointsLoad,
    setTrailEditActive,
    undoDrawPoint,
    undoEditChange,
    updateDrawActive,
  ]);

  useEffect(() => {
    if (!mapReady || (!navGridDrawActive && !trailEditActive)) return;
    const map = mapRef.current;
    if (!map) return;
    const handler = (event) => {
      const latlng = getRotationAwareLatLng(map, event);
      if (!latlng) return;
      if (drawContextRef.current === "trailEdit") {
        addEditPointAt(latlng);
        return;
      }
      addDrawPoint(latlng);
    };
    map.on("click", handler);
    return () => {
      map.off("click", handler);
    };
  }, [mapReady, navGridDrawActive, trailEditActive, addDrawPoint, addEditPointAt]);

  const handleMoveNode = useCallback(
    async (node, latlng, fromLatLng = null) => {
      try {
        if (!node || !latlng) return;
        if (navGridMutationBusyRef.current) {
          logEvent("warn", "NAVGRID_MOVE_NODE_SKIPPED_BUSY");
          return;
        }
        const fromLat = Number(fromLatLng?.lat ?? node?.lat);
        const fromLon = Number(fromLatLng?.lng ?? fromLatLng?.lon ?? node?.lon);
        const toLat = Number(latlng?.lat);
        const toLon = Number(latlng?.lng ?? latlng?.lon);
        if (
          !Number.isFinite(fromLat) ||
          !Number.isFinite(fromLon) ||
          !Number.isFinite(toLat) ||
          !Number.isFinite(toLon)
        ) {
          logEvent("warn", "NAVGRID_MOVE_NODE_INVALID_COORDS", {
            nodeLat: fromLat,
            nodeLon: fromLon,
            targetLat: toLat,
            targetLon: toLon,
          });
          return;
        }
        if (Math.abs(fromLat - toLat) < 1e-8 && Math.abs(fromLon - toLon) < 1e-8) return;
        const grid = loadNavGrid();
        const threshold = 5;
        const allowedIds =
          Array.isArray(navGridNodesFilter) && navGridNodesFilter.length
            ? new Set(navGridNodesFilter)
            : null;
        const graph =
          navGridNodesGraph && Array.isArray(navGridNodesGraph?.nodes)
            ? navGridNodesGraph
            : buildNavGridGraph(grid);

        // 1) Caminho principal: mover por ID de nó no grafo (preciso para nós intermédios).
        let movedByGraph = 0;
        let nextGrid = grid;
        if (node?.id && graph?.segmentNodeIds && Array.isArray(grid?.segments)) {
          const nextSegments = grid.segments.map((seg) => {
            if (allowedIds && !allowedIds.has(seg?.id)) return seg;
            const segNodeIds = Array.isArray(graph.segmentNodeIds?.[seg.id])
              ? graph.segmentNodeIds[seg.id]
              : [];
            const pts = Array.isArray(seg?.points) ? seg.points : [];
            if (!segNodeIds.length || !pts.length) return seg;
            let changed = false;
            const nextPts = pts.map((p, idx) => {
              if (segNodeIds[idx] !== node.id) return p;
              movedByGraph += 1;
              changed = true;
              return { ...p, lat: toLat, lon: toLon };
            });
            return changed ? { ...seg, points: nextPts } : seg;
          });
          if (movedByGraph > 0) {
            nextGrid = { ...grid, segments: nextSegments };
          }
        }

        const movePointsNearScoped = (inputGrid) => {
          if (!allowedIds) return movePointsNear(inputGrid, { lat: fromLat, lon: fromLon }, { lat: toLat, lon: toLon }, threshold);
          let movedLocal = 0;
          const nextSegments = (Array.isArray(inputGrid?.segments) ? inputGrid.segments : []).map((seg) => {
            if (!allowedIds.has(seg?.id)) return seg;
            const pts = Array.isArray(seg?.points) ? seg.points : [];
            const nextPts = pts.map((p) => {
              const d = haversineMeters({ lat: fromLat, lon: fromLon }, p);
              if (d <= threshold) {
                movedLocal += 1;
                return { ...p, lat: toLat, lon: toLon };
              }
              return p;
            });
            return { ...seg, points: nextPts };
          });
          return { grid: { ...inputGrid, segments: nextSegments }, moved: movedLocal };
        };

        // 2) Fallback antigo por proximidade (mantido para compatibilidade).
        let moved = movedByGraph;
        if (!moved) {
          const movedNear = movePointsNearScoped(grid);
          nextGrid = movedNear.grid;
          moved = movedNear.moved;
        }
        if (!moved) {
          // Fallback: move o vértice mais próximo (filtrado quando aplicável).
          let best = null;
          (Array.isArray(grid?.segments) ? grid.segments : []).forEach((seg, sIdx) => {
            if (allowedIds && !allowedIds.has(seg?.id)) return;
            const pts = Array.isArray(seg?.points) ? seg.points : [];
            pts.forEach((p, pIdx) => {
              if (!Number.isFinite(p?.lat) || !Number.isFinite(p?.lon)) return;
              const d = haversineMeters({ lat: fromLat, lon: fromLon }, p);
              if (!best || d < best.dist) best = { sIdx, pIdx, dist: d };
            });
          });
          if (best && best.dist <= Math.max(threshold * 8, 120)) {
            const segs = (Array.isArray(grid?.segments) ? grid.segments : []).map((seg, sIdx) => {
              if (sIdx !== best.sIdx) return seg;
              const pts = Array.isArray(seg?.points) ? [...seg.points] : [];
              const prev = pts[best.pIdx];
              if (!prev) return seg;
              pts[best.pIdx] = { ...prev, lat: toLat, lon: toLon };
              return { ...seg, points: pts };
            });
            nextGrid = { ...grid, segments: segs };
            moved = 1;
          }
        }
        if (!moved) {
          showToast("Nenhum nó movido.", "warning");
          return;
        }
        // Auto-fusão: se o nó for largado em cima/perto de outro,
        // unifica coordenadas para o mesmo ponto final.
        const autoFuseMeters = 5;
        const segsForFuse = Array.isArray(nextGrid?.segments) ? nextGrid.segments : [];
        let fusedExtra = 0;
        const fusedSegments = segsForFuse.map((seg) => {
          // Fusão global: inclui nós intermédios de qualquer trecho.
          // (não limitar ao filtro/trecho ativo)
          const pts = Array.isArray(seg?.points) ? seg.points : [];
          let changed = false;
          const nextPts = pts.map((p) => {
            const pLat = Number(p?.lat);
            const pLon = Number(p?.lon);
            if (!Number.isFinite(pLat) || !Number.isFinite(pLon)) return p;
            // Ignora o próprio ponto já movido.
            if (Math.abs(pLat - toLat) < 1e-8 && Math.abs(pLon - toLon) < 1e-8) return p;
            const d = haversineMeters({ lat: toLat, lon: toLon }, { lat: pLat, lon: pLon });
            if (d <= autoFuseMeters) {
              fusedExtra += 1;
              changed = true;
              return { ...p, lat: toLat, lon: toLon };
            }
            return p;
          });
          return changed ? { ...seg, points: nextPts } : seg;
        });
        if (fusedExtra > 0) {
          nextGrid = { ...nextGrid, segments: fusedSegments };
          logEvent("info", "NAVGRID_MOVE_NODE_AUTO_FUSE", {
            fusedExtra,
            autoFuseMeters,
            toLat,
            toLon,
          });
        }
        navGridMutationBusyRef.current = true;
        await persistNavGridUpdate(
          nextGrid,
          fusedExtra > 0 ? "Nó movido e fundido." : "Nó movido."
        );
      } catch (err) {
        logEvent("error", "NAVGRID_MOVE_NODE_PERSIST_FAIL", {
          message: String(err?.message || err),
        });
        showToast("Falha ao mover nó.", "error");
      } finally {
        navGridMutationBusyRef.current = false;
      }
    },
    [navGridNodesFilter, navGridNodesGraph, persistNavGridUpdate]
  );

  const handleDeleteNode = useCallback(
    async (node) => {
      try {
        if (!node) return;
        if (navGridMutationBusyRef.current) {
          logEvent("warn", "NAVGRID_DELETE_NODE_SKIPPED_BUSY");
          return;
        }
        const grid = loadNavGrid();
        const threshold = 5;
        logEvent("info", "NAVGRID_DELETE_NODE_START", {
          nodeLat: Number(node?.lat),
          nodeLon: Number(node?.lon),
          threshold,
          segments: Array.isArray(grid?.segments) ? grid.segments.length : 0,
          isIOS,
          isAndroid,
        });
        const { grid: nextGrid, removed } = removePointsNear(
          grid,
          { lat: node.lat, lon: node.lon },
          threshold
        );
        if (!removed) {
          showToast("Nenhum nó removido.", "warning");
          return;
        }
        navGridMutationBusyRef.current = true;
        const previousSegments = Array.isArray(grid?.segments) ? grid.segments : [];
        const nextSegments = Array.isArray(nextGrid?.segments) ? nextGrid.segments : [];
        const previousById = new Map(
          previousSegments.map((segment) => [String(segment?.id || ""), segment])
        );
        const nextIds = new Set(nextSegments.map((segment) => String(segment?.id || "")));
        const upserts = nextSegments.filter((segment) => {
          const id = String(segment?.id || "");
          return !previousById.has(id) || previousById.get(id) !== segment;
        });
        const removedIds = previousSegments
          .map((segment) => String(segment?.id || ""))
          .filter((id) => id && !nextIds.has(id));
        await persistNavGridUpdate(
          nextGrid,
          "Nós removidos.",
          { upserts, removedIds },
          { cloudFirst: isIOS }
        );
        logEvent("info", "NAVGRID_DELETE_NODE_DONE", {
          removed: Number(removed || 0),
          nextSegments: Array.isArray(nextGrid?.segments) ? nextGrid.segments.length : 0,
        });
      } catch (err) {
        logEvent("error", "NAVGRID_DELETE_NODE_PERSIST_FAIL", {
          message: String(err?.message || err),
        });
        showToast("Falha ao remover nó.", "error");
      } finally {
        navGridMutationBusyRef.current = false;
      }
    },
    [isAndroid, isIOS, persistNavGridUpdate]
  );

  const handleSplitNode = useCallback(
    async (node) => {
      try {
        if (!node) return;
        if (navGridMutationBusyRef.current) {
          logEvent("warn", "NAVGRID_SPLIT_NODE_SKIPPED_BUSY");
          return;
        }
        const nodeLat = Number(node?.lat);
        const nodeLon = Number(node?.lon);
        if (!Number.isFinite(nodeLat) || !Number.isFinite(nodeLon)) {
          showToast("Nó inválido para dividir.", "warning");
          return;
        }
        const grid = loadNavGrid();
        const threshold = 5;
        const allowedIds =
          Array.isArray(navGridNodesFilter) && navGridNodesFilter.length
            ? new Set(navGridNodesFilter)
            : null;
        const segments = Array.isArray(grid?.segments) ? grid.segments : [];

        let best = null;
        segments.forEach((seg, sIdx) => {
          if (allowedIds && !allowedIds.has(seg?.id)) return;
          const pts = Array.isArray(seg?.points) ? seg.points : [];
          if (pts.length < 3) return;
          for (let pIdx = 1; pIdx < pts.length - 1; pIdx += 1) {
            const p = pts[pIdx];
            const d = haversineMeters({ lat: nodeLat, lon: nodeLon }, p);
            if (!best || d < best.dist) {
              best = { sIdx, pIdx, dist: d };
            }
          }
        });

        if (!best || best.dist > Math.max(threshold * 8, 120)) {
          showToast("Seleciona um nó intermédio para dividir.", "warning");
          return;
        }

        const target = segments[best.sIdx];
        const targetPoints = Array.isArray(target?.points) ? target.points : [];
        const left = targetPoints.slice(0, best.pIdx + 1);
        const right = targetPoints.slice(best.pIdx);
        if (left.length < 2 || right.length < 2) {
          showToast("Divisão inválida neste nó.", "warning");
          return;
        }

        const baseName = String(target?.name || "Trecho").trim() || "Trecho";
        const now = Date.now();
        const segA = {
          ...target,
          id: `${target.id || "seg"}-a-${now}`,
          name: `${baseName} (A)`,
          points: left,
          createdAt: now,
          updatedAt: now,
        };
        const segB = {
          ...target,
          id: `${target.id || "seg"}-b-${now}`,
          name: `${baseName} (B)`,
          points: right,
          createdAt: now,
          updatedAt: now,
        };
        const nextSegments = segments.flatMap((seg, idx) =>
          idx === best.sIdx ? [segA, segB] : [seg]
        );
        const nextGrid = { ...grid, segments: nextSegments, updatedAt: now };
        navGridMutationBusyRef.current = true;
        logEvent("info", "NAVGRID_SPLIT_NODE_START", {
          sourceSegmentId: String(target?.id || ""),
          sourcePoints: targetPoints.length,
          leftSegmentId: segA.id,
          leftPoints: left.length,
          rightSegmentId: segB.id,
          rightPoints: right.length,
          splitIndex: best.pIdx,
          distanceMeters: Number(best.dist.toFixed(2)),
        });
        await persistNavGridUpdate(nextGrid, "Trecho dividido em dois.", {
          upserts: [segA, segB],
          removedIds: target?.id ? [target.id] : [],
        });
        logEvent("info", "NAVGRID_SPLIT_NODE_DONE", {
          sourceSegmentId: String(target?.id || ""),
          createdSegmentIds: [segA.id, segB.id],
          nextSegments: nextSegments.length,
        });
      } catch (err) {
        logEvent("error", "NAVGRID_SPLIT_NODE_PERSIST_FAIL", {
          message: String(err?.message || err),
        });
        showToast("Falha ao dividir trecho.", "error");
      } finally {
        navGridMutationBusyRef.current = false;
      }
    },
    [navGridNodesFilter, persistNavGridUpdate]
  );

  const handleAddNodeAt = useCallback(
    async (latlng) => {
      try {
        if (!latlng) return;
        if (navGridMutationBusyRef.current) {
          logEvent("warn", "NAVGRID_ADD_NODE_SKIPPED_BUSY");
          return;
        }
        const map = mapRef.current;
        if (!map) return;
        const point = { lat: latlng.lat, lon: latlng.lng };
        const grid = loadNavGrid();
        const allowedIds =
          Array.isArray(navGridNodesFilter) && navGridNodesFilter.length
            ? new Set(navGridNodesFilter)
            : null;
        const segments = (grid.segments || []).filter(
          (seg) =>
            Array.isArray(seg?.points) &&
            seg.points.length >= 2 &&
            (!allowedIds || allowedIds.has(seg.id))
        );
        if (!segments.length) {
          showToast("Seleciona um trecho para adicionar nós.", "warning");
          return;
        }

        const clickPt = map.latLngToLayerPoint(latlng);
        const distToSegment = (p, a, b) => {
          const ax = a.x;
          const ay = a.y;
          const bx = b.x;
          const by = b.y;
          const dx = bx - ax;
          const dy = by - ay;
          if (dx === 0 && dy === 0) {
            const px = p.x - ax;
            const py = p.y - ay;
            return Math.hypot(px, py);
          }
          const t = ((p.x - ax) * dx + (p.y - ay) * dy) / (dx * dx + dy * dy);
          const clamped = Math.max(0, Math.min(1, t));
          const projX = ax + clamped * dx;
          const projY = ay + clamped * dy;
          return Math.hypot(p.x - projX, p.y - projY);
        };

        let best = null;
        segments.forEach((segment) => {
          const pts = Array.isArray(segment.points) ? segment.points : [];
          for (let i = 0; i < pts.length - 1; i++) {
            const a = pts[i];
            const b = pts[i + 1];
            if (
              !Number.isFinite(a?.lat) ||
              !Number.isFinite(a?.lon) ||
              !Number.isFinite(b?.lat) ||
              !Number.isFinite(b?.lon)
            )
              continue;
            const ap = map.latLngToLayerPoint([a.lat, a.lon]);
            const bp = map.latLngToLayerPoint([b.lat, b.lon]);
            const dist = distToSegment(clickPt, ap, bp);
            if (!best || dist < best.dist) {
              best = { segment, idx: i, dist };
            }
          }
        });

        if (!best?.segment) {
          showToast("Não foi possível associar o nó ao trecho.", "warning");
          return;
        }
        const { grid: nextGrid, inserted } = insertPointInSegmentAtIndex(
          grid,
          best.segment.id,
          point,
          best.idx
        );
        if (!inserted) {
          showToast("Não foi possível adicionar o nó.", "error");
          return;
        }
        navGridMutationBusyRef.current = true;
        await persistNavGridUpdate(nextGrid, "Nó adicionado.");
      } catch (err) {
        logEvent("error", "NAVGRID_ADD_NODE_PERSIST_FAIL", {
          message: String(err?.message || err),
        });
        showToast("Falha ao adicionar nó.", "error");
      } finally {
        navGridMutationBusyRef.current = false;
      }
    },
    [navGridNodesFilter, persistNavGridUpdate]
  );

  const handleAddNodeAtRef = useRef(handleAddNodeAt);
  useEffect(() => {
    handleAddNodeAtRef.current = handleAddNodeAt;
  }, [handleAddNodeAt]);

  useEffect(() => {
    if (!mapReady || !navGridAddNodeActive) return;
    const map = mapRef.current;
    if (!map) return;
    const handler = (event) => {
      const latlng = getRotationAwareLatLng(map, event);
      if (!latlng) return;
      handleAddNodeAtRef.current?.(latlng);
    };
    map.on("click", handler);
    return () => {
      map.off("click", handler);
    };
  }, [mapReady, navGridAddNodeActive]);

  // moved: events/groups/activeEvent derived earlier to avoid TDZ in hooks

  // 🧭 Heading + follow lógico
  const [headingDeg, setHeadingDeg] = useState(null);
  const [follow, setFollow] = useState(true);
  const [followEnabled, setFollowEnabled] = useState(false);

  // Último bearing aplicado (para estabilidade)
  const lastBearingRef = useRef(null);

  // 🧭 Compass Mode: off | needle | follow
  const [compassMode, setCompassMode] = useState("off");

  // -------------------------------------------------------------
  // 🧭 Interações conforme compassMode
  // (Estado 2 = follow: SEM drag. Estado 0/1: drag ON)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isAdminUser) {
      setNavGridVisible(false);
      setNavGridNodesVisible(false);
      setNavGridEditNodesActive(false);
      setNavGridAddNodeActive(false);
      setNavGridDeleteNodesActive(false);
      setNavGridSplitNodeActive(false);
      setNavGridNodeMode(null);
    }
  }, [isAdminUser]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    try {
      map._manualRotationEnabled = compassMode !== "follow";
      if (compassMode === "follow") map.dragging.disable();
      else map.dragging.enable();
    } catch {}
  }, [mapReady, compassMode]);

  // -------------------------------------------------------------
  // ICONS — GUIA + HALO + VIEWER
  // -------------------------------------------------------------
  const trekkinIcon = L.divIcon({
    className: "",
    html: `
      <div class="tk-guide-wrapper">
        <div class="tk-needle"></div>
        <div class="tk-guide-dot-inner"></div>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  const trekkinHaloIcon = L.divIcon({
    className: "tk-guide-halo",
    html: `<div class="tk-halo"></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    interactive: false,
  });

  const trekkinIconBlue = L.divIcon({
    className: "",
    html: `
      <div style="
        width:18px;
        height:18px;
        background:#007bff;
        border-radius:50%;
        border:2px solid white;
        box-shadow:0 0 6px rgba(0,123,255,0.45);
      "></div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

  // -------------------------------------------------------------
  // 🧭 Compass listener — iOS permission event + smoothing
  // -------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;

    function onOrientation(e) {
      const raw = getCompassHeading(e);
      if (!Number.isFinite(raw)) return;

      if (
        lastRawHeadingRef.current != null &&
        Math.abs(raw - lastRawHeadingRef.current) < 2
      ) {
        return;
      }

      smoothHeadingRef.current = smoothAngle(smoothHeadingRef.current, raw, 0.12);
      lastRawHeadingRef.current = raw;
      setHeadingDeg(smoothHeadingRef.current);
    }

    function attachCompass() {
      mapDebug("🧭 Compass listener ATTACHED (iOS)");
      window.addEventListener("deviceorientationabsolute", onOrientation);
      window.addEventListener("deviceorientation", onOrientation);
    }

    window.addEventListener("trekkin:compass-permission-granted", attachCompass, {
      once: true,
    });

    return () => {
      window.removeEventListener("trekkin:compass-permission-granted", attachCompass);
      window.removeEventListener("deviceorientationabsolute", onOrientation);
      window.removeEventListener("deviceorientation", onOrientation);
    };
  }, []);

  // -------------------------------------------------------------
  // 🧭 COMPASS MODE — vindo do TopControlBar
  // -------------------------------------------------------------
  useEffect(() => {
    function onCompassMode(e) {
      const mode = e?.detail?.mode;
      if (!["off", "needle", "follow"].includes(mode)) return;

      mapDebug("[MapView] 🧭 compassMode =", mode);
      setCompassMode(mode);

      // ao sair do follow: reset visual
      if (mode !== "follow") {
        const map = mapRef.current;
        if (map) applyMapRotation(map, 0);
      }
    }

    window.addEventListener("trekkin:set-compass-mode", onCompassMode);
    return () =>
      window.removeEventListener("trekkin:set-compass-mode", onCompassMode);
  }, []);

  // -------------------------------------------------------------
  // 🎯 FOLLOW ENABLE — vindo do TopControlBar
  // -------------------------------------------------------------
  useEffect(() => {
    function onFollowEnabled(e) {
      if (typeof e?.detail?.enabled !== "boolean") return;
      const enabled = e.detail.enabled;
      setFollowEnabled(enabled);
      if (!enabled) setFollow(false);
    }

    window.addEventListener("trekkin:set-follow-enabled", onFollowEnabled);
    return () =>
      window.removeEventListener("trekkin:set-follow-enabled", onFollowEnabled);
  }, []);

  // -------------------------------------------------------------
  // 🧭 ANDROID — receber heading da bússola NATIVA
  // -------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return;

    let last = null;

    function onNativeHeading(e) {
      const h = e?.detail?.heading;
      if (!Number.isFinite(h)) return;

      try {
        if (import.meta.env.DEV) {
        mapDebug("🧭 [ANDROID] native heading", { raw: h, ts });
      }
      } catch {}

      if (last == null) {
        last = h;
        setHeadingDeg(h);
        return;
      }

      let diff = h - last;
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;

      const smooth = last + diff * 0.25;
      last = (smooth + 360) % 360;

      setHeadingDeg(last);
    }

    window.addEventListener("trekkin:native-heading", onNativeHeading);

    return () => {
      window.removeEventListener("trekkin:native-heading", onNativeHeading);
      last = null;
    };
  }, []);

  // -------------------------------------------------------------
  // Init GPS / Viewer tracking
  // -------------------------------------------------------------
  useEffect(() => {
    if (isViewerMode) return;
    let cancelled = false;
    let started = false;
    let startTimer = null;

    const clearStartTimer = () => {
      if (!startTimer) return;
      clearTimeout(startTimer);
      startTimer = null;
    };

    const startIfNeeded = () => {
      if (cancelled || started || startTimer) return;
      startTimer = setTimeout(() => {
        startTimer = null;
        if (cancelled || started) return;
        started = true;
        startTracking();
      }, 800);
    };

    const stopIfNeeded = () => {
      clearStartTimer();
      if (!started) return;
      started = false;
      stopTracking();
    };

    const syncTrackingState = () => {
      const isVisible =
        typeof document === "undefined" ||
        document.visibilityState === "visible";
      if (isRecording || isVisible) {
        startIfNeeded();
      } else {
        stopIfNeeded();
      }
    };

    syncTrackingState();
    try {
      document.addEventListener("visibilitychange", syncTrackingState);
    } catch {}

    return () => {
      cancelled = true;
      try {
        document.removeEventListener("visibilitychange", syncTrackingState);
      } catch {}
      stopIfNeeded();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewerMode, isRecording]);

  useEffect(() => {
    if (!isRecording) return;
    requestAndroidAlwaysPermission?.();
  }, [isRecording, requestAndroidAlwaysPermission]);

  useEffect(() => {
    if (!isSharing) return;
    if (/Android/i.test(navigator.userAgent || "")) {
      requestAndroidForegroundPermission?.();
    }
  }, [isSharing, requestAndroidForegroundPermission]);

  // Viewer pede a sua própria posição 1 vez
  useEffect(() => {
    if (!isViewerMode) return;
    if (!("geolocation" in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        usePanelsStore.getState().setUserPosition({
          lat: latitude,
          lon: longitude,
          ele: 0,
          acc: accuracy,
          spd: 0,
          ts: Date.now(),
        });
      },
      () => {},
      { enableHighAccuracy: true }
    );
  }, [isViewerMode]);

  // -------------------------------------------------------------
  // PICK POINT MODE (para PanelMapSelectPoint)
  // -------------------------------------------------------------
  useEffect(() => {
    const setPickPoint = (next) => {
      const current = Boolean(window.__trekkinPickPoint);
      window.__trekkinPickPoint = next;
      setMapPickPointActive(next);
      if (current === next) return;
      mapDebug(
        `📌 MapView → modo pickPoint ${next ? "ATIVADO" : "DESATIVADO"}`
      );
    };
    function enablePickPoint() {
      // Selecionar uma coordenada não é um modo bloqueado: o utilizador tem
      // de poder deslocar e ampliar o mapa antes de tocar no local desejado.
      // O modo bússola "follow" desativa o drag, por isso saímos dele aqui.
      setFollow(false);
      setCompassMode("off");
      try {
        const map = mapRef.current;
        map?.dragging?.enable?.();
        map?.touchZoom?.enable?.();
        map?.doubleClickZoom?.enable?.();
        map?.scrollWheelZoom?.enable?.();
      } catch {}
      setPickPoint(true);
    }
    function disablePickPoint() {
      setPickPoint(false);
    }

    window.addEventListener("trekkin:enable-pickpoint", enablePickPoint);
    window.addEventListener("trekkin:disable-pickpoint", disablePickPoint);

    return () => {
      window.removeEventListener("trekkin:enable-pickpoint", enablePickPoint);
      window.removeEventListener("trekkin:disable-pickpoint", disablePickPoint);
    };
  }, []);

  // -------------------------------------------------------------
  // INIT MAP
  // -------------------------------------------------------------
  useEffect(() => {
    if (mapRef.current) return;
    try {
      localStorage.removeItem(FORCE_REMOTE_LITE_KEY);
      localStorage.removeItem(FORCE_REMOTE_PRO_KEY);
    } catch {}

    const map = L.map("mapid", {
      zoomControl: false,
      attributionControl: false,
      closePopupOnClick: true,
      preferCanvas: false,
      minZoom: 7,
      // The native Pro archive has real detail through z15. Mobile may
      // overzoom it visually up to z18 without requesting denser source data.
      maxZoom: isIOS || isAndroid ? MOBILE_MAP_MAX_ZOOM : 19,
      zoom: 13,
      center: readInitialMapCenter(),
      bounceAtZoomLimits: false,
      zoomAnimation: !isIOS,
      markerZoomAnimation: !isIOS,
      fadeAnimation: !isIOS,
      inertia: !isIOS,
      doubleClickZoom: true,
    });
    canvasRendererRef.current = null;

    const mapContainer = map.getContainer();
    let adminLongPressTimer = null;
    let adminLongPressStart = null;
    let adminLongPressTriggered = false;

    const cancelAdminLongPress = () => {
      if (adminLongPressTimer) {
        window.clearTimeout(adminLongPressTimer);
        adminLongPressTimer = null;
      }
      adminLongPressStart = null;
    };

    const beginAdminLongPressAt = ({ clientX, clientY, target, pointerType, button }) => {
      const authUser = useAuthStore.getState()?.user;
      if (String(authUser?.role || "").toLowerCase() !== "admin") return;
      if (pointerType === "mouse" && button !== 0) return;
      if (navGridDrawActiveRef.current || editActiveRef.current) return;
      if (
        target?.closest?.(
          ".leaflet-marker-icon, .leaflet-popup, .leaflet-control, .tk-waypoint-popup, .tk-trail-popup, .tk-navsegment-popup, .leaflet-interactive"
        )
      ) {
        return;
      }

      cancelAdminLongPress();
      adminLongPressStart = { x: clientX, y: clientY };
      adminLongPressTimer = window.setTimeout(() => {
        adminLongPressTimer = null;
        adminLongPressStart = null;
        const latLng = getRotationAwareLatLng(map, { clientX, clientY });
        if (!Number.isFinite(latLng?.lat) || !Number.isFinite(latLng?.lng)) return;
        adminLongPressTriggered = true;
        if (navigator.vibrate) navigator.vibrate(35);
        setAdminQuickCreatePoint({ lat: latLng.lat, lon: latLng.lng });
      }, 750);
    };

    const beginAdminLongPress = (event) => {
      beginAdminLongPressAt(event);
    };

    const moveAdminLongPress = (event) => {
      if (!adminLongPressStart) return;
      if (
        Math.hypot(
          event.clientX - adminLongPressStart.x,
          event.clientY - adminLongPressStart.y
        ) > 12
      ) {
        cancelAdminLongPress();
      }
    };

    const beginAdminTouchLongPress = (event) => {
      if (event.touches?.length !== 1) {
        cancelAdminLongPress();
        return;
      }
      const touch = event.touches[0];
      beginAdminLongPressAt({
        clientX: touch.clientX,
        clientY: touch.clientY,
        target: event.target,
        pointerType: "touch",
        button: 0,
      });
    };

    const moveAdminTouchLongPress = (event) => {
      if (!adminLongPressStart || event.touches?.length !== 1) {
        cancelAdminLongPress();
        return;
      }
      const touch = event.touches[0];
      moveAdminLongPress({ clientX: touch.clientX, clientY: touch.clientY });
    };

    const blockMapContextMenu = (event) => {
      if (String(useAuthStore.getState()?.user?.role || "").toLowerCase() === "admin") {
        event.preventDefault();
      }
    };

    const suppressClickAfterAdminLongPress = (event) => {
      if (!adminLongPressTriggered) return;
      adminLongPressTriggered = false;
      event.preventDefault();
      event.stopPropagation();
    };

    mapContainer.style.webkitTouchCallout = "none";
    mapContainer.addEventListener("pointerdown", beginAdminLongPress, true);
    mapContainer.addEventListener("pointermove", moveAdminLongPress, true);
    mapContainer.addEventListener("pointerup", cancelAdminLongPress, true);
    mapContainer.addEventListener("pointercancel", cancelAdminLongPress, true);
    mapContainer.addEventListener("touchstart", beginAdminTouchLongPress, {
      capture: true,
      passive: true,
    });
    mapContainer.addEventListener("touchmove", moveAdminTouchLongPress, {
      capture: true,
      passive: true,
    });
    mapContainer.addEventListener("touchend", cancelAdminLongPress, true);
    mapContainer.addEventListener("touchcancel", cancelAdminLongPress, true);
    mapContainer.addEventListener("contextmenu", blockMapContextMenu, true);
    mapContainer.addEventListener("click", suppressClickAfterAdminLongPress, true);

    map.on("dragstart", () => {
      cancelAdminLongPress();
      isDraggingRef.current = true;
    });
    map.on("dragend", () => {
      isDraggingRef.current = false;
    });
    map.on("zoomstart", () => {
      cancelAdminLongPress();
      isZoomingRef.current = true;
      try {
        if (zoomLevelHideTimeoutRef.current) {
          window.clearTimeout(zoomLevelHideTimeoutRef.current);
          zoomLevelHideTimeoutRef.current = null;
        }
      } catch {}
      try {
        const z = Number(map.getZoom?.());
        if (Number.isFinite(z)) setZoomLevelLabel(z.toFixed(1));
      } catch {}
      setShowZoomLevelLabel(true);
    });
    map.on("zoomend", () => {
      isZoomingRef.current = false;
      setDirectionZoomTick((v) => v + 1);
      try {
        const z = Number(map.getZoom?.());
        if (Number.isFinite(z)) setZoomLevelLabel(z.toFixed(1));
      } catch {}
      try {
        if (zoomLevelHideTimeoutRef.current) {
          window.clearTimeout(zoomLevelHideTimeoutRef.current);
        }
      } catch {}
      zoomLevelHideTimeoutRef.current = window.setTimeout(() => {
        setShowZoomLevelLabel(false);
      }, 1000);
    });
    const refreshWaypointViewport = () => {
      try {
        if (waypointViewportDebounceRef.current) {
          window.clearTimeout(waypointViewportDebounceRef.current);
        }
      } catch {}
      waypointViewportDebounceRef.current = window.setTimeout(() => {
        setWaypointViewportTick((v) => v + 1);
      }, 120);
    };
    map.on("moveend", refreshWaypointViewport);
    map.on("zoomend", refreshWaypointViewport);

    // 🖐️ user interaction tracking (não mexe no estado do botão)
    const markUserInteraction = () => {
      lastInteractionRef.current = Date.now();
      setFollow(false);
    };

    map.on("dragstart", markUserInteraction);
    map.on("zoomstart", markUserInteraction);
    map.on("mousedown", markUserInteraction);
    map.on("touchstart", markUserInteraction);

    // Base raster OSM (usada apenas quando estilo selecionado for OpenStreet)
    fallbackRasterRef.current = createRetryTileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        subdomains: ["a", "b", "c"],
        maxZoom: 19,
        className: "tk-osm-base-tile",
        attribution: "© OpenStreetMap",
        updateWhenIdle: false,
        keepBuffer: isIOS ? 8 : 4,
        tileTimeoutMs: isIOS ? 25000 : 20000,
        maxRetryRounds: isIOS ? 2 : 1,
        retryDelayMs: isIOS ? 600 : 300,
        tileCacheName: RASTER_TILE_CACHE_NAME,
        crossOrigin: true,
      }
    );
    try {
      fallbackRasterRef.current.setZIndex(1);
    } catch {}
    liteRasterRef.current = createRetryTileLayer(
      "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
      {
        subdomains: ["a", "b", "c"],
        className: "tk-lite-base-tile",
        maxZoom: 19,
        maxNativeZoom: 17,
        attribution: "© OpenTopoMap (CC-BY-SA) · © OpenStreetMap",
        updateWhenIdle: true,
        updateWhenZooming: false,
        updateInterval: isIOS ? 300 : 200,
        keepBuffer: isIOS ? 8 : 4,
        tileTimeoutMs: isIOS ? 25000 : 20000,
        maxRetryRounds: isIOS ? 2 : 1,
        retryDelayMs: isIOS ? 600 : 300,
        tileCacheName: RASTER_TILE_CACHE_NAME,
        crossOrigin: true,
      }
    );
    let proTileFallbackCount = 0;
    proRasterBaseRef.current = createRetryTileLayer(
      "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
      {
        subdomains: ["a", "b", "c"],
        className: "tk-pro-base-tile",
        maxZoom: 19,
        maxNativeZoom: 17,
        attribution: "© OpenTopoMap (CC-BY-SA) · © OpenStreetMap",
        updateWhenIdle: true,
        updateWhenZooming: false,
        updateInterval: isIOS ? 300 : 200,
        keepBuffer: isIOS ? 8 : 4,
        tileTimeoutMs: isIOS ? 25000 : 20000,
        maxRetryRounds: isIOS ? 2 : 1,
        retryDelayMs: isIOS ? 600 : 300,
        parentFallback: false,
        parentFallbackLevels: 0,
        cacheBustRetries: false,
        cacheBustParam: "tkpro",
        rejectBlankTiles: true,
        tileCacheName: RASTER_TILE_CACHE_NAME,
        onTileFallback: (payload = {}) => {
          const type = payload?.type || "unknown";
          if (type === "parent-start") return;
          proTileFallbackCount += 1;
          if (proTileFallbackCount > 20 && proTileFallbackCount % 50 !== 0) return;
          logEvent(type === "failed" ? "warn" : "info", "MAP_PRO_RASTER_TILE_FALLBACK", {
            platform: isIOS ? "ios" : isAndroid ? "android" : "web",
            count: proTileFallbackCount,
            type,
            z: Number.isFinite(Number(payload?.z)) ? Number(payload.z) : null,
            x: Number.isFinite(Number(payload?.x)) ? Number(payload.x) : null,
            y: Number.isFinite(Number(payload?.y)) ? Number(payload.y) : null,
            parentZ: Number.isFinite(Number(payload?.parentZ))
              ? Number(payload.parentZ)
              : null,
            parentX: Number.isFinite(Number(payload?.parentX))
              ? Number(payload.parentX)
              : null,
            parentY: Number.isFinite(Number(payload?.parentY))
              ? Number(payload.parentY)
              : null,
            fallbackLevel: Number.isFinite(Number(payload?.level))
              ? Number(payload.level)
              : null,
          });
        },
      }
    );
    proRasterShadeRef.current = isIOS
      ? null
      : L.tileLayer(
          "https://services.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}",
          {
            className: "tk-pro-hillshade",
            maxZoom: 15,
            maxNativeZoom: 15,
            opacity: 0.18,
            attribution: "Hillshade © Esri",
            updateWhenIdle: !isAndroid,
            crossOrigin: true,
          }
        );
    try {
      fallbackRasterRef.current.setZIndex(1);
      liteRasterRef.current.setZIndex(2);
      proRasterBaseRef.current.setZIndex(3);
      proRasterShadeRef.current?.setZIndex?.(2);
    } catch {}
    try {
      let proTileLoadStartedAt = 0;
      let proTileFirstLogged = false;
      let proTileErrorCount = 0;
      proRasterBaseRef.current.on("loading", () => {
        proTileLoadStartedAt = Date.now();
        proTileFirstLogged = false;
        logEvent("info", "MAP_PRO_RASTER_LOADING", {
          platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        });
      });
      proRasterBaseRef.current.on("tileerror", (event) => {
        try {
          const tile = event?.tile;
          if (tile?.style) {
            tile.style.opacity = "0";
            tile.style.visibility = "hidden";
          }
        } catch {}
        const coords = event?.coords || {};
        proTileErrorCount += 1;
        if (proTileErrorCount <= 12 || proTileErrorCount % 50 === 0) {
          logEvent("warn", "MAP_PRO_RASTER_TILE_ERROR", {
            platform: isIOS ? "ios" : isAndroid ? "android" : "web",
            count: proTileErrorCount,
            z: Number.isFinite(Number(coords.z)) ? Number(coords.z) : null,
            x: Number.isFinite(Number(coords.x)) ? Number(coords.x) : null,
            y: Number.isFinite(Number(coords.y)) ? Number(coords.y) : null,
          });
        }
      });
      proRasterBaseRef.current.on("tileload", () => {
        if (proTileFirstLogged) return;
        proTileFirstLogged = true;
        if (isAndroid) androidMapBaseVisibleAtRef.current = Date.now();
        try {
          window.dispatchEvent(new CustomEvent("trekkin:map-base-visible"));
        } catch {}
        const resumePerf = appResumePerfRef.current;
        const resumeAgeMs = resumePerf?.startedAt
          ? Date.now() - resumePerf.startedAt
          : null;
        const resumeToBaseMs =
          Number.isFinite(resumeAgeMs) && resumeAgeMs >= 0 && resumeAgeMs < 20000
            ? resumeAgeMs
            : null;
        if (resumeToBaseMs != null && !resumePerf.firstTileLogged) {
          resumePerf.firstTileLogged = true;
          logEvent("info", "APP_RESUME_FIRST_TILE", {
            platform: isIOS ? "ios" : isAndroid ? "android" : "web",
            style: effectiveMapStyle,
            source: mapBaseSourceKey,
            resumeToBaseMs,
          });
        }
        logEvent("info", "MAP_PRO_RASTER_FIRST_TILE", {
          platform: isIOS ? "ios" : isAndroid ? "android" : "web",
          elapsedMs: proTileLoadStartedAt ? Date.now() - proTileLoadStartedAt : null,
          resumeToBaseMs,
        });
      });
      proRasterBaseRef.current.on("load", () => {
        if (isAndroid) androidMapBaseVisibleAtRef.current = Date.now();
        try {
          window.dispatchEvent(new CustomEvent("trekkin:map-base-visible"));
        } catch {}
        const resumePerf = appResumePerfRef.current;
        const resumeAgeMs = resumePerf?.startedAt
          ? Date.now() - resumePerf.startedAt
          : null;
        const resumeToBaseMs =
          Number.isFinite(resumeAgeMs) && resumeAgeMs >= 0 && resumeAgeMs < 20000
            ? resumeAgeMs
            : null;
        logEvent("info", "MAP_PRO_RASTER_LOAD", {
          platform: isIOS ? "ios" : isAndroid ? "android" : "web",
          elapsedMs: proTileLoadStartedAt ? Date.now() - proTileLoadStartedAt : null,
          resumeToBaseMs,
        });
      });
    } catch {}
    // Pane event routes
    if (!map.getPane("eventRoutesPane")) {
      map.createPane("eventRoutesPane");
      const pane = map.getPane("eventRoutesPane");
      pane.style.zIndex = 430;
      pane.style.pointerEvents = "none";
    }
    if (!map.getPane("trailDirectionPane")) {
      map.createPane("trailDirectionPane");
      const pane = map.getPane("trailDirectionPane");
      pane.style.zIndex = 435;
      pane.style.pointerEvents = "none";
    }
    if (!map.getPane("recordingTrackPane")) {
      map.createPane("recordingTrackPane");
      const pane = map.getPane("recordingTrackPane");
      pane.style.zIndex = 440;
      pane.style.pointerEvents = "none";
    }
    if (!map.getPane("trailStagesLinePane")) {
      map.createPane("trailStagesLinePane");
      const pane = map.getPane("trailStagesLinePane");
      pane.style.zIndex = 610;
      pane.style.pointerEvents = "none";
    }
    if (!map.getPane("trailStagesPointPane")) {
      map.createPane("trailStagesPointPane");
      const pane = map.getPane("trailStagesPointPane");
      pane.style.zIndex = 640;
      pane.style.pointerEvents = "none";
    }

    const isInteractiveOverlayClick = (ev) => {
      try {
        const oe = ev?.originalEvent;
        if (!oe) return false;
        if (oe._trekkinMarkerHandled === true) return true;
        const target = oe.target;
        if (!target || typeof target.closest !== "function") return false;
        return !!target.closest(
          ".leaflet-marker-icon, .leaflet-popup, .tk-waypoint-popup, .tk-trail-popup, .tk-navsegment-popup, .leaflet-interactive"
        );
      } catch {
        return false;
      }
    };

    // Pickpoint click
    map.on("click", function (ev) {
      if (isInteractiveOverlayClick(ev)) return;
      const clickLatLng = getRotationAwareLatLng(map, ev);
      const lat = clickLatLng?.lat;
      const lon = clickLatLng?.lng;
      const pickMode = planPickModeRef.current;
      const stopPlanCtx = stopPlanPickRef.current;
      if (
        stopPlanCtx?.active &&
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        !pickMode
      ) {
        const appendFromMap = window.__trekkinAppendStageFromWaypoint;
        if (typeof appendFromMap === "function") {
          appendFromMap({
            lat,
            lon,
            name: "Ponto no mapa",
            waypointId: null,
          });
          return;
        }
      }
      if (
        pickMode &&
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        (pickMode === "start" || pickMode === "end" || pickMode === "waypoint")
      ) {
        if (pickMode === "start") {
          setPlanStart({ lat, lon, source: "map" });
          setPlanEnd(null);
        } else if (pickMode === "end") {
          setPlanEnd({ lat, lon, source: "map" });
        } else {
          addPlanWaypoint({
            id: `manual-${Date.now()}-${Math.round(Math.random() * 100000)}`,
            name: "Ponto intermédio",
            lat,
            lon,
            source: "map",
            manual: true,
          });
          showToast("Ponto intermédio adicionado.");
        }
        setPlanPickMode(null);
        setPlanFinalizeRequested(false);
        window.dispatchEvent(new CustomEvent("trekkin:disable-pickpoint"));
        const targetPanel = pickReturnPanelRef.current;
        if (targetPanel?.name) {
          setActivePanel(targetPanel.name, targetPanel.props || {});
        }
        pickReturnPanelRef.current = { name: "waypoints", props: {} };
      }
      if (!window.__trekkinPickPoint) return;

      mapDebug("🗺️ [MapView] PICKPOINT click:", lat, lon);

      window.dispatchEvent(
        new CustomEvent("trekkin:map-click", { detail: { lat, lon } })
      );
    });

    // ANDROID FIX — força render real
    setTimeout(() => {
      try {
        map.invalidateSize(true);
        map._onResize();
      } catch {}
    }, 220);

    baseLayerRef.current = L.polyline([], {
      color: "#007bff",
      dashArray: "6 6",
      weight: 3,
      interactive: false,
    }).addTo(map);
    baseDirectionLayerRef.current = L.layerGroup().addTo(map);
    eventDirectionLayerRef.current = L.layerGroup().addTo(map);

    recordLayerRef.current = L.polyline([], {
      color: "#dc3545",
      weight: 4,
      interactive: false,
      pane: "recordingTrackPane",
    }).addTo(map);

    accuracyRef.current = null;

    userMarkerRef.current = L.marker([0, 0], {
      icon: trekkinIcon,
      zIndexOffset: 1000,
    }).addTo(map);

    if (!isViewerMode) {
      guideHaloRef.current = L.marker([0, 0], {
        icon: trekkinHaloIcon,
        zIndexOffset: 900,
        interactive: false,
      }).addTo(map);
    }

    mapRef.current = map;
    window.__trekkinMap = map;
    map._rotateTarget = document.getElementById("map-visual");
    map._manualRotationEnabled = true;
    enableTwoFingerMapRotation(map);
    enableRotationAwareMapDragging(map);
    const reapplyCurrentMapRotation = () => {
      const rotation = Number(map._rotationDeg || 0);
      applyMapRotation(map, rotation);
      try {
        requestAnimationFrame(() => applyMapRotation(map, rotation));
      } catch {}
    };
    map.on("popupopen popupclose moveend zoomend viewreset", reapplyCurrentMapRotation);

    const perfNow = typeof performance !== "undefined" ? performance.now() : 0;
    const bootT0 = window.__TREKKIN_BOOT_T0 || 0;
    const deltaMs = bootT0 ? Math.round(perfNow - bootT0) : null;
    mapDebug(
      `[MAP] ready perfNow=${perfNow} deltaSinceBootMs=${deltaMs}`
    );
    logEvent("info", "MAP_READY", {
      platform: isIOS ? "ios" : isAndroid ? "android" : "web",
      style: resolvedMapStyle,
      deltaSinceBootMs: deltaMs,
      zoom: Number(map.getZoom?.()) || null,
    });
    if (typeof window !== "undefined") {
      window.__TREKKIN_MAP_READY = true;
      window.dispatchEvent(new CustomEvent("trekkin:map-ready"));
    }
    setMapReady(true);
    try {
      const z0 = Number(map.getZoom?.());
      if (Number.isFinite(z0)) setZoomLevelLabel(z0.toFixed(1));
    } catch {}
    setTimeout(() => map.invalidateSize(true), 120);
    setTimeout(() => setWaypointViewportTick((v) => v + 1), 160);
  }, [trekkinIcon, trekkinHaloIcon, isViewerMode, resolvedMapStyle]);

  useEffect(() => {
    return () => {
      try {
        if (zoomLevelHideTimeoutRef.current) {
          window.clearTimeout(zoomLevelHideTimeoutRef.current);
          zoomLevelHideTimeoutRef.current = null;
        }
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!mapReady) return undefined;
    const map = mapRef.current;
    if (!map) return undefined;
    const syncBoundsState = () => {
      try {
        setMapCenterOutsidePmtilesBounds(
          !isInsidePortugalPmtilesBounds(map.getCenter?.())
        );
      } catch {
        setMapCenterOutsidePmtilesBounds(false);
      }
    };
    syncBoundsState();
    map.on("moveend zoomend", syncBoundsState);
    return () => {
      try {
        map.off("moveend zoomend", syncBoundsState);
      } catch {}
    };
  }, [mapReady]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    const useRemoteProRasterOverlay = false;
    if (shouldDeferNativeBaseMap) {
      try {
        if (fallbackRasterRef.current && map.hasLayer(fallbackRasterRef.current)) {
          map.removeLayer(fallbackRasterRef.current);
        }
        if (liteRasterRef.current && map.hasLayer(liteRasterRef.current)) {
          map.removeLayer(liteRasterRef.current);
        }
        if (proRasterShadeRef.current && map.hasLayer(proRasterShadeRef.current)) {
          map.removeLayer(proRasterShadeRef.current);
        }
        if (proRasterBaseRef.current && map.hasLayer(proRasterBaseRef.current)) {
          map.removeLayer(proRasterBaseRef.current);
        }
      } catch {}
      logEvent("info", "MAP_NATIVE_BASE_DEFERRED", {
        platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        style: effectiveMapStyle,
        hasBaseLayer: !!baseMapLayerRef.current,
      });
      return undefined;
    }
    if (effectiveMapStyle === "openstreet") {
      try {
        if (baseMapLayerRef.current) {
          map.removeLayer(baseMapLayerRef.current);
          baseMapLayerRef.current = null;
        }
        if (liteRasterRef.current && map.hasLayer(liteRasterRef.current)) map.removeLayer(liteRasterRef.current);
        if (proRasterShadeRef.current && map.hasLayer(proRasterShadeRef.current)) map.removeLayer(proRasterShadeRef.current);
        if (proRasterBaseRef.current && map.hasLayer(proRasterBaseRef.current)) map.removeLayer(proRasterBaseRef.current);
        if (fallbackRasterRef.current && !map.hasLayer(fallbackRasterRef.current)) {
          fallbackRasterRef.current.addTo(map);
        }
      } catch {}
      return undefined;
    }
    try {
      if (fallbackRasterRef.current && map.hasLayer(fallbackRasterRef.current)) {
        map.removeLayer(fallbackRasterRef.current);
      }
      if (baseMapLayerRef.current) {
        map.removeLayer(baseMapLayerRef.current);
        baseMapLayerRef.current = null;
      }
      if (
        !useOnlineFallbackUnderLocalMap &&
        liteRasterRef.current &&
        map.hasLayer(liteRasterRef.current)
      ) {
        map.removeLayer(liteRasterRef.current);
      }
      if (proRasterShadeRef.current && map.hasLayer(proRasterShadeRef.current)) map.removeLayer(proRasterShadeRef.current);
      if (isIOS) {
        cleanupProHillshadeTiles(map, {
          reason: "ios-pro-remote-no-hillshade",
          platform: "ios",
        });
      }
      if (
        !useOnlineFallbackUnderLocalMap &&
        proRasterBaseRef.current &&
        map.hasLayer(proRasterBaseRef.current)
      ) {
        map.removeLayer(proRasterBaseRef.current);
      }
      if (useOnlineFallbackUnderLocalMap) {
        if (effectiveMapStyle === "lite") {
          if (proRasterBaseRef.current && map.hasLayer(proRasterBaseRef.current)) {
            map.removeLayer(proRasterBaseRef.current);
          }
          if (fallbackRasterRef.current && map.hasLayer(fallbackRasterRef.current)) {
            map.removeLayer(fallbackRasterRef.current);
          }
          if (liteRasterRef.current && !map.hasLayer(liteRasterRef.current)) {
            liteRasterRef.current.addTo(map);
          }
          liteRasterRef.current?.setOpacity?.(1);
          liteRasterRef.current?.setZIndex?.(2);
        } else if (effectiveMapStyle === "pro") {
          if (liteRasterRef.current && map.hasLayer(liteRasterRef.current)) {
            map.removeLayer(liteRasterRef.current);
          }
          if (proRasterBaseRef.current && !map.hasLayer(proRasterBaseRef.current)) {
            proRasterBaseRef.current.addTo(map);
          }
          proRasterBaseRef.current?.setOpacity?.(1);
          proRasterBaseRef.current?.setZIndex?.(2);
        }
      }
      if (effectiveMapStyle === "lite") {
        if (fallbackRasterRef.current && map.hasLayer(fallbackRasterRef.current)) {
          map.removeLayer(fallbackRasterRef.current);
        }
        if (
          !useOnlineFallbackUnderLocalMap &&
          liteRasterRef.current &&
          map.hasLayer(liteRasterRef.current)
        ) {
          map.removeLayer(liteRasterRef.current);
        }
        if (proRasterBaseRef.current && map.hasLayer(proRasterBaseRef.current)) {
          map.removeLayer(proRasterBaseRef.current);
        }
      }
      if (effectiveMapStyle === "pro") {
        if (!useLocalProPmtiles) {
          logEvent("info", "MAP_PRO_REMOTE_VECTOR_UNDERLAY", {
            platform: isIOS ? "ios" : isAndroid ? "android" : "web",
            mapBaseSourceKey,
          });
        }
        if (
          !useOnlineFallbackUnderLocalMap &&
          fallbackRasterRef.current &&
          map.hasLayer(fallbackRasterRef.current)
        ) {
          map.removeLayer(fallbackRasterRef.current);
        }
      }
    } catch {}
    ensurePmtilesProtocol();
    const sourceMode = useLocalBasePmtiles ? "local" : "remote";
    const liteStyle =
      effectiveMapStyle === "pro"
        ? buildProStyle({ overlay: useTransparentPmtilesBackground, sourceMode })
        : buildLiteStyle({ overlay: useTransparentPmtilesBackground, sourceMode });
    const mapLibreLayer = createMapLibreLayer({
      style: liteStyle,
      className: "tk-base-lite-layer",
      // The Leaflet parent is already a viewport-diagonal square for rotation.
      // A second overscan plus the iPhone DPR created a ~10k WebGL canvas.
      rotationOverscan: false,
      pixelRatio: isIOS || isAndroid ? 1 : undefined,
      // Em WebViews móveis, uma cache sem limite cresce rapidamente durante
      // pan/zoom e pode reiniciar o processo gráfico no iOS e no Android.
      maxTileCacheSize: isIOS || isAndroid ? 24 : undefined,
      maxTileCacheZoomLevels: isIOS || isAndroid ? 2 : undefined,
      fadeDuration: isIOS || isAndroid ? 0 : undefined,
      // Limit vector redraws while two fingers pan and zoom together. The
      // final position is always rendered by `moveend`/`zoomend`.
      interactionUpdateInterval: isIOS || isAndroid ? 50 : 0,
      zIndex: effectiveMapStyle === "lite" ? 4 : undefined,
    });
    const mapLibreStartedAt = Date.now();
    const handleMapLibreLoad = () => {
      const resumePerf = appResumePerfRef.current;
      const resumeToBaseMs = resumePerf?.startedAt
        ? Date.now() - resumePerf.startedAt
        : null;
      logEvent("info", "MAP_BASE_LAYER_LOAD", {
        platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        style: effectiveMapStyle,
        elapsedMs: Date.now() - mapLibreStartedAt,
        resumeToBaseMs,
      });
      try {
        window.dispatchEvent(new CustomEvent("trekkin:map-base-visible"));
      } catch {}
    };
    const handleWebglContextLost = () => {
      logEvent("warn", "MAP_BASE_WEBGL_CONTEXT_LOST", {
        platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        style: effectiveMapStyle,
      });
    };
    const handleWebglContextRestored = () => {
      logEvent("info", "MAP_BASE_WEBGL_CONTEXT_RESTORED", {
        platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        style: effectiveMapStyle,
      });
    };
    const handleMapLibreError = (event) => {
      const rawErr = event?.error || {};
      const innerErr = rawErr?.error || {};
      const errMsg = String(innerErr?.message || rawErr?.message || "unknown");
      const tileInfo = (() => {
        try {
          const t = rawErr?.tile || innerErr?.tile || null;
          const id = t?.tileID || t?.id || null;
          const canonical = id?.canonical || id || null;
          const z = Number(canonical?.z);
          const x = Number(canonical?.x);
          const y = Number(canonical?.y);
          return {
            z: Number.isFinite(z) ? z : null,
            x: Number.isFinite(x) ? x : null,
            y: Number.isFinite(y) ? y : null,
            sourceId: String(rawErr?.sourceId || innerErr?.sourceId || rawErr?.source || innerErr?.source || ""),
            sourceLayer: String(rawErr?.sourceLayer || innerErr?.sourceLayer || ""),
          };
        } catch {
          return { z: null, x: null, y: null, sourceId: "", sourceLayer: "" };
        }
      })();
      const online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
      const hasLocalMapFiles = (() => {
        try {
          if (hasUsableNativeLocalMapHint(effectiveMapStyle)) return true;
          const lite = String(localStorage.getItem(LOCAL_PM_LITE_KEY) || "").trim();
          const pro = String(localStorage.getItem(LOCAL_PM_PRO_KEY) || "").trim();
          const contours = String(localStorage.getItem(LOCAL_PM_CONTOURS_KEY) || "").trim();
          if (!contours) return false;
          return effectiveMapStyle === "pro" ? !!pro : !!lite;
        } catch {
          return false;
        }
      })();
      const hasLocalProReady = (() => {
        try {
          if (hasUsableNativeLocalMapHint("pro")) return true;
          const pro = String(localStorage.getItem(LOCAL_PM_PRO_KEY) || "").trim();
          const contours = String(localStorage.getItem(LOCAL_PM_CONTOURS_KEY) || "").trim();
          return !!pro && !!contours;
        } catch {
          return false;
        }
      })();
      logEvent("error", "MAP_STYLE_RUNTIME_ERROR", {
        style: effectiveMapStyle,
        platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        error: errMsg.slice(0, 240),
        online,
        hasLocalMapFiles,
        hasLocalProReady,
        ...tileInfo,
      });
      logEvent("error", "MAP_TILE_ERROR", {
        style: effectiveMapStyle,
        platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        source: useLocalBasePmtiles ? "local" : "remote",
        online,
        ...tileInfo,
        error: errMsg.slice(0, 240),
      });
      const sourceId = String(tileInfo?.sourceId || "").toLowerCase();
      const isContoursError = sourceId === "contours";
      if (isContoursError && effectiveMapStyle !== "openstreet") {
        try {
          const disableKey =
            effectiveMapStyle === "pro"
              ? RUNTIME_DISABLE_CONTOURS_PRO_KEY
              : RUNTIME_DISABLE_CONTOURS_LITE_KEY;
          if (sessionStorage.getItem(disableKey) !== "1") {
            sessionStorage.setItem(disableKey, "1");
            logEvent("warn", "MAP_CONTOURS_DISABLED_AFTER_TILE_ERROR", {
              style: effectiveMapStyle,
              platform: isIOS ? "ios" : isAndroid ? "android" : "web",
              online,
              ...tileInfo,
              error: errMsg.slice(0, 240),
            });
            window.dispatchEvent(new CustomEvent("trekkin:map-style-changed"));
          }
        } catch {}
        return;
      }
      const isLoadFailed = /load failed/i.test(errMsg);
      if (isLoadFailed && effectiveMapStyle !== "openstreet") {
        const fs = window?.Capacitor?.Plugins?.Filesystem;
        const diagLocalFile = async () => {
          try {
            const targetFiles =
              effectiveMapStyle === "lite"
                ? [
                    { path: "portugal-lite.pmtiles", key: LOCAL_PM_LITE_KEY },
                    { path: "portugal-contours.pmtiles", key: LOCAL_PM_CONTOURS_KEY },
                  ]
                : [
                    { path: "portugal-pro.pmtiles", key: LOCAL_PM_PRO_KEY },
                    { path: "portugal-contours.pmtiles", key: LOCAL_PM_CONTOURS_KEY },
                  ];
            for (const file of targetFiles) {
              let savedUrl = "";
              try {
                savedUrl = String(localStorage.getItem(file.key) || "").trim();
              } catch {}
              let uri = "";
              let statSize = null;
              let getUriError = "";
              let statError = "";
              try {
                const uriRes = await fs?.getUri?.({ path: file.path, directory: "DATA" });
                uri = String(uriRes?.uri || "").trim();
              } catch (e) {
                getUriError = String(e?.message || e || "");
              }
              try {
                const st = await fs?.stat?.({ path: file.path, directory: "DATA" });
                const maybeSize = Number(st?.size ?? st?.fileSize ?? 0);
                statSize = Number.isFinite(maybeSize) ? maybeSize : null;
              } catch (e) {
                statError = String(e?.message || e || "");
              }
              logEvent("error", "MAP_LOCAL_FILE_DIAG", {
                style: effectiveMapStyle,
                platform: isIOS ? "ios" : isAndroid ? "android" : "web",
                online,
                sourceId,
                filePath: file.path,
                storageKey: file.key,
                savedUrl: savedUrl.slice(0, 260),
                fsUri: uri.slice(0, 260),
                statSize,
                getUriError: getUriError.slice(0, 260),
                statError: statError.slice(0, 260),
              });
            }
          } catch {}
        };
        diagLocalFile();
      }
      const isDecodeError = /failed to decode data/i.test(errMsg);
      const isRemoteReadError =
        /load failed|failed to decode data|bad response code|etag|fetch|network/i.test(
          errMsg
        );
      if (
        mapSourceMode === "auto" &&
        online &&
        !useLocalBasePmtiles &&
        localNativeStyleAvailable &&
        !isContoursError &&
        isRemoteReadError
      ) {
        logEvent("warn", "MAP_REMOTE_FAILED_SWITCHING_OFFLINE", {
          style: effectiveMapStyle,
          platform: isIOS ? "ios" : isAndroid ? "android" : "web",
          sourceId,
          error: errMsg.slice(0, 240),
          ...tileInfo,
        });
        return;
      }
      const isLocalBaseFailure =
        useLocalBasePmtiles &&
        effectiveMapStyle !== "openstreet" &&
        (isLoadFailed || isDecodeError);
      if (online && isLocalBaseFailure) {
        if (nativeMapRuntime) {
          logEvent("warn", "MAP_LOCAL_SOURCE_ERROR_RETAINED", {
            style: effectiveMapStyle,
            platform: isIOS ? "ios" : isAndroid ? "android" : "web",
            online,
            sourceId,
            error: errMsg.slice(0, 240),
            ...tileInfo,
          });
          return;
        }
        try {
          if (effectiveMapStyle === "pro") {
            localStorage.setItem(FORCE_REMOTE_PRO_KEY, "1");
            localStorage.removeItem(NATIVE_PRO_READY_KEY);
          } else {
            localStorage.setItem(FORCE_REMOTE_LITE_KEY, "1");
            localStorage.removeItem(NATIVE_LITE_READY_KEY);
          }
          logEvent("warn", "MAP_LOCAL_SOURCE_DISABLED_AFTER_ERROR", {
            style: effectiveMapStyle,
            platform: isIOS ? "ios" : isAndroid ? "android" : "web",
            online,
            error: errMsg.slice(0, 240),
            ...tileInfo,
          });
          window.dispatchEvent(new CustomEvent("trekkin:map-style-changed"));
        } catch {}
        return;
      }
      if (!online && isLoadFailed && effectiveMapStyle !== "openstreet") {
        const fs = window?.Capacitor?.Plugins?.Filesystem;
        const isValidLocalPmtilesUrl = (value) => {
          const v = String(value || "").trim();
          if (!v) return false;
          return (
            /^https?:\/\//i.test(v) ||
            /^capacitor:\/\//i.test(v) ||
            /^file:\/\//i.test(v) ||
            /^native-map:\/\//i.test(v) ||
            /^trekkin-map:\/\//i.test(v) ||
            v.startsWith("/_capacitor_file_") ||
            v.includes("localhost/_capacitor_file_")
          );
        };
        const rehydrate = async (fileName, storageKey) => {
          try {
            if (localStorage.getItem(MAPS_CLEARED_LOCAL_KEY)) return false;
            const uri = await fs?.getUri?.({ path: fileName, directory: "DATA" });
            const raw = String(uri?.uri || "").trim();
            if (!raw) return false;
            const nextUrl = window?.Capacitor?.convertFileSrc
              ? window.Capacitor.convertFileSrc(raw)
              : raw;
            const normalized = String(nextUrl || "").trim();
            if (!isValidLocalPmtilesUrl(normalized)) {
              try {
                localStorage.removeItem(storageKey);
              } catch {}
              logEvent("warn", "MAP_LOCAL_URL_INVALID_DROPPED", {
                style: effectiveMapStyle,
                storageKey,
                fileName,
                raw: raw.slice(0, 220),
                converted: normalized.slice(0, 220),
              });
              return false;
            }
            localStorage.setItem(storageKey, normalized);
            return true;
          } catch {
            return false;
          }
        };
        (async () => {
          try {
            let changed = false;
            if (effectiveMapStyle === "lite") {
              changed = (await rehydrate("portugal-lite.pmtiles", LOCAL_PM_LITE_KEY)) || changed;
            } else if (effectiveMapStyle === "pro") {
              changed = (await rehydrate("portugal-pro.pmtiles", LOCAL_PM_PRO_KEY)) || changed;
            }
            changed = (await rehydrate("portugal-contours.pmtiles", LOCAL_PM_CONTOURS_KEY)) || changed;
            try {
              if (effectiveMapStyle === "lite") localStorage.removeItem(FORCE_REMOTE_LITE_KEY);
              if (effectiveMapStyle === "pro") localStorage.removeItem(FORCE_REMOTE_PRO_KEY);
            } catch {}
            if (changed) window.dispatchEvent(new CustomEvent("trekkin:map-style-changed"));
          } catch {}
        })();
        return;
      }
      // Em modo online não alternar source automaticamente.
      if (online && effectiveMapStyle !== "openstreet") {
        return;
      }
      if (isIOS && effectiveMapStyle !== "openstreet" && isDecodeError) {
        const now = Date.now();
        const dedupeKey = `${effectiveMapStyle}:decode`;
        if (
          mapBaseErrorToastRef.current.key === dedupeKey &&
          now - Number(mapBaseErrorToastRef.current.ts || 0) < 20000
        ) {
          return;
        }
        mapBaseErrorToastRef.current = { key: dedupeKey, ts: now };
        showToast("Erro ao carregar o mapa base.");
        return;
      }
      if (!isIOS) return;
      const now = Date.now();
      const dedupeKey = `${effectiveMapStyle}:${errMsg.slice(0, 80)}`;
      if (
        mapBaseErrorToastRef.current.key === dedupeKey &&
        now - Number(mapBaseErrorToastRef.current.ts || 0) < 20000
      ) {
        return;
      }
      mapBaseErrorToastRef.current = { key: dedupeKey, ts: now };
      showToast("Erro ao carregar o mapa base.");
    };
    mapLibreLayer.on?.("maplibre-load", handleMapLibreLoad);
    mapLibreLayer.on?.("maplibre-webgl-context-lost", handleWebglContextLost);
    mapLibreLayer.on?.(
      "maplibre-webgl-context-restored",
      handleWebglContextRestored
    );
    mapLibreLayer.on?.("maplibre-error", handleMapLibreError);
    baseMapLayerRef.current = mapLibreLayer;
    baseMapLayerRef.current.addTo(map);
    if (useRemoteProRasterOverlay) {
      try {
        if (proRasterBaseRef.current && !map.hasLayer(proRasterBaseRef.current)) {
          proRasterBaseRef.current.addTo(map);
        }
        proRasterBaseRef.current?.setOpacity?.(1);
        proRasterBaseRef.current?.setZIndex?.(3);
      } catch {}
    }
    return () => {
      if (!map) return;
      try {
        mapLibreLayer.off?.("maplibre-load", handleMapLibreLoad);
      } catch {}
      try {
        mapLibreLayer.off?.("maplibre-webgl-context-lost", handleWebglContextLost);
      } catch {}
      try {
        mapLibreLayer.off?.(
          "maplibre-webgl-context-restored",
          handleWebglContextRestored
        );
      } catch {}
      try {
        mapLibreLayer.off?.("maplibre-error", handleMapLibreError);
      } catch {}
      if (baseMapLayerRef.current) {
        try {
          map.removeLayer(baseMapLayerRef.current);
        } catch {}
        baseMapLayerRef.current = null;
      }
      try {
        if (
          useRemoteProRasterOverlay &&
          proRasterBaseRef.current &&
          map.hasLayer(proRasterBaseRef.current)
        ) {
          map.removeLayer(proRasterBaseRef.current);
        }
      } catch {}
    };
  }, [
    mapReady,
    effectiveMapStyle,
    isIOS,
    isAndroid,
    nativeMapRuntime,
    useLocalProPmtiles,
    useLocalLitePmtiles,
    useLocalBasePmtiles,
    localNativeStyleAvailable,
    shouldDeferNativeBaseMap,
    mapBaseSourceKey,
    useOnlineFallbackUnderLocalMap,
    useTransparentPmtilesBackground,
  ]);

  useEffect(() => {
    const usesPmtilesBaseMap =
      effectiveMapStyle === "lite" ||
      (effectiveMapStyle === "pro" && useLocalProPmtiles);
    if (!mapReady || !usesPmtilesBaseMap || useOnlineFallbackUnderLocalMap) {
      return undefined;
    }
    const map = mapRef.current;
    if (!map) return undefined;
    const timers = new Set();
    let lastLoggedAt = 0;

    const purgeRemoteRaster = (reason = "sync") => {
      let removedLayers = 0;
      let removedDom = 0;
      try {
        [
          fallbackRasterRef.current,
          liteRasterRef.current,
          proRasterBaseRef.current,
          proRasterShadeRef.current,
        ].forEach((layer) => {
          try {
            if (layer && map.hasLayer?.(layer)) {
              map.removeLayer(layer);
              removedLayers += 1;
            }
          } catch {}
        });
      } catch {}
      try {
        cleanupProHillshadeTiles(map, {
          reason: `local-pro-${reason}`,
          platform: isIOS ? "ios" : isAndroid ? "android" : "web",
        });
      } catch {}
      try {
        const pane = map.getPane?.("tilePane") || map.getPanes?.()?.tilePane;
        const nodes = pane?.querySelectorAll?.(
          ".tk-osm-base-tile,.tk-lite-base-tile,.tk-pro-base-tile,.tk-pro-hillshade"
        );
        nodes?.forEach?.((node) => {
          try {
            node.remove();
            removedDom += 1;
          } catch {}
        });
      } catch {}
      if (removedLayers || removedDom) {
        const now = Date.now();
        if (now - lastLoggedAt > 1500) {
          lastLoggedAt = now;
          logEvent("info", "MAP_PMTILES_RASTER_PURGED", {
            reason,
            platform: isIOS ? "ios" : isAndroid ? "android" : "web",
            style: effectiveMapStyle,
            removedLayers,
            removedDom,
          });
        }
      }
    };

    const schedulePurge = (reason, delayMs = 0) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        purgeRemoteRaster(reason);
      }, delayMs);
      timers.add(timer);
    };

    purgeRemoteRaster("mount");
    schedulePurge("mount-next-frame", 80);
    schedulePurge("mount-settle", 350);
    const handleLayerAdd = () => schedulePurge("layeradd", 0);
    const handleMapSettled = () => schedulePurge("map-settled", 0);
    const handleBaseVisible = () => schedulePurge("base-visible", 0);
    map.on("layeradd", handleLayerAdd);
    map.on("zoomend moveend viewreset", handleMapSettled);
    window.addEventListener("trekkin:map-base-visible", handleBaseVisible);
    return () => {
      map.off("layeradd", handleLayerAdd);
      map.off("zoomend moveend viewreset", handleMapSettled);
      window.removeEventListener("trekkin:map-base-visible", handleBaseVisible);
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, [
    mapReady,
    effectiveMapStyle,
    useLocalProPmtiles,
    useOnlineFallbackUnderLocalMap,
    isIOS,
    isAndroid,
  ]);

  useEffect(() => {
    if (!isAndroid || !mapReady) return undefined;
    let appStateHandle = null;
    const timers = new Set();
    let refreshSequence = 0;
    let lastRefreshSequenceAt = 0;
    let pendingResumeRefresh = null;
    const runRefresh = (reason) => {
      const now = Date.now();
      if (now - lastRefreshSequenceAt < 700) {
        logEvent("debug", "MAP_BASE_ANDROID_RESUME_REFRESH_COALESCED", {
          reason,
          elapsedMs: now - lastRefreshSequenceAt,
          style: effectiveMapStyle,
        });
        return;
      }
      lastRefreshSequenceAt = now;
      const sequence = ++refreshSequence;
      const map = mapRef.current;
      const baseLayer = baseMapLayerRef.current;
      const osmRaster = fallbackRasterRef.current;
      if (!map) return;
      const proRasterBase = proRasterBaseRef.current;
      const delays =
        effectiveMapStyle === "pro" || effectiveMapStyle === "openstreet"
          ? [0]
          : [0, 80, 240, 700, 1500];
      delays.forEach((delayMs) => {
        const timer = window.setTimeout(() => {
          timers.delete(timer);
          if (sequence !== refreshSequence) return;
          let vectorOk = false;
          let rasterOk = false;
          const shouldUseProRaster =
            effectiveMapStyle === "pro" &&
            !useLocalProPmtiles &&
            !shouldDeferNativeBaseMap;
          const proAlreadyMounted =
            shouldUseProRaster &&
            proRasterBase &&
            map.hasLayer(proRasterBase);
          let mountedRasterRedrawn = false;
          let mountedRasterKept = false;
          let mountedRasterInvalidateSkipped = false;
          let mountedRasterLoadedTiles = 0;
          if (proAlreadyMounted) {
            mountedRasterKept = true;
            androidMapBaseRefreshAtRef.current = Date.now();
            try {
              mountedRasterLoadedTiles = Object.values(
                proRasterBase?._tiles || {}
              ).filter((entry) => {
                const tile = entry?.el;
                return !!(
                  tile?.classList?.contains?.("leaflet-tile-loaded") ||
                  (tile?.complete && Number(tile?.naturalWidth || 0) > 0)
                );
              }).length;
            } catch {
              mountedRasterLoadedTiles = 0;
            }
            if (mountedRasterLoadedTiles > 0) {
              mountedRasterInvalidateSkipped = true;
            } else {
              try {
                map.invalidateSize(false);
              } catch {}
              try {
                proRasterBase.redraw?.();
                mountedRasterRedrawn = true;
              } catch {}
            }
            const nudgeStartedAt = Date.now();
            const canNudge =
              nudgeStartedAt - Number(androidMapPaintNudgeAtRef.current || 0) > 900;
            if (canNudge) {
              androidMapPaintNudgeAtRef.current = nudgeStartedAt;
              const pulsePaint = (pulseDelayMs) => {
                const pulseTimer = window.setTimeout(() => {
                  timers.delete(pulseTimer);
                  try {
                    const container = map.getContainer?.();
                    const tilePane = map.getPane?.("tilePane");
                    if (container) {
                      container.classList.add("tk-android-map-paint-pulse");
                      // Force a sync layout read; Android WebView often commits
                      // the pending raster frame only after user input/layout.
                      void container.offsetHeight;
                    }
                    if (tilePane) tilePane.style.opacity = "0.999";
                    recordLayerRef.current?.bringToFront?.();
                    recordLayerRef.current?.redraw?.();
                    window.requestAnimationFrame?.(() => {
                      window.requestAnimationFrame?.(() => {
                        try {
                          if (container) {
                            container.classList.remove("tk-android-map-paint-pulse");
                          }
                          if (tilePane) tilePane.style.opacity = "";
                          if (
                            pulseDelayMs === 0 &&
                            mountedRasterInvalidateSkipped
                          ) {
                            window.dispatchEvent(
                              new CustomEvent("trekkin:map-base-visible", {
                                detail: {
                                  source: "android_resume_preserved_tiles",
                                  loadedTiles: mountedRasterLoadedTiles,
                                },
                              })
                            );
                          }
                        } catch {}
                      });
                    });
                  } catch {}
                }, pulseDelayMs);
                timers.add(pulseTimer);
              };
              pulsePaint(0);
              pulsePaint(120);
              pulsePaint(360);
            logEvent("info", "MAP_ANDROID_RESUME_PAINT_NUDGE", {
              reason,
              delayMs,
              sequence,
              rasterRedrawn: mountedRasterRedrawn,
              loadedTiles: mountedRasterLoadedTiles,
              style: effectiveMapStyle,
            });
            }
          }
          if (!proAlreadyMounted && effectiveMapStyle !== "openstreet") {
            try {
              map.invalidateSize(false);
            } catch {}
            try {
              vectorOk = !!baseLayer?.refresh?.(reason);
            } catch {}
          }
          try {
            if (effectiveMapStyle === "pro") {
              if (!shouldUseProRaster && !useOnlineFallbackUnderLocalMap) {
                if (proRasterBase && map.hasLayer(proRasterBase)) {
                  map.removeLayer(proRasterBase);
                }
                rasterOk = false;
              } else if (proRasterBase && !map.hasLayer(proRasterBase)) {
                proRasterBase.addTo(map);
                rasterOk = true;
              } else {
                rasterOk = !!proRasterBase;
              }
            } else if (effectiveMapStyle === "openstreet") {
              if (osmRaster && !map.hasLayer(osmRaster)) {
                osmRaster.addTo(map);
              }
              rasterOk = !!osmRaster;
            }
          } catch {}
          logEvent("info", "MAP_BASE_ANDROID_RESUME_REFRESH", {
            reason,
            delayMs,
            sequence,
            vectorOk,
            rasterOk,
            proAlreadyMounted,
            mountedRasterRedrawn,
            mountedRasterKept,
            mountedRasterInvalidateSkipped,
            mountedRasterLoadedTiles,
            style: effectiveMapStyle,
          });
        }, delayMs);
        timers.add(timer);
      });
    };
    const scheduleResumeRefresh = (reason) => {
      if (pendingResumeRefresh) {
        pendingResumeRefresh.reason = reason;
        return;
      }
      const pending = { reason, timer: null, finish: null };
      const finish = () => {
        if (pendingResumeRefresh !== pending) return;
        window.removeEventListener(
          "trekkin:android-track-resume-ready",
          finish
        );
        if (pending.timer) {
          window.clearTimeout(pending.timer);
          timers.delete(pending.timer);
        }
        pendingResumeRefresh = null;
        window.requestAnimationFrame?.(() => runRefresh(pending.reason));
      };
      pending.finish = finish;
      pendingResumeRefresh = pending;
      window.addEventListener(
        "trekkin:android-track-resume-ready",
        finish,
        { once: true }
      );
      pending.timer = window.setTimeout(finish, 1200);
      timers.add(pending.timer);
    };
    const handleVisible = () => {
      if (document?.visibilityState === "visible") {
        scheduleResumeRefresh("visibility");
      }
    };
    document?.addEventListener?.("visibilitychange", handleVisible);
    try {
      const app = window?.Capacitor?.Plugins?.App;
      app?.addListener?.("appStateChange", ({ isActive }) => {
        if (isActive) scheduleResumeRefresh("app_state");
      })?.then?.((handle) => {
        appStateHandle = handle;
      });
    } catch {}
    return () => {
      document?.removeEventListener?.("visibilitychange", handleVisible);
      if (pendingResumeRefresh?.finish) {
        window.removeEventListener(
          "trekkin:android-track-resume-ready",
          pendingResumeRefresh.finish
        );
      }
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      try {
        appStateHandle?.remove?.();
      } catch {}
    };
  }, [
    isAndroid,
    mapReady,
    effectiveMapStyle,
    nativeMapRuntime,
    useLocalProPmtiles,
    useOnlineFallbackUnderLocalMap,
    shouldDeferNativeBaseMap,
  ]);

  // Fallback controlado por estilo. Só fica por baixo dos PMTiles quando há rede.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    const osmFallback = fallbackRasterRef.current;
    const liteFallback = liteRasterRef.current;
    const topoFallback = proRasterBaseRef.current;
    if (!map || !osmFallback || !topoFallback) return;
    const syncLowZoomFallback = () => {
      try {
        if (effectiveMapStyle === "openstreet") {
          if (map.hasLayer(topoFallback)) map.removeLayer(topoFallback);
          if (!map.hasLayer(osmFallback)) osmFallback.addTo(map);
          return;
        }

        // Pro tem de usar uma única cartografia visual. Não montar OSM por baixo:
        // quando uma tile Pro atrasa/falha, misturar OSM cria quadrados de outro mapa.
        if (effectiveMapStyle === "pro") {
          if (map.hasLayer(osmFallback)) map.removeLayer(osmFallback);
          const zoom = Number(map.getZoom?.());
          const useGlobalOverview =
            networkOnline === true &&
            Number.isFinite(zoom) &&
            zoom <= 7;
          if (useGlobalOverview) {
            if (!map.hasLayer(topoFallback)) topoFallback.addTo(map);
            topoFallback.setOpacity?.(1);
            topoFallback.setZIndex?.(3);
            return;
          }
          if (shouldDeferNativeBaseMap) {
            if (useOnlineFallbackUnderLocalMap && useLocalProPmtiles) {
              if (!map.hasLayer(topoFallback)) topoFallback.addTo(map);
              topoFallback.setOpacity?.(1);
              topoFallback.setZIndex?.(2);
              return;
            }
            if (map.hasLayer(topoFallback)) map.removeLayer(topoFallback);
            return;
          }
          try {
            topoFallback.setZIndex?.(3);
          } catch {}
          if (
            useOnlineFallbackUnderLocalMap &&
            useLocalProPmtiles &&
            !map.hasLayer(topoFallback)
          ) {
            topoFallback.addTo(map);
          }
          if (
            !useOnlineFallbackUnderLocalMap &&
            map.hasLayer(topoFallback)
          ) {
            map.removeLayer(topoFallback);
          }
          return;
        }

        if (effectiveMapStyle === "lite") {
          if (map.hasLayer(topoFallback)) map.removeLayer(topoFallback);
          if (
            !useOnlineFallbackUnderLocalMap &&
            map.hasLayer(osmFallback)
          ) {
            map.removeLayer(osmFallback);
          }
          if (liteFallback) {
            if (
              !useOnlineFallbackUnderLocalMap &&
              map.hasLayer(liteFallback)
            ) {
              map.removeLayer(liteFallback);
            }
          }
          return;
        }

        if (map.hasLayer(osmFallback)) map.removeLayer(osmFallback);
        if (map.hasLayer(topoFallback)) map.removeLayer(topoFallback);
      } catch {}
    };
    syncLowZoomFallback();
    map.on("zoomend", syncLowZoomFallback);
    return () => {
      try {
        map.off("zoomend", syncLowZoomFallback);
      } catch {}
      try {
        if (map.hasLayer(osmFallback)) map.removeLayer(osmFallback);
        if (map.hasLayer(topoFallback)) map.removeLayer(topoFallback);
      } catch {}
    };
  }, [
    mapReady,
    effectiveMapStyle,
    nativeMapRuntime,
    networkOnline,
    useLocalProPmtiles,
    shouldDeferNativeBaseMap,
    mapBaseSourceKey,
    useOnlineFallbackUnderLocalMap,
  ]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    const shade = proRasterShadeRef.current;
    if (!map || !shade) return;
    const syncProHillshade = () => {
      try {
        if (
          effectiveMapStyle === "pro" &&
          !useLocalProPmtiles &&
          !(nativeMapRuntime && hasUsableNativeLocalMapHint("pro")) &&
          !shouldDeferNativeBaseMap
        ) {
          if (!map.hasLayer(shade)) shade.addTo(map);
          shade.setOpacity?.(0.16);
          shade.setZIndex?.(3);
          return;
        }
        if (map.hasLayer(shade)) map.removeLayer(shade);
      } catch {}
    };
    syncProHillshade();
    map.on("zoomend", syncProHillshade);
    return () => {
      try {
        map.off("zoomend", syncProHillshade);
      } catch {}
      try {
        if (map.hasLayer(shade)) map.removeLayer(shade);
      } catch {}
    };
  }, [
    mapReady,
    effectiveMapStyle,
    nativeMapRuntime,
    useLocalProPmtiles,
    shouldDeferNativeBaseMap,
  ]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    const isIOSLocal =
      typeof navigator !== "undefined" &&
      (/iPad|iPhone|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine !== false;
    const hasLocalPro = (() => {
      try {
        if (hasUsableNativeLocalMapHint("pro")) return true;
        const pro = String(localStorage.getItem(LOCAL_PM_PRO_KEY) || "").trim();
        const contours = String(localStorage.getItem(LOCAL_PM_CONTOURS_KEY) || "").trim();
        const forceRemote = localStorage.getItem(FORCE_REMOTE_PRO_KEY) === "1";
        return !!pro && !!contours && !forceRemote;
      } catch {
        return false;
      }
    })();
    const maxZoom = isIOS || isAndroid ? MOBILE_MAP_MAX_ZOOM : 19;
    try {
      map.setMaxZoom(maxZoom);
      if (map.getZoom() > maxZoom) {
        map.setZoom(maxZoom, { animate: false });
      }
    } catch {}
  }, [mapReady, effectiveMapStyle]);

  useEffect(() => {
    const clearRuntimeOverrideIfOnline = () => {
      try {
        if (typeof navigator !== "undefined" && navigator.onLine === true) {
          if (sessionStorage.getItem(RUNTIME_MAP_STYLE_OVERRIDE_KEY)) {
            sessionStorage.removeItem(RUNTIME_MAP_STYLE_OVERRIDE_KEY);
            window.dispatchEvent(new CustomEvent("trekkin:map-style-changed"));
          }
        }
      } catch {}
    };
    clearRuntimeOverrideIfOnline();
    window.addEventListener("online", clearRuntimeOverrideIfOnline);
    return () => window.removeEventListener("online", clearRuntimeOverrideIfOnline);
  }, []);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!map.getPane("navGridPane")) {
      map.createPane("navGridPane");
      const pane = map.getPane("navGridPane");
      pane.style.zIndex = 410;
    }
    if (!map.getPane("navGridRoutePane")) {
      map.createPane("navGridRoutePane");
      const pane = map.getPane("navGridRoutePane");
      pane.style.zIndex = 430;
    }
    if (!map.getPane("drawTrailPane")) {
      map.createPane("drawTrailPane");
      const pane = map.getPane("drawTrailPane");
      pane.style.zIndex = 420;
    }
    if (!map.getPane("waypointsPane")) {
      map.createPane("waypointsPane");
      const pane = map.getPane("waypointsPane");
      pane.style.zIndex = 620;
      pane.style.pointerEvents = "auto";
    }
    if (!map.getPane("trailMarkersPane")) {
      map.createPane("trailMarkersPane");
      const pane = map.getPane("trailMarkersPane");
      pane.style.zIndex = 610;
      pane.style.pointerEvents = "auto";
    }
    if (!navGridLayerRef.current) {
      navGridLayerRef.current = L.layerGroup([], { pane: "navGridPane" }).addTo(
        map
      );
    }
    if (!drawLayerRef.current) {
      drawLayerRef.current = L.polyline([], {
        color: "#0d6efd",
        weight: 4,
        dashArray: "12 6",
        lineJoin: "round",
        smoothFactor: 0,
        pane: "drawTrailPane",
        renderer: canvasRendererRef.current || undefined,
      }).addTo(map);
    }
    if (!editLayerRef.current) {
      editLayerRef.current = L.polyline([], {
        color: "#0d6efd",
        weight: 4,
        lineJoin: "round",
        smoothFactor: 0,
        renderer: canvasRendererRef.current || undefined,
      }).addTo(map);
    }
    if (!drawDotsLayerRef.current) {
      drawDotsLayerRef.current = L.layerGroup([], {
        pane: "drawTrailPane",
      }).addTo(map);
    }
    refreshDrawLayer();
    navGridLayerRef.current.clearLayers();
    if (!isAdminUser) return;
    if (isIOS && trailEditActive) return;
    if (!navGridVisible) return;
    const createActionButton = (label, bgColor) => {
      const btn = L.DomUtil.create("button", "");
      btn.textContent = label;
      btn.style.flex = "1";
      btn.style.minWidth = "82px";
      btn.style.padding = "6px 8px";
      btn.style.borderRadius = "6px";
      btn.style.border = "none";
      btn.style.color = "white";
      btn.style.fontSize = "12px";
      btn.style.cursor = "pointer";
      btn.style.background = bgColor;
      return btn;
    };

    const dispatchSegmentFocus = (segmentId) => {
      if (typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("trekkin:navgrid-focus-segment", {
          detail: { segmentId },
        })
      );
    };

    navGridSegments.forEach((segment) => {
      const points = Array.isArray(segment.points)
        ? segment.points
            .map((point) =>
              point && Number.isFinite(point.lat) && Number.isFinite(point.lon)
                ? [point.lat, point.lon]
                : null
            )
            .filter(Boolean)
        : [];
      if (points.length < 2) return;
      const segmentColor = getActivityModeColor(segment.activityMode);
      const isPreviewSegment = String(segment?.id || "") === String(navGridPreviewSegmentId || "");
      if (segment.premium) {
        L.polyline(points, {
          color: "#ff9800",
          weight: 7,
          opacity: 0.72,
          interactive: false,
          renderer: canvasRendererRef.current || undefined,
        }).addTo(navGridLayerRef.current);
      }
      const polyline = L.polyline(points, {
        color: isPreviewSegment ? "#00b86b" : segmentColor,
        weight: isPreviewSegment ? 9 : segment.premium ? 4 : 3.5,
        opacity: isPreviewSegment ? 1 : 0.9,
        renderer: canvasRendererRef.current || undefined,
      }).addTo(navGridLayerRef.current);
      if (mapPickPointActive) {
        const pickLine = L.polyline(points, {
          color: segmentColor,
          weight: 20,
          opacity: 0.001,
          bubblingMouseEvents: false,
          renderer: canvasRendererRef.current || undefined,
        }).addTo(navGridLayerRef.current);
        pickLine.on("click", (event) => {
          const lat = Number(event?.latlng?.lat);
          const lon = Number(event?.latlng?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
          L.DomEvent.stopPropagation(event);
          // Reutiliza o clique normal do mapa para servir tanto o seletor
          // genérico como o criador de rotas (início, fim e intermédios).
          map.fire("click", {
            latlng: L.latLng(lat, lon),
            originalEvent: null,
            trekkinNavGridPick: true,
          });
        });
      }
      if (canManageNavGrid && segment?.id) {
        const popup = L.DomUtil.create("div", "tk-navsegment-popup");
        popup.style.display = "flex";
        popup.style.flexDirection = "column";
        popup.style.gap = "6px";
        popup.style.width = "100%";

        const title = L.DomUtil.create("div", "", popup);
        title.textContent = segment.name || "Trecho";
        title.style.fontWeight = "600";
        title.style.color = "#2c6b3f";
        title.style.fontSize = "13px";
        title.style.whiteSpace = "nowrap";
        title.style.textOverflow = "ellipsis";
        title.style.overflow = "hidden";

        const modeLabel = L.DomUtil.create("div", "", popup);
        modeLabel.textContent = `Modo: ${getActivityModeLabel(
          normalizeActivityMode(segment.activityMode, ACTIVITY_MODE_WALK)
        )}`;
        modeLabel.style.fontSize = "11px";
        modeLabel.style.fontWeight = "700";
        modeLabel.style.color = "#475569";

        const row = L.DomUtil.create("div", "", popup);
        row.style.display = "flex";
        row.style.gap = "6px";
        row.style.flexWrap = "wrap";
        row.style.justifyContent = "space-between";

        const classifyButton = createActionButton("Classificar", "#198754");
        L.DomEvent.on(classifyButton, "click", (event) => {
          L.DomEvent.stopPropagation(event);
          setClassifySegmentDraft({
            id: segment.id,
            name: segment.name || "Trecho",
            activityMode: normalizeActivityMode(
              segment.activityMode,
              ACTIVITY_MODE_WALK
            ),
          });
          polyline.closePopup();
        });

        const editButton = createActionButton("Editar nós", "#0d6efd");
        L.DomEvent.on(editButton, "click", (event) => {
          L.DomEvent.stopPropagation(event);
          dispatchSegmentFocus(segment.id);
          handleEditSegment(segment);
          polyline.closePopup();
        });

        const mergeButton = createActionButton("Fundir pontos", "#6f42c1");
        L.DomEvent.on(mergeButton, "click", (event) => {
          L.DomEvent.stopPropagation(event);
          const selected = mergeSegmentsRef.current;
          if (!selected.length) {
            mergeSegmentsRef.current = [segment.id];
            showToast("Seleciona o segundo trecho. Apenas as duas extremidades mais próximas serão fundidas.");
          } else if (!selected.includes(segment.id)) {
            const nextSelection = [...selected, segment.id];
            handleMergeSegments(nextSelection.slice(0, 2));
            mergeSegmentsRef.current = [];
          } else {
            mergeSegmentsRef.current = [];
            showToast("Seleção de fusão cancelada.", "info");
          }
          polyline.closePopup();
        });

        const optimizeButton = createActionButton("Otimizar", "#0f766e");
        L.DomEvent.on(optimizeButton, "click", (event) => {
          L.DomEvent.stopPropagation(event);
          handleOptimizeSegment(segment);
          polyline.closePopup();
        });

        const deleteButton = createActionButton("Eliminar", "#dc3545");
        L.DomEvent.on(deleteButton, "click", (event) => {
          L.DomEvent.stopPropagation(event);
          handleDeleteSegment(segment);
          polyline.closePopup();
        });

        row.appendChild(classifyButton);
        row.appendChild(editButton);
        row.appendChild(optimizeButton);
        row.appendChild(mergeButton);
        row.appendChild(deleteButton);
        L.DomEvent.disableClickPropagation(popup);
        polyline.bindPopup(popup, {
          minWidth: 200,
          className: "tk-navsegment-popup",
        });
        polyline.on("click", (event) => {
          if (window.__trekkinPickPoint) {
            const lat = Number(event?.latlng?.lat);
            const lon = Number(event?.latlng?.lng);
            if (Number.isFinite(lat) && Number.isFinite(lon)) {
              L.DomEvent.stopPropagation(event);
              map.fire("click", {
                latlng: L.latLng(lat, lon),
                originalEvent: null,
                trekkinNavGridPick: true,
              });
            }
            return;
          }
          if (event?.latlng) {
            polyline.openPopup(event.latlng);
          } else {
            polyline.openPopup();
          }
        });
      }
    });
    if (navGridLayerProcessingIdRef.current) {
      const processingId = navGridLayerProcessingIdRef.current;
      navGridLayerProcessingIdRef.current = null;
      const finish = () => {
        window.dispatchEvent(
          new CustomEvent("trekkin:processing", {
            detail: { active: false, id: processingId },
          })
        );
      };
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(() => window.requestAnimationFrame(finish));
      } else {
        window.setTimeout(finish, 32);
      }
    }
  }, [
    mapReady,
    isAdminUser,
    isIOS,
    trailEditActive,
    navGridVisible,
    mapPickPointActive,
    navGridSegments,
    navGridPreviewSegmentId,
    refreshDrawLayer,
    handleEditSegment,
    handleDeleteSegment,
    handleOptimizeSegment,
    handleMergeSegments,
  ]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!segmentStartsLayerRef.current) {
      segmentStartsLayerRef.current = L.layerGroup().addTo(map);
    }
    const layer = segmentStartsLayerRef.current;
    layer.clearLayers();
    const rows = Array.isArray(segmentStartsPreview) ? segmentStartsPreview : [];
    if (!rows.length) return;
    const bootIconUrl = resolveBootIconUrl();
    const icon = L.divIcon({
      className: "",
      html: `
        <div style="
          width:20px;height:20px;border-radius:50%;
          border:2px solid #d4a017;
          background:#fff7d6;
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 1px 3px rgba(0,0,0,0.3);
        ">
          <img src="${bootIconUrl}" alt="" draggable="false"
            style="
              width:12px;height:12px;display:block;object-fit:contain;
              filter: brightness(0) saturate(100%);
              -webkit-filter: brightness(0) saturate(100%);
              pointer-events:none;-webkit-user-drag:none;user-select:none;
            " />
        </div>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
      popupAnchor: [0, -12],
    });
    rows.forEach((seg) => {
      const lat = Number(seg?.lat);
      const lon = Number(seg?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      const marker = L.marker([lat, lon], { icon }).addTo(layer);
      const top10 = Array.isArray(seg?.top10) ? seg.top10 : [];
      const lines = top10.length
        ? top10
            .slice(0, 10)
            .map((r) => {
              const rk = Math.max(1, Math.floor(Number(r?.rank) || 0));
              const nm = String(r?.name || "Utilizador");
              return `<div>#${rk} ${nm} - <b>${formatSecondsLabel(Number(r?.durationSec) || 0)}</b></div>`;
            })
            .join("")
        : "<div>Sem classificações ainda.</div>";
      marker.bindPopup(
        `<div style="min-width:220px">
          <div style="font-weight:700;color:#166534;margin-bottom:6px">${String(seg?.name || "Segmento")}</div>
          <div style="font-size:12px;color:#111827">${lines}</div>
        </div>`,
        { className: "tk-waypoint-popup" }
      );
    });
  }, [mapReady, segmentStartsPreview]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!navGridRouteLayerRef.current) {
      navGridRouteLayerRef.current = L.polyline([], {
        color: "#007bff",
        weight: 4,
        opacity: 0.85,
        dashArray: "6 6",
        smoothFactor: 0,
        pane: "navGridRoutePane",
        renderer: canvasRendererRef.current || undefined,
      }).addTo(map);
    }
    if (!navGridRouteExternalLayerRef.current) {
      navGridRouteExternalLayerRef.current = L.layerGroup([], {
        pane: "navGridRoutePane",
      }).addTo(map);
    }
    if (!navGridRouteDirectionLayerRef.current) {
      navGridRouteDirectionLayerRef.current = L.layerGroup([], {
        pane: "trailDirectionPane",
      }).addTo(map);
    }
    if (isIOS && trailEditActive) {
      navGridRouteLayerRef.current.setLatLngs([]);
      navGridRouteExternalLayerRef.current.clearLayers();
      navGridRouteDirectionLayerRef.current.clearLayers();
      return;
    }
    if (
      Array.isArray(navGridRouteColoredSegments) &&
      navGridRouteColoredSegments.length
    ) {
      navGridRouteLayerRef.current.setLatLngs([]);
    } else if (!Array.isArray(navGridRoutePath) || !navGridRoutePath.length) {
      navGridRouteLayerRef.current.setLatLngs([]);
    } else {
      const routeCoords = navGridRoutePath
        .map((point) => {
          if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
            return null;
          }
          return [point.lat, point.lon];
        })
        .filter(Boolean);
      if (routeCoords.length) {
        navGridRouteLayerRef.current.setLatLngs(routeCoords);
      } else {
        navGridRouteLayerRef.current.setLatLngs([]);
      }
    }

    navGridRouteExternalLayerRef.current.clearLayers();
    if (
      Array.isArray(navGridRouteColoredSegments) &&
      navGridRouteColoredSegments.length
    ) {
      navGridRouteColoredSegments.forEach((segment, idx) => {
        const segCoords = (Array.isArray(segment?.path) ? segment.path : [])
          .map((point) => {
            if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
              return null;
            }
            return [point.lat, point.lon];
          })
          .filter(Boolean);
        if (segCoords.length > 1) {
          L.polyline(segCoords, {
            color: String(segment?.color || "#007bff"),
            weight: 4,
            opacity: 0.9,
            dashArray: idx === 0 ? "6 6" : "10 6",
            smoothFactor: 0,
            pane: "navGridRoutePane",
            renderer: canvasRendererRef.current || undefined,
          }).addTo(navGridRouteExternalLayerRef.current);
        }
      });
    }
    if (
      Array.isArray(navGridRouteExternalSegments) &&
      navGridRouteExternalSegments.length
    ) {
      navGridRouteExternalSegments.forEach((segment) => {
        const externalCoords = (Array.isArray(segment) ? segment : [])
          .map((point) => {
            if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) {
              return null;
            }
            return [point.lat, point.lon];
          })
          .filter(Boolean);
        if (externalCoords.length > 1) {
          L.polyline(externalCoords, {
            color: "#6b7280",
            weight: 4,
            opacity: 0.9,
            dashArray: "10 8",
            smoothFactor: 0,
            pane: "navGridRoutePane",
            renderer: canvasRendererRef.current || undefined,
          }).addTo(navGridRouteExternalLayerRef.current);
        }
      });
    }

    const directionPath =
      !planActive && Array.isArray(navGridRoutePath) && navGridRoutePath.length > 1
        ? navGridRoutePath
        : (!planActive && Array.isArray(navGridRouteColoredSegments)
            ? navGridRouteColoredSegments
            : []
          ).flatMap((segment) =>
            Array.isArray(segment?.path) ? segment.path : []
          );
    renderDirectionMarkers(navGridRouteDirectionLayerRef, directionPath, {
      color: "#007bff",
      pane: "trailDirectionPane",
      targetArrows: 48,
      maxArrows: 96,
      size: 16,
    });
  }, [
    mapReady,
    isIOS,
    trailEditActive,
    navGridRoutePath,
    navGridRouteExternalSegments,
    navGridRouteColoredSegments,
    planActive,
    renderDirectionMarkers,
    directionZoomTick,
  ]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!navGridNodesLayerRef.current) {
      navGridNodesLayerRef.current = L.layerGroup().addTo(map);
    }
    navGridNodesLayerRef.current.clearLayers();
    if (!isAdminUser) return;
    if (isIOS && trailEditActive) return;
    if (!navGridNodesVisible) return;
    // Enquanto desenha um novo trecho da navgrid, não renderizar os nós globais
    // (todos os pontos de todos os trechos), para evitar sobrecarga visual/perf.
    const isDrawingNavGridSegment =
      navGridDrawActive &&
      drawContextRef.current === "navgrid" &&
      !navGridEditNodesActive &&
      !navGridAddNodeActive &&
      !navGridDeleteNodesActive;
    if (isDrawingNavGridSegment) return;

    const nodes = Array.isArray(navGridNodesGraph?.nodes)
      ? navGridNodesGraph.nodes
      : [];
    if (!nodes.length) return;

    const hasFilter = Array.isArray(navGridNodesFilter) && navGridNodesFilter.length;
    const allowedNodeIds = new Set();
    if (hasFilter) {
      navGridNodesFilter.forEach((segmentId) => {
        const ids = navGridNodesGraph?.segmentNodeIds?.[segmentId];
        if (Array.isArray(ids)) {
          ids.forEach((nodeId) => {
            if (nodeId) allowedNodeIds.add(nodeId);
          });
        }
      });
    }

    // Os segmentos com geometria simplificada são densificados de 10 em 10 m
    // apenas no grafo de routing. Esses pontos técnicos não existem no trecho
    // guardado e não devem aparecer como nós editáveis. Caso contrário, um
    // trecho já otimizado parece continuar cheio de pontos e arrastar um deles
    // acaba por atuar por proximidade sobre uma geometria que o utilizador não
    // criou. Em edição focada, mostrar exatamente os vértices persistidos.
    const storedEditNodes = navGridEditNodesActive && hasFilter
      ? navGridSegments
          .filter((segment) => navGridNodesFilter.includes(segment?.id))
          .flatMap((segment) =>
            (Array.isArray(segment?.points) ? segment.points : []).map((point, pointIndex) => ({
              id: `stored-${segment.id}-${pointIndex}`,
              segmentId: segment.id,
              pointIndex,
              lat: Number(point?.lat),
              lon: Number(point?.lon ?? point?.lng),
              ...(Number.isFinite(Number(point?.ele ?? point?.alt ?? point?.elevation))
                ? { ele: Number(point.ele ?? point.alt ?? point.elevation) }
                : {}),
            }))
          )
      : null;
    const nodesToShow = Array.isArray(storedEditNodes)
      ? storedEditNodes
      : hasFilter
      ? nodes.filter((node) => allowedNodeIds.has(node.id))
      : nodes;
    if (!nodesToShow.length) return;

    if (navGridEditNodesActive) {
      const makeIcon = (mode) => {
        const color =
          mode === "delete"
            ? "#b02a37"
            : mode === "add"
            ? "#0d6efd"
            : mode === "split"
            ? "#ff8f00"
            : "#1b6e40";
        return L.divIcon({
          className: "tk-navnode-icon",
          html: `<span style="
            display:block;
            width:16px;
            height:16px;
            border-radius:50%;
            border:2px solid rgba(255,255,255,0.9);
            background:${color};
            box-shadow:0 0 8px rgba(0,0,0,0.3);
          "></span>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });
      };

      nodesToShow.forEach((node) => {
        if (!Number.isFinite(node?.lat) || !Number.isFinite(node?.lon)) return;
        const marker = L.marker([node.lat, node.lon], {
          icon: makeIcon(navGridNodeMode),
          draggable: navGridNodeMode === "move",
          interactive: true,
          riseOnHover: true,
        });

        marker.on("add", () => {
          const element = marker.getElement?.();
          if (element) {
            element.style.cursor = navGridNodeMode === "add" ? "crosshair" : "default";
          }
        });

        marker.on("mousedown", (event) => L.DomEvent.stopPropagation(event));
        marker.on("click", (event) => {
          L.DomEvent.stopPropagation(event);
          if (navGridNodeMode === "delete") {
            Promise.resolve(handleDeleteNode(node)).catch((err) => {
              logEvent("error", "NAVGRID_DELETE_NODE_UNHANDLED", {
                message: String(err?.message || err),
              });
            });
            return;
          }
          if (navGridNodeMode === "split") {
            Promise.resolve(handleSplitNode(node)).catch((err) => {
              logEvent("error", "NAVGRID_SPLIT_NODE_UNHANDLED", {
                message: String(err?.message || err),
              });
            });
          }
        });

        if (navGridNodeMode === "move") {
          let dragStartLatLng = null;
          marker.on("dragstart", () => {
            dragStartLatLng = marker.getLatLng?.() || null;
            try {
              map.dragging.disable();
            } catch (err) {
              logEvent("warn", "NAVGRID_NODE_DRAGSTART_FAIL", {
                message: String(err?.message || err),
              });
            }
          });
          marker.on("dragend", (event) => {
            try {
              map.dragging.enable();
            } catch (err) {
              logEvent("warn", "NAVGRID_NODE_DRAGEND_ENABLE_FAIL", {
                message: String(err?.message || err),
              });
            }
            const endLatLng = event?.target?.getLatLng?.() || marker.getLatLng?.() || null;
            Promise.resolve(handleMoveNode(node, endLatLng, dragStartLatLng)).catch((err) => {
              logEvent("error", "NAVGRID_MOVE_NODE_UNHANDLED", {
                message: String(err?.message || err),
              });
            });
          });
        }

        navGridNodesLayerRef.current.addLayer(marker);
      });
      return;
    }

    nodesToShow.forEach((node) => {
      if (!Number.isFinite(node?.lat) || !Number.isFinite(node?.lon)) return;
      L.circleMarker([node.lat, node.lon], {
        radius: 5,
        weight: 2,
        color: "#ffffff",
        fillColor: "#2c6b3f",
        fillOpacity: 0.9,
        interactive: false,
      }).addTo(navGridNodesLayerRef.current);
    });
  }, [
    mapReady,
    isAdminUser,
    isIOS,
    trailEditActive,
    navGridDrawActive,
    navGridNodesVisible,
    navGridNodesGraph,
    navGridSegments,
    navGridNodesFilter,
    navGridEditNodesActive,
    navGridNodeMode,
    handleDeleteNode,
    handleSplitNode,
    handleMoveNode,
  ]);

  useEffect(() => {
    if (!mapReady) return undefined;
    const container = mapRef.current?.getContainer?.();
    if (!container) return undefined;
    const previousCursor = container.style.cursor;
    if (navGridEditNodesActive) {
      container.style.cursor = navGridNodeMode === "add" ? "crosshair" : "default";
    }
    return () => {
      container.style.cursor = previousCursor;
    };
  }, [mapReady, navGridEditNodesActive, navGridNodeMode]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    const waypointPane = "waypointsPane";
    const layerOptions = waypointPane ? { pane: waypointPane } : undefined;
    if (!waypointsLayerRef.current) {
      waypointsLayerRef.current = L.layerGroup([], layerOptions).addTo(map);
    }
    const layer = waypointsLayerRef.current;
    const iosSafeMode =
      typeof window !== "undefined" &&
      (window.__TREKKIN_IOS_SAFE_MODE__ === true ||
        window.localStorage.getItem("trekkin.ios.safeMode") === "1");
    if (iosSafeMode) {
      layer.clearLayers();
      return;
    }
    if (
      isIOS &&
      (trailEditActive ||
        navGridEditNodesActive ||
        navGridAddNodeActive ||
        navGridDeleteNodesActive)
    ) {
      layer.clearLayers();
      return;
    }
    if (!waypointsVisible && !exclusiveFocusedWaypoint) {
      layer.clearLayers();
      return;
    }
    const hasPlanContext =
      !!planActive ||
      !!planPickMode ||
      Number.isFinite(Number(planStart?.lat)) ||
      Number.isFinite(Number(planStart?.lon ?? planStart?.lng)) ||
      Number.isFinite(Number(planEnd?.lat)) ||
      Number.isFinite(Number(planEnd?.lon ?? planEnd?.lng)) ||
      (Array.isArray(planWaypoints) && planWaypoints.length > 0);
    const filters = new Set(
      waypointCategories.filter((cat) => typeof cat === "string" && cat)
    );
    const markers = waypointsMarkersRef.current;
    const waypointsForLayer = exclusiveFocusedWaypoint
      ? [exclusiveFocusedWaypoint]
      : normalizedWaypoints;
    const waypointCount = Array.isArray(waypointsForLayer) ? waypointsForLayer.length : 0;
    const simpleWaypointMode = isIOS || ((isIOS || isAndroid) && waypointCount >= 80);
    const viewportBounds = null;
    const visibleKeys = new Set();
    const canAddToPlan = canPlanRoute && hasPlanContext;
    const makeWaypointIcon = (color, iconName) => {
      const safeColor = String(color || "#28a745");
      let safeIcon = String(iconName || "").trim();
      if (isIOS && /\.webp$/i.test(safeIcon)) {
        safeIcon = safeIcon.replace(/\.webp$/i, ".png");
      }
      const isSvgIcon = safeIcon.toLowerCase().endsWith(".svg");
      const hasIcon = !!safeIcon;
      const iconUrl = hasIcon ? `${base}icons/${safeIcon}` : "";
      if (simpleWaypointMode) {
        // Mobile com muitos waypoints: marcador leve, mas mantendo ícone visível.
        const html = safeIcon
          ? `
          <div class="tk-waypoint-marker-visual" style="
            width:18px;height:18px;border-radius:50%;
            border:2px solid ${safeColor};
            background:#ffffff;
            display:flex;align-items:center;justify-content:center;
            box-shadow:0 1px 2px rgba(0,0,0,0.25);
          ">
            <img src="${iconUrl}" alt="" draggable="false"
              style="width:11px;height:11px;display:block;object-fit:contain;pointer-events:none;-webkit-user-drag:none;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none;" />
          </div>`
          : `
          <div class="tk-waypoint-marker-visual" style="
            width:14px;height:14px;border-radius:50%;
            border:2px solid #ffffff;
            background:${safeColor};
            box-shadow:0 1px 2px rgba(0,0,0,0.25);
          "></div>`;
        return L.divIcon({
          className: "",
          html,
          iconSize: safeIcon ? [18, 18] : [14, 14],
          iconAnchor: safeIcon ? [9, 9] : [7, 7],
          popupAnchor: [0, -8],
        });
      }
      const html = safeIcon
        ? `
        <div class="tk-waypoint-marker-visual" style="
          width:18px;height:18px;border-radius:50%;
          border:2px solid ${safeColor};
          background:#ffffff;
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 1px 2px rgba(0,0,0,0.25);
        ">
          ${
            isSvgIcon
              ? `<span style="
            width:11px;height:11px;display:block;background:${safeColor};
            -webkit-mask-image:url('${iconUrl}');
            -webkit-mask-repeat:no-repeat;
            -webkit-mask-position:center;
            -webkit-mask-size:contain;
            mask-image:url('${iconUrl}');
            mask-repeat:no-repeat;
            mask-position:center;
            mask-size:contain;
          "></span>`
              : `<img src="${iconUrl}" alt="" draggable="false" style="width:11px;height:11px;display:block;object-fit:contain;pointer-events:none;-webkit-user-drag:none;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none;" />`
          }
        </div>`
        : `
        <div class="tk-waypoint-marker-visual" style="width:14px;height:14px;border-radius:50%;background:${safeColor};border:2px solid #ffffff;box-shadow:0 1px 2px rgba(0,0,0,0.25);"></div>`;
      return L.divIcon({
        className: "",
        html,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        popupAnchor: [0, -10],
      });
    };
    const getKey = (wp) =>
      wp?.id ||
      `lat:${Number(wp?.lat || 0)}|lon:${Number(wp?.lon || 0)}|name:${
        wp?.name || ""
      }`;
    const updateDistanceLine = (entry, wp) => {
      if (!entry?.popupRoot) return;
      const last = planLastRef.current;
      const hasLast =
        last && Number.isFinite(last.lat) && Number.isFinite(last.lon);
      if (!canAddToPlan || !hasLast) {
        if (entry.distanceEl) {
          entry.distanceEl.textContent = "";
          entry.distanceEl.style.display = "none";
        }
        return;
      }
      const dist = haversineMeters(last, wp);
      if (!entry.distanceEl) {
        const distanceLine = L.DomUtil.create("div", "tk-waypoint-popup-muted", entry.popupRoot);
        distanceLine.style.fontSize = "12px";
        distanceLine.style.color = "#666";
        entry.distanceEl = distanceLine;
      }
      entry.distanceEl.textContent = `${language === "en" ? "Distance" : "Distância"}: ${(dist / 1000).toFixed(2)} km`;
      entry.distanceEl.style.display = "block";
    };
    const isEntryPopupOpen = (entry) => {
      try {
        return !!entry?.marker?.isPopupOpen?.() || !!entry?.hitMarker?.isPopupOpen?.();
      } catch {
        return false;
      }
    };
    const updateUserDistanceLine = (entry, wp) => {
      if (!entry?.popupRoot) return;
      const userLat = userPositionRef.current?.lat;
      const userLon = userPositionRef.current?.lon;
      const hasUser = Number.isFinite(userLat) && Number.isFinite(userLon);
      if (!hasUser) {
        if (entry.userDistanceEl) {
          entry.userDistanceEl.textContent = "";
          entry.userDistanceEl.style.display = "none";
        }
        return;
      }
      const dist = haversineMeters({ lat: userLat, lon: userLon }, wp);
      if (!entry.userDistanceEl) {
        const distanceLine = L.DomUtil.create("div", "tk-waypoint-popup-muted", entry.popupRoot);
        distanceLine.style.fontSize = "12px";
        distanceLine.style.color = "#666";
        entry.userDistanceEl = distanceLine;
      }
      entry.userDistanceEl.textContent = `${language === "en" ? "Current distance" : "Distância atual"}: ${(dist / 1000).toFixed(2)} km`;
      entry.userDistanceEl.style.display = "block";
    };
    const createPopup = (entry, wp) => {
      const pendingTargetId = navWaypointPending?.target?.id || null;
      const popup = L.DomUtil.create("div", "tk-waypoint-popup");
      const blockPartnerContentInProgress = () => {
        if (wp.partnerContentInProgress !== true || isAdminMode) return false;
        showToast(
          language === "en"
            ? "Content under construction. Coming soon!"
            : "Conteúdo em construção! Brevemente disponível!",
          "info"
        );
        return true;
      };
      popup.style.display = "flex";
      popup.style.flexDirection = "column";
      popup.style.gap = "6px";
      popup.style.width = "100%";

      const title = L.DomUtil.create("div", "tk-waypoint-popup-title", popup);
      title.textContent = language === "en"
        ? (wp.translations?.en?.name || wp.name || "Waypoint")
        : (wp.name || "Waypoint");
      title.style.fontWeight = "600";
      title.style.color = "#2c6b3f";
      title.style.fontSize = "13px";
      title.style.whiteSpace = "nowrap";
      title.style.textOverflow = "ellipsis";
      title.style.overflow = "hidden";
      title.style.cursor = "grab";
      title.style.touchAction = "none";
      title.style.userSelect = "none";
      title.title = "Arrasta para mover o mapa";

      const imageUrlRaw =
        wp.imageUrl || wp.image || wp.photoUrl || wp.photo || wp.coverImage || null;
      const imageCandidates = disableWaypointImagesForStability
        ? []
        : buildWaypointImageCandidates(imageUrlRaw, isIOS);
      let lazyImageHolder = null;
      let lazyImageLoaded = false;
      if (imageCandidates.length) {
        lazyImageHolder = L.DomUtil.create("div", "", popup);
        lazyImageHolder.style.width = "100%";
        lazyImageHolder.style.maxWidth = "280px";
        lazyImageHolder.style.alignSelf = "center";
      }

      const meta = L.DomUtil.create("div", "tk-waypoint-popup-meta", popup);
      meta.style.display = "flex";
      meta.style.flexDirection = "column";
      meta.style.gap = "4px";

      const coordsLine = L.DomUtil.create("div", "tk-waypoint-popup-muted", meta);
      coordsLine.style.fontSize = "12px";
      coordsLine.style.color = "#666";
      coordsLine.textContent = `${wp.lat?.toFixed?.(5) ?? wp.lat}, ${wp.lon?.toFixed?.(5) ?? wp.lon}`;

      const descText = language === "en"
        ? (wp.translations?.en?.desc || wp.translations?.en?.description || wp.translations?.en?.details || wp.translations?.en?.text || wp.translations?.en?.note || "")
        : (wp.desc || wp.description || wp.details || "");
      if (descText) {
        const descLine = L.DomUtil.create("div", "tk-waypoint-popup-description", meta);
        descLine.style.fontSize = "12px";
        descLine.style.color = "#555";
        descLine.style.whiteSpace = "pre-wrap";
        descLine.style.overflowWrap = "anywhere";
        descLine.textContent = descText;
      }

      const telText = readWaypointContactValue(wp, ["tel", "phone", "telefone"]);
      const emailText = readWaypointContactValue(wp, ["email", "mail", "eMail"]);
      const webText = readWaypointContactValue(wp, ["www", "web", "website", "url"]);
      createWaypointContactButtons(meta, {
        tel: telText,
        email: emailText,
        web: webText,
      }, language);

      const categoryLabel = language === "en"
          ? (wp.translations?.en?.categoryName || ({ Alojamentos: "Accommodation", Refeições: "Food" }[wp.categoryName]) || wp.categoryName || wp.categoryId)
          : (wp.categoryName || wp.categoryId);
      if (categoryLabel) {
        const categoryLine = L.DomUtil.create("div", "tk-waypoint-popup-muted", meta);
        categoryLine.style.fontSize = "12px";
        categoryLine.style.color = "#666";
        categoryLine.textContent = categoryLabel;
      }

      if (pendingTargetId && pendingTargetId === wp.id) {
        const pendingLine = L.DomUtil.create("div", "tk-waypoint-popup-pending", meta);
        pendingLine.style.fontSize = "12px";
        pendingLine.style.color = "#f59f0a";
        pendingLine.style.fontWeight = "600";
        pendingLine.textContent = language === "en" ? "Heading to the network…" : "A caminho da rede...";
      }

      let partnerActionsRow = null;
      if (wp.accommodationPartner === true && (wp.partnerVisible === true || isAdminMode)) {
        const partnerBadge = L.DomUtil.create("div", "tk-waypoint-popup-partner", meta);
        partnerBadge.style.fontSize = "12px";
        partnerBadge.style.fontWeight = "700";
        partnerBadge.style.color = "#16803c";
        partnerBadge.style.display = "flex";
        partnerBadge.style.alignItems = "center";
        partnerBadge.style.gap = "6px";
        const partnerLabel = L.DomUtil.create("span", "", partnerBadge);
        const discount = Number(wp.partnerAppDiscountPercent);
        partnerLabel.textContent = Number.isInteger(discount) && discount > 0 && discount <= 100
          ? (language === "en" ? `Trekkin partner (${discount}% discount)` : `Parceiro Trekkin (${discount}% Desconto)`)
          : (language === "en" ? "Trekkin partner" : "Parceiro Trekkin");
        if (Number.isInteger(discount) && discount > 0 && discount <= 100) {
          partnerLabel.title = language === "en"
            ? "Discount when requesting availability through the app"
            : "Desconto ao pedir disponibilidade através da app";
        }
        const verifiedBadge = L.DomUtil.create("span", "", partnerBadge);
        verifiedBadge.textContent = "✓";
        verifiedBadge.title = language === "en" ? "Verified partner" : "Parceiro verificado";
        verifiedBadge.setAttribute("aria-label", language === "en" ? "Verified partner" : "Parceiro verificado");
        verifiedBadge.style.width = "17px";
        verifiedBadge.style.height = "17px";
        verifiedBadge.style.borderRadius = "50%";
        verifiedBadge.style.display = "inline-flex";
        verifiedBadge.style.alignItems = "center";
        verifiedBadge.style.justifyContent = "center";
        verifiedBadge.style.background = "#16803c";
        verifiedBadge.style.color = "#fff";
        verifiedBadge.style.fontSize = "11px";
        verifiedBadge.style.fontWeight = "900";
        verifiedBadge.style.lineHeight = "1";
        const partnerRow = L.DomUtil.create("div", "", popup);
        partnerActionsRow = partnerRow;
        partnerRow.style.display = "flex";
        partnerRow.style.flexDirection = "column";
        partnerRow.style.gap = "6px";
        const addPartnerButton = (label, mode, featured = false) => {
          const button = L.DomUtil.create("button", `tk-waypoint-popup-action${featured ? " tk-waypoint-popup-action-featured" : ""}`, partnerRow);
          button.type = "button";
          button.style.width = "100%";
          button.style.minHeight = "42px";
          button.style.padding = "8px 12px";
          button.style.border = featured ? "1px solid #16803c" : "1px solid #d0d5dd";
          button.style.borderRadius = "9px";
          button.style.background = featured ? "#16803c" : "#fff";
          button.style.color = featured ? "#fff" : "#176b39";
          button.style.fontSize = "13px";
          button.style.fontWeight = "700";
          button.style.display = "flex";
          button.style.alignItems = "center";
          button.style.gap = "10px";
          const icon = L.DomUtil.create("img", "", button);
          icon.src = `${base}icons/${mode === "trails" ? "trail.svg" : mode === "recommendations" ? "waypoints.svg" : "calendar-plus.svg"}`;
          icon.alt = "";
          icon.style.width = "20px";
          icon.style.height = "20px";
          if (featured) icon.style.filter = "brightness(0) invert(1)";
          const text = L.DomUtil.create("span", "", button);
          text.textContent = label;
          text.style.flex = "1";
          text.style.textAlign = "left";
          const arrow = L.DomUtil.create("span", "", button);
          arrow.textContent = "›";
          arrow.style.fontSize = "24px";
          arrow.style.lineHeight = "1";
          L.DomEvent.on(button, "click", (event) => {
            L.DomEvent.stop(event);
            if ((mode === "trails" || mode === "recommendations") && blockPartnerContentInProgress()) {
              return;
            }
            if (mode === "recommendations") {
              setPartnerRecommendationCategory("all");
              setPartnerRecommendationIndex(0);
              setPartnerRecommendationsPanel(wp);
              closePopupHard();
              return;
            }
            window.dispatchEvent(new CustomEvent("trekkin:accommodation-partner", { detail: { mode, partner: wp } }));
          });
        };
        addPartnerButton(language === "en" ? "Nearby trails" : "Trilhos próximos", "trails");
        addPartnerButton(language === "en" ? "Recommended Places" : "Locais Recomendados", "recommendations");
        addPartnerButton(language === "en" ? "Request availability" : "Pedir disponibilidade", "availability", true);
      }

      const hasActions =
        canNavigateWaypoints ||
        canAddToPlan ||
        (!canAddToPlan && canManageWaypoints) ||
        (stopPlanPickActive === true) ||
        (stageStartWaypointPickActive === true);
      const row = hasActions ? L.DomUtil.create("div", "", popup) : null;
      if (row) {
        row.style.display = "flex";
        row.style.flexDirection = "column";
        row.style.gap = "6px";
      }

      const actionIcons = {
        Navegar: "navigation.svg",
        "Iniciar caminhada": "boot.svg",
        "Iniciar pedalada": "bicycle.svg",
        "Start hike": "boot.svg",
        "Start ride": "bicycle.svg",
        Adicionar: "plus.svg",
        Início: "flag.svg",
        Final: "stop-nav-waypoint.svg",
        "Definir paragem": "location.svg",
        "Início etapas": "flag.svg",
      };
      const createActionButton = (label, _bgColor, targetRow = row, admin = false) => {
        if (!targetRow) return null;
        const btn = L.DomUtil.create("button", `tk-waypoint-popup-action${admin ? " tk-waypoint-popup-action-admin" : ""}${label === "Eliminar" ? " tk-waypoint-popup-action-danger" : ""}`, targetRow);
        btn.style.flex = admin ? "1 1 0" : "none";
        btn.style.width = admin ? "auto" : "100%";
        btn.style.minHeight = admin ? "38px" : "42px";
        btn.style.padding = admin ? "7px 10px" : "8px 12px";
        btn.style.borderRadius = admin ? "0" : "9px";
        btn.style.border = admin ? "none" : "1px solid #d0d5dd";
        btn.style.color = label === "Eliminar" ? "#dc3545" : admin ? "#475467" : "#176b39";
        btn.style.fontSize = "12px";
        btn.style.fontWeight = "700";
        btn.style.cursor = "pointer";
        btn.style.background = "transparent";
        btn.style.display = "flex";
        btn.style.alignItems = "center";
        btn.style.justifyContent = admin ? "center" : "flex-start";
        btn.style.gap = "9px";
        const iconName = admin
          ? (label === "Eliminar" ? "trash.svg" : "edit.svg")
          : (actionIcons[label] || "location.svg");
        const icon = L.DomUtil.create("img", "", btn);
        icon.src = `${base}icons/${iconName}`;
        icon.alt = "";
        icon.style.width = "19px";
        icon.style.height = "19px";
        const text = L.DomUtil.create("span", "", btn);
        text.textContent = label;
        if (!admin) {
          text.style.flex = "1";
          text.style.textAlign = "left";
          const arrow = L.DomUtil.create("span", "", btn);
          arrow.textContent = "›";
          arrow.style.fontSize = "23px";
          arrow.style.lineHeight = "1";
        }
        return btn;
      };
      const closePopupHard = () => {
        try {
          entry.marker?.closePopup?.();
        } catch {}
        try {
          entry.hitMarker?.closePopup?.();
        } catch {}
        try {
          mapRef.current?.closePopup?.();
        } catch {}
      };
      const ensurePlanRoutePermission = () => {
        const allowed = !!can("PanelNav", "plan_route");
        if (!allowed) {
          showPlanUpgradeToast("❗ O teu plano não permite planear rotas.", "warning");
        }
        return allowed;
      };
      const ensureWaypointAccessForPlan = () => {
        if (canUsePremiumSegments) return true;
        const st = usePanelsStore.getState();
        const prevAnchor =
          (Array.isArray(st?.planWaypoints) && st.planWaypoints.length
            ? st.planWaypoints[st.planWaypoints.length - 1]
            : st?.planStart) ||
          userPosition ||
          null;
        const originLat = Number(prevAnchor?.lat);
        const originLon = Number(prevAnchor?.lon);
        const targetLat = Number(wp?.lat);
        const targetLon = Number(wp?.lon);
        if (
          !Number.isFinite(originLat) ||
          !Number.isFinite(originLon) ||
          !Number.isFinite(targetLat) ||
          !Number.isFinite(targetLon)
        ) {
          return true;
        }
        const fullGrid = loadNavGrid();
        const allowedGrid = filterNavGridByAccess(fullGrid, {
          allowPremium: false,
          activityMode,
        });
        if (!allowedGrid?.segments?.length || !fullGrid?.segments?.length) return true;
        const mergeMeters =
          Number(allowedGrid.mergeMeters ?? allowedGrid.routeMergeMeters) || 30;
        const canRouteViaNearestNodes = (gridBase) => {
          if (!gridBase?.segments?.length) return false;
          const graphBase = buildNavGridGraph(gridBase, {
            mergeMeters,
            mergeEndpointsOnly: false,
            activityMode,
          });
          const fromNear = findNearestNodeInGraph(graphBase, {
            lat: originLat,
            lon: originLon,
          });
          const toNear = findNearestNodeInGraph(graphBase, {
            lat: targetLat,
            lon: targetLon,
          });
          const fromNode =
            fromNear?.node &&
            Number.isFinite(Number(fromNear.node.lat)) &&
            Number.isFinite(Number(fromNear.node.lon))
              ? { lat: Number(fromNear.node.lat), lon: Number(fromNear.node.lon) }
              : null;
          const toNode =
            toNear?.node &&
            Number.isFinite(Number(toNear.node.lat)) &&
            Number.isFinite(Number(toNear.node.lon))
              ? { lat: Number(toNear.node.lat), lon: Number(toNear.node.lon) }
              : null;
          if (!fromNode || !toNode) return false;
          const res = computeNavGridRoute(gridBase, fromNode, toNode, {
            mergeMeters,
            maxSnapMeters: 1200,
          });
          return !!res?.ok;
        };
        const routeAllowed = computeNavGridRoute(
          allowedGrid,
          { lat: originLat, lon: originLon },
          { lat: targetLat, lon: targetLon },
          { mergeMeters, maxSnapMeters: 400 }
        );
        if (routeAllowed?.ok || canRouteViaNearestNodes(allowedGrid)) return true;
        const routePremium = computeNavGridRoute(
          fullGrid,
          { lat: originLat, lon: originLon },
          { lat: targetLat, lon: targetLon },
          { mergeMeters, maxSnapMeters: 400 }
        );
        if (routePremium?.ok || canRouteViaNearestNodes(fullGrid)) {
          showToast(
            "Para aceder a este local apenas com guia presencial ou guia online.",
            "warning"
          );
          return false;
        }
        return true;
      };

      if (canNavigateWaypoints) {
        const navButton = createActionButton(
          waypointStartLabel(activityMode, language),
          "#ff9800"
        );
        if (navButton) {
          L.DomEvent.on(navButton, "click", (event) => {
            L.DomEvent.stopPropagation(event);
            navigateToWaypoint(wp);
            closePopupHard();
          });
        }
      }

      const canAddNavWaypointFromMap =
        navWaypointAddMode === true && !!can("PanelNav", "add_nav_waypoint");
      if (canAddNavWaypointFromMap) {
        const addNavButton = createActionButton("Adicionar", "#198754");
        if (addNavButton) {
          L.DomEvent.on(addNavButton, "click", (event) => {
            L.DomEvent.stopPropagation(event);
            const payload = {
              id: wp.id,
              name: wp.name || "Waypoint",
              lat: wp.lat,
              lon: wp.lon,
            };
            const samePoint = (a, b) => {
              const aLat = Number(a?.lat);
              const aLon = Number(a?.lon ?? a?.lng);
              const bLat = Number(b?.lat);
              const bLon = Number(b?.lon ?? b?.lng);
              return (
                Number.isFinite(aLat) &&
                Number.isFinite(aLon) &&
                Number.isFinite(bLat) &&
                Number.isFinite(bLon) &&
                Math.abs(aLat - bLat) < 1e-7 &&
                Math.abs(aLon - bLon) < 1e-7
              );
            };
            try {
              const state = usePanelsStore.getState();
              const currentTarget = state?.navWaypointTarget || null;
              const pendingTarget = state?.navWaypointPending?.target || null;
              if (currentTarget) {
                const sameById =
                  String(currentTarget?.id || "").trim() &&
                  String(payload?.id || "").trim() &&
                  String(currentTarget.id) === String(payload.id);
                if (!sameById && !samePoint(currentTarget, payload)) {
                  enqueueNavWaypoint(payload);
                }
              } else if (pendingTarget) {
                const sameById =
                  String(pendingTarget?.id || "").trim() &&
                  String(payload?.id || "").trim() &&
                  String(pendingTarget.id) === String(payload.id);
                if (!sameById && !samePoint(pendingTarget, payload)) {
                  enqueueNavWaypoint(payload);
                }
              } else {
                setNavWaypointTarget(payload);
              }
            } catch {
              if (navWaypointTarget?.id) {
                enqueueNavWaypoint(payload);
              } else {
                setNavWaypointTarget(payload);
              }
            }
            setNavWaypointAddMode(false);
            showToast("Waypoint adicionado à navegação.", "success");
            setActivePanel("nav");
            closePopupHard();
          });
        }
      }

      const canAddStopPlanStage = stopPlanPickActive === true;
      const canPickStopPlanStart = stageStartWaypointPickActive === true;

      if (canAddToPlan) {
        const stPlan = usePanelsStore.getState();
        const hasPlanStartSet =
          Number.isFinite(Number(stPlan?.planStart?.lat)) &&
          Number.isFinite(Number(stPlan?.planStart?.lon ?? stPlan?.planStart?.lng));
        const hasPlanEndSet =
          Number.isFinite(Number(stPlan?.planEnd?.lat)) &&
          Number.isFinite(Number(stPlan?.planEnd?.lon ?? stPlan?.planEnd?.lng));
        const addButton = createActionButton("Adicionar", "#198754");
        if (addButton) {
          L.DomEvent.on(addButton, "click", (event) => {
            L.DomEvent.stopPropagation(event);
            try {
              if (!ensurePlanRoutePermission()) return;
              if (!ensureWaypointAccessForPlan()) return;
              const payload = {
                id: wp.id,
                name: wp.name || "Waypoint",
                lat: wp.lat,
                lon: wp.lon,
              };
              addPlanWaypoint(payload);
              if (typeof window !== "undefined") {
                window.setTimeout(() => {
                  window.dispatchEvent(
                    new CustomEvent("trekkin:plan-route-added", {
                      detail: { waypoint: payload, prompt: true },
                    })
                  );
                }, 0);
              }
              showToast("Waypoint adicionado ao percurso.", "success");
              closePopupHard();
            } catch (err) {
              showToast("Erro ao adicionar waypoint ao percurso.", "error");
              try {
                console.error("[PLAN_ROUTE] add waypoint failed", err);
              } catch {}
            }
          });
        }

        const startButton = !hasPlanStartSet
          ? createActionButton("Início", "#0d6efd")
          : null;
        if (startButton) {
          L.DomEvent.on(startButton, "click", (event) => {
            L.DomEvent.stopPropagation(event);
            try {
              if (!ensurePlanRoutePermission()) return;
              const lat = Number(wp.lat);
              const lon = Number(wp.lon);
              setPlanStart({
                lat,
                lon,
                source: "waypoint",
                waypointId: wp.id || null,
              });
              const st = usePanelsStore.getState();
              const sameEnd =
                Number.isFinite(Number(st?.planEnd?.lat)) &&
                Number.isFinite(Number(st?.planEnd?.lon ?? st?.planEnd?.lng)) &&
                Number(st.planEnd.lat) === lat &&
                Number(st.planEnd.lon ?? st.planEnd.lng) === lon;
              if (sameEnd) setPlanEnd(null);
              setPlanPickMode(null);
              window.dispatchEvent(new CustomEvent("trekkin:disable-pickpoint"));
              showToast("Início definido por waypoint.", "success");
              closePopupHard();
            } catch (err) {
              showToast("Erro ao definir início por waypoint.", "error");
              try {
                console.error("[PLAN_ROUTE] set start failed", err);
              } catch {}
            }
          });
        }

        const endButton = !hasPlanEndSet
          ? createActionButton("Final", "#6f42c1")
          : null;
        if (endButton) {
          L.DomEvent.on(endButton, "click", (event) => {
            L.DomEvent.stopPropagation(event);
            try {
              if (!ensurePlanRoutePermission()) return;
              const st = usePanelsStore.getState();
              const startLat = Number(st?.planStart?.lat);
              const startLon = Number(st?.planStart?.lon ?? st?.planStart?.lng);
              const lat = Number(wp.lat);
              const lon = Number(wp.lon);
              if (!Number.isFinite(startLat) || !Number.isFinite(startLon)) {
                showToast("Define primeiro o ponto inicial.", "warning");
                return;
              }
              if (startLat === lat && startLon === lon) {
                showToast("O ponto final não pode ser igual ao início.", "warning");
                return;
              }
              setPlanEnd({
                lat,
                lon,
                source: "waypoint",
                waypointId: wp.id || null,
              });
              setPlanPickMode(null);
              window.dispatchEvent(new CustomEvent("trekkin:disable-pickpoint"));
              showToast("Final definido por waypoint.", "success");
              closePopupHard();
            } catch (err) {
              showToast("Erro ao definir final por waypoint.", "error");
              try {
                console.error("[PLAN_ROUTE] set end failed", err);
              } catch {}
            }
          });
        }
      }

      if (canAddStopPlanStage) {
        const stageButton = createActionButton("Definir paragem", "#0d6efd");
        if (stageButton) {
          L.DomEvent.on(stageButton, "click", (event) => {
            L.DomEvent.stopPropagation(event);
            const appendFromWaypoint = window.__trekkinAppendStageFromWaypoint;
            if (typeof appendFromWaypoint === "function") {
              appendFromWaypoint({
                lat: Number(wp?.lat),
                lon: Number(wp?.lon),
                name: String(wp?.name || "Waypoint").trim(),
                waypointId: String(wp?.id || "").trim() || null,
                desc: wp?.desc || wp?.description || wp?.details || "",
                tel: wp?.tel || wp?.phone || wp?.telefone || "",
                email: wp?.email || wp?.mail || wp?.eMail || "",
                www: wp?.www || wp?.web || wp?.website || wp?.url || "",
                imageUrl: wp?.imageUrl || wp?.image || wp?.photoUrl || wp?.photo || "",
              });
            }
            closePopupHard();
          });
        }
      }

      if (canPickStopPlanStart) {
        const startStageButton = createActionButton("Início etapas", "#198754");
        if (startStageButton) {
          L.DomEvent.on(startStageButton, "click", (event) => {
            L.DomEvent.stopPropagation(event);
            const pickStart = window.__trekkinSelectStagePlanStartFromWaypoint;
            if (typeof pickStart === "function") {
              const ok = pickStart({
                lat: Number(wp?.lat),
                lon: Number(wp?.lon),
                name: String(wp?.name || "Waypoint").trim(),
                waypointId: String(wp?.id || "").trim() || null,
              });
              if (ok) {
                try {
                  delete window.__trekkinSelectStagePlanStartFromWaypoint;
                } catch {}
                showToast("Início das etapas definido por waypoint.", "success");
              }
            }
            closePopupHard();
          });
        }
      }

      if (
        isAdminMode &&
        partnerRecommendationAdminTarget?.id &&
        wp.accommodationPartner !== true
      ) {
        const isAlreadyRecommended = partnerRecommendationIds.has(String(wp.id));
        const recommendationRow = L.DomUtil.create("div", "", popup);
        recommendationRow.style.display = "flex";
        recommendationRow.style.flexDirection = "column";
        recommendationRow.style.gap = "6px";
        const recommendationButton = createActionButton(
          isAlreadyRecommended
            ? (language === "en" ? "Remove from recommended list" : "Remover da lista recomendada")
            : (language === "en" ? "Add waypoint to recommended list" : "Adicionar waypoint à lista"),
          "#198754",
          recommendationRow
        );
        if (recommendationButton) {
          recommendationButton.classList.add("tk-waypoint-popup-action-recommendation");
          L.DomEvent.on(recommendationButton, "click", async (event) => {
            L.DomEvent.stop(event);
            try {
              if (isAlreadyRecommended) await removePartnerRecommendation(wp);
              else await addPartnerRecommendation(wp);
              closePopupHard();
            } catch (error) {
              console.warn("Falha ao atualizar recomendação do parceiro", error);
              showToast(
                language === "en"
                  ? "Could not update the recommended list."
                  : "Não foi possível atualizar a lista recomendada.",
                "error"
              );
            }
          });
        }
      }

      let waypointAdminRow = null;
      if (!canAddToPlan && canManageWaypoints && isAdminMode) {
        const adminRow = L.DomUtil.create("div", "tk-waypoint-popup-admin-row", popup);
        waypointAdminRow = adminRow;
        adminRow.style.display = "flex";
        adminRow.style.gap = "8px";
        adminRow.style.marginTop = "8px";
        adminRow.style.paddingTop = "8px";
        adminRow.style.borderTop = "1px solid #d0d5dd";
        const editButton = createActionButton("Editar", "#0d6efd", adminRow, true);
        if (!editButton) return;
        L.DomEvent.on(editButton, "click", (event) => {
          L.DomEvent.stopPropagation(event);
          handleEditWaypoint(wp);
          closePopupHard();
        });

        const deleteButton = createActionButton("Eliminar", "#dc3545", adminRow, true);
        if (!deleteButton) return;
        L.DomEvent.on(deleteButton, "click", (event) => {
          L.DomEvent.stopPropagation(event);
          handleDeleteWaypoint(wp);
          closePopupHard();
        });
      }

      const galleryImages = [...new Set([
        ...(Array.isArray(wp.galleryImages) ? wp.galleryImages : []),
        wp.imageUrl || wp.coverImage || wp.image || wp.photoUrl || wp.photo || null,
      ].map((item) => String(item || "").trim()).filter(Boolean))];
      const youtubeUrl = String(wp.youtubeUrl || wp.videoUrl || "").trim();
      if (galleryImages.length || youtubeUrl) {
        const galleryRow = L.DomUtil.create("div", "tk-waypoint-popup-gallery-row", popup);
        galleryRow.style.display = "flex";
        galleryRow.style.flexDirection = "column";
        galleryRow.style.gap = "6px";
        if (partnerActionsRow?.lastElementChild) {
          partnerActionsRow.insertBefore(galleryRow, partnerActionsRow.lastElementChild);
        }
        const galleryButton = createActionButton(
          language === "en" ? "Gallery" : "Galeria",
          "#198754",
          galleryRow
        );
        if (galleryButton) {
          const galleryIcon = galleryButton.querySelector("img");
          if (galleryIcon) galleryIcon.src = `${base}icons/gallery.svg`;
          L.DomEvent.on(galleryButton, "click", (event) => {
            L.DomEvent.stop(event);
            const overlay = document.createElement("div");
            overlay.className = "tk-waypoint-gallery-overlay";
            overlay.style.cssText = "position:fixed;inset:0;z-index:20050;background:rgba(0,0,0,.72);display:flex;align-items:center;justify-content:center;padding:16px";
            const modal = document.createElement("div");
            modal.className = "tk-waypoint-gallery-modal tk-theme-card";
            modal.style.cssText = "width:min(620px,100%);max-height:86vh;overflow:auto;background:var(--panel-bg,#fff);color:var(--text-color,#1f2937);border-radius:16px;padding:16px;display:grid;gap:12px";
            const header = document.createElement("div");
            header.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:12px";
            const heading = document.createElement("strong");
            heading.textContent = `${language === "en" ? "Gallery" : "Galeria"} · ${wp.name || "Waypoint"}`;
            const close = document.createElement("button");
            close.type = "button";
            close.textContent = "×";
            close.setAttribute("aria-label", language === "en" ? "Close gallery" : "Fechar galeria");
            close.style.cssText = "border:0;background:transparent;color:inherit;font-size:28px;line-height:1;cursor:pointer";
            header.append(heading, close);
            modal.appendChild(header);
            if (youtubeUrl) {
              const videoId = extractYouTubeVideoId(youtubeUrl);
              if (videoId) {
                const watchUrl = `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
                const hasNativeBridge = Boolean(window?.webkit?.messageHandlers?.nativeBridge?.postMessage);
                const capacitor = window?.Capacitor;
                const isNative = hasNativeBridge || Boolean(
                  capacitor?.isNativePlatform?.() ||
                  (typeof capacitor?.getPlatform === "function" && capacitor.getPlatform() !== "web")
                );
                if (isNative) {
                  const preview = document.createElement("button");
                  preview.type = "button";
                  preview.className = "tk-waypoint-youtube-preview";
                  preview.setAttribute("aria-label", language === "en" ? "Play video on YouTube" : "Reproduzir vídeo no YouTube");
                  preview.style.backgroundImage = `url(https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg)`;
                  const play = document.createElement("span");
                  play.className = "tk-waypoint-youtube-play";
                  play.textContent = "▶";
                  const label = document.createElement("span");
                  label.className = "tk-waypoint-youtube-label";
                  label.textContent = language === "en" ? "Watch partner video" : "Ver vídeo do parceiro";
                  preview.append(play, label);
                  preview.addEventListener("click", () => {
                    if (hasNativeBridge) {
                      window.webkit.messageHandlers.nativeBridge.postMessage({ action: "openExternalUrl", url: watchUrl });
                      return;
                    }
                    const launcher = capacitor?.Plugins?.AppLauncher;
                    if (launcher?.openUrl) {
                      launcher.openUrl({ url: watchUrl }).catch(() => openExternalUrl(watchUrl, { forceBrowser: true }));
                      return;
                    }
                    openExternalUrl(watchUrl, { forceBrowser: true });
                  });
                  modal.appendChild(preview);
                } else {
                  const iframe = document.createElement("iframe");
                  iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?rel=0`;
                  iframe.title = `YouTube · ${wp.name || "Waypoint"}`;
                  iframe.referrerPolicy = "strict-origin-when-cross-origin";
                  iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
                  iframe.allowFullscreen = true;
                  iframe.style.cssText = "width:100%;aspect-ratio:16/9;border:0;border-radius:12px;background:#000";
                  modal.appendChild(iframe);
                }
              }
            }
            galleryImages.forEach((url, index) => {
              const img = document.createElement("img");
              img.src = url;
              img.alt = `${wp.name || "Waypoint"} · ${index + 1}`;
              img.loading = "lazy";
              img.style.cssText = "display:block;width:100%;max-height:70vh;object-fit:contain;border-radius:12px;background:#111";
              modal.appendChild(img);
            });
            overlay.appendChild(modal);
            const dismiss = () => overlay.remove();
            close.addEventListener("click", dismiss);
            overlay.addEventListener("click", (clickEvent) => { if (clickEvent.target === overlay) dismiss(); });
            document.body.appendChild(overlay);
            closePopupHard();
          });
        }
      }

      if (wp.assistancePartner === true) {
        const serviceRow = L.DomUtil.create("div", "tk-waypoint-popup-service-row", popup);
        serviceRow.style.display = "flex";
        serviceRow.style.flexDirection = "column";
        serviceRow.style.gap = "6px";
        const serviceButton = createActionButton(
          language === "en" ? "Request assistance" : "Pedir assistência",
          "#16803c",
          serviceRow
        );
        if (serviceButton) {
          serviceButton.style.background = "#16803c";
          serviceButton.style.color = "#fff";
          const icon = serviceButton.querySelector("img");
          if (icon) {
            icon.src = `${base}icons/gear.svg`;
            icon.style.filter = "brightness(0) invert(1)";
          }
          L.DomEvent.on(serviceButton, "click", (event) => {
            L.DomEvent.stop(event);
            closePopupHard();
            setActivePanel("bikeService", { mode: "new", waypoint: wp });
          });
        }
      }

      if (wp.rentalPartner === true || (wp.accommodationRentalEnabled === true && wp.rentalPartnerUid)) {
        const rentalRow = L.DomUtil.create("div", "tk-waypoint-popup-service-row", popup);
        const rentalButton = createActionButton(
          language === "en" ? "Rent a bicycle" : "Alugar bicicleta",
          "#16803c",
          rentalRow
        );
        if (rentalButton) {
          rentalButton.style.background = "#16803c";
          rentalButton.style.color = "#fff";
          L.DomEvent.on(rentalButton, "click", (event) => {
            L.DomEvent.stop(event);
            if (blockPartnerContentInProgress()) return;
            closePopupHard();
            setActivePanel("bikeRental", { mode: "new", waypoint: wp, partnerUid: wp.rentalPartnerUid || wp.partnerUserUid });
          });
        }
      }

      // As ações administrativas ficam no fim do cartão, depois da galeria.
      if (waypointAdminRow) popup.appendChild(waypointAdminRow);

      L.DomEvent.disableClickPropagation(popup);
      entry.popupRoot = popup;
      entry.titleEl = title;
      entry.distanceEl = null;
      entry.userDistanceEl = null;
      if (entry.onPopupOpen) {
        entry.marker.off("popupopen", entry.onPopupOpen);
        entry.hitMarker?.off?.("popupopen", entry.onPopupOpen);
      }
      entry.onPopupOpen = () => {
        const popupElement = popup.closest?.(".leaflet-popup");
        if (popupElement && !popupElement.__trekkinDragEnabled) {
          popupElement.__trekkinDragEnabled = true;
          popupElement.style.touchAction = "none";
          popupElement.style.cursor = "grab";
          let dragState = null;
          let suppressClick = false;
          const finishDrag = (event) => {
            if (!dragState) return;
            try {
              if (event?.pointerId != null) popupElement.releasePointerCapture?.(event.pointerId);
            } catch {}
            suppressClick = dragState.moved;
            dragState = null;
            title.style.cursor = "grab";
            popupElement.style.cursor = "grab";
          };
          L.DomEvent.on(popupElement, "pointerdown", (event) => {
            if (event.button != null && event.button !== 0) return;
            const eventTarget = event.target;
            if (
              eventTarget instanceof Element &&
              eventTarget.closest(
                "button, a, input, select, textarea, [role='button'], [data-tk-waypoint-web='1']"
              )
            ) {
              return;
            }
            event.stopPropagation();
            dragState = {
              pointerId: event.pointerId,
              startX: event.clientX,
              startY: event.clientY,
              lastX: event.clientX,
              lastY: event.clientY,
              moved: false,
            };
            title.style.cursor = "grabbing";
            popupElement.style.cursor = "grabbing";
            try { popupElement.setPointerCapture?.(event.pointerId); } catch {}
          });
          L.DomEvent.on(popupElement, "pointermove", (event) => {
            if (!dragState || event.pointerId !== dragState.pointerId) return;
            event.stopPropagation();
            const rawX = event.clientX - dragState.startX;
            const rawY = event.clientY - dragState.startY;
            if (!dragState.moved && Math.hypot(rawX, rawY) < 5) return;
            dragState.moved = true;
            event.preventDefault();
            const deltaX = event.clientX - dragState.lastX;
            const deltaY = event.clientY - dragState.lastY;
            dragState.lastX = event.clientX;
            dragState.lastY = event.clientY;
            map.panBy([-deltaX, -deltaY], { animate: false });
          });
          L.DomEvent.on(popupElement, "pointerup pointercancel lostpointercapture", finishDrag);
          popupElement.addEventListener("click", (event) => {
            if (!suppressClick) return;
            suppressClick = false;
            event.preventDefault();
            event.stopImmediatePropagation();
          }, true);
        }
        if (lazyImageHolder && !lazyImageLoaded && imageCandidates.length) {
          const img = L.DomUtil.create("img", "", lazyImageHolder);
          let imageIndex = 0;
          img.src = imageCandidates[imageIndex] || "";
          img.alt = "";
          img.style.width = "100%";
          img.style.maxWidth = "280px";
          img.style.aspectRatio = "1 / 1";
          img.style.height = "auto";
          img.style.objectFit = "cover";
          img.style.borderRadius = "8px";
          img.style.alignSelf = "center";
          img.decoding = "async";
          img.onerror = () => {
            imageIndex += 1;
            if (imageIndex < imageCandidates.length) {
              img.src = imageCandidates[imageIndex];
              return;
            }
            try {
              img.remove();
            } catch {}
          };
          lazyImageLoaded = true;
        }
        updateDistanceLine(entry, wp);
        updateUserDistanceLine(entry, wp);
      };
      entry.marker.on("popupopen", entry.onPopupOpen);
      entry.hitMarker?.on?.("popupopen", entry.onPopupOpen);
      updateDistanceLine(entry, wp);
      updateUserDistanceLine(entry, wp);
      entry.marker.bindPopup(popup, {
        minWidth: 180,
        className: "tk-waypoint-popup",
        autoPan: false,
        closeOnClick: true,
      });
      entry.hitMarker?.bindPopup?.(popup, {
        minWidth: 180,
        className: "tk-waypoint-popup",
        autoPan: false,
        closeOnClick: true,
      });
        entry.popup = popup;
        entry.canAddToPlan = canAddToPlan;
        entry.canAddNavWaypointFromMap = canAddNavWaypointFromMap;
        entry.canManageWaypoints = canManageWaypoints;
        entry.isAdminMode = isAdminMode;
        entry.canNavigateWaypoints = canNavigateWaypoints;
        entry.activityMode = activityMode;
        entry.language = language;
        entry.canAddStopPlanStage = canAddStopPlanStage;
        entry.canPickStopPlanStart = canPickStopPlanStart;
        entry.pendingTargetId = pendingTargetId;
        entry.partnerRecommendationTargetId = String(partnerRecommendationAdminTarget?.id || "");
        entry.isPartnerRecommended = partnerRecommendationIds.has(String(wp.id));
        entry.waypointId = wp.id || null;
        entry.lat = Number(wp.lat);
        entry.lon = Number(wp.lon);
      };

    waypointsForLayer.forEach((wp) => {
      if (!Number.isFinite(wp?.lat) || !Number.isFinite(wp?.lon)) return;
      if (viewportBounds && !viewportBounds.contains([Number(wp.lat), Number(wp.lon)])) {
        return;
      }
      if (!exclusiveFocusedWaypoint && filters.size) {
        if (!wp.categoryId) return;
        if (!filters.has(wp.categoryId)) return;
      }
      const key = getKey(wp);
      visibleKeys.add(key);
      const paletteEntry =
        wp.categoryId && typeof waypointCategoryPalette === "object"
          ? waypointCategoryPalette[wp.categoryId]
          : null;
      const markerColor = paletteEntry?.color || "#28a745";
      const markerIconName = String(wp?.icon || "").trim();
      let entry = markers.get(key);
      if (!entry) {
        const marker = L.marker([wp.lat, wp.lon], {
          icon: makeWaypointIcon(markerColor, markerIconName),
          pane: waypointPane || undefined,
          interactive: true,
          bubblingMouseEvents: false,
          keyboard: false,
          riseOnHover: true,
        });
        entry = {
          key,
          marker,
          hitMarker: null,
          color: markerColor,
          iconName: markerIconName,
          popup: null,
          popupRoot: null,
          titleEl: null,
          distanceEl: null,
          onPopupOpen: null,
          name: wp.name || "",
          canAddToPlan: false,
          canAddNavWaypointFromMap: false,
          canManageWaypoints: false,
          isAdminMode: false,
          canNavigateWaypoints: false,
          activityMode: null,
          language: null,
          canAddStopPlanStage: false,
          canPickStopPlanStart: false,
          pendingTargetId: null,
          latestWp: null,
          openPopupBuilder: null,
          onDemandPopupOpen: null,
        };
        marker.on("preclick", (event) => {
          try {
            L.DomEvent.stopPropagation(event);
          } catch {}
        });
        marker.on("add", () => {
          try {
            const el = marker.getElement?.();
            if (!el) return;
            el.setAttribute("draggable", "false");
            el.style.webkitTouchCallout = "none";
            el.style.webkitUserSelect = "none";
            el.style.userSelect = "none";
            el.addEventListener("contextmenu", (ev) => {
              ev.preventDefault();
            });
          } catch {}
        });
        markers.set(key, entry);
      }

      if (!layer.hasLayer(entry.marker)) {
        layer.addLayer(entry.marker);
      }
      if (entry.marker?.setLatLng) {
        entry.marker.setLatLng([wp.lat, wp.lon]);
      }

      if (entry.color !== markerColor || entry.iconName !== markerIconName) {
        entry.marker.setIcon?.(makeWaypointIcon(markerColor, markerIconName));
        entry.color = markerColor;
        entry.iconName = markerIconName;
      }

      if (entry.name !== (wp.name || "")) {
        entry.name = wp.name || "";
        if (entry.titleEl) entry.titleEl.textContent = entry.name || "Waypoint";
      }
      entry.waypointId = wp.id || null;
      entry.lat = Number(wp.lat);
      entry.lon = Number(wp.lon);
      entry.latestWp = wp;

      const needsPopup = !!wp?.id;
      if (!needsPopup) {
        if (entry.popup) {
          entry.marker.unbindPopup();
          entry.hitMarker?.unbindPopup?.();
          if (entry.onPopupOpen) {
            entry.marker.off("popupopen", entry.onPopupOpen);
            entry.hitMarker?.off?.("popupopen", entry.onPopupOpen);
            entry.onPopupOpen = null;
          }
          entry.popup = null;
          entry.popupRoot = null;
          entry.titleEl = null;
          entry.distanceEl = null;
          entry.userDistanceEl = null;
        }
        return;
      }
      const stalePopup =
        !!entry.popup &&
        (entry.canAddToPlan !== canAddToPlan ||
          entry.canAddNavWaypointFromMap !== (navWaypointAddMode === true) ||
          entry.canManageWaypoints !== canManageWaypoints ||
          entry.isAdminMode !== isAdminMode ||
          entry.canNavigateWaypoints !== canNavigateWaypoints ||
          entry.activityMode !== activityMode ||
          entry.language !== language ||
          entry.canAddStopPlanStage !== (stopPlanPickActive === true) ||
          entry.canPickStopPlanStart !== (stageStartWaypointPickActive === true) ||
          entry.partnerRecommendationTargetId !== String(partnerRecommendationAdminTarget?.id || "") ||
          entry.isPartnerRecommended !== partnerRecommendationIds.has(String(wp.id)) ||
          entry.pendingTargetId !== (navWaypointPending?.target?.id || null));

      if (stalePopup) {
        createPopup(entry, wp);
      } else if (entry.popup && isEntryPopupOpen(entry)) {
        updateDistanceLine(entry, wp);
        updateUserDistanceLine(entry, wp);
      } else if (!entry.popup) {
        createPopup(entry, wp);
      }
    });
    markers.forEach((entry, key) => {
      if (!visibleKeys.has(key)) {
        layer.removeLayer(entry.marker);
        markers.delete(key);
      }
    });
    applyMapRotation(map, Number(map._rotationDeg || 0));
    if (cachedWaypoints.length > 0 && waypointsLayerProcessingIdRef.current) {
      const processingId = waypointsLayerProcessingIdRef.current;
      waypointsLayerProcessingIdRef.current = null;
      const afterPaint = () => {
        const finish = () => {
          window.dispatchEvent(
            new CustomEvent("trekkin:processing", {
              detail: { active: false, id: processingId },
            })
          );
        };
        if (typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(finish);
        } else {
          window.setTimeout(finish, 32);
        }
      };
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(afterPaint);
      } else {
        window.setTimeout(afterPaint, 16);
      }
    }
  }, [
    mapReady,
    waypointsVisible,
    exclusiveFocusedWaypoint,
    normalizedWaypoints,
    cachedWaypoints.length,
    waypointCategories,
    waypointCategoryPalette,
    stopPlanPickActive,
    canManageWaypoints,
    canNavigateWaypoints,
    canPlanRoute,
    planActive,
    addPlanWaypoint,
    setActivePanel,
    navigateToWaypoint,
    handleEditWaypoint,
    handleDeleteWaypoint,
    navWaypointPending,
    navWaypointAddMode,
    navWaypointTarget?.id,
    enqueueNavWaypoint,
    setNavWaypointAddMode,
    setNavWaypointTarget,
    isAndroid,
    isIOS,
    trailEditActive,
    navGridEditNodesActive,
    navGridAddNodeActive,
    navGridDeleteNodesActive,
    stageStartWaypointPickActive,
    partnerRecommendationAdminTarget?.id,
    partnerRecommendations,
    activityMode,
    language,
  ]);

  const toCoord = useCallback((value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
    if (typeof value === "string") {
      const cleaned = value.trim().replace(",", ".");
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : NaN;
    }
    return NaN;
  }, []);

  const getStartLatLon = useCallback((item) => {
    const first = Array.isArray(item?.points) && item.points.length ? item.points[0] : null;
    const lat = toCoord(
      item?.startLat ??
        (Array.isArray(first) ? first[0] : first?.lat ?? first?.latitude)
    );
    const lon = toCoord(
      item?.startLon ??
        (Array.isArray(first) ? first[1] : first?.lon ?? first?.lng ?? first?.longitude)
    );
    return { lat, lon };
  }, [toCoord]);

  const createTrailPinIcon = useCallback((color) => {
    const safeColor = String(color || "#28a745").trim() || "#28a745";
    const iconSrc = `${base}icons/follow.svg`;
    const html = `
      <div style="
        width:18px;
        height:18px;
        border-radius:50%;
        background:#ffffff;
        border:2px solid ${safeColor};
        box-shadow:0 1px 2px rgba(0,0,0,0.25);
        display:flex;
        align-items:center;
        justify-content:center;
      ">
        <span style="
          width:11px;
          height:11px;
          display:block;
          background:${safeColor};
          -webkit-mask-image:url('${iconSrc}');
          -webkit-mask-repeat:no-repeat;
          -webkit-mask-position:center;
          -webkit-mask-size:contain;
          mask-image:url('${iconSrc}');
          mask-repeat:no-repeat;
          mask-position:center;
          mask-size:contain;
        "></span>
      </div>
    `;
    return L.divIcon({
      className: "",
      html,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
      popupAnchor: [0, -10],
      tooltipAnchor: [0, -12],
    });
  }, [base]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!publicTrailsLayerRef.current) {
      publicTrailsLayerRef.current = L.layerGroup().addTo(map);
    }
    const layer = publicTrailsLayerRef.current;
    const markers = publicTrailsMarkersRef.current;
    const publicTrailMarkerColor = "#facc15";

    if (!publicTrailsMapState?.on) {
      layer.clearLayers();
      markers.clear();
      return;
    }

    const items = Array.isArray(publicTrailsMapState?.items)
      ? publicTrailsMapState.items
      : [];
    const visibleKeys = new Set();
    const getKey = (item) =>
      String(
        item?.filename ||
          item?.url ||
          `${Number(item?.startLat || 0)}:${Number(item?.startLon || 0)}:${item?.name || ""}`
      );

    items.forEach((item) => {
      const { lat, lon } = getStartLatLon(item);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      const key = getKey(item);
      visibleKeys.add(key);

      let entry = markers.get(key);
      if (!entry) {
        const marker = L.marker([lat, lon], {
          icon: createTrailPinIcon(publicTrailMarkerColor),
          pane: "trailMarkersPane",
        });
        entry = {
          marker,
          hitMarker: null,
          item,
          onClick: null,
          tooltipLabel: "",
        };
        markers.set(key, entry);
      } else {
        entry.marker.setLatLng([lat, lon]);
        entry.marker.setIcon?.(createTrailPinIcon(publicTrailMarkerColor));
        entry.item = item;
      }

      bindPublicTrailCardPopup(entry, entry.item);
      entry.marker.unbindTooltip();
      entry.tooltipLabel = "";

      if (!layer.hasLayer(entry.marker)) {
        layer.addLayer(entry.marker);
      }
    });

    markers.forEach((entry, key) => {
      if (!visibleKeys.has(key)) {
        layer.removeLayer(entry.marker);
        markers.delete(key);
      }
    });
  }, [mapReady, publicTrailsMapState, getStartLatLon, createTrailPinIcon, bindPublicTrailCardPopup]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!navTrailsLayerRef.current) {
      navTrailsLayerRef.current = L.layerGroup().addTo(map);
    }
    const layer = navTrailsLayerRef.current;
    const markers = navTrailsMarkersRef.current;

    if (!navTrailsMapState?.on) {
      layer.clearLayers();
      markers.clear();
      return;
    }

    const items = Array.isArray(navTrailsMapState?.items)
      ? navTrailsMapState.items
      : [];
    const renderSeq = ++navTrailsRenderSeqRef.current;
    let aborted = false;
    const visibleKeys = new Set();
    const getKey = (item) =>
      String(
        item?.id ||
          item?.url ||
          `${Number(item?.startLat || 0)}:${Number(item?.startLon || 0)}:${item?.name || ""}`
      );

    const resolveStartForItem = async (item) => {
      const direct = getStartLatLon(item);
      if (Number.isFinite(direct.lat) && Number.isFinite(direct.lon)) return direct;

      try {
        const points = Array.isArray(item?.points) ? item.points : [];
        if (points.length) {
          const first = points[0];
          const lat = toCoord(Array.isArray(first) ? first[0] : first?.lat ?? first?.latitude);
          const lon = toCoord(Array.isArray(first) ? first[1] : first?.lon ?? first?.lng ?? first?.longitude);
          if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
        }
      } catch {}

      try {
        const url = resolveNavTrailMapUrl(item);
        if (!url) return { lat: NaN, lon: NaN };
        const res = await fetch(url);
        if (!res.ok) return { lat: NaN, lon: NaN };
        const xml = await res.text();
        const parsed = await parseGPX(xml);
        const pts = Array.isArray(parsed?.points) ? parsed.points : [];
        if (!pts.length) return { lat: NaN, lon: NaN };
        const first = pts[0];
        const lat = toCoord(first?.lat ?? first?.latitude);
        const lon = toCoord(first?.lon ?? first?.lng ?? first?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return { lat: NaN, lon: NaN };
        return { lat, lon };
      } catch {
        return { lat: NaN, lon: NaN };
      }
    };

    (async () => {
      const resolved = await Promise.all(
        items.map(async (item) => ({
          item,
          start: await resolveStartForItem(item),
        }))
      );
      if (aborted || navTrailsRenderSeqRef.current !== renderSeq) return;

      resolved.forEach(({ item, start }) => {
        const { lat, lon } = start || {};
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        const key = getKey(item);
        visibleKeys.add(key);

        let entry = markers.get(key);
        if (!entry) {
          const marker = L.marker([lat, lon], {
            icon: createTrailPinIcon("#dc2626"),
            pane: "trailMarkersPane",
          });
          entry = { marker, hitMarker: null, item, onClick: null, tooltipLabel: "" };
          markers.set(key, entry);
        } else {
          entry.marker.setLatLng([lat, lon]);
          entry.marker.setIcon?.(createTrailPinIcon("#dc2626"));
          entry.item = item;
        }

        bindTrailMarkerPopup(entry, entry.item, openNavTrailFromMap, "Trilho do painel Nav");

        const label = String(item?.name || item?.id || "Trilho do painel Nav");
        if (entry.tooltipLabel !== label) {
          entry.marker.unbindTooltip();
          entry.marker.bindTooltip(label, { direction: "top", offset: [0, -22] });
          entry.tooltipLabel = label;
        }

        if (!layer.hasLayer(entry.marker)) layer.addLayer(entry.marker);
      });

      markers.forEach((entry, key) => {
        if (!visibleKeys.has(key)) {
          layer.removeLayer(entry.marker);
          markers.delete(key);
        }
      });
    })();

    return () => {
      aborted = true;
    };
  }, [
    mapReady,
    navTrailsMapState,
    openNavTrailFromMap,
    getStartLatLon,
    createTrailPinIcon,
    resolveNavTrailMapUrl,
    toCoord,
    bindTrailMarkerPopup,
  ]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!alertsLayerRef.current) {
      alertsLayerRef.current = L.layerGroup().addTo(map);
    }
    alertsLayerRef.current.clearLayers();
    if (!alertsLayerOn || !canViewMapAlerts) return;

    const filtered = mapAlerts.filter((entry) => {
      if (!entry?.coords || !Number.isFinite(entry.coords.lat) || !Number.isFinite(entry.coords.lon)) {
        return false;
      }
      if (alertsFilter.category && alertsFilter.category !== "all") {
        if (entry.category !== alertsFilter.category) return false;
      }
      if (alertsFilter.type && entry.type !== alertsFilter.type) return false;
      if (alertsFilter.sub && getAlertSubLabel(entry) !== alertsFilter.sub) return false;
      return true;
    });

    filtered.forEach((entry) => {
      const color = ALERT_CATEGORY_COLORS[entry.category] || "#28a745";
      const iconName = ALERT_CATEGORY_ICONS[entry.category] || "alert.svg";
      const icon = L.divIcon({
        className: "tk-alert-marker",
        html: `<div style="
          width:28px;
          height:28px;
          border-radius:50%;
          background:${color};
          border:2px solid #fff;
          box-shadow:0 4px 10px rgba(0,0,0,0.25);
          display:flex;
          align-items:center;
          justify-content:center;
        "><img src="${base}icons/${iconName}" alt="" style="width:16px;height:16px;filter:drop-shadow(0 0 2px rgba(0,0,0,0.35));"/></div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        popupAnchor: [0, -12],
      });
      const marker = L.marker([entry.coords.lat, entry.coords.lon], { icon });
      marker.bindTooltip(entry.label || "Alerta", {
        direction: "top",
        offset: [0, -6],
      });

      const popup = L.DomUtil.create("div", "tk-alert-popup");
      popup.style.display = "flex";
      popup.style.flexDirection = "column";
      popup.style.gap = "6px";
      popup.style.width = "100%";

      const title = L.DomUtil.create("div", "", popup);
      title.textContent = entry.label || "Alerta";
      title.style.fontWeight = "600";
      title.style.color = "#2c6b3f";
      title.style.fontSize = "13px";

      const categoryLine = L.DomUtil.create("div", "", popup);
      const categoryLabel = ALERT_CATEGORY_LABELS[entry.category] || entry.category || "Alerta";
      const typeLabel = toAlertText(entry.type, "");
      const subLabel = getAlertSubLabel(entry);
      const metaParts = [categoryLabel, typeLabel, subLabel].filter(Boolean);
      categoryLine.textContent = metaParts.join(" • ");
      categoryLine.style.fontSize = "12px";
      categoryLine.style.color = "#555";

      if (entry.note) {
        const note = L.DomUtil.create("div", "", popup);
        note.textContent = entry.note;
        note.style.fontSize = "12px";
        note.style.color = "#333";
      }

      if (entry.imageUrl) {
        const imageCandidates = buildWaypointImageCandidates(entry.imageUrl, isIOS);
        if (imageCandidates.length) {
          const img = L.DomUtil.create("img", "", popup);
          let imageIndex = 0;
          img.src = imageCandidates[imageIndex] || "";
          img.alt = entry.label || "Alerta";
          img.loading = "lazy";
          img.decoding = "async";
          img.style.width = "100%";
          img.style.borderRadius = "8px";
          img.style.border = "1px solid rgba(0,0,0,0.08)";
          img.onerror = () => {
            imageIndex += 1;
            if (imageIndex < imageCandidates.length) {
              img.src = imageCandidates[imageIndex];
              return;
            }
            try {
              img.remove();
            } catch {}
          };
        }
      }

      if (entry.ts) {
        const time = L.DomUtil.create("div", "", popup);
        time.textContent = new Date(entry.ts).toLocaleString("pt-PT");
        time.style.fontSize = "11px";
        time.style.color = "#666";
      }

      const confirmedAtValue = Number(entry.confirmedAt || 0) || 0;
      const isOwnAlert = Boolean(user?.uid && String(entry.userId || "") === String(user.uid));
      if (!isOwnAlert && (user?.uid || confirmedAtValue)) {
        const buttonWrapper = L.DomUtil.create("div", "", popup);
        buttonWrapper.style.display = "flex";
        buttonWrapper.style.justifyContent = "flex-start";
        buttonWrapper.style.marginTop = "6px";

        const confirmButton = L.DomUtil.create("button", "", buttonWrapper);
        const cuid = user?.uid;
        confirmButton.textContent = confirmedAtValue
          ? "Confirmado"
          : "Confirmar alerta";
        confirmButton.disabled = !!confirmedAtValue || !cuid;
        confirmButton.style.padding = "6px 12px";
        confirmButton.style.borderRadius = "10px";
        confirmButton.style.border = confirmedAtValue
          ? "1px solid rgba(0,0,0,0.15)"
          : "1px solid rgba(40,167,69,0.6)";
        confirmButton.style.background = confirmedAtValue ? "#f0f0f0" : "#28a745";
        confirmButton.style.color = confirmedAtValue ? "#333" : "#fff";
        confirmButton.style.fontSize = "11px";
        confirmButton.style.cursor = confirmedAtValue ? "default" : "pointer";
        confirmButton.style.boxShadow = confirmedAtValue
          ? "none"
          : "0 4px 10px rgba(40,167,69,0.4)";

        if (!confirmedAtValue && cuid) {
          L.DomEvent.on(confirmButton, "click", async (event) => {
            L.DomEvent.stopPropagation(event);
            confirmButton.disabled = true;
            confirmButton.textContent = "Confirmando…";
            const now = Date.now();
            const success = await confirmAlert(entry);
            if (success) {
              confirmButton.textContent = "Confirmado";
            } else {
              confirmButton.textContent = "Confirmar alerta";
              confirmButton.disabled = false;
            }
          });
        }

        L.DomEvent.disableClickPropagation(buttonWrapper);
      }

      // Enquanto o aviso automático estiver visível não ligamos o segundo
      // popup do marcador. Assim existe sempre uma única caixa por alerta.
      if (!proximityAlert) {
        marker.bindPopup(popup, { minWidth: 200, className: "tk-alert-popup" });
      }
      alertsLayerRef.current.addLayer(marker);
    });
  }, [mapReady, alertsLayerOn, canViewMapAlerts, mapAlerts, alertsFilter, proximityAlert]);

  // -------------------------------------------------------------
  // Center button
  // -------------------------------------------------------------
  useEffect(() => {
    const handler = (event) => {
      const map = mapRef.current;
      if (!map) return;
      const targetLat = Number(event?.detail?.lat);
      const targetLon = Number(event?.detail?.lon ?? event?.detail?.lng);
      const targetZoom = Number(event?.detail?.zoom);
      if (Number.isFinite(targetLat) && Number.isFinite(targetLon)) {
        map.setView([targetLat, targetLon], Number.isFinite(targetZoom) ? targetZoom : 16, {
          animate: true,
          paddingBottomRight: [0, 90],
        });
        lastInteractionRef.current = Date.now();
        setTimeout(() => map.invalidateSize(true), 80);
        return;
      }

      if (isViewerMode) {
        const live = useShareStore.getState().liveData;
        if (!live?.lat || !live?.lon) return;
        map.setView([live.lat, live.lon], 17, {
          animate: true,
          paddingBottomRight: [0, 90],
        });
      } else {
        const pos = usePanelsStore.getState().userPosition;
        if (!pos?.lat || !pos?.lon) return;
        map.setView([pos.lat, pos.lon], 17, {
          animate: true,
          paddingBottomRight: [0, 90],
        });
      }

      setFollow(!!followEnabled);
      lastInteractionRef.current = Date.now();
      setTimeout(() => map.invalidateSize(true), 80);
    };

    window.addEventListener("trekkin:center-map", handler);
    return () => window.removeEventListener("trekkin:center-map", handler);
  }, [isViewerMode, followEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      const map = mapRef.current;
      if (!map) return;
      const detail = event?.detail || {};
      const waypointId = String(detail?.waypointId || "").trim();
      const waypointName = String(detail?.waypointName || "").trim();
      const waypointDesc = String(
        detail?.description || detail?.desc || detail?.details || ""
      ).trim();
      const waypointTel = readWaypointContactValue(detail, ["tel", "phone", "telefone"]);
      const waypointEmail = readWaypointContactValue(detail, ["email", "mail", "eMail"]);
      const waypointWeb = readWaypointContactValue(detail, ["www", "web", "website", "url"]);
      const waypointImageRaw = String(
        detail?.imageUrl ||
          detail?.image ||
          detail?.photoUrl ||
          detail?.photo ||
          detail?.coverImage ||
          ""
      ).trim();
      const waypointImageCandidates = disableWaypointImagesForStability
        ? []
        : buildWaypointImageCandidates(waypointImageRaw, isIOS);
      const waypointImage = waypointImageCandidates[0] || "";
      const targetLat = Number(detail?.lat);
      const targetLon = Number(detail?.lon ?? detail?.lng);
      const targetZoom = Number(detail?.zoom);

      const exclusiveFocus = detail?.exclusive === true;
      try { window.localStorage.setItem("waypointsLayerOn", "1"); } catch {}
      let selectedCategories = [];
      let selectedPalette = {};
      try {
        const parsed = JSON.parse(window.localStorage.getItem("waypointsSelectedCategories") || "[]");
        selectedCategories = Array.isArray(parsed) ? parsed : [];
      } catch {}
      try {
        const parsed = JSON.parse(window.localStorage.getItem("waypointsCategoryPalette") || "{}");
        selectedPalette = parsed && typeof parsed === "object" ? parsed : {};
      } catch {}
      window.dispatchEvent(new CustomEvent("trekkin:waypoints-map-filter", {
        detail: { on: true, categories: selectedCategories, categoryPalette: selectedPalette },
      }));
      if (exclusiveFocus) {
        const originalWaypoint =
          detail?.waypoint && typeof detail.waypoint === "object"
            ? detail.waypoint
            : {};
        setExclusiveFocusedWaypoint({
          ...originalWaypoint,
          id: waypointId || originalWaypoint?.id,
          name: waypointName || originalWaypoint?.name,
          lat: targetLat,
          lon: targetLon,
          imageUrl: waypointImageRaw || originalWaypoint?.imageUrl,
          desc: waypointDesc || originalWaypoint?.desc || originalWaypoint?.description,
          tel: waypointTel || originalWaypoint?.tel,
          email: waypointEmail || originalWaypoint?.email,
          www: waypointWeb || originalWaypoint?.www,
          icon: detail?.icon || originalWaypoint?.icon,
        });
        // O foco exclusivo do parceiro é temporário. Não altera a
        // visibilidade global nem as categorias escolhidas no mapa.
      }

      const escapeHtml = (value) =>
        String(value || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\"/g, "&quot;")
          .replace(/'/g, "&#39;");

      const renderFallbackPopupHtml = (label, lat, lon) => {
        const parts = [];
        parts.push(`<div style="font-weight:600;color:#2c6b3f;">${escapeHtml(label)}</div>`);
        if (waypointImage) {
          parts.push(
            `<img src="${escapeHtml(waypointImage)}" alt="" style="width:100%;max-width:220px;aspect-ratio:1 / 1;height:auto;object-fit:cover;border-radius:8px;align-self:center;display:block;" />`
          );
        }
        if (waypointDesc) {
          parts.push(
            `<div style="font-size:12px;color:#555;white-space:pre-wrap;">${escapeHtml(waypointDesc)}</div>`
          );
        }
        if (waypointTel || waypointEmail || waypointWeb) {
          const btnStyle =
            "display:inline-flex;align-items:center;justify-content:center;flex:0 0 auto;border:1px solid #d7d7d7;background:#fff;color:#276b3e;border-radius:10px;padding:5px 12px;font-size:13px;font-weight:800;line-height:1.2;text-decoration:none;white-space:nowrap;";
          const buttons = [];
          if (waypointTel) {
            const telHref = `tel:${String(waypointTel).replace(/[^\d+]/g, "") || waypointTel}`;
            buttons.push(
              `<a href="${escapeHtml(telHref)}" style="${btnStyle}">Ligar</a>`
            );
          }
          if (waypointEmail) {
            buttons.push(
              `<a href="mailto:${escapeHtml(waypointEmail)}" style="${btnStyle}">E-mail</a>`
            );
          }
          if (waypointWeb) {
            buttons.push(
              `<button type="button" data-tk-waypoint-web="1" style="${btnStyle};cursor:pointer;">WWW</button>`
            );
          }
          parts.push(
            `<div style="display:flex;gap:6px;flex-wrap:nowrap;margin-top:2px;">${buttons.join(
              ""
            )}</div>`
          );
        }
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          parts.push(
            `<div style="font-size:12px;color:#666;">${escapeHtml(
              `${lat.toFixed(5)}, ${lon.toFixed(5)}`
            )}</div>`
          );
        }
        return `<div style="display:flex;flex-direction:column;gap:6px;max-width:240px;">${parts.join(
          ""
        )}</div>`;
      };

      const bindFallbackWaypointWebButton = () => {
        if (!waypointWeb || typeof window === "undefined") return;
        window.setTimeout(() => {
          try {
            const buttons = document.querySelectorAll(
              ".leaflet-popup.tk-waypoint-popup [data-tk-waypoint-web='1']"
            );
            buttons.forEach((btn) => {
              if (btn.dataset.tkWaypointWebBound === "1") return;
              btn.dataset.tkWaypointWebBound = "1";
              btn.addEventListener("click", (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                const okConfirm = window.confirm("Queres abrir o website no navegador?");
                if (!okConfirm) return;
                const ok = openWaypointWebsite(waypointWeb);
                if (!ok) showToast("Não foi possível abrir o website.", "warning");
              });
            });
          } catch {}
        }, 0);
      };

      const tryFocus = () => {
        const markers = waypointsMarkersRef.current;
        if (!markers || typeof markers.forEach !== "function") return false;
        let match = null;
        if (waypointId && markers.get(waypointId)) {
          match = markers.get(waypointId);
        } else if (waypointId) {
          markers.forEach((entry) => {
            if (match) return;
            if (String(entry?.waypointId || "") === waypointId) {
              match = entry;
            }
          });
        }
        if (
          !match &&
          Number.isFinite(targetLat) &&
          Number.isFinite(targetLon)
        ) {
          let best = null;
          markers.forEach((entry) => {
            if (best && best.dist <= 5) return;
            const lat = Number(entry?.lat);
            const lon = Number(entry?.lon);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
            const dist = haversineMeters(
              { lat: targetLat, lon: targetLon },
              { lat, lon }
            );
            if (!best || dist < best.dist) {
              best = { dist, entry };
            }
          });
          if (best?.entry && best.dist <= 120) {
            match = best.entry;
          }
        }
        if (!match?.marker) return false;
        const lat =
          Number.isFinite(Number(match.lat)) ? Number(match.lat) : Number(targetLat);
        const lon =
          Number.isFinite(Number(match.lon)) ? Number(match.lon) : Number(targetLon);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          map.setView([lat, lon], Number.isFinite(targetZoom) ? targetZoom : 16, {
            animate: true,
            paddingBottomRight: [0, 90],
          });
        }
        try {
          if (!match.marker.getPopup?.()) {
            const label =
              waypointName ||
              String(match?.name || "").trim() ||
              (waypointId ? `Waypoint ${waypointId}` : "Waypoint");
            match.marker.bindPopup(
              renderFallbackPopupHtml(label, lat, lon),
              {
                minWidth: 180,
                className: "tk-waypoint-popup",
              }
            );
          }
          match.marker.openPopup();
          bindFallbackWaypointWebButton();
        } catch {}
        lastInteractionRef.current = Date.now();
        return true;
      };

      const centerFallback = () => {
        if (!Number.isFinite(targetLat) || !Number.isFinite(targetLon)) return;
        map.setView(
          [targetLat, targetLon],
          Number.isFinite(targetZoom) ? targetZoom : 16,
          {
            animate: true,
            paddingBottomRight: [0, 90],
          }
        );
        const label =
          waypointName ||
          (waypointId ? `Waypoint ${waypointId}` : "Objetivo da missão");
        try {
          L.popup({
            closeButton: true,
            className: "tk-waypoint-popup",
            autoClose: true,
            closeOnClick: true,
            maxWidth: 280,
          })
            .setLatLng([targetLat, targetLon])
            .setContent(renderFallbackPopupHtml(label, targetLat, targetLon))
            .openOn(map);
          bindFallbackWaypointWebButton();
        } catch {}
        lastInteractionRef.current = Date.now();
      };

      // Fora do acesso exclusivo pelo parceiro, mantém o comportamento normal
      // de focar um waypoint pertencente à camada geral.
      if (!exclusiveFocus) {
        setExclusiveFocusedWaypoint(null);
        setWaypointsVisible(true);
      }

      const delays = [0, 160, 420, 900, 1600, 2600];
      let solved = false;
      delays.forEach((delay) => {
        window.setTimeout(() => {
          if (solved) return;
          solved = tryFocus();
          if (!solved && delay === delays[delays.length - 1]) {
            centerFallback();
          }
        }, delay);
      });
    };

    window.addEventListener("trekkin:focus-waypoint", handler);
    return () => window.removeEventListener("trekkin:focus-waypoint", handler);
  }, []);

  // -------------------------------------------------------------
  // ⏱️ AUTO-FOLLOW APÓS 30s (SEM MUDAR O ESTADO/BOTÃO)
  //  - volta a seguir posição (re-centra)
  //  - mantém zoom atual
  // -------------------------------------------------------------
  useEffect(() => {
    if (isViewerMode) return;
    if (backgroundIdle) return;
    if (!followEnabled) return;

    const id = setInterval(() => {
      const diff = Date.now() - lastInteractionRef.current;
      if (diff <= 30000) return;

      if (
        navGridDrawActiveRef.current &&
        drawContextRef.current === "navgrid"
      ) {
        return;
      }

      setFollow(true);

      const map = mapRef.current;
      if (!map) return;

      const pos = usePanelsStore.getState().userPosition;
      if (pos?.lat && pos?.lon) {
        map.setView([pos.lat, pos.lon], map.getZoom(), {
          animate: true,
          paddingBottomRight: [0, 90],
        });
      }
    }, 2000);

    return () => clearInterval(id);
  }, [isViewerMode, backgroundIdle, followEnabled]);

  // -------------------------------------------------------------
  // GPS Update (modo normal)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!mapReady || isViewerMode) return;

    const map = mapRef.current;
    const now = Date.now();
    if (/Android/i.test(navigator.userAgent || "")) {
      const resumeStartedAt = Number(
        window?.__TREKKIN_ANDROID_RESUME_PERF?.startedAt || 0
      );
      const inResumeWindow =
        resumeStartedAt > 0 && now - resumeStartedAt < 5000;
      const minUiIntervalMs = inResumeWindow ? 180 : 700;
      if (now - lastUiGpsTsRef.current < minUiIntervalMs) return;
      lastUiGpsTsRef.current = now;
    }
    const tsOf = (f) =>
      Number(f?.ts ?? f?.time ?? f?.timestamp ?? 0) || 0;
    const storePos = userPosition || null;
    const storeTs = tsOf(storePos);
    const isBufferedFix = (f) => {
      const src = f?.__src || f?.source || f?.src || "";
      return src === "android-buffer" || src === "android-bg";
    };

    const candidates = [lastFix, lastLiveFix, storePos].filter(
      (f) => f && typeof f.lat === "number" && typeof f.lon === "number"
    );

    const nowTs = Date.now();
    const nonBufferedFresh = candidates
      .filter((f) => !isBufferedFix(f))
      .filter((f) => {
        const ts = tsOf(f);
        return !ts || nowTs - ts < 5000;
      })
      .sort((a, b) => tsOf(b) - tsOf(a));

    const liveFix = (() => {
      if (isRecording) {
        const recCandidates = [lastFix, lastLiveFix].filter(
          (f) => f && typeof f.lat === "number" && typeof f.lon === "number"
        );
        const recNonBuffered = recCandidates
          .filter((f) => !isBufferedFix(f))
          .filter((f) => {
            const ts = tsOf(f);
            return !ts || nowTs - ts < 5000;
          })
          .sort((a, b) => tsOf(b) - tsOf(a));
        return (
          recNonBuffered[0] ||
          recCandidates.sort((a, b) => tsOf(b) - tsOf(a))[0] ||
          storePos ||
          null
        );
      }
      return (
        nonBufferedFresh[0] ||
        candidates.sort((a, b) => tsOf(b) - tsOf(a))[0] ||
        null
      );
    })();
    if (!liveFix || typeof liveFix.lat !== "number" || typeof liveFix.lon !== "number") return;

    const lat = Number(liveFix.lat);
    const lon = Number(liveFix.lon);
    const acc = Number(liveFix.acc || 0);

    const fixTs = tsOf(liveFix);

    const isAndroid = /Android/i.test(navigator.userAgent || "");

    if (isAndroid) {
      const lastTs = lastMarkerFixTsRef.current || 0;
      const fixSource = liveFix.__src || liveFix.source || "";
      const isBuffered =
        fixSource === "android-buffer" || fixSource === "android-bg";

      // ❌ Fix fora de ordem → IGNORA (causa do saltitar)
      if (fixTs && lastTs && fixTs < lastTs) {
        return;
      }

      // ❌ Replay de fixes antigos ao sair de BG
      if (
        fixTs &&
        document.visibilityState === "visible" &&
        Date.now() - fixTs > 15000 &&
        !isBuffered
      ) {
        return;
      }

      lastMarkerFixTsRef.current = fixTs || Date.now();
    }

    const speed = typeof liveFix.speed === "number" ? liveFix.speed : 0;
    const storePoints = usePanelsStore.getState().points || [];
    const lastRecorded =
      isRecording && storePoints.length ? storePoints[storePoints.length - 1] : null;
    const markerLat = lat;
    const markerLon = lon;
    const markerAcc = acc;
    const markerEle = Number.isFinite(liveFix?.alt) ? Number(liveFix.alt) : 0;
    const markerSpd = speed ? speed * 3.6 : 0;
    const markerTs = Number(fixTs || Date.now());
    const ll = L.latLng(markerLat, markerLon);

    userMarkerRef.current.setLatLng(ll);
    if (guideHaloRef.current) guideHaloRef.current.setLatLng(ll);

    // -------------------------------------------------------------
    // 🧭 Direção do mapa (computar bearing)
    // -------------------------------------------------------------
    let rotationDeg = null;

    if (compassMode === "follow") {
      let gpsBearing = null;

      if (path.length >= 2) {
        const a = path[path.length - 2];
        const b = path[path.length - 1];
        gpsBearing = bearingDeg(a, b);
      }

      const compassSafe =
        compassMode === "follow" && !isDraggingRef.current ? headingDeg : null;

      rotationDeg = resolveMapBearing({
        speed,
        gpsBearing,
        compassHeading: compassSafe,
        lastBearing: lastBearingRef.current,
      });

      if (rotationDeg != null) lastBearingRef.current = rotationDeg;
    }

    // -------------------------------------------------------------
    // 🧭 ROTAÇÃO AUTOMÁTICA — FOLLOW (SUAVE + IMEDIATA)
    // -------------------------------------------------------------
    if (
      compassMode === "follow" &&
      rotationDeg != null &&
      !isDraggingRef.current
    ) {
      const now = performance.now();

      // throttle leve (~30fps)
      if (now - lastRotationTsRef.current > 30) {
        lastRotationTsRef.current = now;

        smoothBearingRef.current = smoothBearing(
          smoothBearingRef.current,
          rotationDeg,
          0.18
        );

        applyMapRotation(map, -smoothBearingRef.current);
      }
    }
    // -------------------------------------------------------------
    // 🧭 CONE DO GUIA (agulha) — só no estado needle
    // -------------------------------------------------------------
    const needle = userMarkerRef.current?.getElement()?.querySelector(".tk-needle");

    if (needle) {
      if (compassMode === "needle") {
        needle.style.display = "block";
        const deg = Number.isFinite(headingDeg) ? headingDeg : 0;
        needle.style.transform = `translate(-50%, -100%) rotate(${deg}deg)`;
      } else {
        needle.style.display = "none";
      }
    }

    // -------------------------------------------------------------
    // Follow (mantém zoom atual: NÃO força 17)
    // -------------------------------------------------------------
    let didSetView = false;
    const setFollowView = (zoom) => {
      const currentCenter = map.getCenter?.();
      const distanceFromCenter =
        currentCenter && typeof map.distance === "function"
          ? Number(map.distance(currentCenter, ll))
          : Number.POSITIVE_INFINITY;
      const resumeStartedAt = Number(
        window?.__TREKKIN_ANDROID_RESUME_PERF?.startedAt || 0
      );
      const inAndroidResumeWindow =
        isAndroid && resumeStartedAt > 0 && Date.now() - resumeStartedAt < 12000;
      const minMoveMeters = isAndroid
        ? inAndroidResumeWindow
          ? 250
          : 8
        : 0;
      const currentZoom = Number(map.getZoom?.());
      const zoomChanged =
        Number.isFinite(zoom) &&
        (!Number.isFinite(currentZoom) || Math.abs(currentZoom - zoom) > 0.01);
      const resumeCameraCoolingDown =
        inAndroidResumeWindow &&
        Date.now() - lastAndroidResumeCameraTsRef.current < 3000;
      const resumeCameraShouldWait =
        inAndroidResumeWindow &&
        Date.now() - resumeStartedAt < 5000 &&
        Number.isFinite(distanceFromCenter) &&
        distanceFromCenter < 300 &&
        !zoomChanged;

      if (
        Number.isFinite(distanceFromCenter) &&
        (resumeCameraShouldWait ||
          distanceFromCenter < minMoveMeters ||
          (resumeCameraCoolingDown && distanceFromCenter < 120)) &&
        !zoomChanged
      ) {
        if (
          isAndroid &&
          inAndroidResumeWindow &&
          Date.now() - lastAndroidResumeCameraSkipLogRef.current > 1500
        ) {
          lastAndroidResumeCameraSkipLogRef.current = Date.now();
          logEvent("debug", "MAP_ANDROID_RESUME_CAMERA_SKIPPED", {
            resumeMs: Date.now() - resumeStartedAt,
            distanceFromCenterM: Math.round(distanceFromCenter),
            zoom,
            reason: resumeCameraShouldWait
              ? "resume_stabilizing"
              : resumeCameraCoolingDown
                ? "cooldown"
                : "small_move",
          });
        }
        return false;
      }

      map.setView(ll, zoom, {
        animate: !isAndroid,
        paddingBottomRight: [0, 90],
      });
      if (isAndroid && inAndroidResumeWindow) {
        lastAndroidResumeCameraTsRef.current = Date.now();
        logEvent("info", "MAP_ANDROID_RESUME_CAMERA_SET", {
          resumeMs: Date.now() - resumeStartedAt,
          distanceFromCenterM: Number.isFinite(distanceFromCenter)
            ? Math.round(distanceFromCenter)
            : null,
          zoom,
        });
      }
      return true;
    };

    if (!firstFixRef.current) {
      firstFixRef.current = true;
      if (followEnabled) {
        if (
          !(
            navGridDrawActiveRef.current &&
            drawContextRef.current === "navgrid"
          )
        ) {
          didSetView = setFollowView(17);
        }
      }
    } else if (
      followEnabled &&
      follow &&
      !isDraggingRef.current &&
      !isZoomingRef.current
    ) {
      if (
        !(
          navGridDrawActiveRef.current &&
          drawContextRef.current === "navgrid"
        )
      ) {
        didSetView = setFollowView(map.getZoom());
      }
    }

    if (didSetView && !isAndroid) {
      setTimeout(() => map.invalidateSize(true), 80);
    }

    // Store
    if (!storePos || fixTs > storeTs) {
      usePanelsStore.getState().setUserPosition({
        lat: markerLat,
        lon: markerLon,
        ele: markerEle,
        acc: markerAcc,
        spd: markerSpd,
        ts: markerTs,
      });
    }

    // LIVE SHARE update
    if (isSharing && !isExpeditionMode) {
      const panelsState = usePanelsStore.getState();
      const metrics =
        typeof panelsState.getMetrics === "function" ? panelsState.getMetrics() : null;

      const pts = panelsState.points || [];
      const lastPoint = pts[pts.length - 1];

      updateShareData({
        lat: markerLat,
        lon: markerLon,
        ele: markerEle ?? lastPoint?.ele ?? 0,
        acc: markerAcc,
        spd: markerSpd,
        ts: markerTs,
        stats: metrics || {},
        pathPoint:
          panelsState.isRecording && lastPoint
            ? {
                lat: lastPoint.lat,
                lon: lastPoint.lon,
                ele: lastPoint.ele ?? liveFix.alt ?? 0,
                ts: lastPoint.ts || liveFix.ts,
              }
            : {
                lat: markerLat,
                lon: markerLon,
                ele: markerEle ?? 0,
                ts: markerTs,
              },
      });
    }

    // Group member location update (apenas se evento LIVE)
    try {
      const { updateMyLocationInGroup, clearMemberLocation } = useGroupStore.getState();

      if (!isExpeditionMode && typeof updateMyLocationInGroup === "function") {
        const gid = currentGroupId || null;

        const { activeEvent, isLive } = getActiveEventForGroupSafe(
          groups,
          currentGroup || gid,
          eventsWithLive
        );

        const authUser = useAuthStore.getState().user;
        const uid = authUser?.uid || null;

        const isGuide =
          uid &&
          activeEvent &&
          (activeEvent.guideId === uid ||
            activeEvent.guideUid === uid ||
            activeEvent.guide === uid);

        const shareGuideInLive = activeEvent?.shareGuideInLive !== false;

        if (isLive) {
          if (!canCurrentUserUseResolvedLiveEvent(activeEvent)) return;

          if (isGuide && !shareGuideInLive) {
            try {
              if (gid && uid && typeof clearMemberLocation === "function") {
                clearMemberLocation(gid, uid);
              }
            } catch (e) {
              console.warn("⚠️ Falha ao limpar localização do guia:", e);
            }
            return;
          }

          updateMyLocationInGroup({ lat: markerLat, lon: markerLon, acc: markerAcc });
        }
      }
    } catch (e) {
      console.warn("⚠️ Erro ao atualizar localização no LIVE:", e);
    }
  }, [
    lastFix,
    lastLiveFix,
    userPosition?.lat,
    userPosition?.lon,
    userPosition?.ts,
    mapReady,
    follow,
    path.length,
    isSharing,
    updateShareData,
    isViewerMode,
    currentGroup,
    groups,
    events,
    eventRegistrations,
    headingDeg,
    compassMode,
    isRecording,
    isExpeditionMode,
    followEnabled,
    userEvents,
    canCurrentUserUseResolvedLiveEvent,
  ]);

  // -------------------------------------------------------------
  // Group members — multi-live guide view + hide guide per event
  // -------------------------------------------------------------
  useEffect(() => {
    if (!mapReady || isViewerMode) return;

    const map = mapRef.current;
    if (!map) return;

    const existing = groupMarkersRef.current;
    const myUid = user?.uid || null;

    const eventsArr = Array.isArray(events)
      ? events
      : events && typeof events === "object"
        ? Object.values(events)
        : [];

    const groupsArr = Array.isArray(groups)
      ? groups
      : groups && typeof groups === "object"
        ? Object.values(groups)
        : [];

    const gidsForGuideLive = [];

    if (myUid && groupsArr.length && eventsArr.length) {
      groupsArr.forEach((g) => {
        if (!g) return;
        const gidCandidate = g.id || g.groupId || g.firebaseId || g.key || null;
        if (!gidCandidate) return;

        const { activeEvent, isLive } = getActiveEventForGroupSafe(
          groups,
          gidCandidate,
          eventsWithLive
        );

        const isGuideHere =
          isLive &&
          activeEvent &&
          (activeEvent.guideUid === myUid ||
            activeEvent.guideId === myUid ||
            activeEvent.guide === myUid);

        if (isGuideHere) gidsForGuideLive.push({ gid: gidCandidate, activeEvent });
      });
    }

    const keepIds = new Set();

    let membersMode = "normal";
    let membersSourceCount = 0;

    if (gidsForGuideLive.length > 0) {
      membersMode = "guide-multi-live";
      // GUIA multi-live
      gidsForGuideLive.forEach(({ gid }) => {
        const membersObj =
          gid && memberLocations && memberLocations[gid] ? memberLocations[gid] : {};
        membersSourceCount += Object.keys(membersObj || {}).length;

        const members = Object.entries(membersObj).map(([uidKey, loc]) => ({
          uid: uidKey,
          ...loc,
        }));

        members.forEach((m) => {
          if (!Number.isFinite(m.lat) || !Number.isFinite(m.lon)) return;
          if (myUid && m.uid === myUid) return;

          const idBase = m.uid || m.userId || m.email || `loc-${Math.random().toString(36).slice(2)}`;
          const id = `${gid}:${idBase}`;

          keepIds.add(id);

          const ll = [m.lat, m.lon];

          const rawLabel = m.name || m.displayName || m.username || m.email || "Trekker";
          let firstName = rawLabel.trim();

          const atIndex = firstName.indexOf("@");
          if (atIndex > 0) firstName = firstName.slice(0, atIndex);

          const spaceIndex = firstName.indexOf(" ");
          if (spaceIndex > 0) firstName = firstName.slice(0, spaceIndex);

          firstName = firstName.slice(0, 12) || "Trekker";

          const html = `
            <div style="
              display:flex;
              flex-direction:column;
              align-items:center;
              transform: translate(-50%, -50%);
            ">
              <div style="
                width:16px;
                height:16px;
                background:#28a745;
                border-radius:50%;
                border:2px solid white;
                box-shadow:0 0 6px rgba(0,0,0,0.35);
              "></div>
              <div style="
                margin-top:4px;
                padding:2px 6px;
                background:rgba(0,0,0,0.65);
                color:white;
                font-size:11px;
                border-radius:4px;
                white-space:nowrap;
              " class="tk-member-label">${firstName}</div>
            </div>
          `;

          if (!existing[id]) {
            existing[id] = L.marker(ll, {
              icon: L.divIcon({ html, className: "", iconSize: [0, 0], iconAnchor: [0, 0] }),
              interactive: false,
              keyboard: false,
            }).addTo(map);
          } else {
            existing[id].setLatLng(ll);
            existing[id].setIcon(L.divIcon({ html, className: "", iconSize: [0, 0], iconAnchor: [0, 0] }));
          }
        });
      });
    } else {
      // normal: apenas grupo atual, só se LIVE
      const gid = currentGroupId || null;

      const { activeEvent, isLive } = getActiveEventForGroupSafe(
        groups,
        currentGroup || gid,
        eventsWithLive
      );

      const membersObj =
        gid && memberLocations && memberLocations[gid] ? memberLocations[gid] : {};
      membersSourceCount = Object.keys(membersObj || {}).length;

      const members = Object.entries(membersObj).map(([uidKey, loc]) => ({
        uid: uidKey,
        ...loc,
      }));

      const guideUid = activeEvent?.guideUid || activeEvent?.guideId || activeEvent?.guide || null;
      const shareGuideInLive = activeEvent?.shareGuideInLive !== false;

      if (isLive) {
        members.forEach((m) => {
          if (!Number.isFinite(m.lat) || !Number.isFinite(m.lon)) return;
          if (myUid && m.uid === myUid) return;
          if (guideUid && m.uid === guideUid && !shareGuideInLive) return;

          const id = m.uid || m.userId || m.email || `loc-${Math.random().toString(36).slice(2)}`;
          keepIds.add(id);

          const ll = [m.lat, m.lon];

          const rawLabel = m.name || m.displayName || m.username || m.email || "Trekker";
          let firstName = rawLabel.trim();

          const atIndex = firstName.indexOf("@");
          if (atIndex > 0) firstName = firstName.slice(0, atIndex);

          const spaceIndex = firstName.indexOf(" ");
          if (spaceIndex > 0) firstName = firstName.slice(0, spaceIndex);

          firstName = firstName.slice(0, 12) || "Trekker";

          const html = `
            <div style="
              display:flex;
              flex-direction:column;
              align-items:center;
              transform: translate(-50%, -50%);
            ">
              <div style="
                width:16px;
                height:16px;
                background:#28a745;
                border-radius:50%;
                border:2px solid white;
                box-shadow:0 0 6px rgba(0,0,0,0.35);
              "></div>
              <div style="
                margin-top:4px;
                padding:2px 6px;
                background:rgba(0,0,0,0.65);
                color:white;
                font-size:11px;
                border-radius:4px;
                white-space:nowrap;
              " class="tk-member-label">${firstName}</div>
            </div>
          `;

          if (!existing[id]) {
            existing[id] = L.marker(ll, {
              icon: L.divIcon({ html, className: "", iconSize: [0, 0], iconAnchor: [0, 0] }),
              interactive: false,
              keyboard: false,
            }).addTo(map);
          } else {
            existing[id].setLatLng(ll);
            existing[id].setIcon(L.divIcon({ html, className: "", iconSize: [0, 0], iconAnchor: [0, 0] }));
          }
        });
      }
    }

    const membersDiag = {
      uid: myUid,
      mode: membersMode,
      gidsForGuideLive: gidsForGuideLive.length,
      currentGroupId: currentGroupId || null,
      keepCount: keepIds.size,
      sourceCount: membersSourceCount,
      existingCount: Object.keys(existing || {}).length,
    };
    const membersDiagKey = `${membersDiag.uid || "nouid"}:${membersDiag.mode}:${membersDiag.currentGroupId || "nogid"}:${membersDiag.gidsForGuideLive}:${membersDiag.sourceCount}:${membersDiag.keepCount}:${membersDiag.existingCount}`;
    const now = Date.now();
    if (
      liveMembersRenderDiagRef.current.key !== membersDiagKey ||
      now - liveMembersRenderDiagRef.current.ts > 10000
    ) {
      liveMembersRenderDiagRef.current = { key: membersDiagKey, ts: now };
      logEvent("info", "LIVE_MAP_RENDER_MEMBERS", membersDiag);
      console.info("[LIVE_MAP_RENDER_MEMBERS]", membersDiag);
    }

    Object.keys(existing).forEach((id) => {
      if (!keepIds.has(id)) {
        map.removeLayer(existing[id]);
        delete existing[id];
        mapDebug("[MapView] 🧹 Marcador removido:", id);
      }
    });
  }, [
    mapReady,
    isViewerMode,
    currentGroup,
    currentGroupId,
    memberLocations,
    groups,
    events,
    eventsWithLive,
    user,
  ]);

  // -------------------------------------------------------------
  // Rasto do guia em LIVE → verde (apenas no dispositivo do guia)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!mapReady || !recordLayerRef.current) return;

    const gid = currentGroupId || null;
    const { activeEvent, isLive } = getActiveEventForGroupSafe(
      groups,
      currentGroup || gid,
      eventsWithLive
    );

    const uid = user?.uid || null;

    const isGuide =
      uid &&
      activeEvent &&
      (activeEvent.guideId === uid || activeEvent.guideUid === uid || activeEvent.guide === uid);

    const shareGuideInLive = activeEvent?.shareGuideInLive !== false;

    if (isLive && isGuide && shareGuideInLive) recordLayerRef.current.setStyle({ color: "#28a745" });
    else recordLayerRef.current.setStyle({ color: "#dc3545" });
  }, [mapReady, currentGroup, groups, events, user]);

  // -------------------------------------------------------------
  // AUTO-SELECIONAR GRUPO APÓS LOGIN
  // -------------------------------------------------------------
  useEffect(() => {
    if (!mapReady || isViewerMode) return;

    const authUser = useAuthStore.getState().user;
    if (!authUser?.uid) return;

    const uid = authUser.uid;

    const gidCurrent = currentGroupId || null;
    if (gidCurrent) {
      const { activeEvent, isLive } = getActiveEventForGroupSafe(
        groups,
        currentGroup || gidCurrent,
        eventsWithLive
      );
      if (activeEvent && isLive) {
        const canSeeEvent = useEventsStore.getState().canUserSeeEventLive;
        const eventId = activeEvent.id || activeEvent.eventId;
        if (!canSeeEvent || canSeeEvent(eventId)) return;
      }
      // Se o grupo atual não tem LIVE válido, tentamos resolver via eventsLive.
    }

    const groupsArr = Array.isArray(groups)
      ? groups
      : groups && typeof groups === "object"
        ? Object.values(groups)
        : [];

    const eventsArr = Array.isArray(events)
      ? events
      : events && typeof events === "object"
        ? Object.values(events)
        : [];

    // 1) Preferir grupo LIVE do eventsLive onde o user é membro
    let alive = true;
    const activeUserEventIds = Object.entries(userEvents || {})
      .filter(([, registration]) => {
        const status = String(
          registration?.status ||
          registration?.state ||
          registration?.registrationStatus ||
          registration ||
          ""
        ).trim().toLowerCase();
        return status && ![
          "cancelled", "canceled", "declined", "rejected", "removed", "expired"
        ].includes(status);
      })
      .map(([eventId]) => String(eventId));

    const resolveViaEventsLive = () =>
      Promise.all(
        activeUserEventIds.map(async (eventId) => {
          try {
            const snap = await get(ref(db, `/eventsLive/${eventId}`));
            return snap.exists() ? { id: eventId, ...(snap.val() || {}) } : null;
          } catch {
            return null;
          }
        })
      )
        .then((entries) => {
          if (!alive) return null;
          const liveEvent =
            entries.find(
              (ev) =>
                ev?.isLive === true &&
                ev?.groupId &&
                (canCurrentUserUseResolvedLiveEvent(ev) || hasActiveUserEvent(ev?.id))
            );
          if (!liveEvent) return null;
          return (
            groupsArr.find(
              (g) => g.id === liveEvent.groupId || g.groupId === liveEvent.groupId
            ) || {
              id: liveEvent.groupId,
              groupId: liveEvent.groupId,
              activeEventId: liveEvent.id || liveEvent.eventId || null,
              eventId: liveEvent.id || liveEvent.eventId || null,
              event: liveEvent,
            }
          );
        })
        .catch((e) => {
          console.warn("[MapView] auto joinGroup eventsLive falhou:", e);
          return null;
        });

    resolveViaEventsLive().then((liveGroup) => {
      if (!alive || liveGroup) {
        if (liveGroup) {
          try {
            const { joinGroup } = useGroupStore.getState();
            if (typeof joinGroup === "function") {
              mapDebug("[MapView] 🎯 auto joinGroup via eventsLive →", liveGroup.id || liveGroup.groupId);
              joinGroup(liveGroup);
            }
          } catch (e) {
            console.warn("[MapView] auto joinGroup falhou:", e);
          }
        }
        return;
      }

      if (gidCurrent) return;

      // 2) Grupo com activeEventId (se existir)
      let targetGroup =
        groupsArr.find(
          (g) => {
            if (!g?.members?.[uid]) return false;
            const eventId = g.activeEventId || g.activeEvent || g.eventId || null;
            if (!eventId) return false;
            return eventsArr.some(
              (ev) =>
                ev?.isLive === true &&
                (ev?.id === eventId ||
                  ev?.eventId === eventId ||
                  String(ev?.id || "") === String(eventId))
            );
          }
        ) || null;

      // 3) Grupo que tem evento LIVE no array events
      if (!targetGroup && eventsArr.length > 0) {
        targetGroup = groupsArr.find((g) => {
          if (!g?.members || !g.members[uid]) return false;
          return eventsArr.some(
            (ev) =>
              ev.isLive &&
              ev.groupId &&
              (ev.groupId === g.id ||
                ev.groupId === g.groupId ||
                ev.groupId === g.firebaseId ||
                ev.groupId === g.key)
          );
        });
      }

      // 4) Fallback: qualquer grupo onde é membro
      if (!targetGroup) targetGroup = groupsArr.find((g) => g.members?.[uid]);

      if (targetGroup) {
        try {
          const { joinGroup } = useGroupStore.getState();
          if (typeof joinGroup === "function") {
            mapDebug("[MapView] 🎯 auto joinGroup no load →", targetGroup.id || targetGroup.groupId);
            joinGroup(targetGroup);
          }
        } catch (e) {
          console.warn("[MapView] auto joinGroup falhou:", e);
        }
      }
    });

    return () => {
      alive = false;
    };
  }, [
    mapReady,
    isViewerMode,
    groups,
    events,
    currentGroup,
    userEvents,
    eventsWithLive,
    canCurrentUserUseResolvedLiveEvent,
  ]);

  // -------------------------------------------------------------
  // AUTO-LISTEN REGISTRATIONS DO EVENTO ATIVO DO GRUPO
  // -------------------------------------------------------------
  useEffect(() => {
    if (!mapReady || isViewerMode) return;

    const activeEvent = liveEventState?.activeEvent || null;
    const activeEventId = activeEvent?.id || activeEvent?.eventId || null;
    if (!activeEventId) return;
    if (typeof listenEventRegistrations !== "function") return;

    if (lastRegistrationEventIdRef.current === activeEventId) return;
    lastRegistrationEventIdRef.current = activeEventId;

    try {
      mapDebug("[MapView] ▶️ auto listenEventRegistrations para", activeEventId);
      listenEventRegistrations(activeEventId);
    } catch (e) {
      console.warn("[MapView] auto listenEventRegistrations falhou:", e);
    }
  }, [mapReady, isViewerMode, liveEventState, listenEventRegistrations]);

  // -------------------------------------------------------------
  // EVENT ROUTE — via hook useEventRouteRenderer
  // -------------------------------------------------------------
  useEventRouteRenderer({
    mapReady: mapReady && eventRouteReady,
    isViewerMode,
    mapRef,
    currentGroup,
    events: eventsWithLive,
    groups,
    getEventRouteForMap: useEventsStore.getState().getEventRouteForMap,
    listenEvents: useEventsStore.getState().listenEvents,
    eventRouteLayerRef,
    eventDirectionLayerRef,
    altEventRoutesRef,
    extraEventRoutesRef,
    groupMarkersRef,
    lastEventRouteKeyRef,
    routePointsRef,
    onRoutePointsChange: () => setRoutePointsVersion((prev) => prev + 1),
  });

  // -------------------------------------------------------------
  // ✅ Garantir limpeza do trilho quando LIVE é desligado
  // -------------------------------------------------------------
  useEffect(() => {
    if (!mapReady) return;
    const { activeEvent, isLive } = liveEventState || {};
    if (activeEvent && isLive) return;
    const hasEventRoute =
      !!eventRouteLayerRef.current ||
      !!eventDirectionLayerRef.current ||
      Object.keys(altEventRoutesRef.current || {}).length > 0 ||
      (Array.isArray(routePointsRef.current?.main) &&
        routePointsRef.current.main.length > 0);
    if (!hasEventRoute) return;
    if (window.__clearEventRoute) window.__clearEventRoute();
  }, [mapReady, liveEventState]);

  // -------------------------------------------------------------
  // MULTI LIVE EVENT ROUTES (guia/admin)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!mapReady || isViewerMode) return;

    const map = mapRef.current;
    if (!map) return;

    const authUser = user || useAuthStore.getState().user;
    const uid = authUser?.uid || null;
    const role = String(authUser?.role || "free").toLowerCase();
    const isAdmin = role === "admin";
    const isGuideRole = role === "guide" || isAdmin;

    if (!uid || !isGuideRole) {
      const existing = extraEventRoutesRef.current || {};
      Object.keys(existing).forEach((eventId) => {
        if (existing[eventId]?.layer) {
          map.removeLayer(existing[eventId].layer);
        }
        delete existing[eventId];
      });
      extraEventFetchRef.current = {};
      return;
    }

    const eventsArr = Array.isArray(eventsWithLive)
      ? eventsWithLive
      : eventsWithLive && typeof eventsWithLive === "object"
        ? Object.values(eventsWithLive)
        : [];

    const groupsArr = Array.isArray(groups)
      ? groups
      : groups && typeof groups === "object"
        ? Object.values(groups)
        : [];

    const activeEvent = liveEventState?.activeEvent || null;
    const activeEventId = activeEvent?.id || activeEvent?.eventId || activeEvent?.key || null;

    const canSeeEvent = useEventsStore.getState().canUserSeeEventLive;

    const candidates = eventsArr.filter((ev) => {
      if (!ev || ev.isLive !== true) return false;
      const eventId = ev.id || ev.eventId || ev.key || null;
      if (!eventId) return false;
      if (activeEventId && eventId === activeEventId) return false;

      const isGuide =
        ev.guideId === uid ||
        ev.guideUid === uid ||
        ev.guide === uid ||
        ev.coGuideId === uid ||
        ev.coGuideUid === uid;

      if (!isAdmin && !isGuide) return false;
      if (typeof canSeeEvent === "function" && !canSeeEvent(eventId)) return false;
      return true;
    });

    const keepIds = new Set();
    const existing = extraEventRoutesRef.current || {};
    const pending = extraEventFetchRef.current || {};

    candidates.forEach((ev) => {
      const eventId = ev.id || ev.eventId || ev.key;
      if (!eventId) return;
      const url = normalizeTrekkinUrl(
        ev.routeGPXUrl || ev.routeUrl || ev.gpxUrl || null
      );
      if (!url) return;
      keepIds.add(String(eventId));

      const key = `${eventId}|${url}`;
      if (existing[eventId]?.key === key && existing[eventId]?.layer) {
        return;
      }
      if (existing[eventId]?.layer) {
        map.removeLayer(existing[eventId].layer);
      }
      existing[eventId] = { key, layer: null };
      if (pending[eventId] === key) return;
      pending[eventId] = key;

      const fetchUrl = buildGpxFetchUrl(url);
      if (!fetchUrl) return;

      fetch(fetchUrl)
        .then(async (res) => {
          if (!res.ok) throw new Error(`fetch ${res.status}`);
          const xml = await res.text();
          const parsed = await parseGPX(xml);
          if (!parsed?.points?.length) return;
          if (extraEventFetchRef.current?.[eventId] !== key) {
            mapDebug("[MapView] stale extra live route ignored:", eventId);
            return;
          }
          const zoom = map.getZoom?.() ?? 13;
          const pts = toLeafletOptimizedPolyline(
            parsed.points.map((p) => [p.lat, p.lon]),
            { zoom, chunkSize: 1600 }
          );
          const color = colorForEventId(eventId);
          const layer = L.polyline(pts, {
            color,
            weight: 4,
            opacity: 0.75,
            dashArray: "6 6",
            pane: "eventRoutesPane",
          }).addTo(map);
          existing[eventId] = { key, layer };
        })
        .catch((err) => {
          console.warn("[MapView] extra live route falhou:", eventId, err);
        })
        .finally(() => {
          if (pending[eventId] === key) delete pending[eventId];
        });
    });

    Object.keys(existing).forEach((eventId) => {
      if (!keepIds.has(eventId)) {
        if (existing[eventId]?.layer) {
          map.removeLayer(existing[eventId].layer);
        }
        delete existing[eventId];
      }
    });

    extraEventRoutesRef.current = existing;
    extraEventFetchRef.current = pending;
  }, [mapReady, isViewerMode, currentGroup, currentGroupId, groups, eventsWithLive, user]);

  // VIEWER — GUIA (verde) + VIEWER (azul)
  useEffect(() => {
    if (!mapReady || !isViewerMode) return;

    const map = mapRef.current;

    if (liveData?.lat && liveData?.lon) {
      const ll = [liveData.lat, liveData.lon];
      if (!map._guideMarker) map._guideMarker = L.marker(ll, { icon: trekkinIcon }).addTo(map);
      else map._guideMarker.setLatLng(ll);
      if (!viewerCenteredOnWalkerRef.current) {
        viewerCenteredOnWalkerRef.current = true;
        map.setView(ll, Math.max(16, Number(map.getZoom?.()) || 16), { animate: false });
      }
    }

    const viewerPos = usePanelsStore.getState().userPosition;
    if (viewerPos?.lat && viewerPos?.lon) {
      const llv = [viewerPos.lat, viewerPos.lon];
      if (!map._viewerMarker) map._viewerMarker = L.marker(llv, { icon: trekkinIconBlue }).addTo(map);
      else map._viewerMarker.setLatLng(llv);
    }
  }, [mapReady, isViewerMode, liveData, trekkinIcon, trekkinIconBlue]);

  // VIEWER — rasto do guia
  useEffect(() => {
    if (!mapReady || !isViewerMode || !Array.isArray(livePath)) return;

    const map = mapRef.current;
    const zoom = map.getZoom?.() ?? 13;
    const pts = toLeafletOptimizedPolyline(
      livePath.map((p) => [p.lat, p.lon]),
      { zoom, chunkSize: 1400 }
    );

    if (!viewerPathRef.current) {
      viewerPathRef.current = L.polyline(pts, { color: "#28a745", weight: 4, opacity: 0.85 }).addTo(map);
    } else {
      viewerPathRef.current.setLatLngs(pts);
    }
  }, [livePath, mapReady, isViewerMode]);

  // Trail base
  useEffect(() => {
    if (!mapReady) return;
    let rafId = null;
    let timeoutId = null;
    try {
      const renderSeq = ++trailBaseRenderSeqRef.current;
      const renderStartedAt = Date.now();
      const map = mapRef.current;
      const zoom = map?.getZoom?.() ?? 13;
      const rawArr = (Array.isArray(trailBase) ? trailBase : [])
        .map((p) => [Number(p?.lat), Number(p?.lon ?? p?.lng)])
        .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
      if (rawArr.length < 2) {
        if (trailBaseHadPointsRef.current) {
          baseLayerRef.current?.setLatLngs?.([]);
          baseLayerRef.current?.redraw?.();
          renderDirectionMarkers(baseDirectionLayerRef, [], {
            color: "#0d6efd",
            pane: "trailDirectionPane",
            targetArrows: 48,
          });
          trailBaseHadPointsRef.current = false;
        }
        return;
      }
      trailBaseHadPointsRef.current = true;
      const arr = toLeafletOptimizedPolyline(rawArr, {
        zoom,
        chunkSize: 1600,
        // Trilho ativo deve manter máxima fidelidade (sem simplificação visual agressiva)
        targetPoints: 500000,
      });
      baseLayerRef.current.setLatLngs(arr);
      baseLayerRef.current.redraw?.();
      logEvent("info", "MAP_TRAILBASE_LINE_RENDER", {
        rawPoints: rawArr.length,
        renderMs: Date.now() - renderStartedAt,
        zoom,
      });
      const proRasterMounted =
        isAndroid &&
        effectiveMapStyle === "pro" &&
        !useLocalProPmtiles &&
        map &&
        proRasterBaseRef.current &&
        map.hasLayer?.(proRasterBaseRef.current);
      if (map && !proRasterMounted) {
        map.invalidateSize(false);
      }

      const renderDirections = () => {
        if (trailBaseRenderSeqRef.current !== renderSeq) return;
        const dirStartedAt = Date.now();
        const directionSource = flattenLatLngPairs(arr);
        const directionPoints = followDirectionReversedForMap
          ? [...directionSource].reverse()
          : directionSource;
        renderDirectionMarkers(baseDirectionLayerRef, directionPoints, {
          color: "#0d6efd",
          pane: "trailDirectionPane",
          targetArrows: 48,
        });
        logEvent("info", "MAP_TRAILBASE_DIRECTIONS_RENDER", {
          points: directionPoints.length,
          renderMs: Date.now() - dirStartedAt,
          zoom,
        });
      };

      if (typeof window !== "undefined" && window.requestAnimationFrame) {
        rafId = window.requestAnimationFrame(() => {
          timeoutId = window.setTimeout(renderDirections, 0);
        });
      } else {
        timeoutId = setTimeout(renderDirections, 0);
      }
    } catch (e) {
      console.warn("⚠️ Android trailBase render fail:", e);
    }
    return () => {
      if (rafId && typeof window !== "undefined") {
        window.cancelAnimationFrame?.(rafId);
      }
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    trailBase,
    mapReady,
    renderDirectionMarkers,
    followDirectionReversedForMap,
    directionZoomTick,
  ]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!trailStartAssistLayerRef.current) {
      trailStartAssistLayerRef.current = L.polyline([], {
        color: "#6b7280",
        weight: 4,
        opacity: 0.9,
        dashArray: "10 8",
        interactive: false,
      }).addTo(map);
    }
    const layer = trailStartAssistLayerRef.current;
    const route = Array.isArray(trailBase) ? trailBase : [];
    const userLat = Number(userPosition?.lat);
    const userLon = Number(userPosition?.lon);
    if (route.length < 2 || !Number.isFinite(userLat) || !Number.isFinite(userLon)) {
      layer.setLatLngs([]);
      return;
    }
    const startLat = Number(route?.[0]?.lat);
    const startLon = Number(route?.[0]?.lon);
    if (!Number.isFinite(startLat) || !Number.isFinite(startLon)) {
      layer.setLatLngs([]);
      return;
    }

    let minDist = Number.POSITIVE_INFINITY;
    const stride = Math.max(1, Math.floor(route.length / 1800));
    for (let i = 0; i < route.length; i += stride) {
      const p = route[i];
      const lat = Number(p?.lat);
      const lon = Number(p?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      const d = haversineMeters({ lat: userLat, lon: userLon }, { lat, lon });
      if (d < minDist) minDist = d;
    }

    const enterRouteMeters = 35;
    if (Number.isFinite(minDist) && minDist <= enterRouteMeters) {
      layer.setLatLngs([]);
      return;
    }

    layer.setLatLngs([
      [userLat, userLon],
      [startLat, startLon],
    ]);
  }, [mapReady, trailBase, userPosition?.lat, userPosition?.lon]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!trailPreviewLayerRef.current) {
      trailPreviewLayerRef.current = L.layerGroup().addTo(map);
    }

    const layer = trailPreviewLayerRef.current;
    layer.clearLayers();
    const rows = Array.isArray(trailPreviewMapState)
      ? trailPreviewMapState
      : [];
    const palette = ["#2563eb", "#dc2626", "#16a34a", "#9333ea", "#ea580c", "#0891b2"];
    rows.forEach((item, index) => {
      const raw = (Array.isArray(item?.points) ? item.points : [])
        .map((p) => [Number(p?.lat), Number(p?.lon ?? p?.lng)])
        .filter(
          (pair) => Number.isFinite(pair?.[0]) && Number.isFinite(pair?.[1])
        );
      if (raw.length < 2) return;
      const zoom = map.getZoom?.() ?? 13;
      const optimized = toLeafletOptimizedPolyline(raw, {
        zoom,
        chunkSize: 1600,
        targetPoints: 500000,
      });
      L.polyline(optimized, {
        color: palette[index % palette.length],
        weight: 5,
        opacity: 0.9,
        interactive: false,
        pane: "trailStagesLinePane",
      }).addTo(layer);
    });
  }, [mapReady, trailPreviewMapState]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!trailStagesLayerRef.current) {
      trailStagesLayerRef.current = L.layerGroup().addTo(map);
    }
    const layer = trailStagesLayerRef.current;
    layer.clearLayers();
    if (!trailStagesMapState?.on) return;
    const palette = ["#22c55e", "#20c997", "#fd7e14", "#6f42c1", "#dc3545", "#0d6efd"];
    const segments = Array.isArray(trailStagesMapState?.segments)
      ? trailStagesMapState.segments
      : [];
    segments.forEach((seg, idx) => {
      const points = (Array.isArray(seg?.points) ? seg.points : [])
        .map((p) => [Number(p?.lat), Number(p?.lon ?? p?.lng)])
        .filter((pair) => Number.isFinite(pair?.[0]) && Number.isFinite(pair?.[1]));
      if (points.length < 2) return;
      let stageMeters = 0;
      let stageElevGain = 0;
      for (let j = 1; j < points.length; j += 1) {
        stageMeters += haversineMeters(
          { lat: Number(points[j - 1][0]), lon: Number(points[j - 1][1]) },
          { lat: Number(points[j][0]), lon: Number(points[j][1]) }
        );
        const prevEle = Number(
          seg?.points?.[j - 1]?.ele ??
            seg?.points?.[j - 1]?.elevation ??
            seg?.points?.[j - 1]?.alt
        );
        const nextEle = Number(
          seg?.points?.[j]?.ele ??
            seg?.points?.[j]?.elevation ??
            seg?.points?.[j]?.alt
        );
        if (Number.isFinite(prevEle) && Number.isFinite(nextEle)) {
          const diff = nextEle - prevEle;
          if (diff > 0) stageElevGain += diff;
        }
      }
      const payloadElevGain = Number(seg?.elevGain);
      if (Number.isFinite(payloadElevGain) && payloadElevGain >= 0) {
        stageElevGain = payloadElevGain;
      }
      const stageKmLabel = `${(stageMeters / 1000).toFixed(2)} km · ${Math.round(
        stageElevGain
      )} m D+`;
      const color = palette[idx % palette.length];
      const poly = L.polyline(points, {
        color,
        weight: 5,
        opacity: 0.9,
        dashArray: "8 6",
        smoothFactor: 0,
        pane: "trailStagesLinePane",
        interactive: false,
      });
      poly.addTo(layer);
      const end = points[points.length - 1];
      if (end) {
        const numberIcon = L.divIcon({
          className: "tk-stage-number-icon",
          html: `<div style="width:22px;height:22px;border-radius:999px;background:${color};border:2px solid #fff;color:#fff;font-weight:700;font-size:12px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.25);">${idx + 1}</div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });
        const numberMarker = L.marker(end, {
          icon: numberIcon,
          pane: "trailStagesPointPane",
          interactive: false,
        });
        numberMarker.bindTooltip(stageKmLabel, {
          permanent: true,
          direction: "top",
          offset: [0, -10],
          opacity: 0.95,
          className: "tk-stage-km-label",
        });
        numberMarker.addTo(layer);
      }
    });
  }, [mapReady, trailStagesMapState]);

  // Direção visual da rota principal LIVE/evento
  useEffect(() => {
    if (!mapReady) return;
    const mainRoutePoints = routePointsRef.current?.main || [];
    const map = mapRef.current;
    const zoom = map?.getZoom?.() ?? 13;
    const optimizedMainRoute = flattenLatLngPairs(
      toLeafletOptimizedPolyline(
        mainRoutePoints.map((p) => [p.lat, p.lon]),
        { zoom, chunkSize: 1600 }
      )
    );
    const directionPoints = followDirectionReversedForMap
      ? [...optimizedMainRoute].reverse()
      : optimizedMainRoute;
    renderDirectionMarkers(eventDirectionLayerRef, directionPoints, {
      color: "#ff6600",
      pane: "trailDirectionPane",
      targetArrows: 56,
      maxArrows: 72,
      size: 17,
    });
  }, [
    mapReady,
    routePointsVersion,
    renderDirectionMarkers,
    followDirectionReversedForMap,
    directionZoomTick,
  ]);

  // Recorded trail
  useEffect(() => {
    if (!mapReady || !recordLayerRef.current) return;
    if (/Android/i.test(navigator.userAgent || "")) return;

    try {
      const pts = points || [];
      const map = mapRef.current;
      const hasGaps = pts.some((p) => p?.gap);

      if (hasGaps) {
        const segments = [];
        let current = [];
        pts.forEach((p) => {
          if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lon)) return;
          if (p.gap && current.length) {
            segments.push(current);
            current = [];
          }
          current.push([p.lat, p.lon]);
        });
        if (current.length) segments.push(current);
        recordLayerRef.current.setLatLngs(segments);
        pathLatLngsRef.current = segments.flat();
      } else if (pts.length > pathLatLngsRef.current.length) {
        const extra = pts.slice(pathLatLngsRef.current.length);
        extra.forEach((p) => {
          if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lon)) return;
          recordLayerRef.current.addLatLng([p.lat, p.lon]);
        });
        pathLatLngsRef.current.push(
          ...extra
            .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lon))
            .map((p) => [p.lat, p.lon])
        );
      } else {
        pathLatLngsRef.current = pts
          .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lon))
          .map((p) => [p.lat, p.lon]);
        recordLayerRef.current.setLatLngs(pathLatLngsRef.current);
      }

      if (map && /Android/i.test(navigator.userAgent)) {
        map.invalidateSize(false);
        recordLayerRef.current.redraw?.();
      }
    } catch (e) {
      console.warn("⚠️ Android recorded trail render fail:", e);
    }
  }, [points, mapReady]);

  // Recorded trail (Android render buffer)
  useEffect(() => {
    if (!mapReady || !recordLayerRef.current) return;
    if (!/Android/i.test(navigator.userAgent || "")) return;

    // The native recording remains complete. This cap only controls the
    // Leaflet preview kept live on lower-end Android devices.
    const MAX_RENDER_POINTS = 2000;
    const shouldBreakSegment = (prev, next) => {
      if (!prev || !next) return false;
      // Only an explicit recording gap (pause/resume) should split the track.
      // Android background buffering can legitimately deliver consecutive
      // points more than 30 seconds apart; splitting those points made the
      // live MapView incomplete while the saved history remained correct.
      return next?.gap === true;
    };

    const buildSegments = (arr) =>
      (() => {
        const segments = [];
        let current = [];
        let prev = null;
        arr.forEach((p) => {
          if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lon)) return;
          if (prev && shouldBreakSegment(prev, p)) {
            if (current.length) segments.push(current);
            current = [];
          }
          current.push([p.lat, p.lon]);
          prev = p;
        });
        if (current.length) segments.push(current);
        return segments;
      })();

    const sanitizeWindow = (arr) =>
      arr
        .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lon))
        .map((p) => ({
          lat: p.lat,
          lon: p.lon,
          ts: Number(p.ts || p.time || 0) || null,
          gap: p.gap === true,
        }));

    const downsamplePoints = (arr, maxPoints) => {
      if (!Array.isArray(arr) || arr.length <= maxPoints) return arr || [];
      const step = Math.ceil(arr.length / maxPoints);
      const sampled = [];
      for (let i = 0; i < arr.length; i += step) {
        sampled.push(arr[i]);
      }
      const last = arr[arr.length - 1];
      if (sampled[sampled.length - 1] !== last) sampled.push(last);
      return sampled;
    };

    const getStorePoints = () => {
      try {
        const st = usePanelsStore.getState();
        return Array.isArray(st?.points) ? st.points : [];
      } catch {
        return [];
      }
    };

    const renderSignature = (arr) => {
      if (!Array.isArray(arr) || !arr.length) return "0";
      const first = arr[0] || {};
      const last = arr[arr.length - 1] || {};
      return [
        arr.length,
        Number(first.ts || first.time || 0) || 0,
        Number(last.ts || last.time || 0) || 0,
        Number(last.lat || 0).toFixed(6),
        Number(last.lon || 0).toFixed(6),
      ].join(":");
    };

    const logFirstResumeRender = (renderStartedAt, mode) => {
      try {
        const resumePerf = window.__TREKKIN_ANDROID_RESUME_PERF;
        if (resumePerf?.startedAt && !resumePerf.firstRenderLogged) {
          resumePerf.firstRenderLogged = true;
          logEvent("gps", "ANDROID_RESUME_PERF_FIRST_RENDER", {
            resumeToRenderMs: Date.now() - resumePerf.startedAt,
            renderMs: Date.now() - renderStartedAt,
            pointsBefore: Number(resumePerf.pointsBefore || 0),
            renderWindowPoints: androidRenderWindowRef.current.length,
            sinceBgMs: Number(resumePerf.sinceBgMs || 0),
            mode,
          });
        }
      } catch {}
    };

    const applyWindow = () => {
      const renderStartedAt = Date.now();
      const latlngs = buildSegments(androidRenderWindowRef.current);
      recordLayerRef.current.setLatLngs(latlngs);
      recordLayerRef.current.redraw?.();
      logFirstResumeRender(renderStartedAt, "reset");
    };

    const handleAppend = (pts) => {
      if (!Array.isArray(pts) || !pts.length) return;
      let needsFullRedraw = false;
      const appendQueue = [];

      pts.forEach((p) => {
        if (!p) return;
        if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) return;
        const next = {
          lat: p.lat,
          lon: p.lon,
          ts: Number(p.ts || p.time || 0) || null,
          gap: p.gap === true,
        };
        const prev = androidRenderLastPointRef.current;
        if (prev && shouldBreakSegment(prev, next)) {
          needsFullRedraw = true;
        }
        androidRenderWindowRef.current.push(next);
        androidRenderLastPointRef.current = next;
        appendQueue.push(next);
      });
      if (androidRenderWindowRef.current.length > MAX_RENDER_POINTS) {
        needsFullRedraw = true;
        const full = sanitizeWindow(getStorePoints());
        androidRenderWindowRef.current = downsamplePoints(full, MAX_RENDER_POINTS);
        androidRenderLastPointRef.current =
          androidRenderWindowRef.current[androidRenderWindowRef.current.length - 1] || null;
      }
      if (needsFullRedraw) {
        applyWindow();
        return;
      }
      if (!appendQueue.length) return;
      try {
        const renderStartedAt = Date.now();
        const rings = recordLayerRef.current.getLatLngs();
        const lastRing = Array.isArray(rings?.[0]) ? rings[rings.length - 1] : rings;
        appendQueue.forEach((p) => {
          recordLayerRef.current.addLatLng([p.lat, p.lon], lastRing);
        });
        recordLayerRef.current.redraw?.();
        logFirstResumeRender(renderStartedAt, "append");
      } catch (e) {
        applyWindow();
      }
    };

    const handleRender = (ev) => {
      const pts = ev?.detail?.points;
      handleAppend(pts);
      if (ev?.detail?.reason === "resume_compact_buffer") {
        window.dispatchEvent(
          new CustomEvent("trekkin:android-track-resume-ready")
        );
      }
    };

    const handleReset = (ev) => {
      const pts = Array.isArray(ev?.detail?.points) ? ev.detail.points : [];
      const storePts = getStorePoints();
      const preferEventPoints = ev?.detail?.preferEventPoints === true;
      const sourcePts =
        preferEventPoints && pts.length
          ? pts
          : storePts.length > pts.length
          ? storePts
          : pts;
      const clean = sanitizeWindow(sourcePts);
      const signature = renderSignature(clean);
      const now = Date.now();
      if (
        signature &&
        signature === androidTrackRenderSignatureRef.current &&
        now - Number(androidTrackRenderSignatureTsRef.current || 0) < 8000
      ) {
        recordLayerRef.current?.bringToFront?.();
        return;
      }
      androidTrackRenderSignatureRef.current = signature;
      androidTrackRenderSignatureTsRef.current = now;
      androidRenderWindowRef.current = downsamplePoints(clean, MAX_RENDER_POINTS);
      androidRenderLastPointRef.current =
        androidRenderWindowRef.current[androidRenderWindowRef.current.length - 1] || null;
      applyWindow();
      try {
        const resumeStartedAt = Number(
          window?.__TREKKIN_ANDROID_RESUME_PERF?.startedAt || 0
        );
        if (resumeStartedAt > 0 && Date.now() - resumeStartedAt < 12000) {
          logEvent("gps", "ANDROID_TRACK_RESET_RENDER", {
            eventPoints: pts.length,
            storePoints: storePts.length,
            renderedPoints: androidRenderWindowRef.current.length,
            preferEventPoints,
            reason: ev?.detail?.reason || null,
            resumeToRenderMs: Date.now() - resumeStartedAt,
          });
        }
      } catch {}
    };

    window.addEventListener("gps:render", handleRender);
    window.addEventListener("gps:render-reset", handleReset);

    const initialPoints = getStorePoints();
    if (initialPoints.length) {
      handleReset({
        detail: { points: initialPoints, reason: "android_render_effect_init" },
      });
    }

    let lastBaseRepaintAt = 0;
    const rebuildFromStoreOnResume = ({ forceRepaint = false } = {}) => {
      const storePoints = getStorePoints();
      if (!storePoints.length) return;
      if (androidRenderWindowRef.current.length >= storePoints.length) {
        if (!forceRepaint) return;
        const now = Date.now();
        if (now - lastBaseRepaintAt < 1200) return;
        if (now - Number(androidTrackBaseRepaintAtRef.current || 0) < 1200) return;
        lastBaseRepaintAt = now;
        androidTrackBaseRepaintAtRef.current = now;
        const repaintStartedAt = now;
        recordLayerRef.current?.bringToFront?.();
        if (forceRepaint) applyWindow();
        logEvent("gps", "ANDROID_TRACK_REPAINT_AFTER_BASE", {
          points: androidRenderWindowRef.current.length,
          repaintMs: Date.now() - repaintStartedAt,
        });
        return;
      }
      handleReset({
        detail: { points: storePoints, reason: "android_resume_store_rebuild" },
      });
      recordLayerRef.current?.bringToFront?.();
    };

    const handleVisible = () => {
      if (document?.visibilityState === "visible") {
        if (isAndroid) {
          window.requestAnimationFrame?.(() => {
            rebuildFromStoreOnResume({ forceRepaint: true });
          });
          return;
        }
        window.setTimeout(rebuildFromStoreOnResume, 0);
      }
    };
    const handleMapBaseVisible = () => {
      const resumeStartedAt = Number(
        window?.__TREKKIN_ANDROID_RESUME_PERF?.startedAt || 0
      );
      const inResumeWindow =
        resumeStartedAt > 0 && Date.now() - resumeStartedAt < 12000;
      if (!inResumeWindow && Date.now() - lastBaseRepaintAt < 5000) return;
      if (inResumeWindow && androidRenderWindowRef.current.length) {
        recordLayerRef.current?.bringToFront?.();
        return;
      }
      window.setTimeout(
        () => rebuildFromStoreOnResume({ forceRepaint: !inResumeWindow }),
        0
      );
    };

    document?.addEventListener?.("visibilitychange", handleVisible);
    window.addEventListener("trekkin:map-base-visible", handleMapBaseVisible);

    return () => {
      window.removeEventListener("gps:render", handleRender);
      window.removeEventListener("gps:render-reset", handleReset);
      document?.removeEventListener?.("visibilitychange", handleVisible);
      window.removeEventListener("trekkin:map-base-visible", handleMapBaseVisible);
    };
  }, [isAndroid, mapReady]);

  // Android: clear polyline when recording points are cleared
  useEffect(() => {
    if (!mapReady || !recordLayerRef.current) return;
    if (!/Android/i.test(navigator.userAgent || "")) return;
    if (Array.isArray(points) && points.length === 0) {
      androidRenderWindowRef.current = [];
      androidRenderLastPointRef.current = null;
      recordLayerRef.current.setLatLngs([]);
      recordLayerRef.current.redraw?.();
    }
  }, [points, mapReady]);

  // Android: hard reset render window on new recording start.
  // Prevents stale points from previous sessions from pulling the marker/map backwards.
  useEffect(() => {
    if (!mapReady || !recordLayerRef.current) {
      androidWasRecordingRef.current = !!isRecording;
      return;
    }
    if (!/Android/i.test(navigator.userAgent || "")) {
      androidWasRecordingRef.current = !!isRecording;
      return;
    }

    const wasRecording = !!androidWasRecordingRef.current;
    if (isRecording && !wasRecording) {
      androidRenderWindowRef.current = [];
      androidRenderLastPointRef.current = null;
      recordLayerRef.current.setLatLngs([]);
      recordLayerRef.current.redraw?.();
    } else if (!isRecording && wasRecording) {
      androidRenderWindowRef.current = [];
      androidRenderLastPointRef.current = null;
      androidTrackRenderSignatureRef.current = "";
      androidTrackRenderSignatureTsRef.current = 0;
      recordLayerRef.current.setLatLngs([]);
      recordLayerRef.current.redraw?.();
    }

    androidWasRecordingRef.current = !!isRecording;
  }, [isRecording, mapReady]);


  // HUD accuracy color
  const hudColor = (() => {
    const acc = userPosition?.acc;
    if (!acc) return isRecording ? "#dc3545" : "#6c757d";
    if (acc <= 15) return "#28a745";
    if (acc <= 40) return "#ffc107";
    return "#dc3545";
  })();

  // CLEAR GLOBAL
  useEffect(() => {
    window.__clearEventRoute = () => {
      try {
        const map = mapRef.current;
        if (!map) return;

        if (eventRouteLayerRef.current) {
          map.removeLayer(eventRouteLayerRef.current);
          eventRouteLayerRef.current = null;
        }
        if (eventDirectionLayerRef.current) {
          eventDirectionLayerRef.current.clearLayers();
        }

        const altLayers = altEventRoutesRef.current || {};
        Object.keys(altLayers).forEach((altId) => {
          map.removeLayer(altLayers[altId]);
          delete altLayers[altId];
        });

        const extraRoutes = extraEventRoutesRef.current || {};
        Object.keys(extraRoutes).forEach((eventId) => {
          if (extraRoutes[eventId]?.layer) {
            map.removeLayer(extraRoutes[eventId].layer);
          }
          delete extraRoutes[eventId];
        });
        extraEventFetchRef.current = {};

        const memberMarkers = groupMarkersRef.current || {};
        Object.keys(memberMarkers).forEach((markerId) => {
          map.removeLayer(memberMarkers[markerId]);
          delete memberMarkers[markerId];
        });

        routePointsRef.current = { main: [], alts: {} };
        setRoutePointsVersion((prev) => prev + 1);
        lastEventRouteKeyRef.current = null;
      } catch {}
    };
  }, []);

  // Android: keep overlays fresh without invalidating the base map.
  // invalidateSize() on resume was forcing Pro raster tile reloads, producing
  // the white screen/flash seen before the recorded track became visible.
  useEffect(() => {
    if (!/Android/i.test(navigator.userAgent)) return;

    const map = mapRef.current;
    if (!map) return;

    const id = setInterval(() => {
      try {
        recordLayerRef.current?.redraw?.();
        eventRouteLayerRef.current?.redraw?.();
        Object.values(extraEventRoutesRef.current || {}).forEach((item) =>
          item?.layer?.redraw?.()
        );
        Object.values(altEventRoutesRef.current || {}).forEach((l) => l?.redraw?.());
      } catch {}
    }, 1500);

    return () => clearInterval(id);
  }, [mapReady]);

  // -------------------------------------------------------------
  // Render
  // -------------------------------------------------------------
  const partnerRecommendationCategories = Array.from(
    new Set(partnerRecommendations.map((item) => item.recommendationCategory).filter(Boolean))
  );
  const visiblePartnerRecommendations = partnerRecommendations.filter(
    (item) =>
      partnerRecommendationCategory === "all" ||
      item.recommendationCategory === partnerRecommendationCategory
  );
  const currentPartnerRecommendation =
    visiblePartnerRecommendations[
      Math.min(partnerRecommendationIndex, Math.max(0, visiblePartnerRecommendations.length - 1))
    ] || null;
  const currentPartnerRecommendationImage = currentPartnerRecommendation
    ? buildWaypointImageCandidates(
        currentPartnerRecommendation.imageUrl ||
          currentPartnerRecommendation.image ||
          currentPartnerRecommendation.photoUrl ||
          currentPartnerRecommendation.photo ||
          currentPartnerRecommendation.coverImage ||
          null,
        isIOS
      )[0] || ""
    : "";
  const currentPartnerRecommendationIcon = `${base}icons/${String(
    currentPartnerRecommendation?.icon ||
      waypointCategoryPalette?.[currentPartnerRecommendation?.categoryId]?.icon ||
      "waypoints.svg"
  ).trim() || "waypoints.svg"}`;
  const partnerRecommendationIds = new Set(partnerRecommendations.map((item) => String(item.id)));

  const addPartnerRecommendation = async (waypoint) => {
    const partnerId = String(
      partnerRecommendationAdminTarget?.id || partnerRecommendationsPanel?.id || ""
    ).trim();
    const waypointId = String(waypoint?.id || "").trim();
    if (!partnerId || !waypointId) return;
    const category = normalizePartnerRecommendationCategory(
      waypointCategoryPalette?.[waypoint?.categoryId]?.name ||
        waypoint?.categoryName ||
        waypoint?.categoryId ||
        (language === "en" ? "Other" : "Outros")
    );
    await update(ref(db), {
      [`waypoints/${partnerId}/recommendedWaypoints/${waypointId}`]: {
        category,
        addedAt: Date.now(),
        addedBy: user?.uid || null,
      },
      [`waypoints/${waypointId}/partnerRecommendationOwnerId`]: partnerId,
    });
    showToast(language === "en" ? "Recommended place added." : "Local recomendado adicionado.", "success");
  };

  const removePartnerRecommendation = async (waypoint) => {
    const partnerId = String(
      partnerRecommendationAdminTarget?.id || partnerRecommendationsPanel?.id || ""
    ).trim();
    const waypointId = String(waypoint?.id || "").trim();
    if (!partnerId || !waypointId) return;
    await update(ref(db), {
      [`waypoints/${partnerId}/recommendedWaypoints/${waypointId}`]: null,
      [`waypoints/${waypointId}/partnerRecommendationOwnerId`]: null,
    });
    showToast(language === "en" ? "Recommended place removed." : "Local recomendado removido.", "success");
  };

  return (
    <>
      <div
        id="map-wrapper"
        style={{
          position: "fixed",
          inset: 0,
          overflow: "hidden",
          zIndex: hideTopControls ? 1 : 0,
        }}
      >
        <div
          id="map-visual"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: `${squareSize}px`,
            height: `${squareSize}px`,
            transform: "translate(-50%, -50%)",
            transformOrigin: "50% 50%",
            willChange: "transform",
            pointerEvents: "auto",
            // Leaflet owns pan/pinch. Letting the WebView handle the same
            // gesture as page zoom duplicates the transform and can exhaust
            // the mobile graphics process.
            touchAction: "none",
            zIndex: 1,
          }}
        >
          <div
            id="mapid"
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        </div>

        {!/Android/i.test(navigator.userAgent) && (
          <div
            id="map-interaction"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2,
              pointerEvents: "none",
              background: "transparent",
            }}
          />
        )}
      </div>

      <TrekkinActionModal
        open={Boolean(adminQuickCreatePoint)}
        mark="+"
        title="Criar neste ponto"
        text={adminQuickCreatePoint
          ? `${adminQuickCreatePoint.lat.toFixed(6)}, ${adminQuickCreatePoint.lon.toFixed(6)}`
          : ""}
        cancelLabel="Cancelar"
        onClose={() => setAdminQuickCreatePoint(null)}
        actions={adminQuickCreatePoint ? [
          {
            label: "Criar Waypoint",
            primary: true,
            onClick: () => {
              const point = adminQuickCreatePoint;
              setAdminQuickCreatePoint(null);
              setActivePanel("adminWaypoints", {
                pickMode: "create",
                lat: point.lat,
                lon: point.lon,
              });
            },
          },
          {
            label: "Criar Trecho",
            onClick: () => {
              const point = adminQuickCreatePoint;
              setAdminQuickCreatePoint(null);
              window.dispatchEvent(
                new CustomEvent("trekkin:navgrid-draw-start", { detail: { keep: false } })
              );
              window.dispatchEvent(
                new CustomEvent("trekkin:navgrid-draw-seed", {
                  detail: { lat: point.lat, lon: point.lon },
                })
              );
              showToast("Desenho iniciado neste ponto.", "success");
            },
          },
          {
            label: "Criar Segmento",
            onClick: () => {
              setAdminQuickCreatePoint(null);
              setActivePanel("mapSelectPoint", {
                preserve: true,
                mode: "segment_start",
                returnPanel: "adminNavGrid",
                returnProps: {
                  from: "segment_create",
                  segmentCreateDraft: {
                    name: "",
                    startNodeId: "",
                    endNodeId: "",
                    startPoint: null,
                    endPoint: null,
                    xpFirstPlace: 0,
                    activityMode: "walk",
                  },
                },
              });
              showToast("Escolhe o ponto inicial do segmento competitivo.", "success");
            },
          },
        ] : []}
      />

      {partnerRecommendationsPanel && typeof document !== "undefined" && createPortal(
        <div
          className="tk-partner-recommendations-overlay"
          onClick={() => setPartnerRecommendationsPanel(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            background: "rgba(15,23,42,0.46)",
          }}
        >
          <section
            className="tk-theme-card tk-partner-recommendations-card"
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(520px, 100%)",
              maxHeight: "82dvh",
              overflowY: "auto",
              borderRadius: 18,
              padding: 16,
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.1)",
              boxShadow: "0 20px 48px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, color: "#1f6f36", fontSize: 20 }}>
                  {language === "en" ? "Recommended places" : "Locais recomendados"}
                </h3>
                <div className="tk-partner-recommendations-subtitle" style={{ marginTop: 3, fontSize: 12, color: "#667085" }}>
                  {partnerRecommendationsPanel.name || (language === "en" ? "Trekkin partner" : "Parceiro Trekkin")}
                </div>
              </div>
              <button
                type="button"
                aria-label={language === "en" ? "Close" : "Fechar"}
                onClick={() => setPartnerRecommendationsPanel(null)}
                className="tk-partner-recommendations-close"
                style={{ border: 0, background: "transparent", fontSize: 24, cursor: "pointer" }}
              >
                ×
              </button>
            </div>

            {isAdminMode && (
              <button
                type="button"
                className="tk-waypoint-list-action"
                onClick={async () => {
                  setPartnerRecommendationAdminTarget(partnerRecommendationsPanel);
                  setPartnerRecommendationsPanel(null);
                  setExclusiveFocusedWaypoint(null);
                  setWaypointCategories([]);
                  setWaypointsVisible(true);
                  try {
                    window.localStorage.setItem("waypointsLayerOn", "1");
                    mapRef.current?.closePopup?.();
                    const result = await fetchAndCacheWaypoints({ force: true });
                    const list = Array.isArray(result?.list)
                      ? result.list
                      : loadCachedWaypoints();
                    setCachedWaypoints(Array.isArray(list) ? list : []);
                  } catch (error) {
                    console.warn("Falha ao carregar waypoints para recomendação", error);
                    showToast(
                      language === "en"
                        ? "Could not load the waypoints."
                        : "Não foi possível carregar os waypoints.",
                      "error"
                    );
                    return;
                  }
                  showToast(
                    language === "en"
                      ? "Open a waypoint and use Add waypoint to recommended list."
                      : "Abre um waypoint e usa Adicionar waypoint à lista.",
                    "success",
                    6000
                  );
                }}
                style={{ width: "100%", marginTop: 14 }}
              >
                <img src={`${base}icons/plus.svg`} alt="" />
                <span>{language === "en" ? "Select waypoints on the map" : "Selecionar waypoints no mapa"}</span>
                <b aria-hidden="true">›</b>
              </button>
            )}

            {partnerRecommendationCategories.length > 1 && (
              <div style={{ display: "flex", gap: 7, overflowX: "auto", marginTop: 14, paddingBottom: 2 }}>
                {["all", ...partnerRecommendationCategories].map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={`tk-partner-recommendations-filter${partnerRecommendationCategory === category ? " is-active" : ""}`}
                    onClick={() => setPartnerRecommendationCategory(category)}
                    style={{
                      border: `1px solid ${partnerRecommendationCategory === category ? "#1f8f48" : "#d0d5dd"}`,
                      borderRadius: 999,
                      padding: "7px 11px",
                      background: partnerRecommendationCategory === category ? "#eaf7ed" : "transparent",
                      color: "inherit",
                      whiteSpace: "nowrap",
                      fontWeight: 700,
                    }}
                  >
                    {category === "all"
                      ? (language === "en" ? "All" : "Todos")
                      : localizedWaypointCategory(category, language)}
                  </button>
                ))}
              </div>
            )}

            {!currentPartnerRecommendation ? (
              <div className="tk-partner-recommendations-empty" style={{ marginTop: 16, padding: 18, textAlign: "center", color: "#667085" }}>
                {language === "en" ? "No recommended places in this category." : "Sem locais recomendados nesta categoria."}
              </div>
            ) : (
              <article className="tk-partner-recommendation-item" style={{ marginTop: 14, border: "1px solid #d0d5dd", borderRadius: 14, overflow: "hidden" }}>
                <div style={{ padding: 14 }}>
                  <div className="tk-partner-recommendation-heading">
                    <img
                      className={`tk-partner-recommendation-thumbnail${currentPartnerRecommendationImage ? " has-photo" : " is-icon"}`}
                      src={currentPartnerRecommendationImage || currentPartnerRecommendationIcon}
                      alt=""
                      onError={(event) => {
                        if (event.currentTarget.src !== currentPartnerRecommendationIcon) {
                          event.currentTarget.src = currentPartnerRecommendationIcon;
                          event.currentTarget.classList.remove("has-photo");
                          event.currentTarget.classList.add("is-icon");
                        }
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div className="tk-partner-recommendation-category" style={{ color: "#1f8f48", fontSize: 12, fontWeight: 800 }}>
                        {localizedWaypointCategory(currentPartnerRecommendation.recommendationCategory, language, currentPartnerRecommendation)}
                      </div>
                      <h4 style={{ margin: "4px 0 0", fontSize: 18 }}>{localizedWaypointName(currentPartnerRecommendation, language)}</h4>
                    </div>
                  </div>
                  {localizedWaypointDescription(currentPartnerRecommendation, language) && (
                    <p className="tk-partner-recommendation-description" style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.45, color: "#667085" }}>
                      {localizedWaypointDescription(currentPartnerRecommendation, language)}
                    </p>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: isAdminMode ? "1fr 1fr" : "1fr", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      className="tk-waypoint-list-action"
                      onClick={() => {
                        navigateToWaypoint(currentPartnerRecommendation);
                        setPartnerRecommendationsPanel(null);
                      }}
                    >
                      <img src={`${base}icons/${waypointStartIcon(activityMode)}`} alt="" />
                      <span>
                        {waypointStartLabel(activityMode, language)}
                      </span>
                      <b aria-hidden="true">›</b>
                    </button>
                    {isAdminMode && (
                      <button
                        type="button"
                        className="tk-waypoint-list-action tk-waypoint-list-action-danger"
                        onClick={() => removePartnerRecommendation(currentPartnerRecommendation)}
                      >
                        <img src={`${base}icons/trash.svg`} alt="" />
                        <span>{language === "en" ? "Remove from list" : "Remover da lista"}</span>
                      </button>
                    )}
                  </div>
                </div>
              </article>
            )}

            {visiblePartnerRecommendations.length > 1 && (
              <div style={{ display: "grid", gridTemplateColumns: "44px 1fr 44px", alignItems: "center", gap: 8, marginTop: 12 }}>
                <button className="tk-partner-recommendations-page" type="button" disabled={partnerRecommendationIndex <= 0} onClick={() => setPartnerRecommendationIndex((index) => Math.max(0, index - 1))}>‹</button>
                <div style={{ textAlign: "center", fontSize: 12, fontWeight: 700 }}>
                  {partnerRecommendationIndex + 1} / {visiblePartnerRecommendations.length}
                </div>
                <button className="tk-partner-recommendations-page" type="button" disabled={partnerRecommendationIndex >= visiblePartnerRecommendations.length - 1} onClick={() => setPartnerRecommendationIndex((index) => Math.min(visiblePartnerRecommendations.length - 1, index + 1))}>›</button>
              </div>
            )}
            <div className="tk-partner-recommendations-partner-card" style={{ marginTop: 14, padding: 12, border: "1px solid rgba(31,143,72,.3)", borderRadius: 14, background: "rgba(31,143,72,.07)" }}>
              <strong style={{ display: "block", color: "#1f6f36" }}>{language === "en" ? "Trekkin partner" : "Parceiro Trekkin"}</strong>
              <span style={{ display: "block", marginTop: 3, fontSize: 13 }}>{partnerRecommendationsPanel.name || "—"}</span>
              <button
                type="button"
                className="tk-waypoint-list-action"
                style={{ width: "100%", marginTop: 10 }}
                onClick={() => {
                  const partner = partnerRecommendationsPanel;
                  setPartnerRecommendationsPanel(null);
                  window.setTimeout(() => window.dispatchEvent(new CustomEvent("trekkin:focus-waypoint", {
                    detail: {
                      waypointId: partner.id,
                      waypointName: partner.name || partner.title,
                      lat: partner.lat ?? partner.latitude,
                      lon: partner.lon ?? partner.lng ?? partner.longitude,
                      imageUrl: partner.coverImage || partner.imageUrl,
                      description: partner.desc || partner.description,
                      tel: partner.tel || partner.phone,
                      email: partner.email,
                      www: partner.www || partner.website,
                      icon: partner.icon,
                      zoom: 16,
                      exclusive: true,
                      waypoint: partner,
                    },
                  })), 80);
                }}
              >
                <img src={`${base}icons/waypoints.svg`} alt="" />
                <span>{language === "en" ? "View partner" : "Ver parceiro"}</span>
                <b aria-hidden="true">›</b>
              </button>
            </div>
          </section>
        </div>,
        document.body
      )}

      {isAdminMode && partnerRecommendationAdminTarget && (
        <div style={{ position: "fixed", left: 12, right: 12, bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)", zIndex: 11000, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
          <div className="tk-theme-card tk-partner-recommendation-selection-card" style={{ width: "min(480px, 100%)", padding: 10, borderRadius: 14, background: "#fff", boxShadow: "0 10px 30px rgba(0,0,0,.25)", pointerEvents: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
              <strong>{language === "en" ? "Adding places to" : "A adicionar locais a"}</strong>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{partnerRecommendationAdminTarget.name || "Parceiro Trekkin"}</div>
            </div>
            <button type="button" onClick={() => {
              setPartnerRecommendationAdminTarget(null);
              setWaypointsVisible(true);
              setWaypointCategories([]);
              try {
                window.localStorage.setItem("waypointsLayerOn", "1");
              } catch {}
            }} style={{ border: "1px solid #1f8f48", borderRadius: 9, padding: "8px 12px", background: "#1f8f48", color: "#fff", fontWeight: 800 }}>
              {language === "en" ? "Finish" : "Concluir"}
            </button>
          </div>
        </div>
      )}

      {showZoomLevelLabel && zoomLevelLabel && (
        <div
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 10px) + 28px)",
            left: "calc(env(safe-area-inset-left, 0px) + 28px)",
            zIndex: 5600,
            pointerEvents: "none",
            background: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(0,0,0,0.14)",
            borderRadius: 999,
            padding: "2px 10px",
            fontSize: 12,
            fontWeight: 700,
            color: "#1f2937",
            boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
          }}
        >
          Zoom: {zoomLevelLabel}
        </div>
      )}

      {classifySegmentDraft && canManageNavGrid ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 5700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "18px",
            background: "rgba(15,23,42,0.36)",
          }}
          onClick={() => setClassifySegmentDraft(null)}
        >
          <div
            style={{
              width: "min(360px, 100%)",
              background: "#fff",
              borderRadius: 10,
              border: "1px solid rgba(15,23,42,0.12)",
              boxShadow: "0 20px 44px rgba(15,23,42,0.28)",
              padding: "16px",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginBottom: 14,
              }}
            >
              <strong style={{ fontSize: 15, color: "#0f172a" }}>
                Classificar trecho
              </strong>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {classifySegmentDraft.name || "Trecho"}
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 8,
                marginBottom: 16,
              }}
            >
              {[
                ACTIVITY_MODE_WALK,
                ACTIVITY_MODE_BIKE,
                ACTIVITY_MODE_BOTH,
              ].map((mode) => {
                const active =
                  normalizeActivityMode(classifySegmentDraft.activityMode) ===
                  mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() =>
                      setClassifySegmentDraft((prev) =>
                        prev ? { ...prev, activityMode: mode } : prev
                      )
                    }
                    style={{
                      width: "100%",
                      minHeight: 42,
                      borderRadius: 8,
                      border: active
                        ? "2px solid #198754"
                        : "1px solid rgba(15,23,42,0.16)",
                      background: active ? "#e9f7ef" : "#fff",
                      color: active ? "#14532d" : "#1f2937",
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: "pointer",
                      textAlign: "left",
                      padding: "10px 12px",
                    }}
                  >
                    {getActivityModeLabel(mode)}
                  </button>
                );
              })}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setClassifySegmentDraft(null)}
                style={{
                  border: "1px solid rgba(15,23,42,0.16)",
                  background: "#fff",
                  color: "#334155",
                  borderRadius: 8,
                  padding: "9px 12px",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveSegmentClassification}
                style={{
                  border: "none",
                  background: "#198754",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "9px 14px",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!planActive && !navWaypointTarget && navWaypointPending?.entry?.lat && navWaypointPending?.entry?.lon ? (
        <div
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 56px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 5400,
          }}
        >
          <div
            style={{
              border: "1px solid #0d6efd",
              background: "#fff",
              color: "#0d6efd",
              borderRadius: 10,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: 700,
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
              whiteSpace: "nowrap",
            }}
          >
            Segue a linha para entrar na rede
          </div>
        </div>
      ) : null}

      {activeSegmentUi?.mode === "active" || (!!nearSegmentPreview?.segmentId && !activeSegmentUi?.mode) ? (
        <div
          style={{
            position: "fixed",
            left: "50%",
            top: "calc(env(safe-area-inset-top, 0px) + 6px)",
            transform: "translateX(-50%)",
            zIndex: 5500,
            minWidth: 220,
            background: "rgba(255,255,255,0.96)",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 10,
            padding: "6px 8px",
            boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            fontSize: 11,
            color: "#1f2937",
            pointerEvents: "none",
          }}
        >
          {(() => {
            const lb = activeSegmentLeaderboard || {};
            const king = Array.isArray(lb?.maleTop) && lb.maleTop[0] ? lb.maleTop[0] : null;
            const queen = Array.isArray(lb?.femaleTop) && lb.femaleTop[0] ? lb.femaleTop[0] : null;
            const tuSec = activeSegmentUi?.mode === "active" ? (activeSegmentUi?.elapsedSec || 0) : 0;
            const segmentName = activeSegmentUi?.name || nearSegmentPreview?.name || "Segmento";
            const stateLabel = activeSegmentUi?.mode === "active" ? "SEGMENTO ATIVO" : "SEGMENTO PRÓXIMO";
            return (
              <>
                <div style={{ fontWeight: 800, color: "#166534", marginBottom: 4 }}>
                  {stateLabel}: {segmentName}
                </div>
                <div>KING: <b>{king ? formatSecondsLabel(king.bestDurationSec) : "—"}</b></div>
                <div>QUEEN: <b>{queen ? formatSecondsLabel(queen.bestDurationSec) : "—"}</b></div>
                <div>TU: <b>{formatSecondsLabel(tuSec)}</b></div>
              </>
            );
          })()}
        </div>
      ) : null}

      {segmentFinishToast ? (
        <div
          style={{
            position: "fixed",
            left: 12,
            right: 12,
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
            zIndex: 5600,
            background: "rgba(255,255,255,0.98)",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 12,
            boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
            padding: "10px 12px",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>
            Segmento concluído: {segmentFinishToast.segmentName}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: "#1f2937" }}>
            Tempo: <b>{formatSecondsLabel(segmentFinishToast.durationSec)}</b>
            {segmentFinishToast.rankOverall ? (
              <> • Classificação: <b>#{segmentFinishToast.rankOverall}</b></>
            ) : null}
          </div>
          <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => setSegmentFinishToast(null)}
              style={{
                border: "1px solid rgba(0,0,0,0.2)",
                background: "#fff",
                color: "#111827",
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Fechar
            </button>
          </div>
        </div>
      ) : null}

      {trailStagesMapState?.on || stopPlanPickActive ? (
        <div
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 12px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 5400,
          }}
        >
          <button
            type="button"
            onClick={() => {
              const trailId = String(
                trailStagesMapState?.trailId || stopPlanPickRef.current?.trailId || ""
              );
              if (typeof window !== "undefined") {
                window.dispatchEvent(
                  new CustomEvent("trekkin:trail-preview-map", {
                    detail: { on: false, trailId, points: [] },
                  })
                );
                window.dispatchEvent(
                  new CustomEvent("trekkin:trail-stop-plan-visualize", {
                    detail: { on: false, trailId, segments: [] },
                  })
                );
                window.dispatchEvent(
                  new CustomEvent("trekkin:trail-stop-plan", {
                    detail: { on: false, trailId },
                  })
                );
                window.dispatchEvent(new CustomEvent("trekkin:trail-preview-map-state"));
              }
              setActivePanel("trailDetails");
            }}
            style={{
              border: "1px solid #0d6efd",
              background: "#fff",
              color: "#0d6efd",
              borderRadius: 10,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            }}
          >
            Voltar ao planeador
          </button>
        </div>
      ) : null}

      {planActive ? (
        <div
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 56px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 5400,
          }}
        >
          <button
            type="button"
            onClick={() => setActivePanel("waypoints", { mode: "planRoute" })}
            style={{
              border: "1px solid #0d6efd",
              background: "#fff",
              color: "#0d6efd",
              borderRadius: 10,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            }}
          >
            Voltar ao Criador de Rotas
          </button>
        </div>
      ) : null}

      {navGridDrawActive && drawContext === "trail" ? (
        <div
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 100px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 5400,
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined") {
                window.dispatchEvent(
                  new CustomEvent("trekkin:trail-create-open", {
                    detail: {
                      points: (Array.isArray(drawPointsRef.current)
                        ? drawPointsRef.current
                        : []
                      ).map((pt) => ({
                        lat: Number(pt?.lat),
                        lon: Number(pt?.lon ?? pt?.lng),
                        ele: Number.isFinite(Number(pt?.ele)) ? Number(pt.ele) : undefined,
                      })),
                    },
                  })
                );
              }
              setActivePanel("nav", { mode: "trailCreate" });
            }}
            style={{
              border: "1px solid #0d6efd",
              background: "#fff",
              color: "#0d6efd",
              borderRadius: 10,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            }}
          >
            Voltar à criação de trilhos
          </button>
        </div>
      ) : null}

      {!!directionArrowSpacingM &&
        !planActive &&
        ((Array.isArray(trailBase) && trailBase.length > 1) ||
          (Array.isArray(navGridRoutePath) && navGridRoutePath.length > 1) ||
          (Array.isArray(navGridRouteColoredSegments) &&
            navGridRouteColoredSegments.some(
              (segment) => Array.isArray(segment?.path) && segment.path.length > 1
            )) ||
          (Array.isArray(routePointsRef.current?.main) &&
            routePointsRef.current.main.length > 1)) && (
        <div
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 10px) - 4px)",
            left: "calc(env(safe-area-inset-left, 0px) + 34px)",
            zIndex: 5600,
            background: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(0,0,0,0.14)",
            borderRadius: 999,
            padding: "2px 10px",
            fontSize: 12,
            color: "#1f2937",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: 8,
            boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
          }}
        >
          <span
            style={{
              fontSize: 13,
              color: "#0d6efd",
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            ▲
          </span>
          <span
            style={{
              width: 28,
              height: 0,
              borderTop: "2px solid rgba(15,23,42,0.65)",
              display: "inline-block",
            }}
          />
          <span style={{ fontWeight: 600 }}>{directionArrowSpacingM} m</span>
        </div>
      )}

      {navGridEditNodesActive && (
        <div
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 12px)",
            left: "calc(env(safe-area-inset-left, 0px) + 12px)",
            width: "min(90%, 300px)",
            background: "rgba(255,255,255,0.95)",
            borderRadius: 16,
            padding: "12px 14px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            border: "1px solid rgba(0,0,0,0.08)",
            zIndex: 5200,
            pointerEvents: "auto",
            fontSize: 13,
            color: "#1b4332",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Edição ativa</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            {[
              { mode: "move", label: "Mover nó", color: "#1b6e40" },
              { mode: "add", label: "Adicionar nó", color: "#0d6efd" },
              { mode: "delete", label: "Eliminar nó", color: "#b02a37" },
              { mode: "split", label: "Dividir", color: "#ff8f00" },
            ].map(({ mode, label, color }) => {
              const isActive = navGridNodeMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setNodeMode(mode)}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: isActive ? "none" : "1px solid rgba(0,0,0,0.2)",
                    background: isActive ? color : "#f8f9fa",
                    color: isActive ? "white" : "#222",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={closeNavGridEditMode}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 10,
              border: "none",
              background: "#0f5132",
              color: "white",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Encerrar edição
          </button>
        </div>
      )}

      {(!(
        currentPanel?.name === "nav" &&
        currentPanel?.props?.mode === "trailCreate" &&
        drawContext === "trail"
      ) &&
        ((navGridDrawActive &&
        (drawContext === "trail" ||
          drawContext === "eventAlt" ||
          drawContext === "eventMain" ||
          drawContext === "navgrid")) ||
        trailEditActive)) && (
        <div
          style={{
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 12px)",
            left: "calc(env(safe-area-inset-left, 0px) + 12px)",
            width: "min(90%, 260px)",
            background: "rgba(255,255,255,0.95)",
            borderRadius: 16,
            padding: "10px 12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            border: "1px solid rgba(0,0,0,0.08)",
            zIndex: 5200,
            pointerEvents: "auto",
            fontSize: 13,
            color: "#1b4332",
          }}
        >
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={undoTrailDraw}
              style={{
                flex: 1,
                padding: "7px 8px",
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.2)",
                background: "#f8f9fa",
                color: "#222",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              Voltar
            </button>
            <button
              type="button"
              onClick={clearTrailDraw}
              style={{
                flex: 1,
                padding: "7px 8px",
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.2)",
                background: "#f8f9fa",
                color: "#222",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={cancelTrailDraw}
              style={{
                flex: 1,
                padding: "7px 8px",
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.2)",
                background: "#f8f9fa",
                color: "#222",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              Cancelar
            </button>
            {drawContext !== "trail" && (
              <button
                type="button"
                onClick={saveTrailDraw}
                style={{
                  flex: 1,
                  padding: "7px 8px",
                  borderRadius: 10,
                  border: "none",
                  background: "#0f5132",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                Gravar
              </button>
            )}
          </div>
        </div>
      )}

      {proximityAlert && (
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 110px)",
            transform: "translateX(-50%)",
            width: "min(92%, 360px)",
            background: "rgba(255,255,255,0.96)",
            borderRadius: 16,
            padding: "12px 14px",
            boxShadow: "0 10px 28px rgba(0,0,0,0.22)",
            border: "1px solid rgba(0,0,0,0.08)",
            zIndex: 6200,
            pointerEvents: "auto",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1b4332" }}>
            Alerta proximo
          </div>
          <div style={{ fontSize: 13, color: "#2c3e50", marginTop: 4 }}>
            {proximityAlert.entry?.label || "Alerta"}
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            {ALERT_CATEGORY_LABELS[proximityAlert.entry?.category] ||
              proximityAlert.entry?.category ||
              "Alerta"}
            {proximityAlert.distance != null ? ` • ${proximityAlert.distance} m` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              type="button"
              onClick={() => voteProximityAlert("up")}
              style={{
                flex: 1,
                padding: "8px 10px",
                borderRadius: 10,
                border: "none",
                background: "#1f7a3a",
                color: "white",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Confirmar
            </button>
            <button
              type="button"
              onClick={() => voteProximityAlert("down")}
              style={{
                flex: 1,
                padding: "8px 10px",
                borderRadius: 10,
                border: "none",
                background: "#d9534f",
                color: "white",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Nao existe
            </button>
            <button
              type="button"
              onClick={closeProximityAlert}
              style={{
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.2)",
                background: "#f8f9fa",
                color: "#333",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          position: "fixed",
          top: "env(safe-area-inset-top, 10px)",
          left: 14,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: hudColor,
          boxShadow: "0 0 6px rgba(0,0,0,0.3)",
          zIndex: 6000,
        }}
      />
    </>
  );
}
